

import PageHeader from "../components/shell/PageHeader";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  useDeferredValue,
} from "react";
import {
  RefreshCw,
  Download,
  PlugZap,
  Unplug,
  CheckCircle2,
  AlertTriangle,
  LineChart as LineIcon,
  Filter,
  Star,
  Pencil,
  Save,
  X,
  ArrowUpDown,
  Search,
  Globe,
  Clock,
  Calendar,
  Sigma,
  FileText,
  BookOpenText,
  ImagePlus,
  Trash2,
  Eye,
  Wallet,
  SendHorizontal,
  TrendingUp,
  Layers,
  ListChecks,
  Server,
  Hash,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Check,
  Activity,
  // Point d'interrogation, pas le « i » d'information : la pastille ouvre une
  // explication, elle ne signale pas un avertissement.
  CircleHelp,
} from "lucide-react";
import { usePageNav } from "../components/shell/PageNav";
import useIsDesktop from "../hooks/useIsDesktop";

import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  ScatterChart,
  Scatter,
  ComposedChart,
  Area,
  ReferenceLine,
  RadarChart,
  Radar,
  LabelList,
  Label,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";

import {
  API_ROOT,
  getToken,
  apiAgentStatus,
  apiAgentOpenTrades,
  apiAgentRegisterExisting,
  apiAgentGetNotes,
  apiAgentUpsertNote,
} from "../api/api";

// Données de démo du journal — inertes hors NODE_ENV=development (voir module).
import { isMockJournalEnabled, buildMockJournalData } from "../dev/mockJournal";

import { getChartHelp } from "../data/chartHelp";
import {
  applyDisplayOffset,
  getDisplayOffsetMin,
  setDisplayOffsetMin,
  offsetLabel,
  browserOffsetMin,
  OFFSET_CHOICES,
} from "../utils/displayTimezone";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { flushSync, createPortal } from "react-dom";

// Sous-menu de page (Trades / Agenda / Positions / Stats / Coach).
// Sous lg : exposé par le shell via le bouton à droite de la barre du haut,
// qui l'ouvre en drawer depuis la droite (voir usePageNav). Référence stable.
const JOURNAL_TABS = [
  { key: "overview", label: "Aperçu", icon: Activity },
  { key: "trades", label: "Journal", icon: ListChecks },
  { key: "calendar", label: "Agenda", icon: Calendar },
  { key: "positions", label: "Positions", icon: Layers },
  { key: "charts", label: "Stats", icon: TrendingUp },
  { key: "coach", label: "Coach", icon: Sigma },
];

/* ---------------- i18n resources (inline; déplace-les en /locales si tu veux) ---------------- */
const fr = {
  // En-tête de page, bande KPI et onglets : c'était écrit en dur dans le JSX,
  // donc figé en français quelle que soit la langue choisie.
  page: {
    title: "Journal Auto",
    subtitle:
      "Bascule rapide entre journal et graphiques, calendrier intégré, notes cloud et analytics.",
    connected: "Connecté",
    waiting: "En attente",
    lastPing: "Dernier ping",
    server: "Serveur",
    account: "Compte",
    kpiBalance: "Balance",
    kpiEquity: "Equity",
    kpiMargin: "Margin",
    kpiPositions: "Positions",
    kpiPending: "En attente",
    kpiMaxDD: "Max DD",
    hintRealtime: "Mise à jour en temps réel",
    hintWaitingAgent: "En attente de l’agent",
    hintFloat: "Float",
    hintUsage: "Utilisation",
    hintOpen: "Ouvertes",
    hintPendingOrders: "Ordres en attente",
    hintLastPosition: "Dernière position",
    tabJournal: "Journal",
    tabCalendar: "Calendrier",
    tabCalendarShort: "Agenda",
    tabPositions: "Positions",
    tabStats: "Statistiques",
    tabStatsShort: "Stats",
    tabCoach: "Coach",
    statsOpening: "Ouverture des statistiques… les graphiques avancés arrivent.",
    statsPreparing: "Préparation des graphiques avancés…",
    noClosedTrade: "Aucun trade clôturé à analyser",
    noClosedTradeNow: "Aucun trade clôturé à analyser pour le moment.",
    imagesCapped: "Seules les 6 premières images sont conservées.",
    notedShort: "notés",
    noted: "Notés",
    peakPerDay: "Pic/jour",
    duration: "Durée",
    firstPage: "Première page",
    prevPage: "Page précédente",
    nextPage: "Page suivante",
    lastPage: "Dernière page",
    rowsPerPage: "Lignes par page",
    showingRange: "Affichage {{from}} à {{to}} sur {{total}} résultats",
    high: "Élevé",
    upNext: "À suivre",
    protectionRules: "Règles de protection",
    entryChecklist: "Checklist avant entrée",
    nextTradeRules: "Règles du prochain trade",
    setupPlaceholder: "Décris le setup, le contexte ou la logique d’entrée",
    tagsHint:
      "Sépare les tags par des virgules pour retrouver plus vite ce trade.",
    deletePreset: "Supprimer ce tag prédéfini",
    commentPlaceholder:
      "Explique ce qui s’est passé, ce qui a bien marché, ce qu’il faut éviter, et ce que montre le graphique.",
    closePreview: "Fermer l’aperçu",
    notAuthenticated: "Non authentifié (401).",
    infoBalance:
      "Solde du compte, positions ouvertes exclues. Il ne bouge qu’à la clôture d’un trade, ou lors d’un dépôt ou d’un retrait.",
    infoEquity:
      "Solde plus le résultat flottant de tes positions encore ouvertes. C’est la valeur réelle du compte à l’instant présent : si tu fermais tout maintenant, c’est ce qu’il te resterait.",
    infoMargin:
      "Capital immobilisé par tes positions ouvertes, exigé par le broker en garantie. Il redevient disponible à la clôture.",
    infoPositions:
      "Nombre de positions actuellement ouvertes sur le terminal connecté.",
    infoPending:
      "Ordres placés mais pas encore déclenchés : limites et stops en attente du prix.",
    infoMaxDD:
      "Drawdown maximal du compte calculé à partir de l’historique MT5 réel. Les dépôts et retraits sont neutralisés afin qu’un mouvement de capital ne soit jamais pris pour une perte ou un gain de trading. Le calcul inclut les creux réalisés du solde et l’equity flottante actuelle.",
    scopeActive: "Périmètre actif",
    scopeCount: "{{count}} trades retenus",
    ddAllTime: "Sur tout l’historique",
    ddAllTimeShort: "historique",
    ddMt5Verified: "Historique MT5",
    ddLastDays: "Sur les {{days}} derniers jours",
  },
  header: {
    title: "Journal Auto",
    subtitle:
      "Suivi automatique des positions, statistiques, calendrier de trading et journal intelligent.",
    dlBtn: "Agent (EX5)",
  },
  token: {
    label: "Agent Token",
    ph: "Colle ici le token",
    save: "Enregistrer",
    generate: "Générer",
    copy: "Copier",
    copied: "Token copié !",
    disable: "Désactiver",
    stored: "Token enregistré localement.",
    created: "Nouveau token généré.",
    empty: "Token vide.",
    cannotActivate: "Impossible d’activer ce token",
    unauth: "Non authentifié.",
    apiDown: "API injoignable (URL/CORS).",
    createFail: "Erreur création token",
    disabled: "Token désactivé.",
  },
  status: {
    autoRefresh: "Auto-refresh optimisé",
    on: "Actif",
    off: "Inactif",
    refresh: "Actualiser",
    lastPing: "Dernier ping",
    waitingPing: "En attente de ping…",
    tokenState: "Token",
    tokenPresent: "présent (local)",
    tokenMissing: "aucun",
    ddAlert: "Alerte DD ≥ {{pct}}%",
    lossAlert: "Alerte {{streak}}+ pertes",
    connected: "Connecté",
    waiting: "En attente de l’agent…",
    kpi: {
      balance: "Balance",
      equity: "Equity",
      margin: "Margin",
      positions: "# Positions en cours",
      pendings: "# En attente",
      maxdd: "Max DD",
      sharpe: "Sharpe (≈)",
    },
  },
  coachUi: {
    loadingTitle: "Je lis ton journal",
    loadingBody:
      "J’analyse tes trades clôturés, tes pertes répétées, tes meilleures zones et les règles que je dois te recommander.",
    ariaPanel: "Audit professionnel du journal",
    brandBadge: "FullMetrix IA · Audit performance",
    analysisBadge: "Analyse FullMetrix IA",
    doneTitle: "Je viens d’analyser ton journal",
    scoreLabel: "Score",
    riskInline: "Risque {{level}}",
    statScore: "Score global",
    statProfile: "Profil",
    statRisk: "Risque",
    statConfidence: "Confiance",
    tagDiagnostic: "Diagnostic",
    tagStrength: "Zone forte",
    tagAlert: "Alerte",
    close: "Fermer l’audit",
  },
  charts: {
    curve: "Courbe P&L (clos)",
    more: "Plus de graphiques",
    moreHide: "Masquer les graphiques",
    calendar: "Calendrier",
    calendarHide: "Masquer calendrier",
    pnlDist: "Distribution des résultats (par trade)",
    heatmap: "P&L par jour et heure",
    hourPnl: "Performance par heure d’entrée",
    dayPnl: "Performance par jour de la semaine",
    tagPnl: "Performance par stratégie / tag",
    durVsProfit: "Durée des trades vs performance",
    rDist: "Distribution des R-multiples",
    heatLegend:
      "Rouge (−) → Vert (+), moyenne par case. Gris = aucun trade sur ce créneau.",
    displayTz: "Fuseau d'affichage",
    displayTzLocal: "Heure locale",
    tracker: "Trackeur de progrès",
    avgWinLoss: "Moyenne gains / pertes",
    winLossSplit: "Répartition gains / pertes",
    monthlyStats: "Stat mensuelles",
    radarScale: "Échelle : 0 (faible) → 100 (excellent)",
    radarOverall: "Score global",
    weekAxis: "Sem.",
    trackerPick: "Clique une case pour voir le détail du jour.",
    trackerDay: "Détail du jour",
    trackerNoTrade: "Aucun trade ce jour-là.",
    axisPerf: "Performance",
    axisDuration: "Durée du trade",
    axisAvgPnl: "P&L moyen (USD)",
    axisCumPnl: "P&L cumulé (USD)",
    axisEntryHour: "Heure d’entrée",
    winners: "Gagnants",
    losers: "Perdants",
    monthGains: "Gains",
    monthLosses: "Pertes",
    netLabel: "Net",
    tradesCount: "Trades",
    avgShort: "Moyenne",
    cumShort: "Cumul",
  },
  statsFilters: {
    market: "Marché",
    side: "Type d’ordre",
    from: "Du",
    to: "Au",
    quick: "Raccourcis",
  },
  stats: {
    total: "Trades clos",
    winrate: "Win rate",
    avg: "Gain moyen",
    streakW: "Streak G",
    streakL: "Streak P",
    performanceTitle: "Cockpit performance",
    bestTrade: "Meilleur trade",
    worstTrade: "Pire trade",
    recovery: "Recovery",
    netBadge: "net",
    realizedGains: "Gains réalisés",
    realizedGainsSub: "Trades gagnants clôturés",
    realizedLosses: "Pertes réalisées",
    realizedLossesSub: "Trades perdants clôturés",
    flow: "flux",
    scoreRadar: "Radar de score",
    radarWin: "Win",
    radarPF: "PF",
    radarExpect: "Expect.",
    radarRecovery: "Recovery",
    radarDD: "DD",
    radarStreak: "Streak",
  },
  // Descriptions affichées par les pastilles ⓘ. Séparées des libellés :
  // ce sont des phrases, pas des étiquettes, et elles vivent leur propre vie.
  infos: {
    closed:
      "Nombre total de trades qui ont été entièrement fermés sur la période sélectionnée.",
    winrate:
      "Pourcentage de trades gagnants parmi les trades clôturés. (Nombre de trades gagnants ÷ nombre total de trades clos × 100)",
    streakW:
      "C’est le plus grand nombre de trades gagnants consécutifs enregistré sur la période.",
    streakL:
      "C’est le plus grand nombre de trades perdants consécutifs enregistré sur la période.",
    bestTrade:
      "Résultat positif le plus élevé obtenu sur un seul trade clôturé pendant la période.",
    worstTrade:
      "Perte la plus importante enregistrée sur un seul trade clôturé pendant la période.",
    sharpe:
      "La performance obtenue par rapport à la volatilité ou à l’irrégularité des résultats. (plus la valeur est élevée, mieux c’est…)",
    recovery:
      "Mesure la capacité du compte à générer du profit par rapport à son drawdown maximal. (plus la valeur est élevée, mieux c’est…)",
    profitFactor:
      "Comparaison des gains bruts aux pertes brutes. (plus la valeur est élevée, mieux c’est…)",
    expectancy:
      "Le résultat moyen attendu par trade à partir des données historiques : (taux de réussite × gain moyen) − (taux de perte × perte moyenne). Une expectancy positive indique un avantage statistique historique. Elle ne garantit pas les résultats futurs.",
    avgWin: "La moyenne des résultats de tous les trades gagnants.",
    avgLoss: "La moyenne des résultats de tous les trades perdants.",
  },
  calendarBox: {
    title: "Calendrier {{monthYear}}",
    clickHint: "Clique une case pour voir le détail de ce jour.",
    dayDetail: "Détail du jour",
    pickDay: "Clique une date pour voir le détail de la journée.",
    net: "P&L net",
    trades: "Trades",
    winRate: "Taux de réussite",
    bestWorst: "Meilleur / pire",
    tradedDays: "Jours tradés",
    gains: "Gains",
    losses: "Pertes",
    avgPerTradedDay: "Moy. / jour",
    avgPerTradedDayFull: "Moyenne par jour tradé",
    monthWinRate: "Win rate",
  },
  positions: {
    openSection: "Positions ouvertes",
    manage: "Gérer les positions",
    none: "Aucune position ouverte.",
    ticket: "Ticket",
    symbol: "Symbole",
    side: "Sens",
    vol: "Vol.",
    open: "Ouverture",
    openPrice: "Prix O.",
    currentPrice: "Prix actuel",
    sl: "SL",
    tp: "TP",
    pnl: "PNL",
  },
  pendings: {
    title: "Ordres en attente",
    count: "{{n}} ordre(s)",
    none: "Aucun ordre en attente.",
    type: "Type",
    price: "Prix",
  },
  journal: {
    title: "Journal des trades",
    pnlFiltered: "P&L filtré",
    since: "Depuis le {date}",
    csv: "CSV",
    exporting: "Export en cours…",
    exported: "CSV exporté.",
    searchPh: "Rechercher (ticket, symbole)",
    allSymbols: "Tous symboles",
    allSides: "Tous sens",
    allResults: "Tous résultats",
    allAccounts: "Tous les comptes",
    resultBe: "Neutres",
    deposit: "Dépôt",
    withdrawal: "Retrait",
    cashflow: "Mouvement de compte",
    cashflowAmount: "Montant",
    reset: "Réinitialiser",
    none: "Pas encore d’historique.",
    open: "Ouverture",
    openP: "Prix O.",
    close: "Fermeture",
    closeP: "Prix F.",
    profit: "Profit",
    tags: "Tags",
    note: "Note",
    vol: "Vol.",
  },
  note: {
    title: "Note —",
    comment: "Commentaire",
    tags: "Tags (séparés par des virgules)",
    setup: "Setup",
    rating: "Évaluation",
    save: "Sauver",
    cancel: "Annuler",
    savedRemote: "Note enregistrée.",
    savedLocal: "Note sauvegardée localement (backend indisponible).",
  },
  ea: {
    title: "Installer l’Expert Advisor",
    s1: "Télécharge",
    file: "FullMetrixAgent.ex5",
    place: "et place-le dans",
    dir: "MQL5/Experts",
    s2: "Dans MT5 : Navigateur → Experts → glisse sur un graphique.",
    s3: "Renseigne l’AgentToken (ci-dessus) et dans l’EA (input AgentToken).",
    s4: "Autorise Tools → Options → Expert Advisors → Allow WebRequest pour l’URL du serveur fullmetrix.",
  },
  exec: {
    fab: "Exécuter",
    title: "Exécution d’ordres",
    noToken: "Aucun AgentToken actif. Enregistre ton token en haut de la page.",
    tabs: {
      market: "Market",
      pending: "Ordre en attente",
      modify: "Modifier SL/TP",
      close: "Fermer",
      tools: "Outils",
    },
    market: {
      symbol: "Symbole",
      volume: "Volume (lot)",
      sl: "SL (points)",
      tp: "TP (points)",
      buy: "Acheter",
      sell: "Vendre",
      lotCalc: "Calculateur de lot (approx.)",
      risk: "Risk %",
      balOpt: "Balance (optionnel)",
      pointVal: "Valeur du point ($/lot)",
      lotSug: "Lot suggéré",
      hint: "Hypothèse FX standard : ~10$ / point / lot. Ajuste pour métaux/indices.",
    },
    pending: {
      type: "Type",
      price: "Prix",
      place: "Placer l’ordre",
      types: {
        buy_limit: "Buy Limit",
        sell_limit: "Sell Limit",
        buy_stop: "Buy Stop",
        sell_stop: "Sell Stop",
      },
    },
    modify: {
      scope: "Portée",
      symbol: "Symbole",
      all: "Tous",
      do: "Modifier SL/TP",
    },
    close: {
      ticketOpt: "Ticket (optionnel)",
      ticketPh: "ex: 12345678",
      ticketHint:
        "⚠️ Scope batch = symbole/tous (pas ticket). Ce champ ferme un ticket précis.",
      symbol: "Symbole",
      closeTicket: "Fermer ticket",
      closeSymbol: "Fermer symbole",
      closeAll: "Tout fermer",
    },
    tools: {
      be: "Break-even",
      trailing: "Trailing stop",
      scope: "Portée",
      symbol: "Symbole",
      all: "Tous",
      offset: "Offset (points)",
      onlyProfit: "Seulement si en gain",
      setBE: "Mettre au BE",
      trailingPts: "Trailing (points)",
      trEnable: "Activer/Mettre à jour",
      trDisable: "Désactiver",
    },
    sending: "Envoi en cours…",
  },
  misc: {
    yes: "Oui",
    no: "Non",
    symbolShort: "Symb.",
    volShort: "Vol",
    openShort: "Ouv.",
    priceShort: "Prix",
    none: "—",
    connectedDetail: " — {{server}} • {{login}}",
  },
};
const en = {
  page: {
    title: "Auto Journal",
    subtitle:
      "Quick switch between journal and charts, built-in calendar, cloud notes and analytics.",
    connected: "Connected",
    waiting: "Waiting",
    lastPing: "Last ping",
    server: "Server",
    account: "Account",
    kpiBalance: "Balance",
    kpiEquity: "Equity",
    kpiMargin: "Margin",
    kpiPositions: "Positions",
    kpiPending: "Pending",
    kpiMaxDD: "Max DD",
    hintRealtime: "Updating in real time",
    hintWaitingAgent: "Waiting for the agent",
    hintFloat: "Float",
    hintUsage: "Usage",
    hintOpen: "Open",
    hintPendingOrders: "Pending orders",
    hintLastPosition: "Last position",
    tabJournal: "Journal",
    tabCalendar: "Calendar",
    tabCalendarShort: "Calendar",
    tabPositions: "Positions",
    tabStats: "Statistics",
    tabStatsShort: "Stats",
    tabCoach: "Coach",
    statsOpening: "Opening statistics… advanced charts are on their way.",
    statsPreparing: "Preparing advanced charts…",
    noClosedTrade: "No closed trade to analyse",
    noClosedTradeNow: "No closed trade to analyse right now.",
    imagesCapped: "Only the first 6 images are kept.",
    notedShort: "noted",
    noted: "Noted",
    peakPerDay: "Peak/day",
    duration: "Duration",
    firstPage: "First page",
    prevPage: "Previous page",
    nextPage: "Next page",
    lastPage: "Last page",
    rowsPerPage: "Rows per page",
    showingRange: "Showing {{from}} to {{to}} of {{total}} results",
    high: "High",
    upNext: "Up next",
    protectionRules: "Protection rules",
    entryChecklist: "Pre-entry checklist",
    nextTradeRules: "Next trade rules",
    setupPlaceholder: "Describe the setup, the context or the entry logic",
    tagsHint: "Separate tags with commas to find this trade faster.",
    deletePreset: "Delete this preset tag",
    commentPlaceholder:
      "Explain what happened, what worked, what to avoid, and what the chart shows.",
    closePreview: "Close preview",
    notAuthenticated: "Not authenticated (401).",
    infoBalance:
      "Account balance, open positions excluded. It only moves when a trade closes, or on a deposit or withdrawal.",
    infoEquity:
      "Balance plus the floating result of your still-open positions. This is what the account is really worth right now: close everything and this is what you keep.",
    infoMargin:
      "Capital locked by your open positions, required by the broker as collateral. It frees up when they close.",
    infoPositions: "Number of positions currently open on the connected terminal.",
    infoPending:
      "Orders placed but not yet triggered: limits and stops waiting for price.",
    infoMaxDD:
      "Maximum account drawdown calculated from real MT5 history. Deposits and withdrawals are neutralized so an external cash movement is never treated as a trading gain or loss. The calculation includes realized balance dips and the current floating equity dip.",
    scopeActive: "Active scope",
    scopeCount: "{{count}} trades kept",
    ddAllTime: "Across the full history",
    ddAllTimeShort: "all-time",
    ddMt5Verified: "MT5 history",
    ddLastDays: "Over the last {{days}} days",
  },
  header: {
    title: "MT5 Agent — Connection & Journal",
    subtitle:
      "Local token management (legacy). Cloud notes, equity curve, pending orders (no expiry), analytics.",
    dlBtn: "Agent (EX5)",
  },
  token: {
    label: "Agent Token",
    ph: "Paste token here",
    save: "Save",
    generate: "Generate",
    copy: "Copy",
    copied: "Token copied!",
    disable: "Disable",
    stored: "Token saved locally.",
    created: "New token generated.",
    empty: "Empty token.",
    cannotActivate: "Unable to activate this token",
    unauth: "Unauthenticated.",
    apiDown: "API unreachable (URL/CORS).",
    createFail: "Token creation error",
    disabled: "Token disabled.",
  },
  status: {
    autoRefresh: "Optimized auto-refresh",
    on: "On",
    off: "Off",
    refresh: "Refresh",
    lastPing: "Last ping",
    waitingPing: "Waiting for ping…",
    tokenState: "Token",
    tokenPresent: "present (local)",
    tokenMissing: "none",
    ddAlert: "DD Alert ≥ {{pct}}%",
    lossAlert: "Loss streak {{streak}}+",
    connected: "Connected",
    waiting: "Waiting for agent…",
    kpi: {
      balance: "Balance",
      equity: "Equity",
      margin: "Margin",
      positions: "# Positions",
      pendings: "# Pending",
      maxdd: "Max DD",
      sharpe: "Sharpe (≈)",
    },
  },
  coachUi: {
    loadingTitle: "Reading your journal",
    loadingBody:
      "I am reviewing your closed trades, your repeated losses, your best windows and the rules I should recommend.",
    ariaPanel: "Professional journal audit",
    brandBadge: "FullMetrix AI · Performance audit",
    analysisBadge: "FullMetrix AI analysis",
    doneTitle: "I have analysed your journal",
    scoreLabel: "Score",
    riskInline: "{{level}} risk",
    statScore: "Overall score",
    statProfile: "Profile",
    statRisk: "Risk",
    statConfidence: "Confidence",
    tagDiagnostic: "Diagnosis",
    tagStrength: "Strong zone",
    tagAlert: "Alert",
    close: "Close audit",
  },
  charts: {
    curve: "P&L Curve (closed)",
    more: "More charts",
    moreHide: "Hide charts",
    calendar: "Calendar",
    calendarHide: "Hide calendar",
    pnlDist: "Profit distribution (per trade)",
    heatmap: "P&L Heatmap — Day × Hour (open)",
    hourPnl: "P&L by hour (open)",
    dayPnl: "P&L by weekday",
    tagPnl: "P&L by tag (Top 12)",
    durVsProfit: "Duration (min) vs Profit",
    rDist: "R-multiple distribution",
    heatLegend:
      "Red (−) → Green (+), mean per cell. Grey = no trade in that slot.",
    displayTz: "Display timezone",
    displayTzLocal: "Local time",
    tracker: "Progress tracker",
    avgWinLoss: "Average wins / losses",
    winLossSplit: "Wins / losses split",
    monthlyStats: "Monthly stats",
    radarScale: "Scale: 0 (weak) → 100 (excellent)",
    radarOverall: "Overall score",
    weekAxis: "Wk.",
    trackerPick: "Click a cell to see that day in detail.",
    trackerDay: "Day detail",
    trackerNoTrade: "No trade that day.",
    axisPerf: "Performance",
    axisDuration: "Trade duration",
    axisAvgPnl: "Average P&L (USD)",
    axisCumPnl: "Cumulative P&L (USD)",
    axisEntryHour: "Entry hour",
    winners: "Winners",
    losers: "Losers",
    monthGains: "Gains",
    monthLosses: "Losses",
    netLabel: "Net",
    tradesCount: "Trades",
    avgShort: "Average",
    cumShort: "Total",
  },
  statsFilters: {
    market: "Market",
    side: "Order type",
    from: "From",
    to: "To",
    quick: "Shortcuts",
  },
  stats: {
    total: "Closed trades",
    winrate: "Win rate",
    avg: "Avg gain",
    streakW: "Win streak",
    streakL: "Loss streak",
    performanceTitle: "Performance cockpit",
    bestTrade: "Best trade",
    worstTrade: "Worst trade",
    recovery: "Recovery",
    netBadge: "net",
    realizedGains: "Realized gains",
    realizedGainsSub: "Closed winning trades",
    realizedLosses: "Realized losses",
    realizedLossesSub: "Closed losing trades",
    flow: "flow",
    scoreRadar: "Score radar",
    radarWin: "Win",
    radarPF: "PF",
    radarExpect: "Expect.",
    radarRecovery: "Recovery",
    radarDD: "DD",
    radarStreak: "Streak",
  },
  infos: {
    closed:
      "Total number of trades fully closed over the selected period.",
    winrate:
      "Share of winning trades among closed trades. (winning trades ÷ total closed trades × 100)",
    streakW: "Longest run of consecutive winning trades over the period.",
    streakL: "Longest run of consecutive losing trades over the period.",
    bestTrade:
      "Highest positive result achieved on a single closed trade during the period.",
    worstTrade:
      "Largest loss recorded on a single closed trade during the period.",
    sharpe:
      "Performance relative to the volatility — the irregularity — of the results. The higher, the better.",
    recovery:
      "How much profit the account generates relative to its maximum drawdown. The higher, the better.",
    profitFactor: "Gross profit compared to gross loss. The higher, the better.",
    expectancy:
      "Average expected result per trade from historical data: (win rate × average win) − (loss rate × average loss). A positive expectancy indicates a historical statistical edge. It does not guarantee future results.",
    avgWin: "Average result across all winning trades.",
    avgLoss: "Average result across all losing trades.",
  },
  calendarBox: {
    title: "Calendar {{monthYear}}",
    clickHint: "Click a cell to see that day in detail.",
    dayDetail: "Day detail",
    pickDay: "Click a date to see that day in detail.",
    net: "Net P&L",
    trades: "Trades",
    winRate: "Win rate",
    bestWorst: "Best / worst",
    tradedDays: "Traded days",
    gains: "Gains",
    losses: "Losses",
    avgPerTradedDay: "Avg. / day",
    avgPerTradedDayFull: "Average per traded day",
    monthWinRate: "Win rate",
  },
  positions: {
    openSection: "Open positions",
    manage: "Manage positions",
    none: "No open positions.",
    ticket: "Ticket",
    symbol: "Symbol",
    side: "Side",
    vol: "Vol.",
    open: "Open",
    openPrice: "Open Px",
    currentPrice: "Current Px",
    sl: "SL",
    tp: "TP",
    pnl: "PNL",
  },
  pendings: {
    title: "Pending orders",
    count: "{{n}} order(s)",
    none: "No pending orders.",
    type: "Type",
    price: "Price",
  },
  journal: {
    title: "Trade journal",
    pnlFiltered: "Filtered P&L",
    since: "Since {date}",
    csv: "CSV",
    exporting: "Exporting…",
    exported: "CSV exported.",
    searchPh: "Search (ticket, symbol)",
    allSymbols: "All symbols",
    allSides: "All sides",
    allResults: "All results",
    allAccounts: "All accounts",
    resultBe: "Break-even",
    deposit: "Deposit",
    withdrawal: "Withdrawal",
    cashflow: "Account movement",
    cashflowAmount: "Amount",
    reset: "Reset",
    none: "No history yet.",
    open: "Open",
    openP: "Open Px",
    close: "Close",
    closeP: "Close Px",
    profit: "Profit",
    tags: "Tags",
    note: "Note",
    vol: "Vol.",
  },
  note: {
    title: "Note —",
    comment: "Comment",
    tags: "Tags (comma-separated)",
    setup: "Setup",
    rating: "Rating",
    save: "Save",
    cancel: "Cancel",
    savedRemote: "Note saved.",
    savedLocal: "Note saved locally (backend unavailable).",
  },
  ea: {
    title: "Install the Expert Advisor",
    s1: "Download",
    file: "FullMetrixAgent.ex5",
    place: "and put it in",
    dir: "MQL5/Experts",
    s2: "In MT5: Navigator → Experts → drop it on a chart.",
    s3: "Fill the AgentToken (above) and in the EA (input AgentToken).",
    s4: "Allow Tools → Options → Expert Advisors → Allow WebRequest server URL.",
  },
  exec: {
    fab: "Execute",
    title: "Order execution",
    noToken: "No active AgentToken. Save your token at the top of the page.",
    tabs: {
      market: "Market",
      pending: "Pending order",
      modify: "Modify SL/TP",
      close: "Close",
      tools: "Tools",
    },
    market: {
      symbol: "Symbol",
      volume: "Volume (lot)",
      sl: "SL (points)",
      tp: "TP (points)",
      buy: "Buy",
      sell: "Sell",
      lotCalc: "Lot calculator (approx.)",
      risk: "Risk %",
      balOpt: "Balance (optional)",
      pointVal: "Point value ($/lot)",
      lotSug: "Suggested lot",
      hint: "FX assumption: ~$10 / point / lot. Adjust for metals/indices.",
    },
    pending: {
      type: "Type",
      price: "Price",
      place: "Place order",
      types: {
        buy_limit: "Buy Limit",
        sell_limit: "Sell Limit",
        buy_stop: "Buy Stop",
        sell_stop: "Sell Stop",
      },
    },
    modify: {
      scope: "Scope",
      symbol: "Symbol",
      all: "All",
      do: "Apply SL/TP",
    },
    close: {
      ticketOpt: "Ticket (optional)",
      ticketPh: "e.g. 12345678",
      ticketHint:
        "⚠️ Batch scope = symbol/all (not ticket). This field closes a single ticket.",
      symbol: "Symbol",
      closeTicket: "Close ticket",
      closeSymbol: "Close symbol",
      closeAll: "Close all",
    },
    tools: {
      be: "Break-even",
      trailing: "Trailing stop",
      scope: "Scope",
      symbol: "Symbol",
      all: "All",
      offset: "Offset (points)",
      onlyProfit: "Only if in profit",
      setBE: "Set BE",
      trailingPts: "Trailing (points)",
      trEnable: "Enable/Update",
      trDisable: "Disable",
    },
    sending: "Sending…",
  },
  misc: {
    yes: "Yes",
    no: "No",
    symbolShort: "Sym.",
    volShort: "Vol",
    openShort: "Open",
    priceShort: "Price",
    none: "—",
    connectedDetail: " — {{server}} • {{login}}",
  },
};
// Enregistrement inconditionnel, en deep + overwrite. Le garde
// `hasResourceBundle` d'avant sautait l'appel dès le second passage du module :
// au hot-reload, toute clé ajoutée depuis le premier chargement restait absente
// et s'affichait en brut ("charts.avgShort") jusqu'au rechargement complet.
i18n.addResourceBundle("fr", "agent", fr, true, true);
i18n.addResourceBundle("en", "agent", en, true, true);

/* ---------------- utils ---------------- */
const cn = (...x) => x.filter(Boolean).join(" ");
const PREMIUM_FONT_STACK =
  'Inter, "SF Pro Text", "SF Pro Display", "Manrope", "Segoe UI", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif';
const PREMIUM_MONO_STACK =
  '"SF Mono", "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';
const premiumTextStyle = {
  fontFamily: PREMIUM_FONT_STACK,
  letterSpacing: "-0.004em",
};
const premiumMonoStyle = {
  fontFamily: PREMIUM_MONO_STACK,
  letterSpacing: "-0.01em",
};
const SPRING_FX = { type: "spring", stiffness: 420, damping: 28, mass: 0.85 };
const springFX = SPRING_FX;
const isValidDateInput = (s) =>
  typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
const toNumber = (v, def = 0) => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : def;
};
const clamp = (n, a, b) => Math.min(b, Math.max(a, n));
const chooseBySeed = (options = [], seed = 0, fallback = "") => {
  if (!Array.isArray(options) || !options.length) return fallback;
  const index = Math.abs(Math.trunc(seed)) % options.length;
  return options[index] ?? fallback;
};
/* Point de passage UNIQUE des horodatages de trade (48 appels dans ce
   fichier) : y appliquer le fuseau d'affichage corrige d'un coup le journal,
   le calendrier, le tracker et tous les graphiques. Sans reglage choisi,
   `applyDisplayOffset` renvoie l'instant inchange. */
const tsToDate = (tsOrStr) => {
  if (tsOrStr == null) return null;
  if (typeof tsOrStr === "number") {
    const ms = tsOrStr < 1e12 ? tsOrStr * 1000 : tsOrStr;
    return applyDisplayOffset(new Date(ms));
  }
  const d = new Date(tsOrStr);
  return isNaN(+d) ? null : applyDisplayOffset(d);
};
// Clé YYYY-MM-DD en heure LOCALE. toISOString() convertit en UTC : sur un
// fuseau décalé, minuit local tombait la veille et le calendrier sélectionnait
// le mauvais jour.
const toLocalISODate = (d) => {
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const pick = (obj, keys, fallback = undefined) => {
  for (const k of keys) if (obj && obj[k] != null) return obj[k];
  return fallback;
};
const getHour = (ts) => {
  const d = tsToDate(ts);
  return d ? d.getHours() : null;
};
const getWeekday = (ts) => {
  const d = tsToDate(ts);
  return d ? d.getDay() : null;
};

const safeText = (v) => String(v ?? "").trim();
const buildSourceKey = (server, account) =>
  `${safeText(server) || "unknown_server"}::${safeText(account) || "unknown_account"}`;
const buildSourceLabel = (server, account) => {
  const parts = [safeText(server), safeText(account)].filter(Boolean);
  return parts.length ? parts.join(" • ") : "Source inconnue";
};
const toggleArrayValue = (list, value) =>
  list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];

const NOTE_TAG_PRESETS = [
  "breakout",
  "retest",
  "scalp",
  "trend",
  "range",
  "news",
  "london",
  "newyork",
  "asia",
  "gold",
  "A+",
  "FOMO",
];
const NOTE_CUSTOM_TAGS_STORAGE_KEY = "journalauto_custom_note_tags_v1";
const MAX_NOTE_TAG_PRESETS = 24;

const parseTagString = (value = "") =>
  String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const toggleTagString = (value = "", tag = "") => {
  const cleanTag = String(tag).trim();
  if (!cleanTag) return String(value || "");
  const current = parseTagString(value);
  const exists = current.some(
    (item) => item.toLowerCase() === cleanTag.toLowerCase(),
  );
  const next = exists
    ? current.filter((item) => item.toLowerCase() !== cleanTag.toLowerCase())
    : [...current, cleanTag];
  return next.join(", ");
};
const sanitizeTagPreset = (value = "") =>
  String(value).replace(/\s+/g, " ").trim();
const truncateAxisLabel = (value, max = 10) => {
  const label = String(value ?? "").trim();
  if (!label) return "—";
  return label.length > max
    ? `${label.slice(0, Math.max(1, max - 1))}…`
    : label;
};
const formatCompactAxisNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  const abs = Math.abs(n);
  if (abs >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1000)
    return `${(n / 1000).toFixed(abs >= 10000 ? 0 : 1).replace(/\.0$/, "")}k`;
  return `${Math.round(n)}`;
};
const formatDurationTick = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  if (n >= 60) {
    const h = Math.floor(n / 60);
    const m = Math.round(n % 60);
    return m ? `${h}h${m}` : `${h}h`;
  }
  return `${Math.round(n)}m`;
};


// ---------------------------------------------------------------------
// Flux UI temps réel : le navigateur ne poll plus les prix ouverts.
// En local : ws://127.0.0.1:8001/ws/ui/agent
// En production : wss://<host>/ws/ui/agent via Nginx.
// Le token est envoyé dans la première frame et jamais dans l'URL.
// ---------------------------------------------------------------------
const getAgentUiRealtimeUrl = () => {
  if (typeof window === "undefined") return "";

  // `process` n'existe pas forcément dans le navigateur (selon le bundler).
  // `process?.env` provoque quand même un ReferenceError si l'identifiant
  // global `process` est totalement absent. Le garde `typeof` est sûr.
  const explicit =
    typeof process !== "undefined" && process && process.env
      ? String(process.env.REACT_APP_REALTIME_WS_URL || "").trim()
      : "";

  if (explicit) {
    const base = explicit.replace(/\/$/, "");
    return base.endsWith("/ws/ui/agent") ? base : `${base}/ws/ui/agent`;
  }

  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") {
    return "ws://127.0.0.1:8001/ws/ui/agent";
  }

  const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${window.location.host}/ws/ui/agent`;
};

function useAgentUiRealtime(agentToken, handlers = {}) {
  const snapshotHandlerRef = useRef(handlers.onSnapshot);
  const tradeHandlerRef = useRef(handlers.onTradeEvent);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    snapshotHandlerRef.current = handlers.onSnapshot;
  }, [handlers.onSnapshot]);

  useEffect(() => {
    tradeHandlerRef.current = handlers.onTradeEvent;
  }, [handlers.onTradeEvent]);

  useEffect(() => {
    const token = String(agentToken || "").trim();
    if (!token || typeof window === "undefined" || typeof WebSocket === "undefined") {
      setConnected(false);
      return undefined;
    }

    let socket = null;
    let reconnectTimer = null;
    let pingTimer = null;
    let stopped = false;
    let reconnectDelay = 400;

    const clearTimers = () => {
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      if (pingTimer) window.clearInterval(pingTimer);
      reconnectTimer = null;
      pingTimer = null;
    };

    const scheduleReconnect = () => {
      if (stopped || reconnectTimer) return;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, reconnectDelay);
      reconnectDelay = Math.min(reconnectDelay * 1.7, 5000);
    };

    const connect = () => {
      if (stopped) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;

      const url = getAgentUiRealtimeUrl();
      if (!url) return;

      try {
        socket = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }

      socket.onopen = () => {
        try {
          socket.send(JSON.stringify({ type: "auth", role: "ui", token }));
        } catch {
          socket.close();
        }
      };

      socket.onmessage = (event) => {
        let message = null;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }

        if (message?.type === "auth_ok") {
          reconnectDelay = 400;
          setConnected(true);
          if (pingTimer) window.clearInterval(pingTimer);
          pingTimer = window.setInterval(() => {
            if (socket?.readyState === WebSocket.OPEN) {
              try { socket.send('{"type":"ping"}'); } catch {}
            }
          }, 15000);
          return;
        }

        if (message?.type === "account_snapshot") {
          snapshotHandlerRef.current?.(message.payload || message.snapshot || {});
          return;
        }

        if (message?.type === "trade_event") {
          tradeHandlerRef.current?.(message.payload || {});
        }
      };

      socket.onerror = () => {
        try { socket?.close(); } catch {}
      };

      socket.onclose = () => {
        setConnected(false);
        if (pingTimer) window.clearInterval(pingTimer);
        pingTimer = null;
        socket = null;
        scheduleReconnect();
      };
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        try { socket?.close(1000, "page_hidden"); } catch {}
        return;
      }
      if (!socket || socket.readyState === WebSocket.CLOSED) connect();
    };

    connect();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stopped = true;
      clearTimers();
      document.removeEventListener("visibilitychange", onVisibility);
      try { socket?.close(1000, "component_unmount"); } catch {}
      socket = null;
      setConnected(false);
    };
  }, [agentToken]);

  return connected;
}


/* locale-aware formatters */
const localeOf = (lng) => (lng?.startsWith("en") ? "en-US" : "fr-FR");
const fmtDT = (tsOrStr, lng) => {
  const d = tsToDate(tsOrStr);
  return d ? d.toLocaleString(localeOf(lng)) : "-";
};
const fmtDate = (tsOrStr, lng) => {
  const d = tsToDate(tsOrStr);
  return d ? d.toLocaleDateString(localeOf(lng)) : "";
};

/* heat color for heatmap */
// Échelle DIVERGENTE ancrée sur zéro : le signe décide de la teinte, la
// magnitude décide de l'intensité.
//
// L'ancienne version interpolait linéairement de `min` à `max` sur une seule
// rampe rouge→vert. Avec un min à −800 et un max à +1600, le zéro tombait à un
// tiers de la rampe : une case à +115 ressortait brune alors que la légende
// annonce « rouge (−) → vert (+) ». Toute la grille virait au brun-orangé.
const heatColor = (v, min, max, hasTrades = true) => {
  const value = Number(v);
  if (!Number.isFinite(value)) return "#e5e7eb";
  if (!hasTrades || value === 0) return "#e5e7eb";

  const scale = Math.max(Math.abs(Number(min) || 0), Math.abs(Number(max) || 0));
  if (!scale) return "#e5e7eb";

  // Racine carrée : sans elle, un seul créneau extrême écrase toutes les
  // autres cases dans le pâle.
  const ratio = Math.sqrt(Math.min(1, Math.abs(value) / scale));
  // Plancher à 0.18 pour qu'une case faible reste distinguable d'une case vide.
  const t = 0.18 + ratio * 0.82;

  // Interpolation depuis un blanc cassé vers la couleur pleine du signe.
  const target = value > 0 ? [16, 185, 129] : [239, 68, 68];
  const base = [248, 250, 252];
  const mix = target.map((c, i) => Math.round(base[i] + (c - base[i]) * t));
  return `rgb(${mix[0]},${mix[1]},${mix[2]})`;
};

const calendarTone = (
  pnl,
  trades = 0,
  intensityBase = 1,
  isDarkMode = false,
) => {
  if (!trades) {
    return {
      bg: isDarkMode ? "rgba(20,25,39,0.92)" : "rgba(248,250,252,0.86)",
      border: isDarkMode ? "rgba(71,85,105,0.36)" : "rgba(148,163,184,0.24)",
      text: "text-slate-500 dark:text-slate-400",
      value: "text-slate-400 dark:text-slate-500",
      glow: "shadow-none",
    };
  }

  const intensity = clamp(Math.abs(pnl) / Math.max(1, intensityBase), 0, 1);
  if (pnl > 0) {
    return {
      bg: isDarkMode
        ? `rgba(6,95,70,${0.42 + intensity * 0.28})`
        : `rgba(16,185,129,${0.12 + intensity * 0.32})`,
      border: isDarkMode
        ? `rgba(52,211,153,${0.28 + intensity * 0.24})`
        : `rgba(16,185,129,${0.22 + intensity * 0.28})`,
      text: "text-emerald-700 dark:text-emerald-300",
      value: "text-emerald-700 dark:text-emerald-300",
      glow: "",
    };
  }
  if (pnl < 0) {
    return {
      bg: isDarkMode
        ? `rgba(127,29,29,${0.42 + intensity * 0.26})`
        : `rgba(244,63,94,${0.12 + intensity * 0.3})`,
      border: isDarkMode
        ? `rgba(251,113,133,${0.28 + intensity * 0.24})`
        : `rgba(244,63,94,${0.22 + intensity * 0.28})`,
      text: "text-rose-700 dark:text-rose-300",
      value: "text-rose-700 dark:text-rose-300",
      glow: "",
    };
  }
  return {
    bg: isDarkMode ? "rgba(17,24,39,0.96)" : "rgba(59,130,246,0.12)",
    border: isDarkMode ? "rgba(96,165,250,0.34)" : "rgba(59,130,246,0.24)",
    text: "text-sky-700 dark:text-sky-300",
    value: "text-sky-700 dark:text-sky-300",
    glow: "",
  };
};

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

async function optimizeNoteImage(file, opts = {}) {
  if (!file || !String(file.type || "").startsWith("image/")) return null;
  const { maxWidth = 1400, maxHeight = 1400, quality = 0.82 } = opts;
  const source = await fileToDataUrl(file);
  const img = await loadImage(source);
  const ratio = Math.min(
    1,
    maxWidth / (img.width || 1),
    maxHeight / (img.height || 1),
  );
  const width = Math.max(1, Math.round((img.width || 1) * ratio));
  const height = Math.max(1, Math.round((img.height || 1) * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { alpha: false });
  ctx.fillStyle = "#0f172a";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  const mime = file.type === "image/png" ? "image/png" : "image/jpeg";
  const data_url = canvas.toDataURL(
    mime,
    mime === "image/png" ? undefined : quality,
  );
  return {
    id: uid(),
    name:
      file.name ||
      `capture-${Date.now()}.${mime === "image/png" ? "png" : "jpg"}`,
    type: mime,
    size: Math.round((data_url.length * 3) / 4),
    width,
    height,
    data_url,
    created_at: new Date().toISOString(),
  };
}

/* ---------- normalizers ---------- */

function normalizeOpenTrade(t) {
  return {
    kind: pick(t, ["kind", "type"], "position"),
    ticket: String(pick(t, ["ticket", "id", "cmd_id", "trade_id"], "-")),
    symbol: pick(t, ["symbol"], "-"),
    side: pick(t, ["side"], "-"),
    order_type: pick(t, ["order_type"], undefined),
    volume: toNumber(pick(t, ["volume"], 0)),
    open_time: pick(t, ["open_time", "opened_at"], null),
    open_price: toNumber(pick(t, ["open_price", "price"], 0)),
    current_price: toNumber(pick(t, ["current_price", "price_current", "market_price"], 0)),
    requested_price: toNumber(pick(t, ["requested_price", "price_request"], 0)),
    sl: pick(t, ["sl"], null),
    tp: pick(t, ["tp"], null),
    profit: toNumber(pick(t, ["profit"], 0), 0),
    account_login: safeText(
      pick(
        t,
        [
          "account_login",
          "account",
          "login",
          "account_number",
          "account_id",
          "mt5_login",
        ],
        "",
      ),
    ),
    broker_server: safeText(
      pick(
        t,
        [
          "broker_server",
          "server",
          "trade_server",
          "account_server",
          "mt5_server",
          "broker",
        ],
        "",
      ),
    ),
  };
}
function normalizeHistoryTrade(t, idx = 0) {
  const recordTypeRaw = safeText(
    pick(t, ["record_type", "recordType", "entry_type", "entryType"], "trade"),
  ).toLowerCase();
  const rawSide = safeText(pick(t, ["side"], "-")).toLowerCase();
  const cashflowTypeRaw = safeText(
    pick(t, ["cashflow_type", "cashflowType"], ""),
  ).toLowerCase();
  const isCashflow =
    recordTypeRaw === "cashflow" ||
    ["deposit", "withdrawal", "credit", "debit"].includes(rawSide) ||
    ["deposit", "withdrawal", "credit", "debit"].includes(cashflowTypeRaw);
  const cashflowAmount = toNumber(
    pick(t, ["cashflow_amount", "cashflowAmount", "amount", "profit"], 0),
    0,
  );
  const cashflowType = isCashflow
    ? cashflowTypeRaw || rawSide || (cashflowAmount >= 0 ? "deposit" : "withdrawal")
    : "";

  const open = tsToDate(pick(t, ["open_time", "opened_at", "event_time"], null));
  const close = tsToDate(pick(t, ["close_time", "closed_at", "event_time"], null));
  const duration_ms = !isCashflow && open && close ? close - open : undefined;
  const eventTime = pick(
    t,
    ["event_time", "cashflow_time", "close_time", "closed_at", "open_time", "opened_at"],
    null,
  );

  return {
    record_type: isCashflow ? "cashflow" : "trade",
    cashflow_type: cashflowType,
    cashflow_amount: isCashflow ? cashflowAmount : 0,
    event_time: eventTime,
    ticket: String(
      pick(t, ["ticket", "id", "cmd_id", "trade_id"], `#${idx + 1}`),
    ),
    symbol: isCashflow ? pick(t, ["symbol"], "CASH") || "CASH" : pick(t, ["symbol"], "-"),
    side: isCashflow ? cashflowType : rawSide || "-",
    volume: isCashflow ? 0 : toNumber(pick(t, ["volume"], 0)),
    open_time: isCashflow ? eventTime : pick(t, ["open_time", "opened_at"], null),
    open_price: isCashflow ? 0 : toNumber(pick(t, ["open_price", "price"], 0)),
    close_time: isCashflow ? eventTime : pick(t, ["close_time", "closed_at"], null),
    close_price: isCashflow ? 0 : toNumber(pick(t, ["close_price"], 0)),
    profit: isCashflow ? cashflowAmount : toNumber(pick(t, ["profit"], 0)),
    comment: pick(t, ["comment", "reason"], ""),
    status: isCashflow ? "cashflow" : pick(t, ["status"], undefined),
    sl: isCashflow ? null : pick(t, ["sl"], null),
    tp: isCashflow ? null : pick(t, ["tp"], null),
    duration_ms,
    r_multiple: isCashflow ? undefined : pick(t, ["r_multiple"], undefined),
    account_login: safeText(
      pick(
        t,
        [
          "account_login",
          "account",
          "login",
          "account_number",
          "account_id",
          "mt5_login",
        ],
        "",
      ),
    ),
    broker_server: safeText(
      pick(
        t,
        [
          "broker_server",
          "server",
          "trade_server",
          "account_server",
          "mt5_server",
          "broker",
        ],
        "",
      ),
    ),
  };
}

const isCashflowRecord = (row) =>
  Boolean(
    row &&
      (String(row.record_type || "").toLowerCase() === "cashflow" ||
        String(row.status || "").toLowerCase() === "cashflow" ||
        ["deposit", "withdrawal", "credit", "debit"].includes(
          String(row.cashflow_type || row.side || "").toLowerCase(),
        )),
  );

const cashflowAmountOf = (row) =>
  toNumber(row?.cashflow_amount ?? row?.amount ?? row?.profit, 0);

const historyEventTime = (row) =>
  row?.event_time ?? row?.close_time ?? row?.open_time ?? null;

/* ---------- localStorage (safe) ---------- */
const LS_TOKEN = "agent_token";

const JOURNAL_SCOPE_SESSION_KEY = "fm:journal:auto:active_scope";
const JOURNAL_AUTH_PROFILE_KEYS = [
  "user",
  "auth_user",
  "currentUser",
  "fm_user",
  "fullmetrix_user",
  "fullmargin_user",
  "profile",
  "sso_user",
  "user_data",
  "authData",
];
const JOURNAL_AUTH_TOKEN_KEYS = [
  "token",
  "access_token",
  "accessToken",
  "jwt",
  "jwt_token",
  "authToken",
  "AUTH_TOKEN",
  "fm_token",
];

function safeJsonParse(value, fallback = null) {
  try {
    if (value == null || value === "") return fallback;
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function sanitizeStorageScope(scope = "anonymous") {
  return (
    String(scope || "anonymous")
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .slice(0, 90) || "anonymous"
  );
}

function hashStorageValue(value = "") {
  const str = String(value || "");
  let hash = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function makeUserStorageScope(identity = "") {
  const raw = String(identity || "").trim().toLowerCase();
  return raw ? `user_${hashStorageValue(raw)}` : "anonymous";
}

function getUserIdentityFromPayload(payload = {}) {
  const source = payload?.user || payload?.profile || payload?.data || payload || {};
  const candidates = [
    source.id,
    source.user_id,
    source.userId,
    source.fullmargin_id,
    source.fullmarginId,
    source.sub,
    source.email,
    source.username,
  ];

  const found = candidates.find(
    (value) => value !== null && value !== undefined && String(value).trim(),
  );
  return found ? String(found).trim() : "";
}

function decodeJwtPayloadForJournal(token = "") {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const normalized = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    const json = decodeURIComponent(
      Array.from(atob(padded))
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join(""),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function readStorageAreaValue(area, key) {
  try {
    if (typeof window === "undefined") return "";
    return String(window[area]?.getItem(key) || "").trim();
  } catch {
    return "";
  }
}

function writeStorageAreaValue(area, key, value) {
  try {
    if (typeof window === "undefined") return;
    window[area]?.setItem(key, value);
  } catch {
    // ignore
  }
}

function removeStorageAreaValue(area, key) {
  try {
    if (typeof window === "undefined") return;
    window[area]?.removeItem(key);
  } catch {
    // ignore
  }
}

function getJournalTokenCandidates() {
  const out = [];
  for (const key of JOURNAL_AUTH_TOKEN_KEYS) {
    const value =
      readStorageAreaValue("localStorage", key) ||
      readStorageAreaValue("sessionStorage", key);
    if (value) out.push(value);
  }
  return Array.from(new Set(out));
}

function resolveJournalSessionProfile() {
  for (const key of JOURNAL_AUTH_PROFILE_KEYS) {
    const raw =
      readStorageAreaValue("localStorage", key) ||
      readStorageAreaValue("sessionStorage", key);
    if (!raw) continue;
    const parsed = safeJsonParse(raw, raw);
    if (getUserIdentityFromPayload(parsed)) return parsed;
  }

  for (const token of getJournalTokenCandidates()) {
    const payload = decodeJwtPayloadForJournal(token);
    if (getUserIdentityFromPayload(payload)) return payload;
  }

  return null;
}

function resolveJournalScope() {
  const profile = resolveJournalSessionProfile();
  const identity = getUserIdentityFromPayload(profile);
  if (identity) return makeUserStorageScope(identity);

  const token = getJournalTokenCandidates()[0] || "";
  return token ? makeUserStorageScope(token) : "anonymous";
}

function setActiveJournalScope(scope = resolveJournalScope()) {
  const safeScope = sanitizeStorageScope(scope || "anonymous");
  writeStorageAreaValue("sessionStorage", JOURNAL_SCOPE_SESSION_KEY, safeScope);
  return safeScope;
}

function getActiveJournalScope() {
  const stored = readStorageAreaValue("sessionStorage", JOURNAL_SCOPE_SESSION_KEY);
  return sanitizeStorageScope(stored || setActiveJournalScope(resolveJournalScope()));
}

function scopedStorageKey(baseKey, scope = getActiveJournalScope()) {
  return `${baseKey}:${sanitizeStorageScope(scope)}`;
}

function readScopedStorageValue(baseKey, scope = getActiveJournalScope()) {
  const safeScope = sanitizeStorageScope(scope);
  return (
    readStorageAreaValue("localStorage", scopedStorageKey(baseKey, safeScope)) ||
    readStorageAreaValue("sessionStorage", scopedStorageKey(baseKey, safeScope)) ||
    ""
  );
}

function writeScopedStorageValue(baseKey, value, scope = getActiveJournalScope()) {
  const safeScope = sanitizeStorageScope(scope);
  writeStorageAreaValue("localStorage", scopedStorageKey(baseKey, safeScope), value);
}

function getScopedAgentToken(scope = getActiveJournalScope()) {
  const safeScope = sanitizeStorageScope(scope);

  const direct = readScopedStorageValue(LS_TOKEN, safeScope);
  if (direct) return direct;

  const bridgeScope = readStorageAreaValue("sessionStorage", "fm:agent_token:current_scope");
  const bridgeToken = readStorageAreaValue("sessionStorage", "fm:agent_token:current");
  if (bridgeToken && bridgeScope && bridgeScope === safeScope) {
    writeScopedStorageValue(LS_TOKEN, bridgeToken, safeScope);
    return bridgeToken;
  }

  const scopedAccounts = safeJsonParse(
    readScopedStorageValue("agent_accounts_v3", safeScope) || "[]",
    [],
  );
  const scopedIdx = Number(readScopedStorageValue("agent_active_idx", safeScope) || 0);
  const scopedAccountToken = scopedAccounts?.[scopedIdx]?.token || "";
  if (scopedAccountToken) {
    const token = String(scopedAccountToken).trim();
    writeScopedStorageValue(LS_TOKEN, token, safeScope);
    return token;
  }

  const legacyOwner = readStorageAreaValue("localStorage", "agent_token_owner_scope");
  const legacyToken = readStorageAreaValue("localStorage", LS_TOKEN);
  if (legacyToken && legacyOwner && legacyOwner === safeScope) {
    writeScopedStorageValue(LS_TOKEN, legacyToken, safeScope);
    return legacyToken;
  }

  const legacyAccountsOwner = readStorageAreaValue("localStorage", "agent_accounts_owner_scope");
  if (legacyAccountsOwner && legacyAccountsOwner === safeScope) {
    const legacyAccounts = safeJsonParse(
      readStorageAreaValue("localStorage", "agent_accounts_v3") || "[]",
      [],
    );
    const legacyIdx = Number(readStorageAreaValue("localStorage", "agent_active_idx") || 0);
    const token = String(legacyAccounts?.[legacyIdx]?.token || "").trim();
    if (token) {
      writeScopedStorageValue(LS_TOKEN, token, safeScope);
      return token;
    }
  }

  return "";
}

function clearUnsafeLegacyJournalKeysWhenLoggedOut(scope = getActiveJournalScope()) {
  if (sanitizeStorageScope(scope) !== "anonymous") return;

  [
    "agent_token",
    "agent_token_owner_scope",
    "agent_accounts_owner_scope",
    "fm:agent_token:current",
    "fm:agent_token:current_scope",
    "fm_user_display_name",
    NOTE_CUSTOM_TAGS_STORAGE_KEY,
  ].forEach((key) => {
    removeStorageAreaValue("localStorage", key);
    removeStorageAreaValue("sessionStorage", key);
  });

  try {
    if (typeof window !== "undefined" && window.localStorage) {
      Object.keys(window.localStorage).forEach((key) => {
        const isLegacyNotesCache = key.startsWith("notes_") && !key.includes(":user_");
        const isLegacyJournalCache =
          key.startsWith(`${NOTE_CUSTOM_TAGS_STORAGE_KEY}:`) &&
          !key.includes(":user_");
        if (isLegacyNotesCache || isLegacyJournalCache) {
          window.localStorage.removeItem(key);
        }
      });
    }
  } catch {
    // ignore
  }
}

function notesStorageKeyForToken(token = "", scope = getActiveJournalScope()) {
  const tokenKey = token ? hashStorageValue(token) : "no_token";
  return scopedStorageKey(`notes_${tokenKey}`, scope);
}

function readNotesCache(token = "", scope = getActiveJournalScope()) {
  const safeScope = sanitizeStorageScope(scope);
  const scopedRaw = readStorageAreaValue("localStorage", notesStorageKeyForToken(token, safeScope));
  if (scopedRaw) return safeJsonParse(scopedRaw, {});

  // Migration douce : l'ancien cache notes_<token> est repris seulement pour le token actif du scope courant.
  const currentToken = getScopedAgentToken(safeScope);
  if (token && currentToken && currentToken === token) {
    const legacyRaw = readStorageAreaValue("localStorage", `notes_${token}`);
    const legacy = safeJsonParse(legacyRaw, {});
    if (legacy && typeof legacy === "object" && Object.keys(legacy).length) {
      writeStorageAreaValue(
        "localStorage",
        notesStorageKeyForToken(token, safeScope),
        JSON.stringify(legacy),
      );
      return legacy;
    }
  }

  return {};
}

function writeNotesCache(token = "", notes = {}, scope = getActiveJournalScope()) {
  if (!token) return;
  writeStorageAreaValue(
    "localStorage",
    notesStorageKeyForToken(token, scope),
    JSON.stringify(notes || {}),
  );
}

function readCustomTagPresetsForScope(scope = getActiveJournalScope()) {
  try {
    const scopedRaw = readScopedStorageValue(NOTE_CUSTOM_TAGS_STORAGE_KEY, scope);
    const parsed = safeJsonParse(scopedRaw || "[]", []);
    return Array.isArray(parsed)
      ? parsed
          .map((item) => sanitizeTagPreset(item))
          .filter(Boolean)
          .slice(0, MAX_NOTE_TAG_PRESETS)
      : [];
  } catch {
    return [];
  }
}

setActiveJournalScope(resolveJournalScope());

const AUTO_REFRESH_INTERVAL_MS = 12000;
const HISTORY_REFRESH_MS = 90000;
const NOTES_REFRESH_MS = 180000;
const REGISTER_REFRESH_MS = 120000;
const NETWORK_BACKOFF_MS = 20000;
const FULL_HISTORY_PAGE_LIMIT = 500;
const FULL_HISTORY_MAX_ROWS = 50000;
const RECENT_HISTORY_LIMIT = 80;
// Infobulle commune à tous les graphiques. Le rendu par défaut de Recharts est
// un fond blanc avec du texte noir : illisible en thème sombre.
const CHART_TOOLTIP_PROPS = {
  contentStyle: {
    background: "rgba(12,16,30,0.96)",
    border: "1px solid rgba(124,58,237,0.35)",
    borderRadius: 12,
    padding: "8px 10px",
    fontSize: 12,
    color: "#fff",
    boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
  },
  labelStyle: { color: "rgba(255,255,255,0.72)", marginBottom: 4 },
  itemStyle: { color: "#fff", padding: 0 },
  cursor: { fill: "rgba(139,92,246,0.10)" },
};

// Fuseau du navigateur, affiché sous l'axe des heures d'entrée : les heures
// sont lues depuis `open_time` côté client, pas côté serveur.
const localTimeZoneLabel = (() => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "heure locale"
    );
  } catch {
    return "heure locale";
  }
})();

// Montant abrégé pour les étiquettes posées sur les barres, où la place
// manque : "12.4k" plutôt que "12 431.87".
const fmtCompactMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "+";
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}k`;
  return `${sign}${abs.toFixed(0)}`;
};

// Champs date de la barre de filtres des statistiques : même rayon, même
// remplissage et mêmes dégradés que le déclencheur de PremiumSelect, pour que
// les quatre contrôles de la ligne se ressemblent.
const statsDateInputClass =
  "w-full min-w-0 rounded-[22px] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(246,247,252,0.92))] px-4 text-[15px] font-semibold tracking-[-0.01em] text-slate-800 outline-none backdrop-blur-xl transition-colors duration-200 focus-visible:border-violet-300 focus-visible:ring-2 focus-visible:ring-violet-500/20 dark:border-slate-700/85 dark:bg-[linear-gradient(180deg,rgba(17,24,39,0.98),rgba(8,13,26,0.98))] dark:text-white dark:focus-visible:border-violet-400/55";

// Raccourcis de période de la barre de filtres des statistiques.
const STATS_QUICK_RANGES = [
  { days: 7, labelFr: "7 jours", labelEn: "7 days" },
  { days: 30, labelFr: "30 jours", labelEn: "30 days" },
  { days: 90, labelFr: "3 mois", labelEn: "3 months" },
  { days: 182, labelFr: "6 mois", labelEn: "6 months" },
  { days: 365, labelFr: "1 an", labelEn: "1 year" },
];

// Périodes proposées sous la courbe P&L. `days: null` = tout l'historique.
const CURVE_RANGES = [
  { value: "1w", days: 7, labelFr: "1S", labelEn: "1W" },
  { value: "1m", days: 30, labelFr: "1M", labelEn: "1M" },
  { value: "3m", days: 90, labelFr: "3M", labelEn: "3M" },
  { value: "6m", days: 182, labelFr: "6M", labelEn: "6M" },
  { value: "1y", days: 365, labelFr: "1A", labelEn: "1Y" },
  { value: "all", days: null, labelFr: "Tout", labelEn: "All" },
];
const CURVE_RANGE_DAYS = CURVE_RANGES.reduce((acc, r) => {
  acc[r.value] = r.days;
  return acc;
}, {});

const MAX_PNL_CURVE_POINTS = 320;
const MAX_SCATTER_POINTS = 260;
const MAX_CHART_TRADES = 1500;
// Plafond propre au coach, très au-dessus de celui des graphiques : son travail
// est une dizaine de passes O(n) sur des nombres, pas un rendu SVG. Le limiter
// à 1500 comme les graphiques faisait perdre de la matière d'analyse sans
// aucun gain de fluidité.
const MAX_COACH_TRADES = 20000;
// Fenêtre du drawdown « récent ». 90 jours : assez long pour contenir une
// vraie phase de marché, assez court pour rester actionnable.
const RECENT_DD_DAYS = 90;
const CHART_STAGE_DELAYS = [80, 260, 520];

const downsampleArray = (arr, maxPoints) => {
  if (!Array.isArray(arr) || arr.length <= maxPoints) return Array.isArray(arr) ? arr : [];
  if (maxPoints <= 2) return arr.slice(0, Math.max(0, maxPoints));

  const lastIndex = arr.length - 1;
  const step = lastIndex / (maxPoints - 1);
  const sampled = [];
  let previousIndex = -1;

  for (let i = 0; i < maxPoints; i += 1) {
    const index = Math.min(lastIndex, Math.round(i * step));
    if (index !== previousIndex) sampled.push(arr[index]);
    previousIndex = index;
  }

  return sampled;
};

const storage = {
  get(key) {
    try {
      if (typeof window === "undefined" || !window.localStorage) return null;
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      if (typeof window === "undefined" || !window.localStorage) return;
      window.localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  },
  remove(key) {
    try {
      if (typeof window === "undefined" || !window.localStorage) return;
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};

const getAttachmentPreviewSrc = (attachment) =>
  attachment?.data_url ||
  attachment?.dataUrl ||
  attachment?.preview_url ||
  attachment?.previewUrl ||
  attachment?.url ||
  attachment?.path ||
  "";

const mergeNoteRecords = (remote = {}, local = {}) => {
  const next = {};
  const keys = new Set([
    ...Object.keys(remote || {}),
    ...Object.keys(local || {}),
  ]);

  keys.forEach((key) => {
    const remoteNote = remote?.[key] || {};
    const localNote = local?.[key] || {};

    const remoteAttachments = Array.isArray(remoteNote.attachments)
      ? remoteNote.attachments
      : [];
    const localAttachments = Array.isArray(localNote.attachments)
      ? localNote.attachments
      : [];

    next[key] = {
      ...localNote,
      ...remoteNote,
      comment: remoteNote.comment ?? localNote.comment ?? "",
      setup: remoteNote.setup ?? localNote.setup ?? "",
      rating: remoteNote.rating ?? localNote.rating ?? 0,
      tags:
        Array.isArray(remoteNote.tags) && remoteNote.tags.length
          ? remoteNote.tags
          : Array.isArray(localNote.tags)
            ? localNote.tags
            : [],

      // ✅ priorité au backend
      attachments: remoteAttachments.length
        ? remoteAttachments
        : localAttachments,
    };
  });

  return next;
};


function SafeResponsiveContainer({
  children,
  width = "100%",
  height = "100%",
  className = "",
  style = {},
  fallback = null,
  minReadyWidth = 40,
  minReadyHeight = 40,
}) {
  const hostRef = React.useRef(null);
  const frameRef = React.useRef(null);
  const mountedRef = React.useRef(false);
  const [size, setSize] = React.useState({ width: 0, height: 0 });
  const fixedWidth = typeof width === "number";
  const fixedHeight = typeof height === "number";

  useLayoutEffect(() => {
    mountedRef.current = true;
    const node = hostRef.current;
    if (!node || typeof window === "undefined") return undefined;

    const readSize = () => {
      if (!mountedRef.current || !hostRef.current) return;
      const rect = hostRef.current.getBoundingClientRect();
      const measuredWidth = Math.max(
        0,
        Math.floor(rect.width || hostRef.current.clientWidth || 0),
      );
      const measuredHeight = Math.max(
        0,
        Math.floor(rect.height || hostRef.current.clientHeight || 0),
      );
      setSize((current) =>
        current.width === measuredWidth && current.height === measuredHeight
          ? current
          : { width: measuredWidth, height: measuredHeight },
      );
    };

    const scheduleRead = () => {
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      frameRef.current = window.requestAnimationFrame(readSize);
    };

    scheduleRead();

    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(scheduleRead);
      observer.observe(node);
    } else {
      window.addEventListener("resize", scheduleRead, { passive: true });
    }

    return () => {
      mountedRef.current = false;
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      if (observer) observer.disconnect();
      else window.removeEventListener("resize", scheduleRead);
    };
  }, []);

  const measuredWidth = fixedWidth ? width : size.width;
  const measuredHeight = fixedHeight ? height : size.height;
  const ready =
    Number.isFinite(measuredWidth) &&
    Number.isFinite(measuredHeight) &&
    measuredWidth >= minReadyWidth &&
    measuredHeight >= minReadyHeight;

  const chart = ready && React.isValidElement(children)
    ? React.cloneElement(children, {
        width: Math.max(1, Math.floor(measuredWidth)),
        height: Math.max(1, Math.floor(measuredHeight)),
      })
    : null;

  return (
    <div
      ref={hostRef}
      className={cn("h-full w-full min-w-0 overflow-hidden", className)}
      style={{
        minWidth: 1,
        minHeight: fixedHeight ? height : 1,
        height: fixedHeight ? height : undefined,
        width: fixedWidth ? width : undefined,
        contain: "layout paint size",
        ...style,
      }}
    >
      {chart ||
        fallback || <div className="h-full w-full min-w-0" aria-hidden="true" />}
    </div>
  );
}


/* ---------- Barre arrondie côté pointe, pour valeurs signées ---------- */
// Le prop `radius` de Recharts n'est pas fiable sur une barre négative : selon
// le graphique, l'arrondi atterrit côté ligne du zéro au lieu de la pointe. On
// trace le rectangle nous-mêmes : base plate sur le zéro, pointe arrondie.
function SignedRoundedBar(props) {
  const { x, y, width, height, fill, value, radius = 8 } = props;
  const w = Number(width) || 0;
  // Recharts donne une hauteur NÉGATIVE pour une barre sous le zéro, avec `y`
  // posé sur la ligne du zéro. Sans cette normalisation, la barre n'est pas
  // dessinée du tout.
  const rawHeight = Number(height) || 0;
  const h = Math.abs(rawHeight);
  if (!w || !h) return null;

  const top = rawHeight < 0 ? y + rawHeight : y;
  const bottom = top + h;
  const right = x + w;

  const r = Math.max(0, Math.min(radius, w / 2, h));
  const raw = Number(value);
  const positive = Number.isFinite(raw) ? raw >= 0 : rawHeight >= 0;

  const d = positive
    ? // Pointe en haut
      `M${x},${bottom} L${x},${top + r} Q${x},${top} ${x + r},${top} L${right - r},${top} Q${right},${top} ${right},${top + r} L${right},${bottom} Z`
    : // Pointe en bas
      `M${x},${top} L${x},${bottom - r} Q${x},${bottom} ${x + r},${bottom} L${right - r},${bottom} Q${right},${bottom} ${right},${bottom - r} L${right},${top} Z`;

  return <path d={d} fill={fill} />;
}

// Fiches d'aide déjà consultées dans cette session, pour arrêter la pulsation
// de la pastille correspondante.
const HELP_SEEN_PREFIX = "fm:journal:help-seen:";

// Animation d'appel de la pastille d'aide : un anneau qui se propage plus une
// légère respiration de l'icône. Assez pour accrocher l'œil sur une page
// dense, assez discret pour ne pas transformer vingt pastilles en sapin de
// Noël. S'arrête dès que la pastille a été consultée.
const HELP_PULSE_WRAPPER = "relative inline-flex shrink-0 align-middle";
const HELP_PULSE_RING =
  "pointer-events-none absolute inset-0 animate-ping rounded-full bg-violet-500/30 [animation-duration:2.6s] dark:bg-violet-400/30";
const HELP_PULSE_ICON =
  "text-violet-500 animate-pulse [animation-duration:2.6s] dark:text-violet-300";

/* ---------- Positionnement d'une bulle ancrée ---------- */
// Renvoie des coordonnées `fixed` pour une bulle accrochée à un élément.
// Le `fixed` est délibéré : les cartes de statistiques sont en
// `overflow-hidden`, une bulle en `absolute` y serait tronquée.
function anchoredPopoverPos(el, width, minSpaceBelow = 220) {
  if (!el || typeof window === "undefined") return null;
  const r = el.getBoundingClientRect();
  const gap = 8;
  const margin = 12;
  const left = Math.min(
    Math.max(margin, r.left + r.width / 2 - width / 2),
    Math.max(margin, window.innerWidth - width - margin),
  );
  const spaceBelow = window.innerHeight - r.bottom;

  // Bascule au-dessus quand le bas de la fenêtre est trop proche, sinon la
  // bulle sort de l'écran en pied de page.
  if (spaceBelow < minSpaceBelow && r.top > spaceBelow) {
    return {
      left,
      bottom: window.innerHeight - r.top + gap,
      maxHeight: r.top - gap - margin,
    };
  }
  return {
    left,
    top: r.bottom + gap,
    maxHeight: spaceBelow - gap - margin,
  };
}

/* ---------- Pastille d'aide ⓘ ---------- */
// La bulle est positionnée en `fixed` à partir du rectangle du bouton : les
// cartes de statistiques sont toutes en `overflow-hidden`, une bulle en
// `absolute` y serait tronquée.
//
// Survol = aperçu, clic = épinglage (sortie par clic ailleurs ou Échap). Le
// clic est indispensable : sur tactile il n'y a pas de survol.
function InfoHint({
  text,
  title,
  image,
  help = null,
  className = "",
  size = 14,
}) {
  const btnRef = React.useRef(null);
  const cardRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [pinned, setPinned] = React.useState(false);
  const [pos, setPos] = React.useState(null);

  const heading = help?.title || title;
  const visual = help?.image ?? image;

  // Mémorisé pour la session : une fois la fiche ouverte, la pastille cesse
  // de pulser. Persisté pour que la pulsation ne reparte pas à chaque
  // changement d'onglet, qui remonte le composant.
  const [helpSeen, setHelpSeen] = React.useState(() => {
    if (!heading || typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem(HELP_SEEN_PREFIX + heading) === "1";
    } catch {
      return false;
    }
  });

  const markHelpSeen = React.useCallback(() => {
    setHelpSeen(true);
    if (!heading || typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(HELP_SEEN_PREFIX + heading, "1");
    } catch {
      /* storage indisponible : la pulsation repartira, sans conséquence */
    }
  }, [heading]);
  // Les fiches de graphique portent un visuel annoté : il leur faut de la
  // largeur. Une définition courte tient dans 300 px ; une explication longue
  // — celle du drawdown, qui doit lever une confusion — mérite plus large
  // plutôt qu'une colonne étroite qu'il faut faire défiler.
  const width = visual ? 460 : String(text || "").length > 220 ? 400 : 300;

  // Fermeture différée : il y a 8 px de vide entre la pastille et la bulle,
  // et sans ce délai `onMouseLeave` ferme avant que le curseur n'y arrive.
  const closeTimerRef = React.useRef(null);
  const cancelClose = React.useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const place = React.useCallback(() => {
    setPos(anchoredPopoverPos(btnRef.current, width));
  }, [width]);

  const show = React.useCallback(() => {
    cancelClose();
    place();
    setOpen(true);
  }, [cancelClose, place]);

  const hide = React.useCallback(() => {
    cancelClose();
    setOpen(false);
    setPinned(false);
  }, [cancelClose]);

  const scheduleClose = React.useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpen(false), 140);
  }, [cancelClose]);

  React.useEffect(() => cancelClose, [cancelClose]);

  React.useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") hide();
    };
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (cardRef.current?.contains(e.target)) return;
      hide();
    };
    // La bulle est en `fixed` : sans repositionnement elle resterait accrochée
    // à l'écran pendant que le bouton défile sous elle.
    const onMove = () => place();

    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, hide, place]);

  const triggerClass = cn(
    "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:text-violet-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40 dark:text-white/35 dark:hover:text-violet-300",
    open && "text-violet-600 dark:text-violet-300",
    className,
  );

  // Fiche de graphique = modale centrée. Une bulle ancrée sous le bouton ne
  // peut pas contenir un titre, trois paragraphes et un visuel annoté : elle
  // débordait sous la fenêtre et obligeait à scroller la page pour lire.
  if (help) {
    return (
      <>
        <span className={cn(HELP_PULSE_WRAPPER, className)}>
          {!helpSeen ? <span aria-hidden="true" className={HELP_PULSE_RING} /> : null}
          <button
            ref={btnRef}
            type="button"
            aria-label={help.labels?.ctaTitle || heading || "Aide"}
            aria-haspopup="dialog"
            aria-expanded={open}
            title={help.labels?.ctaTitle}
            onClick={(e) => {
              e.stopPropagation();
              markHelpSeen();
              setOpen(true);
            }}
            className={cn(triggerClass, !helpSeen && HELP_PULSE_ICON)}
          >
            <CircleHelp size={size} strokeWidth={2} />
          </button>
        </span>

        {open && typeof document !== "undefined"
          ? createPortal(
              <div
                className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
                role="dialog"
                aria-modal="true"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) hide();
                }}
              >
            <div
              ref={cardRef}
              className="flex max-h-[86vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-2xl dark:border-white/15 dark:bg-[#11162a]"
            >
              <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-3.5 dark:border-white/10">
                <h3 className="min-w-0 text-sm font-semibold text-slate-900 dark:text-white">
                  {heading}
                </h3>
                <button
                  type="button"
                  onClick={hide}
                  aria-label="Fermer"
                  className="-mr-1 rounded-lg p-1 text-slate-400 transition-colors hover:text-slate-700 dark:hover:text-white"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
                <p className="text-[13px] leading-relaxed text-slate-600 dark:text-white/70">
                  {help.intro}
                </p>
                {help.meaning ? (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 dark:text-white/40">
                      {help.labels?.meaning}
                    </div>
                    <p className="mt-1 text-[13px] leading-relaxed text-slate-600 dark:text-white/70">
                      {help.meaning}
                    </p>
                  </div>
                ) : null}
                {help.howTo ? (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 dark:text-white/40">
                      {help.labels?.howTo}
                    </div>
                    <p className="mt-1 text-[13px] leading-relaxed text-slate-600 dark:text-white/70">
                      {help.howTo}
                    </p>
                  </div>
                ) : null}
                {visual ? (
                  <img
                    src={visual}
                    alt={heading || ""}
                    className="w-full rounded-xl border border-slate-200 dark:border-white/10"
                    loading="lazy"
                  />
                ) : null}
                </div>
                </div>
              </div>,
              document.body,
            )
          : null}
      </>
    );
  }

  // Définition courte d'une métrique : bulle ancrée, survol ou clic.
  return (
    <>
      <span className={cn(HELP_PULSE_WRAPPER, className)}>
        {!helpSeen ? <span aria-hidden="true" className={HELP_PULSE_RING} /> : null}
        <button
          ref={btnRef}
          type="button"
          aria-label={heading || "Aide"}
          aria-expanded={open}
          onMouseEnter={() => {
            markHelpSeen();
            show();
          }}
          onMouseLeave={() => {
            if (!pinned) scheduleClose();
          }}
          onFocus={show}
          onBlur={() => {
            if (!pinned) scheduleClose();
          }}
          onClick={(e) => {
            e.stopPropagation();
            markHelpSeen();
            if (pinned) {
              hide();
              return;
            }
            setPinned(true);
            show();
          }}
          className={cn(triggerClass, !helpSeen && HELP_PULSE_ICON)}
        >
          <CircleHelp size={size} strokeWidth={2} />
        </button>
      </span>

      {/* Portail vers <body> : `backdrop-filter` — présent sur toutes les
          cartes — établit un bloc conteneur pour les descendants `fixed`. La
          bulle se retrouvait positionnée par rapport à la carte alors que ses
          coordonnées viennent de `getBoundingClientRect()`, donc de la
          fenêtre. Elle partait hors cadre, ou se faisait rogner par
          l'`overflow-hidden` de la carte. */}
      {open && pos && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={cardRef}
              role="tooltip"
              className="fixed z-[9999] overflow-y-auto rounded-xl border border-slate-200 bg-white p-3 text-left shadow-xl dark:border-white/15 dark:bg-[#11162a]"
              style={{
                left: pos.left,
                top: pos.top,
                bottom: pos.bottom,
                width,
                maxHeight: Math.max(120, pos.maxHeight || 320),
              }}
              onMouseEnter={cancelClose}
              onMouseLeave={() => {
                if (!pinned) scheduleClose();
              }}
            >
              <p className="text-[12px] leading-relaxed text-slate-600 dark:text-white/70">
                {text}
              </p>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/* ---------- Tuile du cockpit performance ---------- */
// Définie au niveau module, et pas dans JournalAutoPage : une fonction
// recréée à chaque rendu est un nouveau type de composant pour React, qui
// démonte puis remonte tout le sous-arbre. L'état interne de la pastille ⓠ
// (`open`) était détruit avant même de pouvoir s'afficher — la page se rend
// souvent (polling 3 s, étapes de rendu des graphiques, useTransition).
function StatTile({
  label,
  value,
  subtle,
  info,
  icon: Icon,
  iconTone = "bg-violet-500/12 text-violet-600 dark:bg-violet-500/14 dark:text-violet-300",
  valueTone,
  emptyLabel = "—",
}) {
  return (
    <div className="flex items-start justify-between gap-2 rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-white/60">
          <span className="truncate">{label}</span>
          {info ? <InfoHint title={label} text={info} size={13} /> : null}
        </div>
        <div
          className={cn(
            "mt-1 text-lg font-semibold tabular-nums",
            valueTone,
            subtle && "text-slate-600 dark:text-white/70",
          )}
        >
          {(value ?? value === 0) ? value : emptyLabel}
        </div>
      </div>
      {Icon ? (
        <span
          className={cn(
            "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
            iconTone,
          )}
        >
          <Icon size={15} />
        </span>
      ) : null}
    </div>
  );
}

// Recharts requis : ResponsiveContainer, ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine
// import { ResponsiveContainer, ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from "recharts";

function PnLCurve({
  data = [],
  title = "Courbe P&L (clos)",
  height = 340, // number (px) ou "100%"
  color = "#8b5cf6",
  containerClassName = "",
  chartClassName = "",
  help = null,
  rangeOptions = [],
  range = "all",
  onRangeChange,
}) {
  // ID de dégradé stable
  const gradIdRef = React.useRef(null);
  if (!gradIdRef.current)
    gradIdRef.current = `eqFill-${Math.random().toString(36).slice(2)}-pnl`;
  const gradId = gradIdRef.current;

  // Données sûres
  const safeData = React.useMemo(
    () =>
      Array.isArray(data) && data.length
        ? data
        : [{ date: new Date().toISOString().slice(0, 10), equity: 0 }],
    [data],
  );

  // Bornes Y
  const vals = React.useMemo(
    () => safeData.map((d) => Number(d.equity)).filter(Number.isFinite),
    [safeData],
  );
  const yDomain = React.useMemo(() => {
    if (!vals.length) return ["auto", "auto"];
    let min = Math.min(...vals),
      max = Math.max(...vals);
    if (min === max) {
      const pad = Math.max(10, Math.abs(min) * 0.1 || 10);
      return [min - pad, max + pad];
    }
    const pad = Math.max(10, Math.round((max - min) * 0.1));
    return [min - pad, max + pad];
  }, [vals]);

  // Plage dates
  const startLabel = safeData?.[0]?.date ?? "";
  const endLabel = safeData?.[safeData.length - 1]?.date ?? "";

  // Équité & direction (UNIQUEMENT basée sur la courbe)
  const firstEquity = Number(safeData[0]?.equity ?? 0);
  const lastEquity = Number(safeData[safeData.length - 1]?.equity ?? 0);
  const delta = +(lastEquity - firstEquity).toFixed(2);
  const isUp = delta > 0;
  const isFlat = delta === 0;

  const badgeCls = isFlat
    ? "text-slate-700 bg-slate-50 dark:text-slate-300 dark:bg-white/5 border-slate-200/60 dark:border-white/10"
    : isUp
      ? "text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-500/10 border-emerald-200/60 dark:border-emerald-400/30"
      : "text-rose-700 bg-rose-50 dark:text-rose-300 dark:bg-rose-500/10 border-rose-200/60 dark:border-rose-400/30";

  const tooltipStyle = {
    background: "rgba(15,18,32,0.92)",
    border: "1px solid rgba(124,58,237,.25)",
    borderRadius: 12,
    padding: "8px 10px",
    color: "white",
  };

  const fmtNum = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return "0.00";
    return x.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const yTick = (v) => {
    const abs = Math.abs(v);
    if (abs >= 1e6) return (v / 1e6).toFixed(1) + "M";
    if (abs >= 1e3) return Math.round(v / 1e3) + "k";
    return Number(v).toFixed(0);
  };

  const fmtDate = (s) => {
    const d = new Date(s);
    if (isNaN(d)) return String(s ?? "");
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
  };

  const numericHeight = typeof height === "number" ? height : undefined;

  // Recharts centre chaque étiquette sur son tick : la dernière date débordait
  // du cadre à droite. On ancre les extrémités vers l'intérieur.
  const DateTick = ({ x, y, payload, index, visibleTicksCount }) => {
    const anchor =
      index === 0
        ? "start"
        : index === visibleTicksCount - 1
          ? "end"
          : "middle";
    return (
      <text
        x={x}
        y={y}
        dy={12}
        textAnchor={anchor}
        fontSize={12}
        fill="currentColor"
        opacity={0.8}
      >
        {payload?.value}
      </text>
    );
  };

  return (
    <div
      className={
        "rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 overflow-hidden flex flex-col " +
        containerClassName
      }
      aria-label={title}
    >
      {/* En-tête */}
      <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-3 text-slate-800 dark:text-white">
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-violet-600/15 text-violet-500">
            <svg width="16" height="16" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M3 3h2v18H3zM7 11h2v10H7zM11 7h2v14h-2zM15 13h2v8h-2zM19 5h2v16h-2z"
              />
            </svg>
          </span>
          <div className="min-w-0">
            <h3 className="flex items-center gap-1.5 text-base sm:text-lg font-semibold tracking-tight">
              <span className="truncate">{title}</span>
              {help ? <InfoHint help={help} /> : null}
            </h3>
            <div className="text-[11px] sm:text-xs text-slate-500 dark:text-white/60 truncate">
              {startLabel && endLabel
                ? `${fmtDate(startLabel)} → ${fmtDate(endLabel)}`
                : "—"}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {/* Sélecteur de période : segmenté, une seule ligne. */}
          {rangeOptions.length ? (
            <div className="hidden items-center gap-0.5 rounded-full border border-slate-200 bg-slate-50/70 p-0.5 sm:inline-flex dark:border-white/10 dark:bg-white/5">
              {rangeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onRangeChange?.(opt.value)}
                  className={cn(
                    "rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors",
                    range === opt.value
                      ? "bg-violet-600 text-white"
                      : "text-slate-600 hover:text-violet-600 dark:text-white/60 dark:hover:text-violet-300",
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : null}

          {/* Badge = ÉQUITE ACTUELLE ; flèche/couleur = Δ de la courbe */}
          <span
            className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${badgeCls}`}
          >
            {isFlat ? "■" : isUp ? "▲" : "▼"} {fmtNum(lastEquity)}
          </span>
        </div>
      </div>

      {/* Même sélecteur, empilé sous l'en-tête sur mobile : sept puces ne
          tiennent pas à côté du titre en dessous de sm. */}
      {rangeOptions.length ? (
        <div className="flex flex-wrap items-center gap-1 px-4 pb-1 sm:hidden">
          {rangeOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onRangeChange?.(opt.value)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
                range === opt.value
                  ? "border-violet-500 bg-violet-600 text-white"
                  : "border-slate-200 text-slate-600 dark:border-white/10 dark:text-white/60",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}

      {/* Chart */}
      <div
        className={`w-full flex-1 ${chartClassName}`}
        style={numericHeight ? undefined : { height }}
      >
        <SafeResponsiveContainer width="100%" height={numericHeight ?? "100%"}>
          <ComposedChart
            data={safeData}
            margin={{ top: 12, right: 12, bottom: 8, left: 12 }}
          >
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.35" />
                <stop offset="100%" stopColor={color} stopOpacity="0.06" />
              </linearGradient>
            </defs>

            <CartesianGrid
              horizontal
              vertical
              stroke="currentColor"
              strokeDasharray="3 6"
              strokeOpacity={0.28}
            />
            <XAxis
              dataKey="date"
              tick={<DateTick />}
              tickMargin={6}
              axisLine={false}
              tickLine={false}
              minTickGap={24}
            />
            <YAxis
              width={48}
              domain={yDomain}
              tick={{ fontSize: 12, fill: "currentColor", opacity: 0.8 }}
              tickFormatter={yTick}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              labelStyle={{ color: "rgba(255,255,255,0.7)" }}
              itemStyle={{ color: "white" }}
              labelFormatter={(l) => fmtDate(l)}
              formatter={(v) => [fmtNum(v), "Equity"]}
              cursor={{ strokeDasharray: "4 2", opacity: 0.5 }}
            />
            <ReferenceLine
              y={0}
              stroke="currentColor"
              strokeOpacity={0.25}
              strokeDasharray="3 3"
            />

            {/* Une seule série => pas de doublon dans le tooltip */}
            <Area
              type="monotone"
              dataKey="equity"
              stroke={color}
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 4 }}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill={`url(#${gradId})`}
              isAnimationActive={false}
            />
          </ComposedChart>
        </SafeResponsiveContainer>
      </div>
    </div>
  );
}

/* --------- Étiquette d'axe du radar : nom + statistique réelle --------- */
// Recharts n'affiche que le libellé de l'axe. La valeur normalisée qui dessine
// la surface (0–100) ne dit rien au lecteur : on écrit la vraie statistique
// sous le nom, comme sur une fiche de score.
function RadarAxisTick({ x, y, cx, cy, payload, data = [] }) {
  const item = data[payload?.index] || {};
  const dx = x - cx;
  // Ancre déduite de la position autour du centre : les axes de gauche
  // doivent se lire vers l'intérieur, sinon ils sortent de la carte.
  const anchor = Math.abs(dx) < 12 ? "middle" : dx > 0 ? "start" : "end";
  const below = y > cy;

  return (
    <text
      x={x}
      y={y}
      textAnchor={anchor}
      fill="currentColor"
      className="text-slate-600 dark:text-white/70"
    >
      <tspan x={x} dy={below ? 4 : -4} fontSize={11}>
        {payload?.value}
      </tspan>
      <tspan x={x} dy={14} fontSize={12} fontWeight={600}>
        {item.display ?? ""}
      </tspan>
    </text>
  );
}

/* --------- Carte graphique standardisée (même header que PnLCurve) --------- */
function ChartCard({
  title,
  startLabel,
  endLabel,
  badgeText = "", // ex: "▲ Meilleure heure: 14h · +123.45"
  badgeTrend = "flat", // "up" | "down" | "flat"
  height = 224,
  children,
  className = "",
  help = null,
}) {
  const badgeCls =
    badgeTrend === "flat"
      ? "text-slate-700 bg-slate-50 dark:text-slate-300 dark:bg-white/5 border-slate-200/60 dark:border-white/10"
      : badgeTrend === "up"
        ? "text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-500/10 border-emerald-200/60 dark:border-emerald-400/30"
        : "text-rose-700 bg-rose-50 dark:text-rose-300 dark:bg-rose-500/10 border-rose-200/60 dark:border-rose-400/30";

  const arrow = badgeTrend === "flat" ? "■" : badgeTrend === "up" ? "▲" : "▼";

  const iconTone =
    badgeTrend === "up"
      ? "bg-emerald-500/12 text-emerald-600 dark:bg-emerald-500/14 dark:text-emerald-300"
      : badgeTrend === "down"
        ? "bg-rose-500/12 text-rose-600 dark:bg-rose-500/14 dark:text-rose-300"
        : "bg-violet-600/15 text-violet-500 dark:bg-violet-500/14 dark:text-violet-300";

  return (
    <div
      className={
        /* ⚠️ PAS d'`overflow-hidden` ici.
           Les info-bulles des graphiques (recharts, et le pop-up de la heatmap)
           sont positionnées par-dessus la carte : la moindre coupure au bord la
           tronquait, y compris pour recharts qui a pourtant
           `allowEscapeViewBox` — cette option ne fait sortir la bulle du SVG,
           pas d'un ancêtre qui rogne en CSS.
           Le contenu ne déborde pas : chaque bloc a son padding, et le
           graphique est rendu dans un conteneur à la taille de la carte. */
        "rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 " +
        className
      }
    >
      <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-3 text-slate-800 dark:text-white">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-lg",
              iconTone,
            )}
          >
            <svg width="16" height="16" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M3 3h2v18H3zM7 11h2v10H7zM11 7h2v14h-2zM15 13h2v8h-2zM19 5h2v16h-2z"
              />
            </svg>
          </span>
          <div className="min-w-0">
            <h3 className="flex items-center gap-1.5 text-base sm:text-lg font-semibold tracking-tight">
              <span className="truncate">{title}</span>
              {help ? <InfoHint help={help} /> : null}
            </h3>
            {startLabel && endLabel && (
              <div className="text-[11px] sm:text-xs text-slate-500 dark:text-white/60 truncate">
                {startLabel} → {endLabel}
              </div>
            )}
          </div>
        </div>

        {badgeText && (
          <span
            className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${badgeCls}`}
          >
            {arrow} {badgeText}
          </span>
        )}
      </div>

      <div style={{ height }} className="w-full">
        {children}
      </div>
    </div>
  );
}

function DurationPerformanceTooltip({ active, payload, isDarkMode = false }) {
  if (!active || !payload?.length) return null;

  const point = payload?.[0]?.payload || {};
  const duration = Number.isFinite(Number(point.durMin))
    ? Math.max(0, Number(point.durMin))
    : 0;
  const profit = toNumber(point.profit, 0);
  const isPositive = profit > 0;
  const isNegative = profit < 0;

  const profitTone = isPositive
    ? isDarkMode
      ? "text-emerald-200"
      : "text-emerald-700"
    : isNegative
      ? isDarkMode
        ? "text-rose-200"
        : "text-rose-700"
      : isDarkMode
        ? "text-slate-200"
        : "text-slate-700";

  return (
    <div
      className={cn(
        "pointer-events-none rounded-[18px] border px-3 py-2.5 text-xs backdrop-blur-xl",
        isDarkMode
          ? "border-white/12 bg-slate-950/94 text-slate-100 ring-1 ring-white/8"
          : "border-slate-200 bg-white/98 text-slate-800 ring-1 ring-white/80",
      )}
      style={{
        ...premiumTextStyle,
        width: "min(210px, calc(100vw - 32px))",
        maxWidth: "calc(100vw - 32px)",
      }}
    >
      <div
        className={cn(
          "mb-2 flex items-center justify-between gap-3 border-b pb-2",
          isDarkMode ? "border-white/10" : "border-slate-100",
        )}
      >
        <span
          className={cn(
            "font-semibold",
            isDarkMode ? "text-white" : "text-slate-950",
          )}
        >
          Trade analysé
        </span>
        {point.symbol ? (
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[10px] font-semibold",
              isDarkMode
                ? "bg-violet-400/16 text-violet-100"
                : "bg-violet-50 text-violet-700",
            )}
          >
            {point.symbol}
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <div
            className={cn(
              "text-[10px] font-medium uppercase tracking-[0.12em]",
              isDarkMode ? "text-slate-400" : "text-slate-500",
            )}
          >
            Durée
          </div>
          <div
            className={cn(
              "mt-0.5 font-semibold",
              isDarkMode ? "text-slate-100" : "text-slate-900",
            )}
          >
            {formatDurationTick(duration)}
          </div>
        </div>
        <div>
          <div
            className={cn(
              "text-[10px] font-medium uppercase tracking-[0.12em]",
              isDarkMode ? "text-slate-400" : "text-slate-500",
            )}
          >
            P&L
          </div>
          <div className={cn("mt-0.5 font-semibold", profitTone)}>
            {profit >= 0 ? "+" : ""}
            {profit.toFixed(2)}
          </div>
        </div>
      </div>

      {(point.side || point.ticket) && (
        <div
          className={cn(
            "mt-2 flex items-center justify-between gap-2 text-[11px]",
            isDarkMode ? "text-slate-300" : "text-slate-500",
          )}
        >
          <span>{point.side || "—"}</span>
          {point.ticket ? (
            <span className="truncate" style={premiumMonoStyle}>
              #{point.ticket}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

/* ---- SectionTitle (icône + dégradé + micro-anim) ---- */
function SectionTitle({ icon: Icon, title, right = null, accent = "violet" }) {
  const grad =
    accent === "emerald"
      ? "from-emerald-500/50 via-teal-500/40 to-emerald-400/30"
      : accent === "rose"
        ? "from-rose-500/50 via-orange-500/40 to-rose-400/30"
        : "from-violet-600/50 via-fuchsia-500/40 to-violet-400/30"; // default

  return (
    <div className="mb-3" style={premiumTextStyle}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <motion.div
          whileHover={{ y: -1, scale: 1.01 }}
          transition={{
            type: "spring",
            stiffness: 360,
            damping: 24,
            mass: 0.8,
          }}
          className="flex items-center gap-3"
        >
          <span
            className={`inline-flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br ${grad} text-white `}
          >
            {Icon ? <Icon size={16} /> : null}
          </span>
          <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        </motion.div>
        {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
      </div>
      <div className={`mt-2 h-px w-full bg-gradient-to-r ${grad}`} />
    </div>
  );
}

function ChartsPanelSkeleton({
  isDarkMode = false,
  pending = false,
  // Composant hors du composant page : il n'a pas acces au helper de
  // traduction, on lui passe les deux phrases.
  openingLabel = "",
  preparingLabel = "",
}) {
  return (
    <div
      className="mb-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
      aria-live="polite"
    >
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className={cn(
            "overflow-hidden rounded-3xl border p-4 backdrop-blur-xl animate-pulse",
            isDarkMode
              ? "border-white/10 bg-[linear-gradient(180deg,rgba(15,20,36,0.92),rgba(9,13,26,0.9))]"
              : "border-white/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(241,245,249,0.92))]",
          )}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="h-3 w-28 rounded-full bg-slate-200/80 dark:bg-white/10" />
              <div className="mt-3 h-2.5 w-40 rounded-full bg-slate-200/65 dark:bg-white/[0.08]" />
            </div>
            <div className="h-8 w-20 rounded-full bg-slate-200/80 dark:bg-white/10" />
          </div>
          <div className="mt-5 h-[168px] rounded-[24px] bg-slate-200/70 dark:bg-white/[0.06]" />
        </div>
      ))}

      <div
        className={cn(
          "md:col-span-2 xl:col-span-3 rounded-3xl border px-4 py-3 text-sm font-medium backdrop-blur-xl",
          isDarkMode
            ? "border-violet-400/20 bg-violet-500/10 text-white/82"
            : "border-violet-200/70 bg-violet-50/88 text-slate-700",
        )}
      >
        {pending ? openingLabel : preparingLabel}
      </div>
    </div>
  );
}

const weekdayLabel = (index) =>
  ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"][
    index
  ] || "—";

const formatPremiumAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const buildPremiumGroupStats = (items, getKey, getLabel) => {
  const map = new Map();
  items.forEach((trade) => {
    const key = getKey(trade);
    if (key == null || key === "") return;
    const label = getLabel ? getLabel(key, trade) : String(key);
    if (!map.has(key)) {
      map.set(key, {
        key,
        label,
        count: 0,
        wins: 0,
        losses: 0,
        pnl: 0,
      });
    }
    const row = map.get(key);
    const profit = toNumber(trade.profit);
    row.count += 1;
    row.pnl += profit;
    if (profit > 0) row.wins += 1;
    if (profit < 0) row.losses += 1;
  });

  return Array.from(map.values()).map((row) => ({
    ...row,
    pnl: +row.pnl.toFixed(2),
    winRate: row.count ? +((row.wins / row.count) * 100).toFixed(1) : 0,
    avg: row.count ? +(row.pnl / row.count).toFixed(2) : 0,
  }));
};

function buildPremiumJournalCoach(
  history = [],
  metrics = {},
  openPositions = [],
  notes = {},
  lang = "fr",
) {
  // Traduction en ligne plutôt qu'un dictionnaire à clés : l'audit produit
  // ~270 phrases, toutes uniques et souvent interpolées. Nommer chaque clé
  // aurait ajouté un annuaire à maintenir sans rien apporter, et éloigné le
  // texte de la condition qui le déclenche.
  const isEn = String(lang).startsWith("en");
  const T = (frText, enText) => (isEn ? enText : frText);

  // Ces deux helpers masquent volontairement leurs homonymes du module, qui
  // rendent des jours en français et formatent en `fr-FR`. Sans ça, l'audit
  // anglais affichait « Lundi » et « 1 234,56 » au milieu de phrases anglaises.
  // Les masquer ici évite de retoucher les 29 points d'appel.
  const weekdayLabel = (index) =>
    (isEn
      ? [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ]
      : [
          "Dimanche",
          "Lundi",
          "Mardi",
          "Mercredi",
          "Jeudi",
          "Vendredi",
          "Samedi",
        ])[index] || "—";

  const formatPremiumAmount = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0.00";
    return n.toLocaleString(isEn ? "en-US" : "fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
  const closed = (Array.isArray(history) ? history : [])
    .filter((trade) => trade?.close_time != null && !isCashflowRecord(trade))
    .map((trade) => ({ ...trade, profit: toNumber(trade.profit) }))
    .sort((a, b) => {
      const at =
        tsToDate(a.close_time)?.getTime() ||
        tsToDate(a.open_time)?.getTime() ||
        0;
      const bt =
        tsToDate(b.close_time)?.getTime() ||
        tsToDate(b.open_time)?.getTime() ||
        0;
      if (at !== bt) return at - bt;
      // Même départage que `computeJournalMetrics` : sans lui, les séries et
      // le découpage récent/précédent dépendaient de l'ordre arbitraire des
      // clôtures simultanées, fréquentes sur un compte automatisé.
      const ao = tsToDate(a.open_time)?.getTime() || 0;
      const bo = tsToDate(b.open_time)?.getTime() || 0;
      if (ao !== bo) return ao - bo;
      return String(a.ticket || "").localeCompare(String(b.ticket || ""), "en", {
        numeric: true,
      });
    });

  const total = closed.length;

  if (!total) {
    return {
      hasData: false,
      total: 0,
      score: 0,
      grade: T("Aucune donnée", "No data"),
      riskLevel: "—",
      riskKey: "none",
      confidence: "—",
      aiTone: "neutral",
      aiVoiceTitle: T(
        "Je n’ai pas encore assez de trades pour te coacher correctement.",
        "I don't have enough trades yet to coach you properly.",
      ),
      aiVoiceIntro: T(
        "L’analyse sera disponible dès que des positions seront clôturées. Pour l’instant, le diagnostic reste en attente afin d’éviter une conclusion fausse.",
        "The analysis will be available as soon as positions are closed. For now the diagnosis stays on hold to avoid a false conclusion.",
      ),
      aiConversation: [
        T(
          "Je ne vois pas encore de trades clôturés dans ton journal.",
          "I don't see any closed trades in your journal yet.",
        ),
        T(
          "Connecte ton agent MT5, laisse les premiers trades se clôturer, puis relance l’audit pour obtenir un vrai diagnostic.",
          "Connect your MT5 agent, let the first trades close, then run the audit again to get a real diagnosis.",
        ),
      ],
      aiFocusCards: [
        {
          label: T("Étape 1", "Step 1"),
          title: T("Connecter l’agent", "Connect the agent"),
          body: T(
            "Sans données de trades clôturés, je ne peux pas mesurer ta performance réelle.",
            "Without closed-trade data, I can't measure your real performance.",
          ),
          tone: "neutral",
        },
        {
          label: T("Étape 2", "Step 2"),
          title: T("Accumuler des données", "Build up data"),
          body: T(
            "Après quelques trades clôturés, l’audit pourra détecter les forces, les pertes répétées et les zones à éviter.",
            "After a few closed trades, the audit will be able to spot your strengths, repeated losses and the zones to avoid.",
          ),
          tone: "sky",
        },
        {
          label: T("Étape 3", "Step 3"),
          title: T("Revenir ici", "Come back here"),
          body: T(
            "Relance l’analyse dès que ton journal contient assez d’historique.",
            "Run the analysis again as soon as your journal holds enough history.",
          ),
          tone: "emerald",
        },
      ],
      aiNextTradeScript: T(
        "Avant de cliquer, valide un setup clair, un stop défini et une raison d’entrée écrite.",
        "Before you click, validate a clear setup, a defined stop and a written reason for entry.",
      ),
      headline: T(
        "Aucun trade clôturé n’est disponible pour générer un audit fiable.",
        "No closed trade is available to generate a reliable audit.",
      ),
      priorityFocus: T(
        "Connecte ton agent MT5 ou attends la clôture de tes premiers trades.",
        "Connect your MT5 agent or wait for your first trades to close.",
      ),
      traderProfile: T("Journal vide", "Empty journal"),
      dominantWeakness: T("Données insuffisantes", "Not enough data"),
      bestContextText: T(
        "Aucune zone forte détectée",
        "No strong zone detected",
      ),
      dangerContextText: T(
        "Aucune zone à corriger détectée",
        "No zone to fix detected",
      ),
      winRate: 0,
      net: 0,
      grossProfit: 0,
      grossLoss: 0,
      avgWin: 0,
      avgLoss: 0,
      profitFactor: 0,
      expectancy: 0,
      bestTrade: 0,
      worstTrade: 0,
      recoveryFactor: 0,
      maxDDPct: 0,
      bestWin: 0,
      maxLossStreak: 0,
      currentLossStreak: 0,
      consistencyRate: 0,
      avgDailyTrades: 0,
      maxDailyTrades: 0,
      noteRate: 0,
      recentStats: {
        count: 0,
        net: 0,
        winRate: 0,
        profitFactor: 0,
        expectancy: 0,
      },
      previousStats: {
        count: 0,
        net: 0,
        winRate: 0,
        profitFactor: 0,
        expectancy: 0,
      },
      bestSymbol: null,
      worstSymbol: null,
      bestHour: null,
      worstHour: null,
      bestDay: null,
      worstDay: null,
      bestSide: null,
      worstSide: null,
      bestTag: null,
      worstTag: null,
      strengths: [],
      problems: [],
      actions: [],
      hardRules: [],
      professionalPlan: [],
      nextTradeChecklist: [],
      scoreBreakdown: [],
      evidenceStack: [],
      metricExplanations: [],
      contextMatrix: [],
      executionPhases: [],
      sevenDayPlan: [],
      traderHealth: [],
      permissionStatus: {
        label: T("Aucune donnée", "No data"),
        tone: "neutral",
        title: T("Audit indisponible", "Audit unavailable"),
        reason: T(
          "Le journal ne contient pas encore de trades clôturés.",
          "The journal does not contain any closed trades yet.",
        ),
        conditions: [],
      },
      openExposure: 0,
    };
  }
  const wins = closed.filter((trade) => toNumber(trade.profit) > 0).length;
  const losses = closed.filter((trade) => toNumber(trade.profit) < 0).length;
  const flat = Math.max(0, total - wins - losses);
  const net = toNumber(
    metrics?.net,
    closed.reduce((sum, trade) => sum + toNumber(trade.profit), 0),
  );
  const winRate = toNumber(metrics?.winRate, total ? (wins / total) * 100 : 0);
  const grossProfit = toNumber(
    metrics?.grossProfit,
    closed
      .filter((t) => toNumber(t.profit) > 0)
      .reduce((s, t) => s + toNumber(t.profit), 0),
  );
  const grossLoss = Math.abs(
    toNumber(
      metrics?.grossLoss,
      closed
        .filter((t) => toNumber(t.profit) < 0)
        .reduce((s, t) => s + toNumber(t.profit), 0),
    ),
  );
  const avgWin = toNumber(metrics?.avgWin, wins ? grossProfit / wins : 0);
  const avgLoss = Math.abs(
    toNumber(metrics?.avgLoss, losses ? grossLoss / losses : 0),
  );
  const profitFactor = toNumber(
    metrics?.profitFactor,
    grossLoss ? grossProfit / grossLoss : grossProfit > 0 ? grossProfit : 0,
  );
  const expectancy = toNumber(metrics?.expectancy, total ? net / total : 0);
  // Le coach juge sur le drawdown RÉCENT, seul sur lequel le trader peut encore
  // agir. Le drawdown de toute l'histoire reste disponible pour être cité comme
  // contexte, mais il ne décide plus du verdict : un compte qui a quadruplé
  // restait sinon classé « risque critique » à cause d'une chute encaissée
  // quand il valait quatre fois moins.
  const maxDDPctAllTime = toNumber(metrics?.maxDDPct, 0);
  const recentDDDays = toNumber(metrics?.recentDDDays, 90);
  const recentDDTrades = toNumber(metrics?.recentDDTrades, 0);
  // Repli sur l'historique si la fenêtre récente est trop maigre : juger le
  // risque sur trois trades serait aussi faux que le juger sur 2025.
  const maxDDPct =
    recentDDTrades >= 20
      ? toNumber(metrics?.maxDDPctRecent, 0)
      : maxDDPctAllTime;
  // Vrai seulement quand les deux mesures diffèrent assez pour valoir une
  // mention dans le texte.
  const ddWindowDiffers =
    recentDDTrades >= 20 && Math.abs(maxDDPctAllTime - maxDDPct) >= 3;
  const maxLossStreak = toNumber(metrics?.bestLoss, 0);
  const bestTrade = toNumber(
    metrics?.bestTrade,
    closed.length ? Math.max(...closed.map((t) => toNumber(t.profit))) : 0,
  );
  const worstTrade = toNumber(
    metrics?.worstTrade,
    closed.length ? Math.min(...closed.map((t) => toNumber(t.profit))) : 0,
  );
  const recoveryFactor = toNumber(metrics?.recoveryFactor, 0);
  const sharpe = toNumber(metrics?.sharpe, 0);
  const rrRatio = avgLoss > 0 ? avgWin / avgLoss : avgWin > 0 ? 99 : 0;
  const minSample = Math.max(3, Math.ceil(total * 0.07));

  const statsFor = (items = []) => {
    const count = items.length;
    const gp = items
      .filter((t) => toNumber(t.profit) > 0)
      .reduce((s, t) => s + toNumber(t.profit), 0);
    const gl = Math.abs(
      items
        .filter((t) => toNumber(t.profit) < 0)
        .reduce((s, t) => s + toNumber(t.profit), 0),
    );
    const n = items.reduce((s, t) => s + toNumber(t.profit), 0);
    const w = items.filter((t) => toNumber(t.profit) > 0).length;
    return {
      count,
      net: +n.toFixed(2),
      winRate: count ? +((w / count) * 100).toFixed(1) : 0,
      profitFactor: gl ? +(gp / gl).toFixed(2) : gp > 0 ? +gp.toFixed(2) : 0,
      expectancy: count ? +(n / count).toFixed(2) : 0,
    };
  };

  const lastBlockSize =
    total >= 80
      ? 30
      : total >= 40
        ? 20
        : total >= 20
          ? 10
          : Math.max(3, Math.floor(total / 2));
  const recentTrades = closed.slice(Math.max(0, total - lastBlockSize));
  const previousTrades = closed.slice(
    Math.max(0, total - lastBlockSize * 2),
    Math.max(0, total - lastBlockSize),
  );
  const recentStats = statsFor(recentTrades);
  const previousStats = statsFor(previousTrades);
  const recentDelta = recentStats.net - previousStats.net;

  let currentLossStreak = 0;
  for (let i = closed.length - 1; i >= 0; i -= 1) {
    if (toNumber(closed[i]?.profit) < 0) currentLossStreak += 1;
    else break;
  }

  let equity = 0;
  let peak = 0;
  let trough = 0;
  let longestUnderWater = 0;
  let currentUnderWater = 0;
  closed.forEach((trade) => {
    equity += toNumber(trade.profit);
    if (equity >= peak) {
      peak = equity;
      currentUnderWater = 0;
    } else {
      currentUnderWater += 1;
      longestUnderWater = Math.max(longestUnderWater, currentUnderWater);
      trough = Math.min(trough, equity - peak);
    }
  });

  const notesCount = closed.filter((trade) => {
    const note = notes?.[trade.ticket] || trade.note || {};
    return Boolean(
      String(note.comment || "").trim() ||
      String(note.setup || "").trim() ||
      (Array.isArray(note.tags) && note.tags.length),
    );
  }).length;
  const noteRate = total ? Math.round((notesCount / total) * 100) : 0;

  const tagMap = new Map();
  closed.forEach((trade) => {
    const note = notes?.[trade.ticket] || trade.note || {};
    const tags = Array.isArray(note.tags) ? note.tags : [];
    tags.forEach((rawTag) => {
      const tag = String(rawTag || "").trim();
      if (!tag) return;
      const old = tagMap.get(tag) || { tag, count: 0, pnl: 0, wins: 0 };
      old.count += 1;
      old.pnl += toNumber(trade.profit);
      if (toNumber(trade.profit) > 0) old.wins += 1;
      tagMap.set(tag, old);
    });
  });
  const tagStats = Array.from(tagMap.values())
    .map((row) => ({
      ...row,
      pnl: +row.pnl.toFixed(2),
      winRate: row.count ? +((row.wins / row.count) * 100).toFixed(1) : 0,
    }))
    .filter((row) => row.count >= Math.max(2, Math.ceil(total * 0.025)))
    .sort((a, b) => b.pnl - a.pnl);
  const bestTag = tagStats[0] || null;
  const worstTag = [...tagStats].sort((a, b) => a.pnl - b.pnl)[0] || null;

  const symbolStats = buildPremiumGroupStats(
    closed,
    (trade) => safeText(trade.symbol),
    (key) => key,
  ).filter((row) => row.count >= minSample);
  const sideStats = buildPremiumGroupStats(
    closed,
    (trade) => safeText(trade.side || trade.type),
    (key) => key,
  ).filter((row) => row.count >= minSample);
  const hourStats = buildPremiumGroupStats(
    closed,
    (trade) => getHour(trade.open_time ?? trade.close_time),
    (key) => `${String(key).padStart(2, "0")}h`,
  ).filter((row) => row.count >= minSample);
  const dayStats = buildPremiumGroupStats(
    closed,
    (trade) => getWeekday(trade.open_time ?? trade.close_time),
    (key) => weekdayLabel(Number(key)),
  ).filter((row) => row.count >= minSample);
  const dailyStats = buildPremiumGroupStats(
    closed,
    (trade) => {
      const d = tsToDate(trade.close_time ?? trade.open_time);
      return d ? d.toISOString().slice(0, 10) : "";
    },
    (key) => key,
  );

  const bestSymbol = [...symbolStats].sort((a, b) => b.pnl - a.pnl)[0] || null;
  const worstSymbol = [...symbolStats].sort((a, b) => a.pnl - b.pnl)[0] || null;
  const bestHour = [...hourStats].sort((a, b) => b.pnl - a.pnl)[0] || null;
  const worstHour = [...hourStats].sort((a, b) => a.pnl - b.pnl)[0] || null;
  const bestDay = [...dayStats].sort((a, b) => b.pnl - a.pnl)[0] || null;
  const worstDay = [...dayStats].sort((a, b) => a.pnl - b.pnl)[0] || null;
  const bestSide = [...sideStats].sort((a, b) => b.pnl - a.pnl)[0] || null;
  const worstSide = [...sideStats].sort((a, b) => a.pnl - b.pnl)[0] || null;
  const worstTradingDay =
    [...dailyStats].sort((a, b) => a.pnl - b.pnl)[0] || null;
  const bestTradingDay =
    [...dailyStats].sort((a, b) => b.pnl - a.pnl)[0] || null;
  const positiveDays = dailyStats.filter((row) => row.pnl > 0).length;
  const negativeDays = dailyStats.filter((row) => row.pnl < 0).length;
  const consistencyRate = dailyStats.length
    ? Math.round((positiveDays / dailyStats.length) * 100)
    : 0;
  const maxDailyTrades = dailyStats.length
    ? Math.max(...dailyStats.map((row) => row.count))
    : 0;
  const avgDailyTrades = dailyStats.length
    ? dailyStats.reduce((sum, row) => sum + row.count, 0) / dailyStats.length
    : 0;
  const overTradingDays = dailyStats.filter(
    (row) => row.count >= Math.max(8, Math.ceil(avgDailyTrades * 1.8)),
  ).length;

  const openExposure = (
    Array.isArray(openPositions) ? openPositions : []
  ).reduce((sum, trade) => sum + Math.abs(toNumber(trade.volume)), 0);

  const profitabilityScore = clamp(
    8 +
      (profitFactor >= 1.8
        ? 18
        : profitFactor >= 1.35
          ? 14
          : profitFactor >= 1.1
            ? 9
            : profitFactor >= 1
              ? 5
              : profitFactor > 0
                ? -4
                : 0) +
      (expectancy > 0 ? 8 : expectancy < 0 ? -6 : 0) +
      (net > 0 ? 6 : net < 0 ? -6 : 0),
    0,
    32,
  );
  const riskScore = clamp(
    26 -
      (avgLoss > avgWin && avgWin > 0 ? 8 : 0) -
      (maxLossStreak >= 6
        ? 8
        : maxLossStreak >= 4
          ? 5
          : maxLossStreak >= 3
            ? 3
            : 0) -
      (maxDDPct <= -20 ? 9 : maxDDPct <= -12 ? 6 : maxDDPct <= -7 ? 3 : 0) -
      (currentLossStreak >= 3 ? 4 : currentLossStreak >= 2 ? 2 : 0),
    0,
    26,
  );
  const selectivityScore = clamp(
    18 -
      (avgDailyTrades >= 12
        ? 8
        : avgDailyTrades >= 8
          ? 5
          : avgDailyTrades >= 5
            ? 2
            : 0) -
      (maxDailyTrades >= 20 ? 5 : maxDailyTrades >= 12 ? 3 : 0) +
      (bestSymbol?.pnl > 0 && worstSymbol?.pnl < 0 ? 2 : 0),
    0,
    20,
  );
  const consistencyScore = clamp(
    10 +
      (consistencyRate >= 60
        ? 6
        : consistencyRate >= 50
          ? 3
          : consistencyRate < 35 && dailyStats.length >= 5
            ? -4
            : 0) +
      (recentStats.count && recentStats.net > previousStats.net
        ? 3
        : recentStats.count && recentStats.net < previousStats.net
          ? -3
          : 0) +
      (sharpe > 0.25 ? 2 : sharpe < -0.1 ? -2 : 0),
    0,
    16,
  );
  const processScore = clamp(
    noteRate >= 80 ? 6 : noteRate >= 50 ? 4 : noteRate >= 25 ? 2 : 0,
    0,
    6,
  );
  let score = Math.round(
    profitabilityScore +
      riskScore +
      selectivityScore +
      consistencyScore +
      processScore,
  );
  if (total < 10) score = Math.min(score, 55);
  if (total < 5) score = Math.min(score, 42);
  score = Math.round(clamp(score, 10, 96));

  const categoryTone = (value) =>
    value >= 75 ? "emerald" : value >= 55 ? "sky" : value >= 35 ? "amber" : "rose";
  const signedCompact = (value) =>
    `${Number(value) >= 0 ? "+" : ""}${formatPremiumAmount(value)}`;
  const profitabilityCategoryScore = Math.round(
    clamp((profitabilityScore / 32) * 100, 0, 100),
  );
  const riskCategoryScore = Math.round(clamp((riskScore / 26) * 100, 0, 100));
  const disciplineCategoryScore = Math.round(
    clamp(
      (selectivityScore / 20) * 64 +
        (currentLossStreak <= 1 ? 10 : currentLossStreak <= 3 ? 5 : 0) +
        (maxLossStreak <= 2
          ? 12
          : maxLossStreak <= 4
            ? 7
            : maxLossStreak <= 6
              ? 3
              : 0) +
        (overTradingDays === 0 ? 8 : overTradingDays <= 2 ? 4 : 0) +
        (openExposure <= 0 ? 6 : openExposure <= 2 ? 3 : 0),
      0,
      100,
    ),
  );
  const regularityCategoryScore = Math.round(
    clamp((consistencyScore / 16) * 100, 0, 100),
  );
  const journalQualityCategoryScore = Math.round(clamp(noteRate, 0, 100));

  const scoreBreakdown = [
    {
      label: T("Rentabilité", "Profitability"),
      value: profitabilityCategoryScore,
      detail: `PF ${profitFactor ? profitFactor.toFixed(2) : "—"} • Expect. ${signedCompact(expectancy)}`,
      description:
        profitabilityCategoryScore >= 55
          ? T(
              "Les gains commencent à couvrir correctement les pertes.",
              "Gains are starting to cover losses properly.",
            )
          : T(
              "Les pertes pèsent encore trop sur la performance nette.",
              "Losses still weigh too heavily on net performance.",
            ),
      tone: categoryTone(profitabilityCategoryScore),
    },
    {
      label: T("Risque", "Risk"),
      value: riskCategoryScore,
      detail: T(
        `${Math.abs(maxDDPct).toFixed(1)}% DD • série P ${maxLossStreak}`,
        `${Math.abs(maxDDPct).toFixed(1)}% DD • L streak ${maxLossStreak}`,
      ),
      description:
        riskCategoryScore >= 55
          ? T(
              "Le risque reste exploitable avec un cadre strict.",
              "The risk stays workable with a strict framework.",
            )
          : T(
              "Le drawdown ou les séries de pertes imposent une réduction du risque.",
              "The drawdown or the losing streaks call for a risk reduction.",
            ),
      tone: categoryTone(riskCategoryScore),
    },
    {
      label: T("Discipline", "Discipline"),
      value: disciplineCategoryScore,
      detail: T(
        `${avgDailyTrades.toFixed(1)} trades/j • max ${maxDailyTrades}`,
        `${avgDailyTrades.toFixed(1)} trades/day • max ${maxDailyTrades}`,
      ),
      description:
        disciplineCategoryScore >= 55
          ? T(
              "La fréquence reste assez contrôlée pour construire un process.",
              "Your frequency stays controlled enough to build a process.",
            )
          : T(
              "La fréquence ou les pertes consécutives signalent un risque de surtrading.",
              "Your frequency or your consecutive losses point to an overtrading risk.",
            ),
      tone: categoryTone(disciplineCategoryScore),
    },
    {
      label: T("Régularité", "Consistency"),
      value: regularityCategoryScore,
      detail: T(
        `${consistencyRate}% jours positifs • récent ${signedCompact(recentStats.net)}`,
        `${consistencyRate}% positive days • recent ${signedCompact(recentStats.net)}`,
      ),
      description:
        regularityCategoryScore >= 55
          ? T(
              "Les résultats sont plus réguliers sur les jours analysés.",
              "Results are more consistent across the days analysed.",
            )
          : T(
              "La performance reste trop irrégulière d’un bloc de trades à l’autre.",
              "Performance stays too erratic from one block of trades to the next.",
            ),
      tone: categoryTone(regularityCategoryScore),
    },
    {
      label: T("Qualité du journal", "Journal quality"),
      value: journalQualityCategoryScore,
      detail: T(
        `${noteRate}% trades notés • ${notesCount}/${total}`,
        `${noteRate}% trades annotated • ${notesCount}/${total}`,
      ),
      description:
        journalQualityCategoryScore >= 55
          ? T(
              "Le journal contient assez de traces pour corriger précisément.",
              "The journal holds enough traces to fix things precisely.",
            )
          : T(
              "Il manque encore des notes pour identifier les vraies causes des erreurs.",
              "Notes are still missing to identify the real causes of your mistakes.",
            ),
      tone: categoryTone(journalQualityCategoryScore),
    },
  ];

  const grade =
    score >= 85
      ? T("Pro", "Pro")
      : score >= 72
        ? T("Solide", "Solid")
        : score >= 58
          ? T("À structurer", "Needs structure")
          : score >= 42
            ? T("Risque élevé", "High risk")
            : T("Danger", "Danger");
  // Clé stable en plus du libellé : l'affichage colore la pastille de risque
  // selon le niveau, et comparer le libellé traduit renvoyait « vert » sur un
  // risque critique dès qu'on passait en anglais.
  const riskKey =
    score >= 76
      ? "controlled"
      : score >= 62
        ? "medium"
        : score >= 48
          ? "high"
          : "critical";
  const riskLevel =
    riskKey === "controlled"
      ? T("Maîtrisé", "Controlled")
      : riskKey === "medium"
        ? T("Moyen", "Medium")
        : riskKey === "high"
          ? T("Élevé", "High")
          : T("Critique", "Critical");
  const confidence =
    total >= 120
      ? T("Institutionnelle", "Institutional")
      : total >= 80
        ? T("Très forte", "Very high")
        : total >= 50
          ? T("Forte", "High")
          : total >= 30
            ? T("Bonne", "Good")
            : total >= 12
              ? T("Moyenne", "Medium")
              : T("Faible", "Low");

  const problems = [];
  const actions = [];
  const strengths = [];
  const professionalPlan = [];
  const hardRules = [];
  const nextTradeChecklist = [];

  const addProblem = (title, detail, tone = "amber") =>
    problems.push({ title, detail, tone });
  const addAction = (action) => actions.push(action);
  const addRule = (rule) => hardRules.push(rule);
  const addPlan = (plan) => professionalPlan.push(plan);

  if (total < 12) {
    addProblem(
      T("Échantillon limité", "Limited sample"),
      T(
        "L’audit peut donner une direction, mais il faut au moins 30 trades clôturés pour valider une vraie conclusion.",
        "The audit can give you a direction, but at least 30 closed trades are needed to validate a real conclusion.",
      ),
      "amber",
    );
    addAction(
      T(
        "Continue à trader avec la même méthode jusqu’à 30 trades avant de changer toute la stratégie.",
        "Keep trading the same method up to 30 trades before changing the whole strategy.",
      ),
    );
    addPlan(
      T(
        "Pendant les prochains trades, note systématiquement le setup, l’heure, l’émotion et la raison de sortie.",
        "Over the next trades, systematically log the setup, the time, the emotion and the reason for exiting.",
      ),
    );
  }

  if (profitFactor > 0 && profitFactor < 1) {
    addProblem(
      T("Système non rentable", "System not profitable"),
      T(
        `Profit factor ${profitFactor.toFixed(2)} : les pertes encaissées dépassent les gains.`,
        `Profit factor ${profitFactor.toFixed(2)}: the losses taken outweigh the gains.`,
      ),
      "rose",
    );
    addAction(
      T(
        "Objectif numéro 1 : arrêter de chercher plus de volume. Il faut d’abord supprimer les contextes négatifs du journal.",
        "Goal number 1: stop chasing more volume. The negative contexts in the journal have to go first.",
      ),
    );
    addPlan(
      T(
        "Réduis le nombre de trades et conserve uniquement les setups liés aux meilleurs actifs/heures/jours détectés.",
        "Cut the number of trades and keep only the setups tied to the best assets/hours/days detected.",
      ),
    );
    addRule(
      T(
        "Tant que le profit factor reste sous 1.10, aucun lot ne doit être augmenté.",
        "As long as the profit factor stays below 1.10, no lot size may be increased.",
      ),
    );
  } else if (profitFactor >= 1.35) {
    strengths.push(
      T(
        `Base rentable détectée : profit factor ${profitFactor.toFixed(2)}. Le système mérite d’être protégé, pas sur-tradé.`,
        `Profitable base detected: profit factor ${profitFactor.toFixed(2)}. The system deserves to be protected, not overtraded.`,
      ),
    );
  }

  if (expectancy < 0) {
    addProblem(
      T("Expectancy négative", "Negative expectancy"),
      T(
        `Chaque trade coûte en moyenne ${formatPremiumAmount(Math.abs(expectancy))}. Le problème vient soit de la sélection, soit des sorties.`,
        `Each trade costs ${formatPremiumAmount(Math.abs(expectancy))} on average. The problem is either your selection or your exits.`,
      ),
      "rose",
    );
    addAction(
      T(
        "Réduis la fréquence et exige une raison d’entrée écrite avant chaque prise de position.",
        "Cut your frequency and require a written reason for entry before every position.",
      ),
    );
  } else if (expectancy > 0) {
    strengths.push(
      T(
        `Expectancy positive : le journal génère en moyenne +${formatPremiumAmount(expectancy)} par trade clôturé.`,
        `Positive expectancy: the journal generates +${formatPremiumAmount(expectancy)} on average per closed trade.`,
      ),
    );
  }

  if (avgLoss > avgWin && avgWin > 0) {
    addProblem(
      T("Ratio gain/perte défavorable", "Unfavourable win/loss ratio"),
      T(
        `Gain moyen ${formatPremiumAmount(avgWin)} contre perte moyenne ${formatPremiumAmount(avgLoss)}. Les mauvais trades effacent les bons.`,
        `Average win ${formatPremiumAmount(avgWin)} against average loss ${formatPremiumAmount(avgLoss)}. The bad trades wipe out the good ones.`,
      ),
      "rose",
    );
    addAction(
      T(
        "Priorité technique : réduire la perte moyenne. Coupe un trade invalidé sans attendre qu’il devienne émotionnel.",
        "Technical priority: bring the average loss down. Cut an invalidated trade before it turns emotional.",
      ),
    );
    addRule(
      T(
        "Un trade sans stop défini avant l’entrée est interdit.",
        "A trade without a stop defined before entry is forbidden.",
      ),
    );
  } else if (rrRatio >= 1.2 && avgWin > 0) {
    strengths.push(
      T(
        `Bon ratio gain/perte : tes gains moyens couvrent tes pertes moyennes (${rrRatio.toFixed(2)}R).`,
        `Good win/loss ratio: your average gains cover your average losses (${rrRatio.toFixed(2)}R).`,
      ),
    );
  }

  if (winRate < 42 && total >= 20 && rrRatio < 1.2) {
    addProblem(
      T("Win rate faible sans compensation", "Low win rate with no offset"),
      T(
        `Win rate ${winRate.toFixed(1)}% avec un ratio gain/perte insuffisant. Cette combinaison fatigue vite le capital.`,
        `Win rate ${winRate.toFixed(1)}% with an insufficient win/loss ratio. That combination drains capital fast.`,
      ),
      "rose",
    );
    addAction(
      T(
        "Soit tu améliores le taux de réussite avec des entrées plus strictes, soit tu augmentes le ratio reward/risk avant de continuer.",
        "Either you raise the hit rate with stricter entries, or you raise the reward/risk ratio before going on.",
      ),
    );
  } else if (winRate >= 55 && profitFactor >= 1.1) {
    strengths.push(
      T(
        `Bonne précision d’exécution : win rate ${winRate.toFixed(1)}% avec un profit factor supérieur à 1.`,
        `Good execution accuracy: win rate ${winRate.toFixed(1)}% with a profit factor above 1.`,
      ),
    );
  }

  if (maxLossStreak >= 4 || currentLossStreak >= 2) {
    addProblem(
      T("Séries de pertes dangereuses", "Dangerous losing streaks"),
      T(
        `Plus grosse série : ${maxLossStreak}. Série actuelle : ${currentLossStreak}. Sans règle d’arrêt, le risque émotionnel augmente.`,
        `Longest streak: ${maxLossStreak}. Current streak: ${currentLossStreak}. Without a stop rule, the emotional risk keeps rising.`,
      ),
      maxLossStreak >= 5 ? "rose" : "amber",
    );
    addAction(
      T(
        "Mets un coupe-circuit : 2 pertes = pause ; 3 pertes = arrêt de la session.",
        "Set a circuit breaker: 2 losses = break; 3 losses = session over.",
      ),
    );
    addRule(
      T(
        "Après 2 pertes consécutives, pause obligatoire de 30 minutes minimum.",
        "After 2 consecutive losses, a break of at least 30 minutes is mandatory.",
      ),
    );
    addRule(
      T(
        "Après 3 pertes dans la même journée, arrêt total de la session.",
        "After 3 losses in the same day, the session stops completely.",
      ),
    );
  }

  if (maxDDPct <= -7) {
    addProblem(
      T("Drawdown à contrôler", "Drawdown to control"),
      T(
        `Drawdown estimé ${Math.abs(maxDDPct).toFixed(2)}% sur les ${recentDDDays} derniers jours. Le capital subit encore trop les mauvaises phases.${
          ddWindowDiffers
            ? ` Sur tout l’historique le pire creux atteint ${Math.abs(maxDDPctAllTime).toFixed(2)}%, mesuré quand le compte était plus petit.`
            : ""
        }`,
        `Estimated drawdown ${Math.abs(maxDDPct).toFixed(2)}% over the last ${recentDDDays} days. Your capital still takes too much damage in bad phases.${
          ddWindowDiffers
            ? ` Across the full history the worst dip reaches ${Math.abs(maxDDPctAllTime).toFixed(2)}%, measured when the account was smaller.`
            : ""
        }`,
      ),
      maxDDPct <= -15 ? "rose" : "amber",
    );
    addAction(
      T(
        "Pendant le drawdown, baisse le risque et n’augmente pas le lot avant un nouveau sommet de courbe.",
        "While in drawdown, lower your risk and do not increase lot size before a new equity high.",
      ),
    );
    addRule(
      T(
        "Aucune augmentation de lot tant que la courbe n’a pas récupéré le dernier drawdown.",
        "No lot increase until the equity curve has recovered the last drawdown.",
      ),
    );
  }

  if (avgDailyTrades >= 8 || maxDailyTrades >= 15 || overTradingDays >= 2) {
    addProblem(
      T("Surtrading probable", "Likely overtrading"),
      T(
        `Moyenne ${avgDailyTrades.toFixed(1)} trades/jour, pic ${maxDailyTrades}. Trop d’opérations réduit la qualité des décisions.`,
        `Average ${avgDailyTrades.toFixed(1)} trades/day, peak ${maxDailyTrades}. Too many trades lower the quality of your decisions.`,
      ),
      "rose",
    );
    addAction(
      T(
        "Passe en mode sniper : 3 trades maximum par session ou arrêt dès que l’objectif/jour est atteint.",
        "Switch to sniper mode: 3 trades maximum per session, or stop as soon as the daily target is hit.",
      ),
    );
    addRule(
      T(
        "Maximum 3 trades par session jusqu’à stabilisation du profit factor.",
        "Maximum 3 trades per session until the profit factor stabilises.",
      ),
    );
  }

  if (worstSymbol && worstSymbol.pnl < 0) {
    addProblem(
      T(
        `Actif à réduire : ${worstSymbol.label}`,
        `Asset to cut back: ${worstSymbol.label}`,
      ),
      `${worstSymbol.count} trades, P&L ${formatPremiumAmount(worstSymbol.pnl)}, win rate ${worstSymbol.winRate}%.`,
      worstSymbol.pnl < -Math.abs(net) * 0.25 ? "rose" : "amber",
    );
    addAction(
      T(
        `Réduis ou bloque ${worstSymbol.label} pendant 7 jours sauf setup A+ parfaitement confirmé.`,
        `Cut back or block ${worstSymbol.label} for 7 days unless the setup is a perfectly confirmed A+.`,
      ),
    );
  }

  if (worstHour && worstHour.pnl < 0) {
    addProblem(
      T(`Heure faible : ${worstHour.label}`, `Weak hour: ${worstHour.label}`),
      T(
        `${worstHour.count} trades, moyenne ${formatPremiumAmount(worstHour.avg)} par trade.`,
        `${worstHour.count} trades, average ${formatPremiumAmount(worstHour.avg)} per trade.`,
      ),
      "amber",
    );
    addAction(
      T(
        `Évite les entrées autour de ${worstHour.label} tant que cette tranche reste négative.`,
        `Avoid entries around ${worstHour.label} as long as this time window stays negative.`,
      ),
    );
  }

  if (worstDay && worstDay.pnl < 0) {
    addProblem(
      T(`Jour faible : ${worstDay.label}`, `Weak day: ${worstDay.label}`),
      `${worstDay.count} trades, P&L ${formatPremiumAmount(worstDay.pnl)}.`,
      "amber",
    );
    addAction(
      T(
        `Le ${worstDay.label.toLowerCase()}, limite-toi au meilleur setup ou observe sans trader.`,
        // Pas de `toLowerCase()` ici : l'anglais garde la majuscule aux jours.
        `On ${worstDay.label}, stick to your best setup only or just watch without trading.`,
      ),
    );
  }

  if (worstTag && worstTag.pnl < 0) {
    addProblem(
      T(`Tag coûteux : ${worstTag.tag}`, `Costly tag: ${worstTag.tag}`),
      `${worstTag.count} trades, P&L ${formatPremiumAmount(worstTag.pnl)}, win rate ${worstTag.winRate}%.`,
      "amber",
    );
    addAction(
      T(
        `Si le tag ${worstTag.tag} apparaît avant l’entrée, baisse le risque ou annule le trade.`,
        `If the tag ${worstTag.tag} shows up before entry, lower the risk or cancel the trade.`,
      ),
    );
  }

  if (noteRate < 35 && total >= 12) {
    addProblem(
      T("Journalisation insuffisante", "Insufficient journaling"),
      T(
        `Seulement ${noteRate}% des trades sont documentés. L’analyse perd en précision comportementale.`,
        `Only ${noteRate}% of trades are documented. The analysis loses behavioural precision.`,
      ),
      "amber",
    );
    addAction(
      T(
        "Ajoute un tag à chaque trade : setup, session, émotion, raison d’entrée et raison de sortie.",
        "Add a tag to every trade: setup, session, emotion, reason for entry and reason for exit.",
      ),
    );
    addPlan(
      T(
        "Après chaque trade, prends 20 secondes pour noter pourquoi tu es entré et pourquoi tu es sorti.",
        "After every trade, take 20 seconds to write down why you entered and why you exited.",
      ),
    );
  } else if (noteRate >= 60) {
    strengths.push(
      T(
        `Bon process de suivi : ${noteRate}% des trades contiennent des notes ou tags exploitables.`,
        `Good tracking process: ${noteRate}% of trades carry usable notes or tags.`,
      ),
    );
  }

  if (recentStats.count >= 5 && previousStats.count >= 5) {
    if (
      recentDelta > 0 &&
      recentStats.profitFactor >= previousStats.profitFactor
    ) {
      strengths.push(
        T(
          `Amélioration récente : les ${recentStats.count} derniers trades font mieux que la période précédente.`,
          `Recent improvement: the last ${recentStats.count} trades do better than the previous period.`,
        ),
      );
    }
    if (
      recentDelta < 0 &&
      recentStats.profitFactor < previousStats.profitFactor
    ) {
      addProblem(
        T("Dégradation récente", "Recent deterioration"),
        T(
          `Les ${recentStats.count} derniers trades baissent de ${formatPremiumAmount(Math.abs(recentDelta))} vs la période précédente.`,
          `The last ${recentStats.count} trades are down ${formatPremiumAmount(Math.abs(recentDelta))} vs the previous period.`,
        ),
        "amber",
      );
      addAction(
        T(
          "Reviens aux règles qui fonctionnaient avant la dégradation récente, surtout sur l’heure et l’actif.",
          "Go back to the rules that worked before this deterioration, especially on timing and asset choice.",
        ),
      );
    }
  }

  if (bestSymbol && bestSymbol.pnl > 0)
    strengths.push(
      T(
        `${bestSymbol.label} ressort comme actif prioritaire : ${formatPremiumAmount(bestSymbol.pnl)} sur ${bestSymbol.count} trades.`,
        `${bestSymbol.label} stands out as your priority asset: ${formatPremiumAmount(bestSymbol.pnl)} over ${bestSymbol.count} trades.`,
      ),
    );
  if (bestHour && bestHour.pnl > 0)
    strengths.push(
      T(
        `Meilleure fenêtre horaire : ${bestHour.label}, P&L ${formatPremiumAmount(bestHour.pnl)}.`,
        `Best time window: ${bestHour.label}, P&L ${formatPremiumAmount(bestHour.pnl)}.`,
      ),
    );
  if (bestDay && bestDay.pnl > 0)
    strengths.push(
      T(
        `Meilleur jour : ${bestDay.label}, P&L ${formatPremiumAmount(bestDay.pnl)}.`,
        `Best day: ${bestDay.label}, P&L ${formatPremiumAmount(bestDay.pnl)}.`,
      ),
    );
  if (bestTag && bestTag.pnl > 0)
    strengths.push(
      T(
        `Tag performant : ${bestTag.tag}, P&L ${formatPremiumAmount(bestTag.pnl)}.`,
        `Strong tag: ${bestTag.tag}, P&L ${formatPremiumAmount(bestTag.pnl)}.`,
      ),
    );

  if (
    bestSide &&
    worstSide &&
    bestSide.key !== worstSide.key &&
    bestSide.pnl > 0 &&
    worstSide.pnl < 0
  ) {
    addAction(
      T(
        `Favorise ${bestSide.label} et sois beaucoup plus sélectif sur ${worstSide.label}, qui ressort négatif.`,
        `Favour ${bestSide.label} and be far more selective on ${worstSide.label}, which comes out negative.`,
      ),
    );
  }

  if (!problems.length) {
    addProblem(
      T("Aucun blocage critique détecté", "No critical blocker detected"),
      T(
        "Les métriques principales restent cohérentes. Le travail consiste maintenant à protéger les bons contextes et éviter le surtrading.",
        "The main metrics stay coherent. The job now is to protect the good contexts and avoid overtrading.",
      ),
      "emerald",
    );
  }
  if (!strengths.length) {
    strengths.push(
      T(
        "Le journal est exploitable : tu peux maintenant prendre des décisions basées sur les données, pas seulement sur le ressenti.",
        "The journal is usable: you can now make decisions based on data, not just on feel.",
      ),
    );
  }
  if (!actions.length) {
    addAction(
      T(
        "Garde le même cadre et surveille profit factor, drawdown, séries de pertes et fréquence de trades sur les 30 prochains trades.",
        "Keep the same framework and watch profit factor, drawdown, losing streaks and trade frequency over the next 30 trades.",
      ),
    );
  }

  if (!hardRules.length) {
    addRule(
      T(
        "Risque fixe par trade ; aucune augmentation après une perte.",
        "Fixed risk per trade; no increase after a loss.",
      ),
    );
    addRule(
      T(
        "Un seul setup principal pendant 7 jours pour mesurer proprement.",
        "One main setup only for 7 days, so the measurement stays clean.",
      ),
    );
    addRule(
      T(
        "Arrêt immédiat si la journée dépasse la perte maximale prévue.",
        "Stop immediately if the day goes past your planned maximum loss.",
      ),
    );
  }

  addPlan(
    bestSymbol?.label
      ? T(
          `Priorité : travailler ${bestSymbol.label} dans les fenêtres les plus rentables.`,
          `Priority: work ${bestSymbol.label} in the most profitable time windows.`,
        )
      : T(
          "Priorité : garder uniquement les setups les plus clairs.",
          "Priority: keep only the clearest setups.",
        ),
  );
  addPlan(
    worstSymbol?.pnl < 0
      ? T(
          `Réduction : limiter ${worstSymbol.label} ou le passer en observation.`,
          `Reduction: limit ${worstSymbol.label} or move it to watch-only.`,
        )
      : T(
          "Réduction : supprimer les trades pris sans raison écrite.",
          "Reduction: cut out trades taken without a written reason.",
        ),
  );
  addPlan(
    bestHour?.label
      ? T(
          `Timing : privilégier ${bestHour.label} et comparer les résultats après 7 jours.`,
          `Timing: favour ${bestHour.label} and compare the results after 7 days.`,
        )
      : T(
          "Timing : noter l’heure de chaque entrée pour isoler ta meilleure fenêtre.",
          "Timing: log the time of every entry to isolate your best time window.",
        ),
  );
  addPlan(
    T(
      "Revue : chaque soir, relis les trades perdants et classe-les en erreur technique, émotionnelle ou marché invalide.",
      "Review: every evening, reread your losing trades and sort them into technical error, emotional error or invalid market.",
    ),
  );

  nextTradeChecklist.push(
    bestSymbol?.label
      ? T(
          `L’actif fait-il partie de tes zones fortes (${bestSymbol.label}) ?`,
          `Is the asset one of your strong zones (${bestSymbol.label})?`,
        )
      : T(
          "Le setup est-il clairement identifié ?",
          "Is the setup clearly identified?",
        ),
  );
  nextTradeChecklist.push(
    worstHour?.pnl < 0
      ? T(
          `L’entrée évite-t-elle la mauvaise fenêtre ${worstHour.label} ?`,
          `Does the entry avoid the bad time window ${worstHour.label}?`,
        )
      : T(
          "L’heure d’entrée est-elle cohérente avec ton plan ?",
          "Is the entry time consistent with your plan?",
        ),
  );
  nextTradeChecklist.push(
    T(
      "Le stop loss, la perte maximale et le scénario d’invalidation sont-ils définis avant de cliquer ?",
      "Are the stop loss, the maximum loss and the invalidation scenario defined before you click?",
    ),
  );
  nextTradeChecklist.push(
    T(
      "La raison d’entrée peut-elle être écrite en une phrase simple ?",
      "Can the reason for entry be written in one simple sentence?",
    ),
  );
  nextTradeChecklist.push(
    T(
      "Ce trade respecte-t-il le nombre maximum de trades autorisé pour la session ?",
      "Does this trade respect the maximum number of trades allowed for the session?",
    ),
  );

  const primaryIssue = problems[0];
  const dominantWeakness =
    primaryIssue?.title || T("Gestion du risque", "Risk management");
  const traderProfile =
    avgDailyTrades >= 8 || maxDailyTrades >= 15
      ? T(
          "Scalpeur agressif : performance dépendante de la discipline et du filtre d’entrée.",
          "Aggressive scalper: performance hangs on discipline and on your entry filter.",
        )
      : profitFactor >= 1.35 && maxDDPct > -8
        ? T(
            "Trader structuré : base exploitable, priorité à la protection des gains.",
            "Structured trader: usable base, priority is protecting the gains.",
          )
        : avgLoss > avgWin && avgWin > 0
          ? T(
              "Trader à potentiel, mais pertes trop lourdes : priorité à la sortie et au stop.",
              "Trader with potential, but losses are too heavy: priority is the exit and the stop.",
            )
          : expectancy < 0
            ? T(
                "Profil en correction : les règles actuelles doivent être resserrées.",
                "Profile under correction: the current rules need tightening.",
              )
            : T(
                "Profil en construction : les données commencent à donner une direction.",
                "Profile still building: the data is starting to point somewhere.",
              );

  const priorityFocus =
    profitFactor > 0 && profitFactor < 1
      ? T(
          "Rendre le système rentable avant d’augmenter le volume.",
          "Make the system profitable before increasing volume.",
        )
      : avgLoss > avgWin && avgWin > 0
        ? T(
            "Réduire la perte moyenne avant de chercher plus de profits.",
            "Bring the average loss down before chasing more profit.",
          )
        : maxLossStreak >= 4 || currentLossStreak >= 2
          ? T(
              "Installer un coupe-circuit strict contre les séries de pertes.",
              "Put a strict circuit breaker in place against losing streaks.",
            )
          : avgDailyTrades >= 8 || maxDailyTrades >= 15
            ? T(
                "Réduire la fréquence et passer en mode sniper.",
                "Cut the frequency and switch to sniper mode.",
              )
            : noteRate < 35 && total >= 12
              ? T(
                  "Documenter les trades pour détecter les vrais patterns.",
                  "Document your trades to detect the real patterns.",
                )
              : T(
                  "Conserver les meilleurs contextes et éliminer les zones faibles.",
                  "Keep the best contexts and eliminate the weak zones.",
                );

  const bestContextText =
    [
      bestSymbol?.label
        ? T(`Actif ${bestSymbol.label}`, `Asset ${bestSymbol.label}`)
        : null,
      bestHour?.label ? bestHour.label : null,
      bestDay?.label ? bestDay.label : null,
      bestTag?.pnl > 0 ? `Tag ${bestTag.tag}` : null,
    ]
      .filter(Boolean)
      .join(" • ") || T("Pas encore assez de données", "Not enough data yet");

  const dangerContextText =
    [
      worstSymbol?.label && worstSymbol.pnl < 0
        ? T(`Actif ${worstSymbol.label}`, `Asset ${worstSymbol.label}`)
        : null,
      worstHour?.label && worstHour.pnl < 0 ? worstHour.label : null,
      worstDay?.label && worstDay.pnl < 0 ? worstDay.label : null,
      worstTag?.pnl < 0 ? `Tag ${worstTag.tag}` : null,
    ]
      .filter(Boolean)
      .join(" • ") ||
    T("Aucune zone critique confirmée", "No critical zone confirmed");

  const headline =
    total < 12
      ? T(
          "Analyse préliminaire : les premiers signaux sont exploitables, mais il faut plus de trades pour confirmer une vraie tendance.",
          "Preliminary analysis: the first signals are usable, but more trades are needed to confirm a real trend.",
        )
      : score >= 75
        ? T(
            "Le journal montre une base exploitable. Le vrai travail est de protéger les contextes rentables et d’éviter les écarts.",
            "The journal shows a usable base. The real work is protecting the profitable contexts and avoiding slippage from the plan.",
          )
        : score >= 58
          ? T(
              "Le journal montre du potentiel, mais quelques habitudes réduisent fortement la rentabilité.",
              "The journal shows potential, but a few habits are cutting profitability hard.",
            )
          : T(
              "Le journal indique une urgence de contrôle : priorité à la réduction des pertes, du drawdown et du surtrading.",
              "The journal points to an urgent need for control: priority is cutting losses, drawdown and overtrading.",
            );

  const signedMoney = (value) =>
    `${value >= 0 ? "+" : ""}${formatPremiumAmount(value)}`;
  const permissionStatus = (() => {
    if (
      score < 42 ||
      profitFactor < 1 ||
      maxLossStreak >= 6 ||
      maxDailyTrades >= 30
    ) {
      return {
        label: T("Trading limité", "Restricted trading"),
        tone: "rose",
        title: T("Mode protection du capital", "Capital protection mode"),
        reason: T(
          "Les données montrent un niveau de risque trop élevé pour chercher la croissance. L’objectif immédiat est de stopper la dégradation.",
          "The data shows a risk level too high to chase growth. The immediate goal is to stop the bleeding.",
        ),
        conditions: [
          T(
            "Lot fixe ou réduit pendant 7 jours.",
            "Fixed or reduced lot size for 7 days.",
          ),
          T(
            "Stop obligatoire avant chaque entrée.",
            "A stop is mandatory before every entry.",
          ),
          T(
            "Pause après 2 pertes consécutives.",
            "Break after 2 consecutive losses.",
          ),
          T(
            "Maximum 3 trades par session jusqu’à amélioration du profit factor.",
            "Maximum 3 trades per session until the profit factor improves.",
          ),
        ],
      };
    }
    if (score < 62 || maxDDPct <= -7 || avgDailyTrades >= 8) {
      return {
        label: T("Trading sous contrôle", "Trading under control"),
        tone: "amber",
        title: T("Autorisation conditionnelle", "Conditional clearance"),
        reason: T(
          "La performance peut être travaillée, mais chaque entrée doit respecter un filtre strict pour éviter le retour du drawdown.",
          "Performance can be improved, but every entry must pass a strict filter to keep the drawdown from coming back.",
        ),
        conditions: [
          T(
            "Trader uniquement les actifs et heures les mieux classés.",
            "Trade only the top-ranked assets and hours.",
          ),
          T(
            "Aucune augmentation de lot après une perte.",
            "No lot increase after a loss.",
          ),
          T(
            "Chaque trade doit avoir une raison écrite.",
            "Every trade must have a written reason.",
          ),
          T(
            "Arrêt de la journée si la perte maximale prévue est atteinte.",
            "Stop for the day if the planned maximum loss is reached.",
          ),
        ],
      };
    }
    return {
      label: T("Trading autorisé", "Trading cleared"),
      tone: "emerald",
      title: T("Cadre exploitable", "Workable framework"),
      reason: T(
        "Les statistiques montrent une base plus stable. La priorité devient la régularité et la protection des bons contextes.",
        "The statistics show a more stable base. The priority becomes consistency and protecting the good contexts.",
      ),
      conditions: [
        T(
          "Conserver les règles de risque actuelles.",
          "Keep the current risk rules.",
        ),
        T(
          "Protéger les actifs et horaires rentables.",
          "Protect the profitable assets and time slots.",
        ),
        T(
          "Revoir les résultats tous les 30 trades.",
          "Review the results every 30 trades.",
        ),
        T(
          "Augmenter le lot uniquement par palier mesuré.",
          "Increase lot size only in measured steps.",
        ),
      ],
    };
  })();

  const evidenceStack = [
    {
      label: T("Rentabilité nette", "Net profitability"),
      value: signedMoney(net),
      tone: net >= 0 ? "emerald" : "rose",
      detail:
        net >= 0
          ? T(
              "Le compte termine la période en positif.",
              "The account ends the period in positive territory.",
            )
          : T(
              "Le compte termine la période en négatif ; priorité à la réduction des pertes.",
              "The account ends the period negative; priority is cutting losses.",
            ),
    },
    {
      label: T("Pression des pertes", "Loss pressure"),
      value: T(`${maxLossStreak} pertes max`, `${maxLossStreak} losses max`),
      tone:
        maxLossStreak >= 4 ? "rose" : maxLossStreak >= 2 ? "amber" : "emerald",
      detail:
        maxLossStreak >= 4
          ? T(
              "La série maximale indique un risque de revenge trading ou d’absence de coupe-circuit.",
              "The longest streak points to a revenge-trading risk or a missing circuit breaker.",
            )
          : T(
              "La série de pertes reste relativement contrôlée.",
              "The losing streak stays relatively controlled.",
            ),
    },
    {
      label: T("Fréquence", "Frequency"),
      value: T(`${maxDailyTrades} pic/jour`, `${maxDailyTrades} peak/day`),
      tone:
        maxDailyTrades >= 20
          ? "rose"
          : maxDailyTrades >= 10
            ? "amber"
            : "emerald",
      detail:
        maxDailyTrades >= 20
          ? T(
              "La journée la plus active ressemble à du surtrading ; la qualité baisse souvent avec la quantité.",
              "Your busiest day looks like overtrading; quality usually drops as quantity rises.",
            )
          : T(
              "La fréquence reste surveillable.",
              "The frequency stays manageable.",
            ),
    },
    {
      label: T("Qualité du journal", "Journal quality"),
      value: T(`${noteRate}% noté`, `${noteRate}% annotated`),
      tone: noteRate >= 70 ? "emerald" : noteRate >= 35 ? "amber" : "rose",
      detail:
        noteRate >= 70
          ? T(
              "Les notes permettent une lecture comportementale plus précise.",
              "Your notes allow a more precise behavioural read.",
            )
          : T(
              "Sans notes, le système détecte les chiffres mais pas assez les raisons d’entrée et de sortie.",
              "Without notes, the system sees the numbers but not enough of your reasons for entering and exiting.",
            ),
    },
  ];

  const metricExplanations = [
    {
      label: T("Résultat net", "Net result"),
      value: signedMoney(net),
      tone: net >= 0 ? "emerald" : "rose",
      meaning: T(
        "Différence entre tous les gains clôturés et toutes les pertes clôturées.",
        "The difference between all closed gains and all closed losses.",
      ),
      reading:
        net >= 0
          ? T(
              "La période est positive, mais il faut vérifier si le gain vient d’un vrai avantage ou d’un seul gros trade.",
              "The period is positive, but you need to check whether the gain comes from a real edge or from one big trade.",
            )
          : T(
              "La période est négative : le système doit d’abord protéger le capital avant de chercher plus de volume.",
              "The period is negative: the system has to protect capital before chasing more volume.",
            ),
      action:
        net >= 0
          ? T(
              "Garde les mêmes règles et identifie les contextes qui ont produit la majorité du gain.",
              "Keep the same rules and identify the contexts that produced most of the gain.",
            )
          : T(
              "Réduis le nombre de trades, bloque les contextes perdants et conserve uniquement les setups A+.",
              "Cut the number of trades, block the losing contexts and keep only A+ setups.",
            ),
    },
    {
      label: "Profit factor",
      value: profitFactor ? profitFactor.toFixed(2) : "0.00",
      tone:
        profitFactor >= 1.35
          ? "emerald"
          : profitFactor >= 1.05
            ? "amber"
            : "rose",
      meaning: T(
        "Montant gagné divisé par le montant perdu. Au-dessus de 1, le système gagne plus qu’il ne perd.",
        "Amount won divided by amount lost. Above 1, the system wins more than it loses.",
      ),
      reading:
        profitFactor >= 1.35
          ? T(
              "La base est exploitable : les gains couvrent correctement les pertes.",
              "The base is usable: gains cover losses properly.",
            )
          : profitFactor >= 1.05
            ? T(
                "Le système est fragile : une mauvaise série peut faire repasser la courbe en négatif.",
                "The system is fragile: one bad streak can push the curve back into the red.",
              )
            : T(
                "Les pertes absorbent les gains. Augmenter le lot maintenant augmente surtout le risque.",
                "Losses absorb the gains. Raising lot size now mostly raises risk.",
              ),
      action:
        profitFactor >= 1.35
          ? T(
              "Protéger les zones rentables et éviter le surtrading.",
              "Protect the profitable zones and avoid overtrading.",
            )
          : T(
              "Objectif minimum : remonter au-dessus de 1.10 avant toute augmentation de lot.",
              "Minimum goal: get back above 1.10 before any lot increase.",
            ),
    },
    {
      label: "Expectancy",
      value: signedMoney(expectancy),
      tone: expectancy > 0 ? "emerald" : expectancy < 0 ? "rose" : "amber",
      meaning: T(
        "Gain ou perte moyenne attendue par trade selon les trades clôturés.",
        "The average gain or loss expected per trade, based on your closed trades.",
      ),
      reading:
        expectancy > 0
          ? T(
              "Chaque trade apporte en moyenne une valeur positive.",
              "Each trade adds positive value on average.",
            )
          : expectancy < 0
            ? T(
                `Chaque trade coûte en moyenne ${formatPremiumAmount(Math.abs(expectancy))}.`,
                `Each trade costs ${formatPremiumAmount(Math.abs(expectancy))} on average.`,
              )
            : T(
                "La méthode est à l’équilibre, mais pas encore assez solide.",
                "The method breaks even, but it is not solid enough yet.",
              ),
      action:
        expectancy > 0
          ? T(
              "Augmenter seulement la régularité, pas forcément la fréquence.",
              "Increase consistency only, not necessarily frequency.",
            )
          : T(
              "Filtrer les entrées et supprimer les actifs/heures qui tirent la moyenne vers le bas.",
              "Filter your entries and drop the assets/hours dragging the average down.",
            ),
    },
    {
      label: T("Win rate", "Win rate"),
      value: `${winRate.toFixed(1)}%`,
      tone: winRate >= 55 ? "emerald" : winRate >= 42 ? "amber" : "rose",
      meaning: T(
        "Pourcentage de trades clôturés en gain.",
        "Percentage of trades closed in profit.",
      ),
      reading:
        winRate >= 55
          ? T(
              "La précision d’entrée est correcte ; il faut surtout surveiller la taille des pertes.",
              "Your entry accuracy is fine; what needs watching is the size of the losses.",
            )
          : winRate >= 42
            ? T(
                "La précision est moyenne : le ratio gain/perte doit compenser.",
                "Accuracy is average: the win/loss ratio has to make up for it.",
              )
            : T(
                "Beaucoup d’entrées échouent ; sans bon reward/risk, la courbe devient fragile.",
                "A lot of entries fail; without a good reward/risk, the curve turns fragile.",
              ),
      action:
        winRate >= 55
          ? T(
              "Conserver les setups gagnants et éviter d’allonger la liste des opportunités.",
              "Keep the winning setups and resist adding more opportunities to the list.",
            )
          : T(
              "Exiger une confirmation supplémentaire avant chaque entrée.",
              "Require one extra confirmation before every entry.",
            ),
    },
    {
      label: T("Gain moyen / perte moyenne", "Average win / average loss"),
      value: `${formatPremiumAmount(avgWin)} / ${formatPremiumAmount(avgLoss)}`,
      tone: avgWin >= avgLoss ? "emerald" : "rose",
      meaning: T(
        "Compare ce que rapporte un trade gagnant moyen avec ce que coûte un trade perdant moyen.",
        "Compares what an average winning trade brings in with what an average losing trade costs.",
      ),
      reading:
        avgWin >= avgLoss
          ? T(
              "Les gains moyens couvrent les pertes moyennes, ce qui donne plus de marge au système.",
              "Average gains cover average losses, which gives the system more room.",
            )
          : T(
              "Les pertes sont plus lourdes que les gains : quelques mauvais trades peuvent effacer plusieurs bons trades.",
              "Losses are heavier than gains: a few bad trades can wipe out several good ones.",
            ),
      action:
        avgWin >= avgLoss
          ? T(
              "Protéger ce ratio et ne pas couper trop vite les trades gagnants.",
              "Protect this ratio and stop cutting your winners too early.",
            )
          : T(
              "Réduire la perte moyenne : stop clair, invalidation rapide, aucune position laissée par espoir.",
              "Bring the average loss down: clear stop, fast invalidation, no position held on hope.",
            ),
    },
    {
      label: T(
        `Drawdown max (${recentDDDays} j)`,
        `Max drawdown (${recentDDDays}d)`,
      ),
      value: `${Math.abs(maxDDPct || 0).toFixed(2)}%`,
      tone: maxDDPct <= -15 ? "rose" : maxDDPct <= -7 ? "amber" : "emerald",
      meaning: T(
        `Plus forte baisse de la courbe depuis un sommet, sur les ${recentDDDays} derniers jours.${
          ddWindowDiffers
            ? ` Sur tout l’historique : ${Math.abs(maxDDPctAllTime).toFixed(2)}%, un creux daté d’une époque où le capital était plus faible.`
            : ""
        }`,
        `The largest drop in the equity curve from a peak, over the last ${recentDDDays} days.${
          ddWindowDiffers
            ? ` Across the full history: ${Math.abs(maxDDPctAllTime).toFixed(2)}%, a dip from a time when the account was smaller.`
            : ""
        }`,
      ),
      reading:
        maxDDPct <= -15
          ? T(
              "Le capital subit une pression forte ; il faut passer en mode défense.",
              "Capital is under heavy pressure; it is time to switch to defence mode.",
            )
          : maxDDPct <= -7
            ? T(
                "Le risque est visible et doit être stabilisé avant toute croissance.",
                "The risk is visible and must be stabilised before any growth.",
              )
            : T(
                "Le drawdown reste sous contrôle sur la période analysée.",
                "The drawdown stays under control over the period analysed.",
              ),
      action:
        maxDDPct <= -7
          ? T(
              "Réduire le lot et interdire l’augmentation tant que la courbe n’a pas récupéré.",
              "Cut lot size and forbid any increase until the curve has recovered.",
            )
          : T(
              "Conserver la règle de risque actuelle et surveiller les séries de pertes.",
              "Keep the current risk rule and watch the losing streaks.",
            ),
    },
    {
      label: T("Série de pertes", "Losing streak"),
      value: T(
        `${currentLossStreak} actuelle / ${maxLossStreak} max`,
        `${currentLossStreak} current / ${maxLossStreak} max`,
      ),
      tone: maxLossStreak >= 4 || currentLossStreak >= 2 ? "rose" : "emerald",
      meaning: T(
        "Nombre de pertes consécutives. C’est une mesure directe du risque émotionnel.",
        "The number of consecutive losses. It is a direct measure of emotional risk.",
      ),
      reading:
        maxLossStreak >= 4
          ? T(
              "La stratégie ou le comportement manque de coupe-circuit après les pertes.",
              "The strategy or the behaviour lacks a circuit breaker after losses.",
            )
          : T(
              "Les séries restent acceptables, mais la règle d’arrêt doit rester active.",
              "The streaks stay acceptable, but the stop rule has to stay switched on.",
            ),
      action: T(
        "2 pertes = pause. 3 pertes dans la journée = arrêt complet de la session.",
        "2 losses = break. 3 losses in one day = session fully over.",
      ),
    },
    {
      label: T("Trades par jour", "Trades per day"),
      value: T(
        `${avgDailyTrades.toFixed(1)} moy. / ${maxDailyTrades} pic`,
        `${avgDailyTrades.toFixed(1)} avg / ${maxDailyTrades} peak`,
      ),
      tone:
        maxDailyTrades >= 20 || avgDailyTrades >= 8
          ? "rose"
          : avgDailyTrades >= 5
            ? "amber"
            : "emerald",
      meaning: T(
        "Mesure la fréquence de trading et détecte le surtrading.",
        "Measures trading frequency and detects overtrading.",
      ),
      reading:
        maxDailyTrades >= 20
          ? T(
              "Le pic journalier est trop élevé : la fatigue et les entrées impulsives peuvent dégrader la performance.",
              "Your daily peak is too high: fatigue and impulsive entries can wreck performance.",
            )
          : T(
              "La fréquence peut être travaillée avec un plafond clair par session.",
              "The frequency can be worked on with a clear cap per session.",
            ),
      action:
        maxDailyTrades >= 20
          ? T(
              "Limiter à 3 trades par session jusqu’à stabilisation.",
              "Cap it at 3 trades per session until things stabilise.",
            )
          : T(
              "Fixer un nombre maximum de trades avant d’ouvrir MT5.",
              "Set a maximum number of trades before you even open MT5.",
            ),
    },
    {
      label: T("Trades notés", "Annotated trades"),
      value: `${noteRate}%`,
      tone: noteRate >= 70 ? "emerald" : noteRate >= 35 ? "amber" : "rose",
      meaning: T(
        "Part des trades avec commentaire, setup ou tag.",
        "Share of trades carrying a comment, a setup or a tag.",
      ),
      reading:
        noteRate >= 70
          ? T(
              "Le journal devient assez riche pour identifier les vrais patterns.",
              "The journal is getting rich enough to identify the real patterns.",
            )
          : T(
              "Les chiffres existent, mais les raisons d’entrée et de sortie restent trop floues.",
              "The numbers are there, but your reasons for entering and exiting stay too vague.",
            ),
      action: T(
        "Après chaque trade : noter setup, émotion, raison d’entrée, raison de sortie et erreur éventuelle.",
        "After every trade: log the setup, the emotion, the reason for entry, the reason for exit and any mistake.",
      ),
    },
    {
      label: T("Recovery factor", "Recovery factor"),
      value: recoveryFactor ? recoveryFactor.toFixed(2) : "0.00",
      tone:
        recoveryFactor >= 2 ? "emerald" : recoveryFactor > 0 ? "amber" : "rose",
      meaning: T(
        "Capacité du système à produire du profit par rapport au drawdown subi.",
        "The system's ability to produce profit relative to the drawdown it takes.",
      ),
      reading:
        recoveryFactor >= 2
          ? T("La récupération est correcte.", "Recovery is decent.")
          : recoveryFactor > 0
            ? T(
                "La récupération existe, mais reste fragile.",
                "Recovery is there, but it stays fragile.",
              )
            : T(
                "La courbe ne récupère pas encore correctement les phases négatives.",
                "The curve does not yet recover properly from negative phases.",
              ),
      action: T(
        "Réduire les périodes sous l’eau et protéger les gains après une bonne série.",
        "Shorten the underwater periods and protect gains after a good run.",
      ),
    },
    {
      label: T("Meilleur / pire trade", "Best / worst trade"),
      value: `${signedMoney(bestTrade)} / ${signedMoney(worstTrade)}`,
      tone: Math.abs(worstTrade) > Math.abs(bestTrade) ? "rose" : "emerald",
      meaning: T(
        "Compare l’impact du meilleur trade avec le pire trade.",
        "Compares the impact of your best trade with your worst trade.",
      ),
      reading:
        Math.abs(worstTrade) > Math.abs(bestTrade)
          ? T(
              "Le pire trade pèse plus lourd que le meilleur : une seule erreur peut casser plusieurs bonnes décisions.",
              "Your worst trade weighs more than your best: one mistake can undo several good decisions.",
            )
          : T(
              "Le meilleur trade compense correctement le pire trade.",
              "Your best trade properly offsets your worst trade.",
            ),
      action: T(
        "Limiter la taille du pire trade avec un stop obligatoire et une perte maximale quotidienne.",
        "Cap the size of your worst trade with a mandatory stop and a daily maximum loss.",
      ),
    },
    {
      label: T("Régularité journalière", "Daily consistency"),
      value: T(
        `${consistencyRate}% jours positifs`,
        `${consistencyRate}% positive days`,
      ),
      tone:
        consistencyRate >= 55
          ? "emerald"
          : consistencyRate >= 40
            ? "amber"
            : "rose",
      meaning: T(
        "Pourcentage de journées clôturées en positif.",
        "Percentage of days closed in profit.",
      ),
      reading:
        consistencyRate >= 55
          ? T(
              "La régularité commence à apparaître.",
              "Consistency is starting to show.",
            )
          : T(
              "Les résultats journaliers sont encore instables.",
              "Your daily results are still unstable.",
            ),
      action: T(
        "Comparer les journées positives et négatives pour isoler les règles qui protègent le compte.",
        "Compare your positive and negative days to isolate the rules that protect the account.",
      ),
    },
  ];

  const contextMatrix = [
    bestSymbol
      ? {
          label: T("Meilleur actif", "Best asset"),
          value: bestSymbol.label,
          detail: `${signedMoney(bestSymbol.pnl)} • ${bestSymbol.count} trades • ${bestSymbol.winRate}% win`,
          tone: "emerald",
        }
      : null,
    worstSymbol
      ? {
          label: T("Actif à surveiller", "Asset to watch"),
          value: worstSymbol.label,
          detail: `${signedMoney(worstSymbol.pnl)} • ${worstSymbol.count} trades • ${worstSymbol.winRate}% win`,
          tone: worstSymbol.pnl < 0 ? "rose" : "amber",
        }
      : null,
    bestHour
      ? {
          label: T("Meilleure heure", "Best hour"),
          value: bestHour.label,
          detail: T(
            `${signedMoney(bestHour.pnl)} • moyenne ${signedMoney(bestHour.avg)}`,
            `${signedMoney(bestHour.pnl)} • average ${signedMoney(bestHour.avg)}`,
          ),
          tone: "emerald",
        }
      : null,
    worstHour
      ? {
          label: T("Heure faible", "Weak hour"),
          value: worstHour.label,
          detail: T(
            `${signedMoney(worstHour.pnl)} • moyenne ${signedMoney(worstHour.avg)}`,
            `${signedMoney(worstHour.pnl)} • average ${signedMoney(worstHour.avg)}`,
          ),
          tone: worstHour.pnl < 0 ? "rose" : "amber",
        }
      : null,
    bestDay
      ? {
          label: T("Meilleur jour", "Best day"),
          value: bestDay.label,
          detail: `${signedMoney(bestDay.pnl)} • ${bestDay.count} trades`,
          tone: "emerald",
        }
      : null,
    worstDay
      ? {
          label: T("Jour faible", "Weak day"),
          value: worstDay.label,
          detail: `${signedMoney(worstDay.pnl)} • ${worstDay.count} trades`,
          tone: worstDay.pnl < 0 ? "rose" : "amber",
        }
      : null,
  ].filter(Boolean);

  const executionPhases = [
    {
      title: T("Avant le trade", "Before the trade"),
      tone: noteRate < 50 || avgDailyTrades >= 8 ? "amber" : "emerald",
      items: [
        bestSymbol?.label
          ? T(
              `Prioriser ${bestSymbol.label} si le setup est clair.`,
              `Prioritise ${bestSymbol.label} if the setup is clear.`,
            )
          : T(
              "Définir le setup exact avant l’entrée.",
              "Define the exact setup before entering.",
            ),
        worstHour?.pnl < 0
          ? T(
              `Éviter la fenêtre ${worstHour.label} tant qu’elle reste négative.`,
              `Avoid the ${worstHour.label} time window while it stays negative.`,
            )
          : T(
              "Vérifier que l’heure fait partie du plan.",
              "Check that the time slot is part of the plan.",
            ),
        T(
          "Fixer stop, invalidation et perte maximale avant de cliquer.",
          "Set the stop, the invalidation and the maximum loss before you click.",
        ),
      ],
    },
    {
      title: T("Pendant le trade", "During the trade"),
      tone: avgLoss > avgWin && avgWin > 0 ? "rose" : "amber",
      items: [
        T(
          "Ne pas déplacer le stop par émotion.",
          "Never move the stop out of emotion.",
        ),
        avgLoss > avgWin && avgWin > 0
          ? T(
              "Couper rapidement quand le scénario est invalidé.",
              "Cut fast when the scenario is invalidated.",
            )
          : T(
              "Laisser le plan gérer le trade sans intervention inutile.",
              "Let the plan manage the trade without needless interference.",
            ),
        T(
          "Interdire toute augmentation de lot après une perte.",
          "Forbid any lot increase after a loss.",
        ),
      ],
    },
    {
      title: T("Après le trade", "After the trade"),
      tone: currentLossStreak >= 2 ? "rose" : "emerald",
      items: [
        T(
          "Noter la raison d’entrée et la raison de sortie.",
          "Log the reason for entry and the reason for exit.",
        ),
        currentLossStreak >= 2
          ? T(
              "Faire une pause avant toute nouvelle décision.",
              "Take a break before any new decision.",
            )
          : T(
              "Comparer le trade avec la checklist.",
              "Compare the trade against the checklist.",
            ),
        T(
          "Classer l’erreur : technique, émotionnelle ou marché invalide.",
          "Classify the mistake: technical, emotional or invalid market.",
        ),
      ],
    },
  ];

  const sevenDayPlan = [
    {
      day: T("Jour 1", "Day 1"),
      title: T("Protection", "Protection"),
      task: T(
        "Réduire le volume et fixer une perte maximale journalière.",
        "Cut volume and set a daily maximum loss.",
      ),
      measure: T("Aucune augmentation de lot.", "No lot increase."),
    },
    {
      day: T("Jour 2", "Day 2"),
      title: T("Sélection", "Selection"),
      task: bestSymbol?.label
        ? T(
            `Trader uniquement ${bestSymbol.label} ou rester en observation.`,
            `Trade ${bestSymbol.label} only, or stay on the sidelines.`,
          )
        : T(
            "Garder uniquement un setup principal.",
            "Keep one main setup only.",
          ),
      measure: T(
        "3 trades maximum par session.",
        "3 trades maximum per session.",
      ),
    },
    {
      day: T("Jour 3", "Day 3"),
      title: T("Timing", "Timing"),
      task: bestHour?.label
        ? T(
            `Privilégier la fenêtre ${bestHour.label}.`,
            `Favour the ${bestHour.label} time window.`,
          )
        : T(
            "Noter précisément l’heure de chaque entrée.",
            "Log the exact time of every entry.",
          ),
      measure: T("Comparer P&L par heure.", "Compare P&L by hour."),
    },
    {
      day: T("Jour 4", "Day 4"),
      title: T("Nettoyage", "Clean-up"),
      task: worstSymbol?.label
        ? T(
            `Mettre ${worstSymbol.label} en observation sauf setup A+.`,
            `Put ${worstSymbol.label} on watch-only except for A+ setups.`,
          )
        : T(
            "Supprimer les trades sans raison écrite.",
            "Cut out trades taken without a written reason.",
          ),
      measure: T("0 trade impulsif.", "0 impulsive trades."),
    },
    {
      day: T("Jour 5", "Day 5"),
      title: T("Journalisation", "Journaling"),
      task: T(
        "Taguer tous les trades : setup, session, émotion, sortie.",
        "Tag every trade: setup, session, emotion, exit.",
      ),
      measure: T(
        "80% de trades notés minimum.",
        "80% of trades annotated, minimum.",
      ),
    },
    {
      day: T("Jour 6", "Day 6"),
      title: T("Revue", "Review"),
      task: T(
        "Relire les pertes et identifier la cause dominante.",
        "Reread your losses and identify the dominant cause.",
      ),
      measure: T("1 règle corrigée dans le plan.", "1 rule fixed in the plan."),
    },
    {
      day: T("Jour 7", "Day 7"),
      title: T("Validation", "Validation"),
      task: T(
        "Comparer profit factor, drawdown, fréquence et discipline.",
        "Compare profit factor, drawdown, frequency and discipline.",
      ),
      measure: T(
        "Décider si le trading reste limité ou revient normal.",
        "Decide whether trading stays restricted or goes back to normal.",
      ),
    },
  ];

  const aiTone =
    score >= 70 ? "emerald" : score >= 45 ? "amber" : "rose";
  const voiceSeed =
    Math.abs(
      Math.round(score * 7 + total * 11 + (net >= 0 ? 3 : 17) + maxLossStreak * 5),
    ) || 1;

  const aiVoiceTitle =
    total < 12
      ? chooseBySeed(
          [
            T(
              "Premier diagnostic : ton profil commence à se dessiner.",
              "First diagnosis: your profile is starting to take shape.",
            ),
            T(
              "Lecture initiale : quelques signaux se dégagent déjà.",
              "Initial read: a few signals are already coming through.",
            ),
            T(
              "Lecture possible, mais il faut plus de données pour confirmer.",
              "A read is possible, but more data is needed to confirm it.",
            ),
          ],
          voiceSeed,
        )
      : score >= 75
        ? chooseBySeed(
            [
              T(
                "Base sérieuse détectée : verrouille ce qui fonctionne.",
                "Serious base detected: lock in what works.",
              ),
              T(
                "Profil structuré : protège ton avantage avant d’augmenter le volume.",
                "Structured profile: protect your edge before increasing volume.",
              ),
              T(
                "Lecture crédible : rends cette qualité répétable.",
                "Credible read: now make that quality repeatable.",
              ),
            ],
            voiceSeed,
          )
        : score >= 58
          ? chooseBySeed(
              [
                T(
                  "Potentiel réel, mais exécution encore irrégulière.",
                  "Real potential, but execution is still uneven.",
                ),
                T(
                  "Base exploitable, mais rigueur encore insuffisante.",
                  "Usable base, but not enough rigour yet.",
                ),
                T(
                  "Bonne matière de travail : filtre et exécution à renforcer.",
                  "Good material to work with: tighten the filter and the execution.",
                ),
              ],
              voiceSeed,
            )
          : chooseBySeed(
              [
                T(
                  "Alerte prioritaire : protège le capital avant d’accélérer.",
                  "Top-priority alert: protect the capital before speeding up.",
                ),
                T(
                  "Point d’alerte : risque et exécution à reprendre sous contrôle.",
                  "Warning point: risk and execution need to be brought back under control.",
                ),
                T(
                  "Priorité : stopper les fuites avant d’accélérer.",
                  "Priority: stop the leaks before speeding up.",
                ),
              ],
              voiceSeed,
            );

  const aiVoiceIntro =
    total < 12
      ? chooseBySeed(
          [
            T(
              "Lecture précoce : elle est utile, mais encore provisoire. Continue à alimenter le journal avec discipline.",
              "Early read: useful, but still provisional. Keep feeding the journal with discipline.",
            ),
            T(
              "Lecture prudente : les premiers chiffres donnent une direction, pas encore un verdict définitif.",
              "Cautious read: the first numbers give a direction, not yet a final verdict.",
            ),
            T(
              "Coaching possible, avec une marge de prudence parce que le nombre de trades reste limité.",
              "Coaching is possible, with a margin of caution because the number of trades is still limited.",
            ),
          ],
          voiceSeed + 2,
        )
      : chooseBySeed(
          [
            T(
              `J’ai passé en revue ${total} trades clôturés, ton résultat net, ton drawdown, tes séries, tes meilleures fenêtres et tes contextes de contre-performance.`,
              `I reviewed ${total} closed trades, your net result, your drawdown, your streaks, your best time windows and the contexts where you underperform.`,
            ),
            T(
              `J’ai disséqué ${total} trades clôturés pour isoler tes zones de force, tes zones de fuite et la qualité réelle de ton exécution.`,
              `I dissected ${total} closed trades to isolate your strong zones, your leaks and the real quality of your execution.`,
            ),
            T(
              `Je me suis appuyé sur ${total} trades clôturés, ton rendement, ton drawdown, la fréquence, la discipline et les contextes les plus rentables pour établir ce diagnostic.`,
              `I based this diagnosis on ${total} closed trades, your return, your drawdown, your frequency, your discipline and your most profitable contexts.`,
            ),
          ],
          voiceSeed + 3,
        );

  const diagnosticLine =
    profitFactor > 0 && profitFactor < 1
      ? T(
          `Diagnostic central : le Profit factor ${profitFactor.toFixed(2)} confirme que les pertes absorbent encore les gains.`,
          `Core diagnosis: a profit factor of ${profitFactor.toFixed(2)} confirms that losses are still absorbing the gains.`,
        )
      : avgLoss > avgWin && avgWin > 0
        ? T(
            `Diagnostic central : la perte moyenne reste supérieure au gain moyen, le stop et la sortie doivent être resserrés.`,
            `Core diagnosis: your average loss is still bigger than your average win, so the stop and the exit need tightening.`,
          )
        : maxLossStreak >= 4 || currentLossStreak >= 2
          ? T(
              `Diagnostic central : les séries de pertes imposent un coupe-circuit clair avant la prochaine session.`,
              `Core diagnosis: your losing streaks call for a clear circuit breaker before the next session.`,
            )
          : avgDailyTrades >= 8 || maxDailyTrades >= 15
            ? T(
                `Diagnostic central : la fréquence est trop élevée, la sélection doit passer avant le volume.`,
                `Core diagnosis: your frequency is too high, selection has to come before volume.`,
              )
            : noteRate < 35 && total >= 12
              ? T(
                  `Diagnostic central : le journal manque de notes pour expliquer précisément les bonnes et mauvaises décisions.`,
                  `Core diagnosis: the journal lacks the notes needed to explain your good and bad decisions precisely.`,
                )
              : T(
                  `Diagnostic central : les meilleurs contextes doivent être protégés pendant que les zones faibles sont réduites.`,
                  `Core diagnosis: your best contexts need protecting while the weak zones get cut back.`,
                );

  const actionCardTitle =
    profitFactor > 0 && profitFactor < 1
      ? T(
          "Objectif immédiat : repasser au-dessus de 1.10 de Profit factor.",
          "Immediate goal: get the profit factor back above 1.10.",
        )
      : avgLoss > avgWin && avgWin > 0
        ? T(
            "Objectif immédiat : réduire la perte moyenne.",
            "Immediate goal: bring the average loss down.",
          )
        : maxLossStreak >= 4 || currentLossStreak >= 2
          ? T(
              "Objectif immédiat : stopper les séries de pertes.",
              "Immediate goal: stop the losing streaks.",
            )
          : avgDailyTrades >= 8 || maxDailyTrades >= 15
            ? T(
                "Objectif immédiat : passer en mode sniper.",
                "Immediate goal: switch to sniper mode.",
              )
            : noteRate < 35 && total >= 12
              ? T(
                  "Objectif immédiat : documenter les décisions.",
                  "Immediate goal: document your decisions.",
                )
              : T(
                  "Objectif immédiat : protéger les contextes rentables.",
                  "Immediate goal: protect the profitable contexts.",
                );

  const aiConversation = [
    diagnosticLine,
    bestContextText &&
    bestContextText !== T("Pas encore assez de données", "Not enough data yet")
      ? chooseBySeed(
          [
            T(
              `Zone positive à protéger : ${bestContextText}. C’est cette zone qu’il faut travailler avec constance.`,
              `Positive zone to protect: ${bestContextText}. That is the zone to work consistently.`,
            ),
            T(
              `Contexte fort détecté : ${bestContextText}. Reste sélectif ici pour rendre la courbe plus propre.`,
              `Strong context detected: ${bestContextText}. Stay selective here to clean up the curve.`,
            ),
            T(
              `Point d’appui positif : ${bestContextText}. Concentre-toi dessus au lieu de disperser le volume.`,
              `Positive anchor: ${bestContextText}. Focus there instead of spreading your volume thin.`,
            ),
          ],
          voiceSeed + 5,
        )
      : chooseBySeed(
          [
            T(
              "Aucune zone forte n’est encore confirmée. Pour l’instant, priorité à une journalisation propre et régulière.",
              "No strong zone is confirmed yet. For now, the priority is clean, regular journaling.",
            ),
            T(
              "Aucun avantage net n’apparaît encore. Continue à noter chaque trade pour distinguer ce qui est réellement rentable.",
              "No clear edge shows up yet. Keep logging every trade to separate what is genuinely profitable.",
            ),
            T(
              "Aucun contexte premium n’est encore validé. La priorité reste la qualité du process et la traçabilité de chaque décision.",
              "No premium context is validated yet. The priority stays process quality and traceability on every decision.",
            ),
          ],
          voiceSeed + 6,
        ),
    dangerContextText &&
    dangerContextText !==
      T("Aucune zone critique confirmée", "No critical zone confirmed")
      ? chooseBySeed(
          [
            T(
              `Alerte à réduire : ${dangerContextText}. Tant que ce contexte n’est pas assaini, il doit rester sous observation.`,
              `Alert to cut back: ${dangerContextText}. Until this context is cleaned up, it stays on watch.`,
            ),
            T(
              `Fuite principale détectée : ${dangerContextText}. Garde ce contexte uniquement avec un filtre beaucoup plus strict.`,
              `Main leak detected: ${dangerContextText}. Keep this context only behind a far stricter filter.`,
            ),
            T(
              `Signal d’alerte clair : ${dangerContextText}. Limite-le tant que les chiffres ne redeviennent pas sains.`,
              `Clear warning signal: ${dangerContextText}. Limit it until the numbers turn healthy again.`,
            ),
          ],
          voiceSeed + 7,
        )
      : chooseBySeed(
          [
            T(
              "Aucune zone critique n’est parfaitement confirmée, mais les règles de protection doivent rester actives à chaque session.",
              "No critical zone is fully confirmed, but the protection rules have to stay active every session.",
            ),
            T(
              "Aucune alerte majeure n’est dominante, mais le cadre de risque doit rester strict.",
              "No major alert dominates, but the risk framework must stay strict.",
            ),
            T(
              "Aucun contexte catastrophique ne domine, mais l’exécution doit rester plus disciplinée pour stabiliser les résultats.",
              "No disastrous context dominates, but execution has to stay more disciplined to stabilise results.",
            ),
          ],
          voiceSeed + 8,
        ),
  ].filter(Boolean);

  const aiFocusCards = [
    {
      label:
        net >= 0
          ? chooseBySeed(
              [
                T("Lecture positive", "Positive read"),
                T("Point fort détecté", "Strength detected"),
                T("Ce qui fonctionne", "What is working"),
              ],
              voiceSeed + 9,
            )
          : chooseBySeed(
              [
                T("Alerte à corriger", "Alert to fix"),
                T("Lecture IA — alerte", "AI read — alert"),
                T("Point critique du journal", "Critical point in the journal"),
              ],
              voiceSeed + 9,
            ),
      title: traderProfile,
      body:
        net >= 0
          ? chooseBySeed(
              [
                T(
                  `Tu termines la période à ${signedMoney(net)}. Ce résultat est encourageant, mais il doit reposer sur un avantage répétable et non sur un pic isolé.`,
                  `You end the period at ${signedMoney(net)}. That result is encouraging, but it has to rest on a repeatable edge, not on one isolated spike.`,
                ),
                T(
                  `La période se clôture à ${signedMoney(net)}. C’est positif, mais cette rentabilité doit rester propre et structurée.`,
                  `The period closes at ${signedMoney(net)}. That is positive, but this profitability has to stay clean and structured.`,
                ),
                T(
                  `Tu sors avec ${signedMoney(net)}. La prochaine étape consiste à identifier précisément ce qui produit ce gain pour le reproduire avec contrôle.`,
                  `You come out with ${signedMoney(net)}. The next step is pinpointing exactly what produces that gain so you can repeat it under control.`,
                ),
              ],
              voiceSeed + 10,
            )
          : chooseBySeed(
              [
                T(
                  `Tu termines la période à ${signedMoney(net)}. Tu dois d’abord arrêter l’érosion du capital avant de parler d’accélération.`,
                  `You end the period at ${signedMoney(net)}. You have to stop the capital erosion before talking about acceleration.`,
                ),
                T(
                  `Le bilan ressort à ${signedMoney(net)}. La priorité n’est pas de chercher plus d’opportunités, mais de couper ce qui te coûte trop cher.`,
                  `The bottom line comes out at ${signedMoney(net)}. The priority is not finding more opportunities, it is cutting what costs you too much.`,
                ),
                T(
                  `La période se ferme sur ${signedMoney(net)}. Le travail immédiat est de filtrer les contextes faibles et de remettre le risque sous contrôle.`,
                  `The period closes on ${signedMoney(net)}. The immediate job is filtering out the weak contexts and bringing risk back under control.`,
                ),
              ],
              voiceSeed + 11,
            ),
      tone: net >= 0 ? "emerald" : "rose",
    },
    {
      label: chooseBySeed(
        [
          T("Le frein principal", "The main brake"),
          T("Ce qui ralentit ta courbe", "What is slowing your curve"),
          T("Le point de friction", "The friction point"),
        ],
        voiceSeed + 12,
      ),
      title: dominantWeakness,
      body:
        problems?.[0]?.detail ||
        chooseBySeed(
          [
            T(
              "Le point faible principal vient surtout d’un manque de filtre, de règles d’arrêt ou de journalisation régulière.",
              "The main weakness comes mostly from a missing filter, missing stop rules or irregular journaling.",
            ),
            T(
              "Le signal faible le plus visible reste le process : filtrage, discipline et traçabilité ne sont pas encore assez homogènes.",
              "The clearest weak signal is the process: filtering, discipline and traceability are not consistent enough yet.",
            ),
            T(
              "Le problème principal vient surtout du cadre : sans routine claire, il devient difficile de protéger la performance.",
              "The main problem is the framework: without a clear routine, protecting performance gets hard.",
            ),
          ],
          voiceSeed + 13,
        ),
      tone: problems?.[0]?.tone || "amber",
    },
    {
      label:
        aiTone === "rose"
          ? chooseBySeed(
              [
                T("Action obligatoire", "Mandatory action"),
                T("Correction prioritaire", "Priority fix"),
                T("À appliquer maintenant", "Apply this now"),
              ],
              voiceSeed + 14,
            )
          : chooseBySeed(
              [
                T("Ma consigne maintenant", "My instruction right now"),
                T("La priorité immédiate", "The immediate priority"),
                T("Ce qu’il faut voir ensuite", "What to look at next"),
              ],
              voiceSeed + 14,
            ),
      title: actionCardTitle,
      body:
        actions?.[0] ||
        chooseBySeed(
          [
            T(
              "Garde exactement le même cadre sur les prochains trades et mesure les résultats avant de modifier la stratégie.",
              "Keep exactly the same framework over the next trades and measure the results before changing the strategy.",
            ),
            T(
              "Travaille une exécution plus propre sur les prochains trades, puis compare les chiffres avant tout changement de méthode.",
              "Work on cleaner execution over the next trades, then compare the numbers before changing method.",
            ),
            T(
              "On ne change rien dans la précipitation : applique le plan sur quelques trades, puis reviens mesurer ce qui s’améliore vraiment.",
              "Nothing changes in a rush: apply the plan over a few trades, then come back and measure what actually improves.",
            ),
          ],
          voiceSeed + 15,
        ),
      tone: aiTone,
    },
  ];

  const aiNextTradeScript = `${
    nextTradeChecklist?.[0] ||
    T("Le setup est-il clair ?", "Is the setup clear?")
  } ${
    nextTradeChecklist?.[2] ||
    T(
      "Le stop loss et la perte maximale sont-ils définis ?",
      "Are the stop loss and the maximum loss defined?",
    )
  }`;

  const traderHealth = [
    {
      label: T("Santé du compte", "Account health"),
      value: net >= 0 ? T("Positive", "Positive") : T("Fragile", "Fragile"),
      detail:
        net >= 0
          ? T(
              "Le capital reste orienté positivement sur la période analysée.",
              "Capital stays pointed in the right direction over the period analysed.",
            )
          : T(
              "Le capital se dégrade : il faut stopper l’érosion avant d’augmenter l’activité.",
              "Capital is eroding: you have to stop the bleeding before increasing activity.",
            ),
      tone: net >= 0 ? "emerald" : "rose",
    },
    {
      label: T("Santé du risque", "Risk health"),
      value:
        maxDDPct <= -12
          ? T("Sous pression", "Under pressure")
          : maxDDPct <= -7
            ? T("À surveiller", "Needs watching")
            : T("Stable", "Stable"),
      detail:
        maxDDPct <= -12
          ? T(
              "Le drawdown est trop élevé et pèse directement sur la marge de progression.",
              "The drawdown is too high and eats directly into your room to grow.",
            )
          : maxDDPct <= -7
            ? T(
                "Le risque reste exploitable, mais il mérite un encadrement plus strict.",
                "The risk stays workable, but it deserves a stricter framework.",
              )
            : T(
                "Le drawdown reste contenu et compatible avec un cadre sain.",
                "The drawdown stays contained and compatible with a healthy framework.",
              ),
      tone: maxDDPct <= -12 ? "rose" : maxDDPct <= -7 ? "amber" : "emerald",
    },
    {
      label: T("Santé émotionnelle", "Emotional health"),
      value:
        maxLossStreak >= 4 || maxDailyTrades >= 20
          ? T("Instable", "Unstable")
          : T("Contrôlable", "Manageable"),
      detail:
        maxLossStreak >= 4 || maxDailyTrades >= 20
          ? T(
              "Les séries de pertes ou la fréquence trahissent une tension émotionnelle à corriger.",
              "Your losing streaks or your frequency give away emotional tension that needs fixing.",
            )
          : T(
              "Le comportement reste globalement maîtrisé sur la fréquence et la pression.",
              "Your behaviour stays broadly under control on frequency and pressure.",
            ),
      tone: maxLossStreak >= 4 || maxDailyTrades >= 20 ? "rose" : "emerald",
    },
    {
      label: T("Santé du process", "Process health"),
      value:
        noteRate >= 70
          ? T("Documentée", "Documented")
          : noteRate >= 35
            ? T("Partielle", "Partial")
            : T("Aveugle", "Blind"),
      detail:
        noteRate >= 70
          ? T(
              "Les décisions sont suffisamment tracées pour améliorer précisément le système.",
              "Your decisions are traced well enough to improve the system precisely.",
            )
          : noteRate >= 35
            ? T(
                "Le journal existe, mais il manque encore de régularité pour piloter proprement.",
                "The journal exists, but it still lacks the regularity needed to steer properly.",
              )
            : T(
                "Sans journalisation suffisante, l’amélioration reste imprécise et difficile à piloter.",
                "Without enough journaling, improvement stays vague and hard to steer.",
              ),
      tone: noteRate >= 70 ? "emerald" : noteRate >= 35 ? "amber" : "rose",
    },
  ];

  return {
    hasData: true,
    score,
    grade,
    riskLevel,
    riskKey,
    headline,
    confidence,
    aiTone,
    aiVoiceTitle,
    aiVoiceIntro,
    aiConversation,
    aiFocusCards,
    aiNextTradeScript,
    traderProfile,
    priorityFocus,
    actionCardTitle,
    dominantWeakness,
    bestContextText,
    dangerContextText,
    total,
    wins,
    losses,
    flat,
    net,
    winRate: +winRate.toFixed(1),
    profitFactor: +profitFactor.toFixed(2),
    expectancy: +expectancy.toFixed(2),
    avgWin: +avgWin.toFixed(2),
    avgLoss: +avgLoss.toFixed(2),
    rrRatio: rrRatio > 20 ? 20 : +rrRatio.toFixed(2),
    maxDDPct: +maxDDPct.toFixed(2),
    bestTrade,
    worstTrade,
    recoveryFactor,
    sharpe,
    noteRate,
    currentLossStreak,
    maxLossStreak,
    maxDailyTrades,
    avgDailyTrades: +avgDailyTrades.toFixed(1),
    overTradingDays,
    consistencyRate,
    positiveDays,
    negativeDays,
    longestUnderWater,
    recentStats,
    previousStats,
    bestTradingDay,
    worstTradingDay,
    bestSymbol,
    worstSymbol,
    bestHour,
    worstHour,
    bestDay,
    worstDay,
    bestSide,
    worstSide,
    bestTag,
    worstTag,
    strengths: strengths.slice(0, 6),
    problems: problems.slice(0, 6),
    actions: Array.from(new Set(actions)).slice(0, 7),
    hardRules: Array.from(new Set(hardRules)).slice(0, 5),
    professionalPlan: Array.from(new Set(professionalPlan)).slice(0, 5),
    nextTradeChecklist: Array.from(new Set(nextTradeChecklist)).slice(0, 5),
    scoreBreakdown,
    evidenceStack,
    metricExplanations,
    contextMatrix,
    executionPhases,
    sevenDayPlan,
    traderHealth,
    permissionStatus,
    openExposure,
  };
}

const coachToneClasses = (tone = "neutral", isDarkMode = false) => {
  if (tone === "emerald") {
    return isDarkMode
      ? "border-emerald-300/24 bg-[linear-gradient(180deg,rgba(16,185,129,0.12),rgba(6,78,59,0.08))] text-emerald-50"
      : "border-emerald-200/70 bg-[linear-gradient(180deg,rgba(240,253,244,0.96),rgba(220,252,231,0.68))] text-emerald-900 ";
  }
  if (tone === "rose") {
    return isDarkMode
      ? "border-rose-300/24 bg-[linear-gradient(180deg,rgba(244,63,94,0.12),rgba(127,29,29,0.08))] text-rose-50"
      : "border-rose-200/70 bg-[linear-gradient(180deg,rgba(255,241,242,0.94),rgba(255,228,230,0.64))] text-rose-900 ";
  }
  if (tone === "amber") {
    return isDarkMode
      ? "border-amber-300/24 bg-[linear-gradient(180deg,rgba(245,158,11,0.12),rgba(120,53,15,0.08))] text-amber-50"
      : "border-amber-200/70 bg-[linear-gradient(180deg,rgba(255,251,235,0.95),rgba(254,243,199,0.66))] text-amber-900 ";
  }
  if (tone === "sky") {
    return isDarkMode
      ? "border-sky-300/24 bg-[linear-gradient(180deg,rgba(56,189,248,0.12),rgba(8,47,73,0.08))] text-sky-50"
      : "border-sky-200/70 bg-[linear-gradient(180deg,rgba(240,249,255,0.96),rgba(224,242,254,0.64))] text-sky-900 ";
  }
  return isDarkMode
    ? "border-white/10 bg-white/[0.065] text-slate-100"
    : "border-slate-200/75 bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(248,250,252,0.9))] text-slate-900 ";
};

const coachBadgeClasses = (tone = "neutral", isDarkMode = false) => {
  if (tone === "emerald")
    return isDarkMode
      ? "bg-emerald-400/18 text-emerald-50"
      : "bg-emerald-50 text-emerald-700 border border-emerald-200/70";
  if (tone === "rose")
    return isDarkMode
      ? "bg-rose-400/18 text-rose-50"
      : "bg-rose-50 text-rose-700 border border-rose-200/70";
  if (tone === "amber")
    return isDarkMode
      ? "bg-amber-400/18 text-amber-50"
      : "bg-amber-50 text-amber-700 border border-amber-200/70";
  if (tone === "sky")
    return isDarkMode
      ? "bg-sky-400/18 text-sky-50"
      : "bg-sky-50 text-sky-700 border border-sky-200/70";
  return isDarkMode
    ? "bg-white/10 text-slate-100"
    : "bg-slate-50 text-slate-700 border border-slate-200/70";
};

const CoachLabelPill = ({ children, tone = "neutral", isDarkMode = false, className = "" }) => (
  <span
    className={cn(
      "inline-flex max-w-full items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase leading-[1.2] tracking-[0.095em]  ring-1 ring-inset whitespace-normal break-words",
      coachBadgeClasses(tone, isDarkMode),
      isDarkMode ? "ring-white/10" : "ring-white/70",
      className,
    )}
  >
    {children}
  </span>
);

const parseAuditNumber = (value = "") => {
  const normalized = String(value)
    .replace(/[+\s\u00a0]/g, "")
    .replace(/,/g, ".")
    .replace(/[^0-9.-]/g, "");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const auditHighlightTone = (tone = "neutral", isDarkMode = false) => {
  if (tone === "emerald") {
    return isDarkMode
      ? "border-emerald-300/24 bg-emerald-400/14 text-emerald-100"
      : "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (tone === "rose") {
    return isDarkMode
      ? "border-rose-300/24 bg-rose-400/14 text-rose-100"
      : "border-rose-200 bg-rose-50 text-rose-700";
  }
  if (tone === "amber") {
    return isDarkMode
      ? "border-amber-300/24 bg-amber-400/14 text-amber-100"
      : "border-amber-200 bg-amber-50 text-amber-700";
  }
  return isDarkMode
    ? "border-sky-300/24 bg-sky-400/14 text-sky-100"
    : "border-sky-200 bg-sky-50 text-sky-700";
};

const AuditHighlight = ({ children, tone = "neutral", isDarkMode = false }) => (
  <span
    className={cn(
      "mx-0.5 inline-flex items-center rounded-lg border px-1.5 py-0.5 align-baseline font-semibold leading-[1.35]",
      auditHighlightTone(tone, isDarkMode),
    )}
  >
    {children}
  </span>
);

const renderAuditRichText = (text = "", isDarkMode = false, options = {}) => {
  const source = String(text || "");
  if (!source) return null;

  const matches = [];
  const addMatches = (regex, getTone) => {
    for (const match of source.matchAll(regex)) {
      const value = match[0];
      const start = match.index ?? 0;
      const end = start + value.length;
      if (!value.trim()) continue;
      matches.push({
        start,
        end,
        value,
        tone: getTone(value),
      });
    }
  };

  addMatches(/[+-]\s?\d[\d\s\u00a0]*(?:[,.]\d{2})/g, (value) =>
    parseAuditNumber(value) >= 0 ? "emerald" : "rose",
  );

  addMatches(/\bProfit factor\s+\d+(?:[,.]\d+)?/gi, (value) => {
    const raw = value.match(/\d+(?:[,.]\d+)?/)?.[0] || "0";
    const pf = parseAuditNumber(raw);
    return pf >= 1.1 ? "emerald" : pf >= 1 ? "amber" : "rose";
  });

  if (options.highlightContexts !== false) {
    addMatches(/\bActif\s+[^.]+?(?=\.|,|$)/gi, () =>
      options.contextTone || options.tone || "sky",
    );
  }

  const clean = matches
    .sort((a, b) => a.start - b.start || b.end - a.end)
    .filter((match, index, arr) =>
      arr.slice(0, index).every((prev) => match.start >= prev.end || match.end <= prev.start),
    );

  if (!clean.length) return source;

  const nodes = [];
  let cursor = 0;
  clean.forEach((match, index) => {
    if (match.start > cursor) nodes.push(source.slice(cursor, match.start));
    nodes.push(
      <AuditHighlight key={`${match.start}-${match.end}-${index}`} tone={match.tone} isDarkMode={isDarkMode}>
        {match.value}
      </AuditHighlight>,
    );
    cursor = match.end;
  });
  if (cursor < source.length) nodes.push(source.slice(cursor));

  return nodes;
};

const compactAuditMetricTone = (label = "", value, coach = {}) => {
  const key = String(label || "").toLowerCase();
  const numericValue = parseAuditNumber(value);

  if (key === "net" || key === "expectancy") return numericValue >= 0 ? "emerald" : "rose";
  if (key === "pf") {
    const pf = toNumber(coach?.profitFactor, numericValue);
    return pf >= 1.1 ? "emerald" : pf >= 1 ? "amber" : "rose";
  }
  if (key === "win rate") {
    const wr = toNumber(coach?.winRate, numericValue);
    return wr >= 55 ? "emerald" : wr >= 42 ? "amber" : "rose";
  }
  if (key === "dd max") {
    const dd = Math.abs(toNumber(coach?.maxDDPct, numericValue));
    return dd <= 6 ? "emerald" : dd <= 12 ? "amber" : "rose";
  }
  if (key === "notés") {
    const noteRate = toNumber(coach?.noteRate, numericValue);
    return noteRate >= 70 ? "emerald" : noteRate >= 35 ? "amber" : "rose";
  }
  if (key === "pic/jour") {
    const maxDailyTrades = toNumber(coach?.maxDailyTrades, numericValue);
    return maxDailyTrades <= 8 ? "emerald" : maxDailyTrades <= 18 ? "amber" : "rose";
  }
  return "neutral";
};

/* Bouton de pagination : carré, flat, état actif en aplat violet. */
function PageBtn({ children, onClick, disabled, active, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-current={active ? "page" : undefined}
      className={cn(
        "inline-flex h-8 min-w-[32px] items-center justify-center rounded-lg border px-2 text-xs font-semibold transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-40",
        active
          ? "border-violet-600 bg-violet-600 text-white dark:border-violet-500 dark:bg-violet-500"
          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/75 dark:hover:bg-white/10",
      )}
    >
      {children}
    </button>
  );
}

/* ===================================================================== */
/* ---------- Agrégats du journal ---------- */
// Extrait du composant pour être réutilisable : l'audit du coach doit décrire
// EXACTEMENT la même population de trades que les cartes de statistiques.
// Avant, il recevait les agrégats calculés sur tout l'historique mais un
// échantillon tronqué de trades, et mélangeait donc deux populations.
function computeJournalMetrics(
  rows = [],
  {
    language = "fr",
    currentBalanceInput,
    accountMaxDrawdownPct,
    accountMaxDrawdownAmount,
  } = {},
) {
    const sourceRows = Array.isArray(rows) ? rows : [];
    const cashflows = sourceRows.filter(isCashflowRecord);
    const tradeRows = sourceRows.filter((t) => !isCashflowRecord(t));
    const closed = tradeRows.filter((t) => t.close_time != null);
    const total = closed.length;
    const net = closed.reduce((s, t) => s + toNumber(t.profit), 0);
    const wins = closed.filter((t) => toNumber(t.profit) > 0).length;
    const losses = closed.filter((t) => toNumber(t.profit) < 0).length;
    const breakeven = Math.max(0, total - wins - losses);
    const winRate = total ? (wins / total) * 100 : 0;
    const avg = total ? net / total : 0;
    const grossProfit = closed
      .filter((t) => toNumber(t.profit) > 0)
      .reduce((sum, t) => sum + toNumber(t.profit), 0);
    const grossLossRaw = closed
      .filter((t) => toNumber(t.profit) < 0)
      .reduce((sum, t) => sum + toNumber(t.profit), 0);
    const grossLoss = Math.abs(grossLossRaw);
    const avgWin = wins ? grossProfit / wins : 0;
    const avgLoss = losses ? grossLoss / losses : 0;
    const profitFactor = grossLoss
      ? grossProfit / grossLoss
      : grossProfit > 0
        ? grossProfit
        : 0;

    const sorted = [...closed].sort((a, b) => {
      const ca =
        (tsToDate(a.close_time)?.getTime() || 0) -
        (tsToDate(b.close_time)?.getTime() || 0);
      if (ca) return ca;
      const oa =
        (tsToDate(a.open_time)?.getTime() || 0) -
        (tsToDate(b.open_time)?.getTime() || 0);
      if (oa) return oa;
      return String(a.ticket || "").localeCompare(String(b.ticket || ""), "en", {
        numeric: true,
      });
    });

    const currentBalance = toNumber(currentBalanceInput, 0);
    const depositsTotal = cashflows
      .map(cashflowAmountOf)
      .filter((amount) => amount > 0)
      .reduce((sum, amount) => sum + amount, 0);
    const withdrawalsTotal = Math.abs(
      cashflows
        .map(cashflowAmountOf)
        .filter((amount) => amount < 0)
        .reduce((sum, amount) => sum + amount, 0),
    );
    const netCashflow = depositsTotal - withdrawalsTotal;

    // Solde de départ reconstruit avec les mouvements de compte. L'ancienne
    // formule `balance actuelle - P&L` assimilait un dépôt à un gain et un
    // retrait à une perte, ce qui faussait ensuite le drawdown.
    const estimatedStartingBalance = Number.isFinite(currentBalance)
      ? currentBalance - net - netCashflow
      : 0;

    // Courbe P&L pure : seuls les trades la font bouger. Les dépôts/retraits
    // restent visibles dans le journal mais ne deviennent jamais des gains ou
    // pertes de trading.
    let cumulativeTradingPnl = 0;
    const curve = sorted.map((t) => {
      cumulativeTradingPnl += toNumber(t.profit);
      return {
        date: fmtDate(t.close_time, language),
        ts: tsToDate(t.close_time)?.getTime() ?? 0,
        equity: +cumulativeTradingPnl.toFixed(2),
      };
    });

    // Ledger chronologique du compte. Les flux externes décalent à la fois le
    // solde et le sommet de référence : un retrait volontaire ne doit donc pas
    // être interprété comme un drawdown, et un dépôt ne doit pas créer un faux
    // nouveau gain de trading.
    const ledger = [
      ...sorted.map((t) => ({
        type: "trade",
        ts: tsToDate(t.close_time)?.getTime() || 0,
        delta: toNumber(t.profit),
      })),
      ...cashflows.map((t) => ({
        type: "cashflow",
        ts: tsToDate(historyEventTime(t))?.getTime() || 0,
        delta: cashflowAmountOf(t),
      })),
    ].sort((a, b) => a.ts - b.ts);

    let runningBalance = estimatedStartingBalance;
    let peakBalance = estimatedStartingBalance;
    let maxDD = 0;
    let maxDDPct = 0;

    const applyLedgerEvent = (event, state) => {
      let { balance: bal, peak, ddAmount, ddPct } = state;
      if (event.type === "cashflow") {
        bal += event.delta;
        peak += event.delta;
        if (peak < bal) peak = bal;
        return { balance: bal, peak, ddAmount, ddPct };
      }

      bal += event.delta;
      peak = Math.max(peak, bal);
      const drop = bal - peak;
      ddAmount = Math.min(ddAmount, drop);
      if (peak > 0) ddPct = Math.min(ddPct, (drop / peak) * 100);
      return { balance: bal, peak, ddAmount, ddPct };
    };

    for (const event of ledger) {
      const next = applyLedgerEvent(event, {
        balance: runningBalance,
        peak: peakBalance,
        ddAmount: maxDD,
        ddPct: maxDDPct,
      });
      runningBalance = next.balance;
      peakBalance = next.peak;
      maxDD = next.ddAmount;
      maxDDPct = next.ddPct;
    }

    const lastLedgerTs = ledger.length ? ledger[ledger.length - 1].ts : 0;
    const recentFrom = lastLedgerTs
      ? lastLedgerTs - RECENT_DD_DAYS * 86400000
      : 0;
    let recentBalance = estimatedStartingBalance;
    let recentPeak = estimatedStartingBalance;
    const recentEvents = [];

    for (const event of ledger) {
      if (recentFrom && event.ts < recentFrom) {
        const next = applyLedgerEvent(event, {
          balance: recentBalance,
          peak: recentPeak,
          ddAmount: 0,
          ddPct: 0,
        });
        recentBalance = next.balance;
        recentPeak = next.peak;
      } else {
        recentEvents.push(event);
      }
    }

    // La fenêtre récente commence avec le capital réel à sa borne gauche ; le
    // sommet historique antérieur n'est pas transporté dans ce sous-indicateur.
    recentPeak = recentBalance;
    let maxDDRecent = 0;
    let maxDDPctRecent = 0;
    for (const event of recentEvents) {
      const next = applyLedgerEvent(event, {
        balance: recentBalance,
        peak: recentPeak,
        ddAmount: maxDDRecent,
        ddPct: maxDDPctRecent,
      });
      recentBalance = next.balance;
      recentPeak = next.peak;
      maxDDRecent = next.ddAmount;
      maxDDPctRecent = next.ddPct;
    }

    // Quand l'Expert fournit le Max DD calculé directement sur l'historique du
    // compte MT5, cette valeur est prioritaire. Le calcul client ci-dessus ne
    // sert plus que de repli pour les anciens agents.
    const brokerDDPctRaw = Number(accountMaxDrawdownPct);
    const brokerDDAmountRaw = Number(accountMaxDrawdownAmount);
    const hasBrokerDDPct = Number.isFinite(brokerDDPctRaw) && brokerDDPctRaw >= 0;
    const hasBrokerDDAmount =
      Number.isFinite(brokerDDAmountRaw) && brokerDDAmountRaw >= 0;
    if (hasBrokerDDPct) maxDDPct = -Math.abs(brokerDDPctRaw);
    if (hasBrokerDDAmount) maxDD = -Math.abs(brokerDDAmountRaw);

    let bestWin = 0;
    let bestLoss = 0;
    let curW = 0;
    let curL = 0;
    sorted.forEach((t) => {
      const p = toNumber(t.profit);
      if (p > 0) {
        curW += 1;
        bestWin = Math.max(bestWin, curW);
        curL = 0;
      } else if (p < 0) {
        curL += 1;
        bestLoss = Math.max(bestLoss, curL);
        curW = 0;
      } else {
        curW = 0;
        curL = 0;
      }
    });

    const profits = closed.map((t) => toNumber(t.profit));
    const mean = profits.length
      ? profits.reduce((a, b) => a + b, 0) / profits.length
      : 0;
    const sd =
      profits.length > 1
        ? Math.sqrt(
            profits.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
              (profits.length - 1),
          )
        : 0;
    const sharpe = sd ? mean / sd : 0;
    const bestTrade = profits.length ? Math.max(...profits) : 0;
    const worstTrade = profits.length ? Math.min(...profits) : 0;
    const expectancy = total ? net / total : 0;
    const recoveryFactor = maxDD ? net / Math.abs(maxDD) : 0;
    const recentDDTrades = sorted.filter(
      (t) => (tsToDate(t.close_time)?.getTime() || 0) >= recentFrom,
    ).length;

    return {
      total,
      net: +net.toFixed(2),
      wins,
      losses,
      breakeven,
      winRate: +winRate.toFixed(1),
      avg: +avg.toFixed(2),
      grossProfit: +grossProfit.toFixed(2),
      grossLoss: +grossLoss.toFixed(2),
      avgWin: +avgWin.toFixed(2),
      avgLoss: +avgLoss.toFixed(2),
      profitFactor: +profitFactor.toFixed(2),
      expectancy: +expectancy.toFixed(2),
      bestTrade: +bestTrade.toFixed(2),
      worstTrade: +worstTrade.toFixed(2),
      recoveryFactor: +recoveryFactor.toFixed(2),
      curve,
      maxDD: +maxDD.toFixed(2),
      maxDDPct: +maxDDPct.toFixed(2),
      maxDDRecent: +maxDDRecent.toFixed(2),
      maxDDPctRecent: +maxDDPctRecent.toFixed(2),
      maxDDSource: hasBrokerDDPct ? "account" : "journal",
      recentDDDays: RECENT_DD_DAYS,
      recentDDTrades,
      depositsTotal: +depositsTotal.toFixed(2),
      withdrawalsTotal: +withdrawalsTotal.toFixed(2),
      netCashflow: +netCashflow.toFixed(2),
      cashflowCount: cashflows.length,
      estimatedStartingBalance: +estimatedStartingBalance.toFixed(2),
      bestWin,
      bestLoss,
      sharpe: +sharpe.toFixed(2),
    };
}

export default function JournalAutoPage() {
  const { t, i18n } = useTranslation("agent");
  const navigate = useNavigate();
  const L = useCallback((key, def) => t(key, def), [t]); // helper with fallback

  // Fiche d'aide d'un graphique, dans la langue courante.
  const H = useCallback((key) => getChartHelp(key, i18n.language), [i18n.language]);

  // Période affichée par la courbe P&L (indépendante des filtres du journal).
  const [curveRange, setCurveRange] = useState("all");

  // Case du trackeur de progrès : le détail sort en bulle ancrée à la case
  // cliquée, il ne monopolise pas une colonne de la carte.
  //
  // Le positionnement est recalculé avec requestAnimationFrame pendant le
  // scroll/resize. L'effet reste abonné tant que la bulle est ouverte, au lieu
  // d'être détruit/recréé à chaque mise à jour de position : cela supprime les
  // sauts et le scintillement observés pendant le scroll.
  const [trackerPick, setTrackerPick] = useState(null); // { day, pos }
  const trackerAnchorRef = useRef(null);
  const trackerPopoverRef = useRef(null);
  const trackerPopoverRafRef = useRef(null);
  const TRACKER_POPOVER_WIDTH = 232;

  const getTrackerPopoverWidth = useCallback(() => {
    if (typeof window === "undefined") return TRACKER_POPOVER_WIDTH;
    const viewportWidth =
      document.documentElement?.clientWidth || window.innerWidth || TRACKER_POPOVER_WIDTH + 24;
    return Math.min(
      TRACKER_POPOVER_WIDTH,
      Math.max(180, viewportWidth - 24),
    );
  }, []);

  const openTrackerDay = useCallback((cell, event) => {
    if (!cell) return;
    const el = event.currentTarget;
    const width = getTrackerPopoverWidth();

    setTrackerPick((current) => {
      if (current?.day?.key === cell.key) {
        trackerAnchorRef.current = null;
        return null;
      }

      trackerAnchorRef.current = el;
      return {
        day: cell,
        pos: {
          ...anchoredPopoverPos(el, width, 240),
          width,
        },
      };
    });
  }, [getTrackerPopoverWidth]);

  const trackerPopoverOpen = Boolean(trackerPick);

  useEffect(() => {
    if (!trackerPopoverOpen || typeof window === "undefined") return undefined;

    const close = () => {
      if (trackerPopoverRafRef.current) {
        window.cancelAnimationFrame(trackerPopoverRafRef.current);
        trackerPopoverRafRef.current = null;
      }
      trackerAnchorRef.current = null;
      setTrackerPick(null);
    };

    const onKey = (e) => {
      if (e.key === "Escape") close();
    };

    const onDown = (e) => {
      if (trackerAnchorRef.current?.contains(e.target)) return;
      if (trackerPopoverRef.current?.contains(e.target)) return;
      close();
    };

    const reposition = () => {
      if (trackerPopoverRafRef.current) {
        window.cancelAnimationFrame(trackerPopoverRafRef.current);
      }

      trackerPopoverRafRef.current = window.requestAnimationFrame(() => {
        trackerPopoverRafRef.current = null;

        const anchor = trackerAnchorRef.current;
        if (!anchor || !document.documentElement.contains(anchor)) {
          close();
          return;
        }

        const rect = anchor.getBoundingClientRect();
        const viewportWidth =
          document.documentElement?.clientWidth || window.innerWidth || 0;
        const viewportHeight =
          window.visualViewport?.height || window.innerHeight || 0;
        const viewportTop = window.visualViewport?.offsetTop || 0;
        const viewportBottom = viewportTop + viewportHeight;

        // Si la case n'est plus visible, garder une bulle flottante sans
        // ancrage n'a plus de sens : on la ferme proprement.
        const anchorVisible =
          rect.bottom >= viewportTop &&
          rect.top <= viewportBottom &&
          rect.right >= 0 &&
          rect.left <= viewportWidth;

        if (!anchorVisible) {
          close();
          return;
        }

        const width = getTrackerPopoverWidth();
        const nextPos = anchoredPopoverPos(anchor, width, 240);
        if (!nextPos) {
          close();
          return;
        }

        setTrackerPick((current) => {
          if (!current) return current;

          const next = { ...nextPos, width };
          const previous = current.pos || {};
          const unchanged =
            previous.left === next.left &&
            previous.top === next.top &&
            previous.bottom === next.bottom &&
            previous.maxHeight === next.maxHeight &&
            previous.width === next.width;

          return unchanged ? current : { ...current, pos: next };
        });
      });
    };

    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition, { passive: true });
    window.addEventListener("orientationchange", reposition);
    window.visualViewport?.addEventListener("resize", reposition);
    window.visualViewport?.addEventListener("scroll", reposition);

    reposition();

    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("orientationchange", reposition);
      window.visualViewport?.removeEventListener("resize", reposition);
      window.visualViewport?.removeEventListener("scroll", reposition);

      if (trackerPopoverRafRef.current) {
        window.cancelAnimationFrame(trackerPopoverRafRef.current);
        trackerPopoverRafRef.current = null;
      }
    };
  }, [trackerPopoverOpen, getTrackerPopoverWidth]);

  /* token + cache isolés par compte */
  const [activeScope, setActiveScope] = useState(() =>
    setActiveJournalScope(resolveJournalScope()),
  );
  const [agentToken, setAgentToken] = useState(() =>
    getScopedAgentToken(getActiveJournalScope()) || "",
  );
  const hasToken = useMemo(() => !!(agentToken || "").trim(), [agentToken]);

  /* data */
  const [status, setStatus] = useState(null);
  const [openPositions, setOpenPositions] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState({});

  /* ui */
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [flash, setFlash] = useState("");
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState({ key: "time", dir: "desc" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const timerRef = useRef(null);
  const fetchInFlightRef = useRef(false);
  const lastHistoryFetchRef = useRef(0);
  const lastNotesFetchRef = useRef(0);
  const lastRegisterFetchRef = useRef(0);
  const offlineBackoffUntilRef = useRef(0);
  const flashTimerRef = useRef(null);
  const isDesktop = useIsDesktop();
  // Mobile : on atterrit sur « Aperçu » (en-tête + KPI). Desktop : direct « Trades »
  // (l'en-tête y est toujours visible).
  const [activePanel, setActivePanel] = useState(
    () => (typeof window !== "undefined" && window.innerWidth < 1024 ? "overview" : "trades"),
  );
  const [premiumCoachOpen, setPremiumCoachOpen] = useState(false);
  const [premiumCoachLoading, setPremiumCoachLoading] = useState(false);
  const [premiumCoachViewportInset, setPremiumCoachViewportInset] = useState(0);

  /* notes : modale */
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteFor, setNoteFor] = useState(null);
  const [noteDraft, setNoteDraft] = useState({
    comment: "",
    tags: "",
    rating: 0,
    setup: "",
    attachments: [],
  });
  const [noteDropActive, setNoteDropActive] = useState(false);
  const [notePreview, setNotePreview] = useState(null);
  const [noteContentReady, setNoteContentReady] = useState(false);
  const [customTagPresets, setCustomTagPresets] = useState(() =>
    readCustomTagPresetsForScope(getActiveJournalScope()),
  );
  const [newTagPreset, setNewTagPreset] = useState("");
  const noteFileInputRef = useRef(null);
  const noteContentFrameRef = useRef(null);
  const notesRef = useRef(notes);
  const activeScopeRef = useRef(activeScope);
  const agentTokenRef = useRef(agentToken);
  const allNoteTagPresets = useMemo(
    () =>
      Array.from(
        new Set(
          [...NOTE_TAG_PRESETS, ...customTagPresets]
            .map((item) => sanitizeTagPreset(item))
            .filter(Boolean),
        ),
      ).slice(0, MAX_NOTE_TAG_PRESETS),
    [customTagPresets],
  );
  const deferredNoteAttachments = useDeferredValue(noteDraft.attachments);
  const noteTagsArray = useMemo(
    () => parseTagString(noteDraft.tags || ""),
    [noteDraft.tags],
  );
  const noteAttachmentCount = Array.isArray(noteDraft.attachments)
    ? noteDraft.attachments.length
    : 0;


  const premiumCoachTimerRef = useRef(null);
  const [selectedHeatCell, setSelectedHeatCell] = useState(null);
  const [isPanelPending] = useTransition();
  const [chartsHeavyReady, setChartsHeavyReady] = useState(false);
  const [chartsRenderStep, setChartsRenderStep] = useState(0);
  const [advancedChartsRequested, setAdvancedChartsRequested] = useState(false);
  const chartsWarmupTimerRef = useRef(null);
  const chartsStageTimersRef = useRef([]);
  const navigationTeardownRef = useRef(false);
  const chartsPanelOpen = activePanel === "charts";
  const chartsStage1Ready = chartsPanelOpen && chartsRenderStep >= 1;
  const chartsStage2Ready =
    chartsPanelOpen && advancedChartsRequested && chartsRenderStep >= 2;
  const chartsStage3Ready =
    chartsPanelOpen && advancedChartsRequested && chartsRenderStep >= 3;
  const chartsLoadingModalOpen = chartsPanelOpen && chartsRenderStep < 3;
  const modalSidebarLeft = premiumCoachViewportInset > 0
    ? Math.max(0, premiumCoachViewportInset - 16)
    : 0;
  const flashMsg = useCallback((m) => {
    if (flashTimerRef.current) {
      window.clearTimeout(flashTimerRef.current);
      flashTimerRef.current = null;
    }

    setFlash(m);
    flashTimerRef.current = window.setTimeout(() => {
      setFlash("");
      flashTimerRef.current = null;
    }, 2200);
  }, []);

  const clearChartRenderQueue = useCallback(() => {
    navigationTeardownRef.current = true;
    if (chartsWarmupTimerRef.current) {
      window.clearTimeout(chartsWarmupTimerRef.current);
      chartsWarmupTimerRef.current = null;
    }
    chartsStageTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    chartsStageTimersRef.current = [];
    setChartsRenderStep(0);
    setChartsHeavyReady(false);
    setAdvancedChartsRequested(false);
    setSelectedHeatCell(null);
  }, []);

  const handlePanelSwitch = useCallback(
    (nextPanel) => {
      if (!nextPanel || nextPanel === activePanel) return;

      if (activePanel === "charts" || nextPanel !== "charts") {
        flushSync(() => {
          clearChartRenderQueue();
          setActivePanel(nextPanel);
        });
        return;
      }

      flushSync(() => {
        setActivePanel(nextPanel);
      });
    },
    [activePanel, clearChartRenderQueue],
  );

  // Sur desktop l'en-tête est toujours affiché : « Aperçu » n'a pas de contenu
  // propre, on bascule sur « Trades ».
  useEffect(() => {
    if (isDesktop && activePanel === "overview") setActivePanel("trades");
  }, [isDesktop, activePanel]);

  useEffect(() => {
    if (
      (!premiumCoachOpen && !premiumCoachLoading) ||
      typeof document === "undefined"
    )
      return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [premiumCoachOpen, premiumCoachLoading, noteOpen]);

  useEffect(
    () => () => {
      if (premiumCoachTimerRef.current) {
        window.clearTimeout(premiumCoachTimerRef.current);
        premiumCoachTimerRef.current = null;
      }
      if (chartsWarmupTimerRef.current) {
        window.clearTimeout(chartsWarmupTimerRef.current);
        chartsWarmupTimerRef.current = null;
      }
      chartsStageTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      chartsStageTimersRef.current = [];
      if (flashTimerRef.current) {
        window.clearTimeout(flashTimerRef.current);
        flashTimerRef.current = null;
      }
    },
    [],
  );


  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }
    if (!premiumCoachOpen && !premiumCoachLoading && !noteOpen) {
      setPremiumCoachViewportInset(0);
      return undefined;
    }

    const detectPremiumCoachInset = () => {
      if (window.innerWidth < 1024) {
        setPremiumCoachViewportInset(0);
        return;
      }

      const selectors = [
        'aside',
        '[data-sidebar]',
        '[data-sidebar-root]',
        '.sidebar',
        '.app-sidebar',
        '.layout-sidebar',
      ];
      const candidates = Array.from(
        document.querySelectorAll(selectors.join(',')),
      );

      let bestInset = 0;
      candidates.forEach((element) => {
        if (!(element instanceof HTMLElement)) return;
        const rect = element.getBoundingClientRect();
        if (rect.width < 120 || rect.height < 240) return;
        const style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden') return;
        if (rect.left > 32 || rect.top > window.innerHeight - 120) return;
        bestInset = Math.max(bestInset, rect.right);
      });

      setPremiumCoachViewportInset(bestInset > 0 ? Math.round(bestInset + 16) : 0);
    };

    const scheduleInsetDetection = () => {
      window.requestAnimationFrame(detectPremiumCoachInset);
      window.setTimeout(detectPremiumCoachInset, 80);
      window.setTimeout(detectPremiumCoachInset, 180);
    };

    detectPremiumCoachInset();
    const handleResize = () => scheduleInsetDetection();
    window.addEventListener('resize', handleResize, { passive: true });
    document.addEventListener('click', handleResize, true);
    document.addEventListener('transitionrun', handleResize, true);
    document.addEventListener('transitionend', handleResize, true);

    const mutationObserver =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => {
            scheduleInsetDetection();
          })
        : null;

    mutationObserver?.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-expanded', 'data-state', 'data-collapsed'],
    });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => scheduleInsetDetection())
        : null;

    if (resizeObserver) {
      const observed = new Set();
      const observeSidebarCandidates = () => {
        const selectors = [
          'aside',
          '[data-sidebar]',
          '[data-sidebar-root]',
          '.sidebar',
          '.app-sidebar',
          '.layout-sidebar',
        ];
        Array.from(document.querySelectorAll(selectors.join(','))).forEach((element) => {
          if (element instanceof HTMLElement && !observed.has(element)) {
            observed.add(element);
            resizeObserver.observe(element);
          }
        });
      };
      observeSidebarCandidates();
      window.setTimeout(observeSidebarCandidates, 120);
      window.setTimeout(observeSidebarCandidates, 320);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('click', handleResize, true);
      document.removeEventListener('transitionrun', handleResize, true);
      document.removeEventListener('transitionend', handleResize, true);
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
    };
  }, [premiumCoachOpen, premiumCoachLoading, noteOpen]);

  /* filters */
  const [symbolFilter, setSymbolFilter] = useState("all");
  const [sideFilter, setSideFilter] = useState("all");
  // Gain / perte : le journal ne se filtrait que par symbole et par sens.
  const [resultFilter, setResultFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedServers, setSelectedServers] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedSourceKeys, setSelectedSourceKeys] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Raccourci de période de la barre de filtres des statistiques : remplit les
  // deux champs date d'un coup. Déclaré ici, après les états qu'il référence —
  // le tableau de dépendances d'un useCallback est évalué immédiatement.
  const applyStatsQuickRange = useCallback((days) => {
    const to = new Date();
    const from = new Date(to.getTime() - (days - 1) * 86400000);
    setDateFrom(toLocalISODate(from));
    setDateTo(toLocalISODate(to));
  }, []);

  const statsFiltersActive =
    symbolFilter !== "all" ||
    sideFilter !== "all" ||
    Boolean(dateFrom) ||
    Boolean(dateTo);

  /* analytics toggles */

  /* alerts */
  const [ddAlertOn, setDdAlertOn] = useState(true);
  const [lossStreakAlertOn, setLossStreakAlertOn] = useState(true);
  const ddThreshPct = 10;
  const lossStreakThresh = 4;

  const clearJournalSessionState = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    fetchInFlightRef.current = false;
    lastHistoryFetchRef.current = 0;
    lastNotesFetchRef.current = 0;
    lastRegisterFetchRef.current = 0;
    offlineBackoffUntilRef.current = 0;

    setStatus(null);
    setOpenPositions([]);
    setPendingOrders([]);
    setHistory([]);
    setNotes({});
    setSearch("");
    setSymbolFilter("all");
    setSideFilter("all");
    // Le filtre Résultat manquait ici : au changement de jeton, l'historique du
    // nouveau compte arrivait déjà amputé de ses pertes sans que rien ne le dise.
    setResultFilter("all");
    setDateFrom("");
    setDateTo("");
    setSelectedServers([]);
    setSelectedAccounts([]);
    setSelectedSourceKeys([]);
    setPage(1);
    setNoteOpen(false);
    setNoteFor(null);
    setNotePreview(null);
    setNoteDropActive(false);
    setNoteContentReady(false);
    setSelectedHeatCell(null);
    setErr("");
  }, []);

  useEffect(() => {
    activeScopeRef.current = activeScope;
  }, [activeScope]);

  useEffect(() => {
    agentTokenRef.current = agentToken;
  }, [agentToken]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncSessionStorage = () => {
      const nextScope = setActiveJournalScope(resolveJournalScope());
      clearUnsafeLegacyJournalKeysWhenLoggedOut(nextScope);

      const nextToken = getScopedAgentToken(nextScope) || "";
      const scopeChanged = activeScopeRef.current !== nextScope;
      const tokenChanged = agentTokenRef.current !== nextToken;

      if (scopeChanged || tokenChanged) {
        clearJournalSessionState();
        setCustomTagPresets(readCustomTagPresetsForScope(nextScope));
      }

      activeScopeRef.current = nextScope;
      agentTokenRef.current = nextToken;
      setActiveScope(nextScope);
      setAgentToken(nextToken);
    };

    syncSessionStorage();

    const events = [
      "storage",
      "focus",
      "fm:auth-changed",
      "fm:auth:changed",
      "fullmetrix:auth-changed",
      "fm:logout",
      "fm:agent-token-updated",
    ];

    events.forEach((eventName) =>
      window.addEventListener(eventName, syncSessionStorage),
    );

    return () => {
      events.forEach((eventName) =>
        window.removeEventListener(eventName, syncSessionStorage),
      );
    };
  }, [clearJournalSessionState]);

  const isConnected = useMemo(() => {
    const hb = status?.last_heartbeat;
    if (!hb) return false;
    const d = tsToDate(hb);
    if (!d) return false;
    return Date.now() - d.getTime() < 15000;
  }, [status?.last_heartbeat]);

  const statusAccountLogin = safeText(
    status?.account?.login ??
      status?.account?.account ??
      status?.account?.number ??
      status?.account?.id ??
      status?.login ??
      status?.account_number,
  );
  const statusBrokerServer = safeText(
    status?.account?.server ??
      status?.account?.trade_server ??
      status?.account?.broker_server ??
      status?.account?.broker ??
      status?.server,
  );

  const CHART_H = 420; // même hauteur pour la courbe et la colonne Stats

  const getLiveDarkMode = useCallback(() => {
    if (typeof window === "undefined") return false;

    const html = document.documentElement;
    const body = document.body;

    const htmlDark = html.classList.contains("dark");
    const bodyDark = body?.classList?.contains("dark");
    const htmlTheme = html.getAttribute("data-theme");
    const bodyTheme = body?.getAttribute?.("data-theme");

    let storedTheme = null;
    try {
      storedTheme =
        window.localStorage.getItem("fm_theme") ||
        window.localStorage.getItem("theme") ||
        window.localStorage.getItem("color-theme");
    } catch {
      storedTheme = null;
    }

    if (storedTheme === "dark") return true;
    if (storedTheme === "light") return false;

    const prefersDark =
      window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false;

    return Boolean(
      htmlDark ||
      bodyDark ||
      htmlTheme === "dark" ||
      bodyTheme === "dark" ||
      prefersDark,
    );
  }, []);

  const [isDarkMode, setIsDarkMode] = useState(() => getLiveDarkMode());
  const noteModalDark = isDarkMode;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncDarkMode = () => {
      setIsDarkMode(getLiveDarkMode());
    };

    syncDarkMode();

    const observer = new MutationObserver(syncDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme"],
    });

    if (document.body) {
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ["class", "data-theme"],
      });
    }

    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    const onMediaChange = () => syncDarkMode();

    media?.addEventListener?.("change", onMediaChange);
    media?.addListener?.(onMediaChange);

    return () => {
      observer.disconnect();
      media?.removeEventListener?.("change", onMediaChange);
      media?.removeListener?.(onMediaChange);
    };
  }, [getLiveDarkMode]);

  useEffect(() => {
    chartsStageTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    chartsStageTimersRef.current = [];

    if (!chartsPanelOpen) {
      setChartsRenderStep(0);
      setChartsHeavyReady(false);
      setAdvancedChartsRequested(false);
      return undefined;
    }

    navigationTeardownRef.current = false;
    setChartsRenderStep(0);
    setChartsHeavyReady(false);
    setAdvancedChartsRequested(true);

    chartsStageTimersRef.current = CHART_STAGE_DELAYS.map((delay, index) =>
      window.setTimeout(() => {
        if (navigationTeardownRef.current) return;
        const nextStep = index + 1;
        setChartsRenderStep((current) => Math.max(current, nextStep));
        if (nextStep >= 3) setChartsHeavyReady(true);
      }, delay),
    );

    return () => {
      navigationTeardownRef.current = true;
      chartsStageTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      chartsStageTimersRef.current = [];
    };
  }, [chartsPanelOpen]);

  /* ---- fetching ---- */
  const splitOpenResponse = useCallback((openResp) => {
    const ensureArr = (x) => (Array.isArray(x) ? x : []);
    if (!openResp) return { positions: [], pendings: [] };

    const arrA = ensureArr(openResp.open_trades);
    const arrB = Array.isArray(openResp) ? openResp : [];
    const pendA = ensureArr(openResp.pending_orders);

    const merged = [...arrA, ...arrB].map(normalizeOpenTrade);
    const positions = merged.filter(
      (x) =>
        (x.kind || "position") === "position" ||
        (!x.order_type && x.profit != null),
    );
    const pendingsExplicit = pendA.map(normalizeOpenTrade);
    const pendingsGuessed = merged.filter(
      (x) => x.order_type || x.kind === "pending",
    );

    const pendings = [...pendingsExplicit, ...pendingsGuessed].reduce(
      (acc, it) => {
        const has = acc.find((a) => String(a.ticket) === String(it.ticket));
        if (!has) acc.push(it);
        return acc;
      },
      [],
    );

    return { positions, pendings };
  }, []);

  const fetchHistoryPageDirect = useCallback(
    async (
      tok,
      { limit = FULL_HISTORY_PAGE_LIMIT, offset = 0, includeOpen = 1, symbol = "" } = {},
    ) => {
      const params = new URLSearchParams({
        agent_token: tok,
        limit: String(limit),
        offset: String(offset),
        include_open: String(includeOpen),
      });

      if (symbol) params.set("symbol", symbol);

      // URL absolue (API_ROOT) et pas `/api/...` : en chemin relatif, CRA
      // proxifiait vers le `proxy` du package.json (127.0.0.1:5000) au lieu de
      // REACT_APP_API_URL, d'où un ECONNREFUSED en dev.
      const jwt = String(getToken() || "").trim();

      const res = await fetch(`${API_ROOT}/agent/history?${params.toString()}`, {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }

      return res.json();
    },
    [],
  );

  const fetchAllHistoryDirect = useCallback(
    async (tok) => {
      const limit = FULL_HISTORY_PAGE_LIMIT;
      let offset = 0;
      let all = [];
      let hasMore = true;

      while (hasMore) {
        const data = await fetchHistoryPageDirect(tok, {
          limit,
          offset,
          includeOpen: 1,
        });

        const chunk = Array.isArray(data?.history)
          ? data.history
          : Array.isArray(data)
            ? data
            : [];

        all = all.concat(chunk);

        hasMore = Boolean(data?.has_more);
        offset += chunk.length;

        if (!chunk.length || all.length >= FULL_HISTORY_MAX_ROWS) break;
      }

      return all.slice(0, FULL_HISTORY_MAX_ROWS);
    },
    [fetchHistoryPageDirect],
  );

  const fetchRecentHistoryDirect = useCallback(
    async (tok) => {
      const data = await fetchHistoryPageDirect(tok, {
        limit: RECENT_HISTORY_LIMIT,
        offset: 0,
        includeOpen: 1,
      });

      return Array.isArray(data?.history)
        ? data.history
        : Array.isArray(data)
          ? data
          : [];
    },
    [fetchHistoryPageDirect],
  );

  const applyRealtimeSnapshot = useCallback((snapshot) => {
    if (!snapshot || typeof snapshot !== "object") return;

    const account =
      snapshot.account && typeof snapshot.account === "object"
        ? snapshot.account
        : {};
    const liveAt = Number(snapshot.timestamp_ms || snapshot.server_time_ms || Date.now());
    const currentAccount = safeText(
      account.login ?? account.account ?? account.number ?? account.id ?? "",
    );
    const currentServer = safeText(
      account.server ?? account.trade_server ?? account.broker_server ?? account.broker ?? "",
    );

    setStatus((prev) => ({
      ...(prev || {}),
      ok: true,
      connected: true,
      last_heartbeat: Number.isFinite(liveAt) ? liveAt : Date.now(),
      account: {
        ...(prev?.account || {}),
        ...account,
      },
      balance: account.balance ?? prev?.balance ?? 0,
      equity: account.equity ?? prev?.equity ?? account.balance ?? 0,
      server: currentServer || prev?.server || "",
      broker: currentServer || prev?.broker || "",
      login: currentAccount || prev?.login || "",
      account_login: currentAccount || prev?.account_login || "",
      currency: account.currency ?? prev?.currency ?? "USD",
    }));

    if (Array.isArray(snapshot.positions)) {
      setOpenPositions(
        snapshot.positions.map((row) => {
          const item = normalizeOpenTrade(row);
          return {
            ...item,
            account_login: item.account_login || currentAccount,
            broker_server: item.broker_server || currentServer,
          };
        }),
      );
    }

    if (Array.isArray(snapshot.pending_orders)) {
      setPendingOrders(
        snapshot.pending_orders.map((row) => {
          const item = normalizeOpenTrade(row);
          return {
            ...item,
            account_login: item.account_login || currentAccount,
            broker_server: item.broker_server || currentServer,
          };
        }),
      );
    }
  }, []);

  const fetchAll = useCallback(
    async (tokOverride, options = {}) => {
      const tok = (tokOverride ?? agentToken).trim();
      const light = Boolean(options?.light);
      const force = Boolean(options?.force);
      const now = Date.now();

      // Démo locale (?mock=1, dev seulement) : on court-circuite le réseau et on
      // alimente la page avec un jeu déterministe. Aucun jeton requis.
      if (isMockJournalEnabled()) {
        const mock = buildMockJournalData(now);
        const { positions, pendings } = splitOpenResponse(mock.open);

        setStatus(mock.status);
        setOpenPositions(positions);
        setPendingOrders(pendings);
        setHistory(
          (mock.history.history || [])
            .map((t, i) => normalizeHistoryTrade(t, i))
            .sort((a, b) => {
              const at = tsToDate(a.close_time)?.getTime() || 0;
              const bt = tsToDate(b.close_time)?.getTime() || 0;
              return bt - at;
            }),
        );
        setNotes(mock.notes);
        setLoading(false);
        setErr("");
        return;
      }

      if (!tok) {
        setStatus(null);
        setOpenPositions([]);
        setPendingOrders([]);
        setHistory([]);
        setNotes({});
        return;
      }

      if (
        light &&
        !force &&
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }

      if (
        typeof navigator !== "undefined" &&
        navigator.onLine === false
      ) {
        offlineBackoffUntilRef.current = now + NETWORK_BACKOFF_MS;
        if (!light) setErr("Connexion Internet indisponible.");
        return;
      }

      if (light && !force && now < offlineBackoffUntilRef.current) return;
      if (fetchInFlightRef.current) return;

      fetchInFlightRef.current = true;
      setLoading(!light);
      if (!light) setErr("");

      try {
        const shouldRegister =
          !light ||
          force ||
          now - lastRegisterFetchRef.current >= REGISTER_REFRESH_MS;
        const shouldFetchHistory =
          !light || force || now - lastHistoryFetchRef.current >= HISTORY_REFRESH_MS;
        const shouldFetchNotes =
          !light || force || now - lastNotesFetchRef.current >= NOTES_REFRESH_MS;

        if (shouldRegister) {
          await apiAgentRegisterExisting(tok);
          lastRegisterFetchRef.current = Date.now();
        }

        const [s, open, histRaw, n] = await Promise.all([
          apiAgentStatus(tok),
          apiAgentOpenTrades(tok),
          shouldFetchHistory
            ? light
              ? fetchRecentHistoryDirect(tok)
              : fetchAllHistoryDirect(tok)
            : Promise.resolve(null),
          shouldFetchNotes ? apiAgentGetNotes(tok).catch(() => ({})) : Promise.resolve(null),
        ]);

        if (shouldFetchHistory) lastHistoryFetchRef.current = Date.now();
        if (shouldFetchNotes) lastNotesFetchRef.current = Date.now();

        setStatus(s || null);

        const currentAccount = safeText(
          s?.account?.login ??
            s?.account?.account ??
            s?.account?.number ??
            s?.account?.id ??
            s?.login ??
            s?.account_number,
        );

        const currentServer = safeText(
          s?.account?.server ??
            s?.account?.trade_server ??
            s?.account?.broker_server ??
            s?.account?.broker ??
            s?.server,
        );

        const { positions, pendings } = splitOpenResponse(open);

        setOpenPositions(
          positions.map((item) => ({
            ...item,
            account_login: item.account_login || currentAccount,
            broker_server: item.broker_server || currentServer,
          })),
        );

        setPendingOrders(
          pendings.map((item) => ({
            ...item,
            account_login: item.account_login || currentAccount,
            broker_server: item.broker_server || currentServer,
          })),
        );

        if (histRaw !== null) {
          const raw = Array.isArray(histRaw?.history)
            ? histRaw.history
            : Array.isArray(histRaw)
              ? histRaw
              : [];

          const norm = raw
            .map((t, i) => normalizeHistoryTrade(t, i))
            .map((item) => ({
              ...item,
              account_login: item.account_login || currentAccount,
              broker_server: item.broker_server || currentServer,
            }))
            .sort((a, b) => {
              const at =
                tsToDate(a.close_time)?.getTime() ||
                tsToDate(a.open_time)?.getTime() ||
                0;
              const bt =
                tsToDate(b.close_time)?.getTime() ||
                tsToDate(b.open_time)?.getTime() ||
                0;
              return bt - at;
            });

          setHistory((prev) => {
            if (!light) return norm;

            const map = new Map();

            for (const item of prev || []) {
              map.set(String(item.ticket), item);
            }

            for (const item of norm) {
              map.set(String(item.ticket), item);
            }

            return Array.from(map.values()).sort((a, b) => {
              const at =
                tsToDate(a.close_time)?.getTime() ||
                tsToDate(a.open_time)?.getTime() ||
                0;
              const bt =
                tsToDate(b.close_time)?.getTime() ||
                tsToDate(b.open_time)?.getTime() ||
                0;
              return bt - at;
            });
          });
        }

        if (n !== null) {
          const cached = readNotesCache(tok, activeScopeRef.current);
          const mergedNotes = mergeNoteRecords(
            n && typeof n === "object" ? n : {},
            cached,
          );

          setNotes(mergedNotes);
          writeNotesCache(tok, mergedNotes, activeScopeRef.current);
        }

        offlineBackoffUntilRef.current = 0;
        if (!light) setErr("");
      } catch (e) {
        offlineBackoffUntilRef.current = Date.now() + NETWORK_BACKOFF_MS;
        const m = String(e?.message || "");
        if (!light) {
          if (m.includes("401")) setErr(L("page.notAuthenticated"));
          else setErr(`Erreur de récupération: ${e.message || e}`);
        }
        console.error(e);
      } finally {
        fetchInFlightRef.current = false;
        setLoading(false);
      }
    },
    [
      agentToken,
      splitOpenResponse,
      fetchAllHistoryDirect,
      fetchRecentHistoryDirect,
    ],
  );

  const realtimeConnected = useAgentUiRealtime(agentToken, {
    onSnapshot: applyRealtimeSnapshot,
    onTradeEvent: () => fetchAll(undefined, { light: true, force: true }),
  });

  useEffect(() => {
    fetchAll(undefined, { force: true });
  }, [fetchAll]);

  useEffect(() => {
    const canAutoRefresh =
      autoRefresh &&
      hasToken &&
      !chartsPanelOpen &&
      !premiumCoachOpen &&
      !premiumCoachLoading &&
      !noteOpen;

    if (canAutoRefresh) {
      // Le flux WebSocket met balance/equity/positions/P&L à jour en direct.
      // REST devient seulement un filet de cohérence et le fallback si WSS tombe.
      const refreshDelay = realtimeConnected ? 60000 : 4000;
      timerRef.current = window.setInterval(
        () => fetchAll(undefined, { light: true }),
        refreshDelay,
      );
      return () => {
        if (timerRef.current) {
          window.clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };
    }
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return undefined;
  }, [autoRefresh, hasToken, chartsPanelOpen, premiumCoachOpen, premiumCoachLoading, noteOpen, fetchAll, realtimeConnected]);

  useEffect(() => {
    if (!hasToken || typeof window === "undefined") return undefined;
    const handleOnline = () => fetchAll(undefined, { light: true, force: true });
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [hasToken, fetchAll]);

  /* derived + filters */
  const withNotes = useMemo(
    () => history.map((h) => ({ ...h, note: notes[h.ticket] || {} })),
    [history, notes],
  );

  const enrichedHistory = useMemo(
    () =>
      withNotes.map((item) => {
        const server = safeText(item.broker_server || statusBrokerServer);
        const account = safeText(item.account_login || statusAccountLogin);
        return {
          ...item,
          broker_server: server,
          account_login: account,
          source_key: buildSourceKey(server, account),
          source_label: buildSourceLabel(server, account),
        };
      }),
    [withNotes, statusBrokerServer, statusAccountLogin],
  );

  const enrichedOpenPositions = useMemo(
    () =>
      openPositions.map((item) => {
        const server = safeText(item.broker_server || statusBrokerServer);
        const account = safeText(item.account_login || statusAccountLogin);
        return {
          ...item,
          broker_server: server,
          account_login: account,
          source_key: buildSourceKey(server, account),
          source_label: buildSourceLabel(server, account),
        };
      }),
    [openPositions, statusBrokerServer, statusAccountLogin],
  );

  const enrichedPendingOrders = useMemo(
    () =>
      pendingOrders.map((item) => {
        const server = safeText(item.broker_server || statusBrokerServer);
        const account = safeText(item.account_login || statusAccountLogin);
        return {
          ...item,
          broker_server: server,
          account_login: account,
          source_key: buildSourceKey(server, account),
          source_label: buildSourceLabel(server, account),
        };
      }),
    [pendingOrders, statusBrokerServer, statusAccountLogin],
  );

  const sourceOptions = useMemo(() => {
    const map = new Map();
    [
      ...enrichedHistory,
      ...enrichedOpenPositions,
      ...enrichedPendingOrders,
    ].forEach((item) => {
      const key =
        item.source_key ||
        buildSourceKey(item.broker_server, item.account_login);
      if (!map.has(key)) {
        map.set(key, {
          key,
          server: safeText(item.broker_server),
          account: safeText(item.account_login),
          label:
            item.source_label ||
            buildSourceLabel(item.broker_server, item.account_login),
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [enrichedHistory, enrichedOpenPositions, enrichedPendingOrders]);

  const serverOptions = useMemo(() => {
    const set = new Set(
      sourceOptions.map((item) => item.server).filter(Boolean),
    );
    return Array.from(set).sort();
  }, [sourceOptions]);

  /* Clé de source du terminal actuellement connecté (celui dont l'en-tête
     affiche le solde). Sert de sélection par défaut du journal. */
  const connectedSourceKey = useMemo(() => {
    const account = safeText(
      status?.account?.login ??
        status?.account?.account ??
        status?.account?.number ??
        status?.account?.id,
    );
    const server = safeText(
      status?.account?.server ??
        status?.account?.trade_server ??
        status?.account?.broker_server ??
        status?.account?.broker,
    );

    if (!account && !server) return "";
    return buildSourceKey(server, account);
  }, [status]);

  /* Un même token peut être collé sur plusieurs terminaux : le journal
     mélangeait alors les trades de tous les comptes sous un en-tête qui n'en
     décrivait qu'un seul. Par défaut on se cale sur le compte connecté ; les
     autres restent accessibles via le sélecteur « Compte ». */
  const multiAccountDetected = sourceOptions.length > 1;

  useEffect(() => {
    if (!multiAccountDetected) return;
    if (selectedSourceKeys.length) return;
    if (!connectedSourceKey) return;
    if (!sourceOptions.some((item) => item.key === connectedSourceKey)) return;

    setSelectedSourceKeys([connectedSourceKey]);
  }, [
    multiAccountDetected,
    selectedSourceKeys.length,
    connectedSourceKey,
    sourceOptions,
  ]);

  const accountOptions = useMemo(() => {
    const set = new Set(
      sourceOptions.map((item) => item.account).filter(Boolean),
    );
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
  }, [sourceOptions]);

  // Date du plus ancien trade remonté : sert à afficher « depuis quand » le
  // journal couvre l'activité du compte.
  const journalStartDate = useMemo(() => {
    let oldest = null;
    for (const trade of enrichedHistory || []) {
      if (isCashflowRecord(trade)) continue;
      const d = tsToDate(trade.open_time ?? trade.close_time);
      if (!d) continue;
      if (!oldest || d.getTime() < oldest.getTime()) oldest = d;
    }
    return oldest;
  }, [enrichedHistory]);

  const filteredHistory = useMemo(() => {
    let arr = enrichedHistory;
    if (selectedServers.length)
      arr = arr.filter((t) => selectedServers.includes(t.broker_server));
    if (selectedAccounts.length)
      arr = arr.filter((t) => selectedAccounts.includes(t.account_login));
    if (selectedSourceKeys.length)
      arr = arr.filter((t) => selectedSourceKeys.includes(t.source_key));
    if (symbolFilter !== "all")
      arr = arr.filter((t) => t.symbol === symbolFilter);
    if (sideFilter !== "all") arr = arr.filter((t) => t.side === sideFilter);
    if (resultFilter !== "all") {
      // Une position encore ouverte n'a pas de résultat : on l'écarte.
      arr = arr.filter((t) => {
        if (t.close_time == null || isCashflowRecord(t)) return false;
        const profit = toNumber(t.profit);
        if (resultFilter === "win") return profit > 0;
        if (resultFilter === "loss") return profit < 0;
        return profit === 0;
      });
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter(
        (t) =>
          String(t.ticket).toLowerCase().includes(q) ||
          (t.symbol || "").toLowerCase().includes(q) ||
          (t.broker_server || "").toLowerCase().includes(q) ||
          (t.account_login || "").toLowerCase().includes(q),
      );
    }
    if (isValidDateInput(dateFrom)) {
      const from = new Date(dateFrom + "T00:00:00").getTime();
      arr = arr.filter(
        (t) =>
          (tsToDate(t.close_time)?.getTime() ??
            tsToDate(t.open_time)?.getTime() ??
            0) >= from,
      );
    }
    if (isValidDateInput(dateTo)) {
      const to = new Date(dateTo + "T23:59:59").getTime();
      arr = arr.filter(
        (t) =>
          (tsToDate(t.close_time)?.getTime() ??
            tsToDate(t.open_time)?.getTime() ??
            0) <= to,
      );
    }
    arr = [...arr].sort((a, b) => {
      const dir = sortBy.dir === "asc" ? 1 : -1;
      if (sortBy.key === "pnl")
        return dir * (toNumber(a.profit) - toNumber(b.profit));
      if (sortBy.key === "volume")
        return dir * (toNumber(a.volume) - toNumber(b.volume));
      const at =
        tsToDate(a.close_time)?.getTime() ||
        tsToDate(a.open_time)?.getTime() ||
        0;
      const bt =
        tsToDate(b.close_time)?.getTime() ||
        tsToDate(b.open_time)?.getTime() ||
        0;
      return dir * (at - bt);
    });
    return arr;
  }, [
    enrichedHistory,
    selectedServers,
    selectedAccounts,
    selectedSourceKeys,
    symbolFilter,
    sideFilter,
    resultFilter,
    dateFrom,
    dateTo,
    search,
    sortBy,
  ]);

  const deferredFilteredHistory = useDeferredValue(filteredHistory);

  /* Symbole et sens s'appliquent aussi ici : la puce « périmètre actif » vit
     dans la bande KPI, donc elle annonce « XAUUSD » depuis l'onglet Positions —
     où toutes les paires restaient listées et où le compteur de positions
     comptait tout le compte. Le filtre Résultat, lui, n'a pas de sens sur une
     position encore ouverte : elle n'a pas de résultat. */
  const filteredOpenPositions = useMemo(() => {
    let arr = enrichedOpenPositions;
    if (selectedServers.length)
      arr = arr.filter((t) => selectedServers.includes(t.broker_server));
    if (selectedAccounts.length)
      arr = arr.filter((t) => selectedAccounts.includes(t.account_login));
    if (selectedSourceKeys.length)
      arr = arr.filter((t) => selectedSourceKeys.includes(t.source_key));
    if (symbolFilter !== "all")
      arr = arr.filter((t) => t.symbol === symbolFilter);
    if (sideFilter !== "all") arr = arr.filter((t) => t.side === sideFilter);
    return arr;
  }, [
    enrichedOpenPositions,
    selectedServers,
    selectedAccounts,
    selectedSourceKeys,
    symbolFilter,
    sideFilter,
  ]);

  const filteredPendingOrders = useMemo(() => {
    let arr = enrichedPendingOrders;
    if (selectedServers.length)
      arr = arr.filter((t) => selectedServers.includes(t.broker_server));
    if (selectedAccounts.length)
      arr = arr.filter((t) => selectedAccounts.includes(t.account_login));
    if (selectedSourceKeys.length)
      arr = arr.filter((t) => selectedSourceKeys.includes(t.source_key));
    if (symbolFilter !== "all")
      arr = arr.filter((t) => t.symbol === symbolFilter);
    // Un ordre en attente porte « buy_limit » / « sell_stop » dans `type` quand
    // `side` est absent : une égalité stricte les aurait tous écartés.
    if (sideFilter !== "all")
      arr = arr.filter((t) =>
        String(t.side || t.type || "")
          .toLowerCase()
          .includes(sideFilter),
      );
    return arr;
  }, [
    enrichedPendingOrders,
    selectedServers,
    selectedAccounts,
    selectedSourceKeys,
    symbolFilter,
    sideFilter,
  ]);

  /* Date de la dernière position prise : ouvertures en cours comprises, sinon
     un compte encore investi afficherait la date d'un vieux trade clôturé. */
  const lastPositionAt = useMemo(() => {
    let best = 0;

    const scan = (list) => {
      (Array.isArray(list) ? list : []).forEach((trade) => {
        const ts = tsToDate(trade?.open_time)?.getTime() || 0;
        if (ts > best) best = ts;
      });
    };

    scan(filteredOpenPositions);
    scan(filteredHistory.filter((trade) => !isCashflowRecord(trade)));

    return best ? new Date(best) : null;
  }, [filteredOpenPositions, filteredHistory]);

  // KPIs
  const metrics = useMemo(
    () =>
      computeJournalMetrics(filteredHistory, {
        language: i18n.language,
        currentBalanceInput: status?.account?.balance ?? status?.balance,
        accountMaxDrawdownPct:
          status?.account?.max_drawdown_pct ?? status?.max_drawdown_pct,
        accountMaxDrawdownAmount:
          status?.account?.max_drawdown_amount ?? status?.max_drawdown_amount,
      }),
    [
      filteredHistory,
      i18n.language,
      status?.account?.balance,
      status?.balance,
      status?.account?.max_drawdown_pct,
      status?.max_drawdown_pct,
      status?.account?.max_drawdown_amount,
      status?.max_drawdown_amount,
    ],
  );

  // Le filtre de période s'applique AVANT le sous-échantillonnage : filtrer
  // après réduirait une semaine de trades aux quelques points survivants.
  const chartCurveData = useMemo(() => {
    const curve = metrics?.curve || [];
    const days = CURVE_RANGE_DAYS[curveRange];
    if (!days || !curve.length) {
      return downsampleArray(curve, MAX_PNL_CURVE_POINTS);
    }
    // Borne calée sur le dernier trade, pas sur l'heure courante : un compte
    // inactif depuis trois semaines afficherait sinon une courbe vide.
    const last = curve[curve.length - 1]?.ts || Date.now();
    const from = last - days * 86400000;
    const sliced = curve.filter((p) => (p.ts || 0) >= from);
    return downsampleArray(
      sliced.length ? sliced : curve.slice(-2),
      MAX_PNL_CURVE_POINTS,
    );
  }, [metrics?.curve, curveRange]);

  const curveRangeOptions = useMemo(
    () =>
      CURVE_RANGES.map((r) => ({
        value: r.value,
        label: i18n.language.startsWith("en") ? r.labelEn : r.labelFr,
      })),
    [i18n.language],
  );

  const chartHistory = useMemo(() => {
    if (!chartsPanelOpen) return [];
    return (deferredFilteredHistory || [])
      .filter((trade) => !isCashflowRecord(trade))
      .slice(0, MAX_CHART_TRADES);
  }, [chartsPanelOpen, deferredFilteredHistory]);

  const canRunPremiumCoach = useMemo(
    () => Boolean((metrics?.total || 0) > 0),
    [metrics?.total],
  );

  // Échantillon analysé par le coach.
  //
  // Deux défauts corrigés ici. D'abord le plafond, qui était celui des
  // graphiques (1500) : sur un compte à 2500 trades clôturés, l'audit ignorait
  // silencieusement 1000 trades pendant que les cartes de statistiques en
  // affichaient 2500.
  //
  // Ensuite, et c'est le plus grave : la coupe se faisait sur `filteredHistory`,
  // qui est trié selon la colonne cliquée dans le tableau du Journal. Trier par
  // profit décroissant faisait donc analyser au coach les 1500 trades les plus
  // rentables, et son diagnostic changeait selon un tri d'affichage.
  //
  // Désormais : on prend les trades clôturés, on ordonne par date de clôture,
  // et si le compte dépasse le plafond on garde les plus RÉCENTS — un audit
  // doit décrire la période récente, pas un extrait arbitraire.
  const coachHistory = useMemo(() => {
    if (!premiumCoachOpen && !premiumCoachLoading) return [];
    const closed = filteredHistory.filter((t) => t.close_time != null && !isCashflowRecord(t));
    const chronological = [...closed].sort(
      (a, b) =>
        (tsToDate(a.close_time)?.getTime() || 0) -
        (tsToDate(b.close_time)?.getTime() || 0),
    );
    return chronological.length > MAX_COACH_TRADES
      ? chronological.slice(-MAX_COACH_TRADES)
      : chronological;
  }, [premiumCoachOpen, premiumCoachLoading, filteredHistory]);

  // Agrégats recalculés sur CET échantillon. Le coach recevait auparavant les
  // agrégats de tout l'historique avec un échantillon tronqué : il annonçait
  // « 1500 trades analysés » tout en citant le P&L net des 2500.
  const coachMetrics = useMemo(
    () =>
      computeJournalMetrics(coachHistory, {
        language: i18n.language,
        currentBalanceInput: status?.account?.balance ?? status?.balance,
        accountMaxDrawdownPct:
          status?.account?.max_drawdown_pct ?? status?.max_drawdown_pct,
        accountMaxDrawdownAmount:
          status?.account?.max_drawdown_amount ?? status?.max_drawdown_amount,
      }),
    [
      coachHistory,
      i18n.language,
      status?.account?.balance,
      status?.balance,
      status?.account?.max_drawdown_pct,
      status?.max_drawdown_pct,
      status?.account?.max_drawdown_amount,
      status?.max_drawdown_amount,
    ],
  );

  const premiumCoach = useMemo(() => {
    if (!premiumCoachOpen) {
      return {
        hasData: canRunPremiumCoach,
        total: metrics?.total || 0,
        score: 0,
        scoreBreakdown: [],
      };
    }

    return buildPremiumJournalCoach(
      coachHistory,
      coachMetrics,
      filteredOpenPositions,
      notes,
      i18n.language,
    );
  }, [
    premiumCoachOpen,
    canRunPremiumCoach,
    coachHistory,
    coachMetrics,
    filteredOpenPositions,
    notes,
    i18n.language,
  ]);

  const openPremiumCoachAudit = useCallback(() => {
    if (!canRunPremiumCoach) {
      flashMsg(L("page.noClosedTradeNow"));
      return;
    }

    if (premiumCoachTimerRef.current) {
      window.clearTimeout(premiumCoachTimerRef.current);
      premiumCoachTimerRef.current = null;
    }

    setPremiumCoachOpen(false);
    setPremiumCoachLoading(true);

    premiumCoachTimerRef.current = window.setTimeout(() => {
      setPremiumCoachLoading(false);
      setPremiumCoachOpen(true);
      premiumCoachTimerRef.current = null;
    }, 850);
  }, [canRunPremiumCoach, flashMsg]);

  // Publie les onglets dans le sous-menu du shell (drawer mobile).
  usePageNav({
    items: JOURNAL_TABS,
    activeKey: premiumCoachOpen ? "coach" : activePanel,
    onSelect: (key) =>
      key === "coach" ? openPremiumCoachAudit() : handlePanelSwitch(key),
    title: "Journal Auto",
    buttonLabel: "Sections",
    buttonIcon: BookOpenText,
  });

  const closePremiumCoachAudit = useCallback(() => {
    if (premiumCoachTimerRef.current) {
      window.clearTimeout(premiumCoachTimerRef.current);
      premiumCoachTimerRef.current = null;
    }
    setPremiumCoachLoading(false);
    setPremiumCoachOpen(false);
  }, []);

  const grossFlowTotal = useMemo(
    () =>
      Math.max(
        1,
        Math.abs(metrics?.grossProfit || 0) + Math.abs(metrics?.grossLoss || 0),
      ),
    [metrics?.grossProfit, metrics?.grossLoss],
  );

  const gainsSharePct = useMemo(
    () =>
      Math.round((Math.abs(metrics?.grossProfit || 0) / grossFlowTotal) * 100),
    [metrics?.grossProfit, grossFlowTotal],
  );

  const lossesSharePct = useMemo(
    () =>
      Math.round((Math.abs(metrics?.grossLoss || 0) / grossFlowTotal) * 100),
    [metrics?.grossLoss, grossFlowTotal],
  );

  const sourceSummaries = useMemo(() => {
    return sourceOptions.map((source) => {
      const trades = enrichedHistory.filter(
        (item) => item.source_key === source.key,
      );
      const closed = trades.filter((item) => item.close_time != null && !isCashflowRecord(item));
      const net = closed.reduce((sum, item) => sum + toNumber(item.profit), 0);
      const wins = closed.filter((item) => toNumber(item.profit) > 0).length;
      const total = closed.length;
      return {
        ...source,
        total,
        wins,
        winRate: total ? (wins / total) * 100 : 0,
        net: +net.toFixed(2),
      };
    });
  }, [sourceOptions, enrichedHistory]);

  const allSymbols = useMemo(() => {
    const set = new Set(
      enrichedHistory
        .filter((h) => !isCashflowRecord(h))
        .map((h) => h.symbol)
        .filter(Boolean),
    );
    enrichedOpenPositions.forEach((o) => set.add(o.symbol));
    enrichedPendingOrders.forEach((o) => set.add(o.symbol));
    return ["all", ...Array.from(set).sort()];
  }, [enrichedHistory, enrichedOpenPositions, enrichedPendingOrders]);

  useEffect(() => {
    notesRef.current = notes;
  }, [notes]);

  useEffect(() => {
    writeScopedStorageValue(
      NOTE_CUSTOM_TAGS_STORAGE_KEY,
      JSON.stringify(customTagPresets.slice(0, MAX_NOTE_TAG_PRESETS)),
      activeScopeRef.current,
    );
  }, [customTagPresets]);

  const addCustomTagPreset = useCallback(() => {
    const cleanTag = sanitizeTagPreset(newTagPreset);
    if (!cleanTag) return;

    setCustomTagPresets((prev) => {
      if (prev.some((item) => item.toLowerCase() === cleanTag.toLowerCase())) {
        return prev;
      }
      return [...prev, cleanTag].slice(0, MAX_NOTE_TAG_PRESETS);
    });
    setNoteDraft((state) => ({
      ...state,
      tags: toggleTagString(state.tags, cleanTag),
    }));
    setNewTagPreset("");
  }, [newTagPreset]);

  const removeCustomTagPreset = useCallback((tag) => {
    const cleanTag = sanitizeTagPreset(tag);
    if (!cleanTag) return;
    setCustomTagPresets((prev) =>
      prev.filter((item) => item.toLowerCase() !== cleanTag.toLowerCase()),
    );
  }, []);

  const buildNoteDraft = useCallback(
    (trade, note = {}) => ({
      comment: note.comment || trade?.comment || "",
      tags: Array.isArray(note.tags) ? note.tags.join(", ") : "",
      rating: Number(note.rating || 0),
      setup: note.setup || "",
      attachments: Array.isArray(note.attachments) ? note.attachments : [],
    }),
    [],
  );

  useEffect(() => {
    if (!noteOpen) return;
    const previousOverflow = document.body.style.overflow;
    const onKey = (e) => {
      if (e.key === "Escape") closeNoteModal();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [noteOpen]);

  const openNoteModal = useCallback(
    (trade) => {
      if (!trade) return;
      const note = notesRef.current?.[trade.ticket] || {};

      if (noteContentFrameRef.current) {
        window.cancelAnimationFrame?.(noteContentFrameRef.current);
        noteContentFrameRef.current = null;
      }

      flushSync(() => {
        setNotePreview(null);
        setNoteDropActive(false);
        setNoteContentReady(false);
        setNoteFor(trade);
        setNoteDraft(buildNoteDraft(trade, note));
        setNoteOpen(true);
      });

      const openBody = () => {
        noteContentFrameRef.current = null;
        setNoteContentReady(true);
      };

      if (typeof window !== "undefined" && window.requestAnimationFrame) {
        noteContentFrameRef.current = window.requestAnimationFrame(openBody);
      } else {
        window.setTimeout(openBody, 0);
      }
    },
    [buildNoteDraft],
  );
  const closeNoteModal = useCallback(() => {
    if (noteContentFrameRef.current) {
      window.cancelAnimationFrame?.(noteContentFrameRef.current);
      noteContentFrameRef.current = null;
    }
    setNoteOpen(false);
    setNoteContentReady(false);
    setNoteFor(null);
    setNoteDropActive(false);
    setNotePreview(null);
  }, []);

  useEffect(
    () => () => {
      if (noteContentFrameRef.current) {
        window.cancelAnimationFrame?.(noteContentFrameRef.current);
        noteContentFrameRef.current = null;
      }
    },
    [],
  );

  const addNoteImages = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter((file) =>
        String(file?.type || "").startsWith("image/"),
      );
      if (!files.length) return;

      const currentCount = Array.isArray(noteDraft.attachments)
        ? noteDraft.attachments.length
        : 0;
      const capacity = Math.max(0, 6 - currentCount);
      const selected = files.slice(0, capacity);
      if (!selected.length) {
        flashMsg("Limite de 6 images atteinte.");
        return;
      }

      try {
        const processed = (
          await Promise.all(selected.map((file) => optimizeNoteImage(file)))
        ).filter(Boolean);
        if (!processed.length) return;
        setNoteDraft((prev) => ({
          ...prev,
          attachments: [
            ...(Array.isArray(prev.attachments) ? prev.attachments : []),
            ...processed,
          ].slice(0, 6),
        }));
        if (files.length > selected.length)
          flashMsg(L("page.imagesCapped"));
      } catch (error) {
        console.error(error);
        setErr("Impossible de traiter cette image.");
      }
    },
    [flashMsg, noteDraft.attachments],
  );

  const removeNoteAttachment = useCallback((attachmentId) => {
    setNoteDraft((prev) => ({
      ...prev,
      attachments: (Array.isArray(prev.attachments)
        ? prev.attachments
        : []
      ).filter((item) => item.id !== attachmentId),
    }));
  }, []);

  useEffect(() => {
    if (!noteOpen) return undefined;
    const onPaste = (event) => {
      const clipboardFiles = Array.from(event.clipboardData?.items || [])
        .filter(
          (item) =>
            item.kind === "file" &&
            String(item.type || "").startsWith("image/"),
        )
        .map((item) => item.getAsFile())
        .filter(Boolean);
      if (!clipboardFiles.length) return;
      event.preventDefault();
      addNoteImages(clipboardFiles);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [noteOpen, addNoteImages]);

  const saveNote = async () => {
    if (!noteFor) return;

    const ticket = noteFor.ticket;
    const payload = {
      ticket,
      comment: (noteDraft.comment || "").trim(),
      tags: (noteDraft.tags || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      rating: Number(noteDraft.rating) || 0,
      setup: (noteDraft.setup || "").trim(),
      attachments: (Array.isArray(noteDraft.attachments)
        ? noteDraft.attachments
        : []
      ).map((item) => ({
        id: item.id,
        name: item.name,
        type: item.type,
        size: item.size,
        width: item.width,
        height: item.height,
        url: item.url,
        data_url: item.data_url,
        created_at: item.created_at,
      })),
    };

    const next = { ...notes, [ticket]: payload };
    setNotes(next);

    const tok = (agentToken || "").trim();
    writeNotesCache(tok, next, activeScopeRef.current);

    try {
      const res = await apiAgentUpsertNote(tok, payload);
      const savedNote = res?.note;

      if (savedNote) {
        const merged = { ...next, [ticket]: savedNote };
        setNotes(merged);
        writeNotesCache(tok, merged, activeScopeRef.current);
      }

      flashMsg(L("note.savedRemote"));
    } catch (e) {
      console.error("saveNote backend error:", e);
      flashMsg(L("note.savedLocal"));
    }

    closeNoteModal();
  };

  /* ---------- Édition directe dans le tableau ----------
     La version gratuite laisse corriger une ligne au clic ; la Pro imposait de
     passer par la modale « Note ». `patchNote` écrit un champ isolé (note,
     tags, rating) en gardant le reste de la note intact. */
  const [inlineEdit, setInlineEdit] = useState({
    ticket: "",
    field: "",
    value: "",
    initial: "",
  });

  const startInlineEdit = useCallback((ticket, field, value) => {
    const v = String(value ?? "");
    // `initial` sert de témoin : la validation se fait au blur, donc un simple
    // clic pour lire une cellule déclenchait un enregistrement.
    setInlineEdit({ ticket: String(ticket), field, value: v, initial: v });
  }, []);

  const cancelInlineEdit = useCallback(() => {
    setInlineEdit({ ticket: "", field: "", value: "", initial: "" });
  }, []);

  const patchNote = useCallback(
    async (ticket, patch) => {
      if (!ticket) return;

      const current = notesRef.current?.[ticket] || {};
      const payload = {
        ticket,
        comment: String(patch.comment ?? current.comment ?? "").trim(),
        tags: Array.isArray(patch.tags)
          ? patch.tags
          : Array.isArray(current.tags)
            ? current.tags
            : [],
        rating: Number(patch.rating ?? current.rating ?? 0) || 0,
        setup: String(patch.setup ?? current.setup ?? "").trim(),
        attachments: Array.isArray(current.attachments)
          ? current.attachments
          : [],
      };

      // Optimiste : la cellule se met à jour tout de suite, le réseau suit.
      // La ref est écrite ici même — l'effet qui la synchronise ne tourne qu'au
      // rendu suivant, et deux clics rapprochés repartaient d'une note périmée.
      const next = { ...notesRef.current, [ticket]: payload };
      notesRef.current = next;
      setNotes(next);

      // En démo locale, la note reste en mémoire : pas de jeton, donc rien à
      // envoyer — et surtout rien à écrire côté serveur.
      if (isMockJournalEnabled()) return;

      const tok = (agentToken || "").trim();
      writeNotesCache(tok, next, activeScopeRef.current);

      try {
        const res = await apiAgentUpsertNote(tok, payload);
        const savedNote = res?.note;

        if (savedNote) {
          const merged = { ...notesRef.current, [ticket]: savedNote };
          notesRef.current = merged;
          setNotes(merged);
          writeNotesCache(tok, merged, activeScopeRef.current);
        }
      } catch (e) {
        console.error("patchNote backend error:", e);
        flashMsg(L("note.savedLocal"));
      }
    },
    [agentToken, flashMsg, L],
  );

  const commitInlineEdit = useCallback(() => {
    const { ticket, field, value, initial } = inlineEdit;
    if (!ticket || !field) return;

    // Rien n'a changé : pas d'écriture réseau, et surtout pas de note vide créée
    // sur une ligne dont le commentaire affiché venait du terminal MT5.
    if (String(value) === String(initial ?? "")) {
      cancelInlineEdit();
      return;
    }

    if (field === "tags") {
      patchNote(ticket, {
        tags: String(value)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      });
    } else if (field === "comment") {
      patchNote(ticket, { comment: value });
    }

    cancelInlineEdit();
  }, [inlineEdit, patchNote, cancelInlineEdit]);

  const noteModalSurfaceClass = noteModalDark
    ? "border-white/10 bg-[#07101f] text-white"
    : "border-slate-200 bg-white text-slate-900";
  const noteModalHeaderClass = noteModalDark
    ? "border-white/10 bg-[#0b1220]"
    : "border-slate-200 bg-white";
  const noteModalPanelClass = noteModalDark
    ? "border-white/10 bg-[#101827]"
    : "border-slate-200 bg-white";
  const noteModalSoftChipClass = noteModalDark
    ? "border-white/10 bg-white/7 text-slate-200"
    : "border-slate-200 bg-slate-50 text-slate-700";
  const noteModalDropZoneClass = noteModalDark
    ? "bg-[#0f172a] border-white/10"
    : "bg-slate-50 border-slate-200";
  const noteModalEmptyClass = noteModalDark
    ? "border-white/10 bg-[#0f172a] text-slate-400"
    : "border-slate-200 bg-slate-50 text-slate-500";
  const noteModalInputClass = noteModalDark
    ? "bg-[#0f172a] text-white border-white/10 placeholder:text-slate-500"
    : "bg-white text-slate-900 border-slate-200 placeholder:text-slate-400";

  /* export CSV */
  const exportCSV = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const rows = [
        [
          "RecordType",
          "CashflowType",
          "CashflowAmount",
          "Ticket",
          "Symbol",
          "Side",
          "Volume",
          "OpenTime",
          "OpenPrice",
          "SL",
          "TP",
          "CloseTime",
          "ClosePrice",
          "Profit",
          "Comment",
          "Tags",
          "Rating",
          "Setup",
          "Status",
        ],
        ...filteredHistory.map((t) => [
          t.record_type || "trade",
          t.cashflow_type || "",
          isCashflowRecord(t) ? cashflowAmountOf(t) : "",
          t.ticket,
          t.symbol,
          t.side,
          t.volume,
          fmtDT(t.open_time, i18n.language),
          t.open_price,
          t.sl ?? "",
          t.tp ?? "",
          fmtDT(t.close_time, i18n.language),
          t.close_price ?? "",
          t.profit ?? "",
          notes[t.ticket]?.comment ?? t.comment ?? "",
          (notes[t.ticket]?.tags || []).join("|"),
          notes[t.ticket]?.rating || 0,
          notes[t.ticket]?.setup || "",
          t.status || "",
        ]),
      ];

      const csv = rows
        .map((r) =>
          r.map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`).join(","),
        )
        .join("\n");

      await new Promise((resolve) => window.setTimeout(resolve, 280));

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "journal_trading.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      flashMsg(L("journal.exported"));
    } finally {
      setExporting(false);
    }
  }, [exporting, filteredHistory, notes, i18n.language, flashMsg, L]);

  /* pagination */
  const totalRows = filteredHistory.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / perPage));
  const pageData = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredHistory.slice(start, start + perPage);
  }, [filteredHistory, page, perPage]);
  useEffect(() => {
    setPage(1);
  }, [
    symbolFilter,
    sideFilter,
    resultFilter,
    dateFrom,
    dateTo,
    search,
    perPage,
    selectedServers,
    selectedAccounts,
    selectedSourceKeys,
  ]);

  /* La liste peut aussi rétrécir sans qu'aucun filtre bouge : rafraîchissement,
     changement de jeton, retour d'un scope plus court. La page courante sortait
     alors de la plage et le tableau s'affichait vide — « Affichage 251 à 40 sur
     40 » — alors que des lignes existaient. */
  useEffect(() => {
    setPage((p) => Math.min(p, pageCount));
  }, [pageCount]);

  /* Numéros de page affichés : fenêtre glissante autour de la page courante,
     avec « … » et toujours la première et la dernière. */
  const pageNumbers = useMemo(() => {
    if (pageCount <= 7)
      return Array.from({ length: pageCount }, (_, i) => i + 1);

    const out = [1];
    const from = Math.max(2, page - 1);
    const to = Math.min(pageCount - 1, page + 1);

    if (from > 2) out.push("…");
    for (let p = from; p <= to; p += 1) out.push(p);
    if (to < pageCount - 1) out.push("…");

    out.push(pageCount);
    return out;
  }, [page, pageCount]);

  const rangeFrom = totalRows === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeTo = Math.min(page * perPage, totalRows);

  const pageDataWithNotes = useMemo(
    () =>
      pageData.map((row) => {
        const note = notes[row.ticket] || {};
        return {
          row,
          note,
          rating: Number(note.rating || 0),
        };
      }),
    [pageData, notes],
  );

  const mobileHistoryCards = useMemo(
    () =>
      pageDataWithNotes.map(({ row }, i) => (
        <HistoryCard key={String(row.ticket) || i} t={row} />
      )),
    [pageDataWithNotes],
  );

  const desktopHistoryRows = useMemo(() => {
    /* Lignes jointives : le tableau était en `border-spacing:0_12px`, chaque
       ligne devenait une carte arrondie séparée par 12 px de vide. Un simple
       filet en bas suffit et la densité redevient celle d'un tableau. */
    const cellBase =
      "border-b border-slate-200/80 px-3 py-2.5 align-middle transition-colors group-hover:bg-violet-50/70 dark:border-white/10 dark:group-hover:bg-violet-500/[0.07]";
    return pageDataWithNotes.map(({ row, note: n, rating }, i) => {
      if (isCashflowRecord(row)) {
        const amount = cashflowAmountOf(row);
        const isDeposit = amount >= 0;
        return (
          <tr key={String(row.ticket) || i} className="group">
            <td className={`${cellBase} font-mono text-xs text-slate-500 dark:text-white/55`}>
              {row.ticket}
            </td>
            <td className={`${cellBase} font-semibold text-slate-700 dark:text-white/80`}>
              {L("journal.cashflow")}
            </td>
            <td className={cellBase}>
              <span
                className={cn(
                  "inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]",
                  isDeposit
                    ? "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-400/20 dark:bg-sky-500/10 dark:text-sky-300"
                    : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-300",
                )}
              >
                {isDeposit ? L("journal.deposit") : L("journal.withdrawal")}
              </span>
            </td>
            <td className={`${cellBase} text-slate-400 dark:text-white/35`}>—</td>
            <td className={`${cellBase} text-[13px] text-slate-600 dark:text-white/65`}>
              {fmtDT(historyEventTime(row), i18n.language)}
            </td>
            <td className={`${cellBase} text-slate-400 dark:text-white/35`}>—</td>
            <td className={`${cellBase} text-[13px] text-slate-600 dark:text-white/65`}>
              {fmtDT(historyEventTime(row), i18n.language)}
            </td>
            <td className={`${cellBase} text-slate-400 dark:text-white/35`}>—</td>
            <td className={cellBase}>
              <span
                className={cn(
                  "inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold tabular-nums",
                  isDeposit
                    ? "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-400/20 dark:bg-sky-500/10 dark:text-sky-300"
                    : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-300",
                )}
              >
                {amount > 0 ? "+" : ""}{amount.toFixed(2)}
              </span>
            </td>
            <td className={`${cellBase} text-slate-400 dark:text-white/35`}>—</td>
            <td className={`${cellBase} max-w-[210px] truncate text-xs text-slate-500 dark:text-white/50`}>
              {row.comment || L("journal.cashflow")}
            </td>
            <td className={cellBase} />
          </tr>
        );
      }

      const editingThis = inlineEdit.ticket === String(row.ticket);
      const isEditingTags = editingThis && inlineEdit.field === "tags";
      const isEditingComment = editingThis && inlineEdit.field === "comment";

      return (
      <tr key={String(row.ticket) || i} className="group">
        <td
          className={`${cellBase} font-mono text-xs text-slate-700 dark:text-white/80`}
        >
          {row.ticket}
        </td>
        <td
          className={`${cellBase} font-semibold text-slate-800 dark:text-white`}
        >
          {row.symbol}
        </td>
        <td className={`${cellBase}`}>
          <span
            className={cn(
              "inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]",
              row.side === "buy"
                ? "border-emerald-200/80 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                : "border-rose-200/80 bg-rose-50 text-rose-700 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-300",
            )}
          >
            {row.side?.toUpperCase?.() || row.side}
          </span>
        </td>
        <td className={`${cellBase} text-slate-700 dark:text-white/80`}>
          {row.volume}
        </td>
        <td
          className={`${cellBase} text-[13px] text-slate-600 dark:text-white/65`}
        >
          {fmtDT(row.open_time, i18n.language)}
        </td>
        <td className={`${cellBase} text-slate-700 dark:text-white/80`}>
          {row.open_price}
        </td>
        <td
          className={`${cellBase} text-[13px] text-slate-600 dark:text-white/65`}
        >
          {fmtDT(row.close_time, i18n.language)}
        </td>
        <td className={`${cellBase} text-slate-700 dark:text-white/80`}>
          {row.close_price ?? L("misc.none")}
        </td>
        <td className={`${cellBase}`}>
          <span
            className={cn(
              "inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold",
              toNumber(row.profit) >= 0
                ? "border-emerald-200/80 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                : "border-rose-200/80 bg-rose-50 text-rose-700 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-300",
            )}
          >
            {Number.isFinite(row.profit)
              ? row.profit.toFixed(2)
              : (row.profit ?? L("misc.none"))}
          </span>
        </td>
        {/* TAGS — éditable au clic, deux puces max puis un compteur : la
            colonne s'étalait sur toute la largeur restante. */}
        <td className={`${cellBase} w-[150px] max-w-[150px]`}>
          {isEditingTags ? (
            <input
              autoFocus
              value={inlineEdit.value}
              onChange={(e) =>
                setInlineEdit((prev) => ({ ...prev, value: e.target.value }))
              }
              onBlur={commitInlineEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitInlineEdit();
                if (e.key === "Escape") cancelInlineEdit();
              }}
              placeholder="tag1, tag2"
              className="w-full rounded-lg border border-violet-300 bg-white px-2 py-1 text-xs outline-none dark:border-violet-400/40 dark:bg-white/10 dark:text-white"
            />
          ) : (
            <button
              type="button"
              onClick={() =>
                startInlineEdit(row.ticket, "tags", (n.tags || []).join(", "))
              }
              title={(n.tags || []).join(", ") || L("journal.tags")}
              className="w-full rounded-lg px-1 py-0.5 text-left hover:bg-violet-500/10"
            >
              {(n.tags || []).length ? (
                <span className="flex flex-wrap items-center gap-1">
                  {n.tags.slice(0, 2).map((tag, idx) => (
                    <span
                      key={idx}
                      className="max-w-[62px] truncate rounded-full border border-violet-200/40 bg-violet-600/10 px-1.5 py-0.5 text-[11px] text-violet-700 dark:text-violet-300"
                    >
                      {tag}
                    </span>
                  ))}
                  {n.tags.length > 2 ? (
                    <span className="text-[11px] font-semibold text-violet-600 dark:text-violet-300">
                      +{n.tags.length - 2}
                    </span>
                  ) : null}
                </span>
              ) : (
                <span className="text-slate-400 dark:text-white/35">
                  {L("misc.none")}
                </span>
              )}
            </button>
          )}
        </td>

        {/* NOTE — étoiles cliquables (enregistrement immédiat) + commentaire
            éditable au clic, sur une seule ligne. */}
        <td className={`${cellBase} w-[210px] max-w-[210px]`}>
          <div className="mb-1 flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((st) => (
              <button
                key={st}
                type="button"
                title={`${st}/5`}
                onClick={() =>
                  patchNote(row.ticket, { rating: rating === st ? 0 : st })
                }
                className="rounded transition-transform hover:scale-110"
              >
                <Star
                  size={13}
                  className={
                    st <= rating
                      ? "fill-amber-400 text-amber-400"
                      : "text-slate-300 dark:text-white/15"
                  }
                />
              </button>
            ))}
          </div>

          {isEditingComment ? (
            <input
              autoFocus
              value={inlineEdit.value}
              onChange={(e) =>
                setInlineEdit((prev) => ({ ...prev, value: e.target.value }))
              }
              onBlur={commitInlineEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitInlineEdit();
                if (e.key === "Escape") cancelInlineEdit();
              }}
              placeholder={L("journal.note")}
              className="w-full rounded-lg border border-violet-300 bg-white px-2 py-1 text-xs outline-none dark:border-violet-400/40 dark:bg-white/10 dark:text-white"
            />
          ) : (
            <button
              type="button"
              onClick={() =>
                // La cellule affiche `n.comment || row.comment` : partir de la
                // seule note ouvrait un champ vide sur une ligne qui montrait le
                // commentaire du terminal, et effaçait le texte à l'écran.
                startInlineEdit(
                  row.ticket,
                  "comment",
                  n.comment || row.comment || "",
                )
              }
              title={n.comment || row.comment || ""}
              className="w-full truncate rounded-lg px-1 py-0.5 text-left text-xs text-slate-600 hover:bg-violet-500/10 dark:text-white/70"
            >
              {n.comment || row.comment || L("misc.none")}
            </button>
          )}
        </td>
        <td className={`${cellBase} whitespace-nowrap`}>
          <BtnOutline
            size="xs"
            onClick={() => openNoteModal(row)}
            className="rounded-xl border-violet-200/60 bg-violet-50/75 text-violet-700 hover:bg-violet-100 dark:border-violet-400/20 dark:bg-violet-500/10 dark:text-violet-300 dark:hover:bg-violet-500/16"
          >
            <Pencil size={14} /> {L("journal.note")}
          </BtnOutline>
        </td>
      </tr>
      );
    });
  }, [
    pageDataWithNotes,
    i18n.language,
    L,
    openNoteModal,
    inlineEdit,
    startInlineEdit,
    cancelInlineEdit,
    commitInlineEdit,
    patchNote,
  ]);

  /* ---- EXTRA CHARTS data ---- */
  /* ---- Agrégations pour les graphiques + badges ---- */
  const extraCharts = useMemo(() => {
    if (!chartsPanelOpen) {
      const days = i18n.language.startsWith("en")
        ? ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
        : ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
      return {
        byHour: new Array(24).fill(0).map((_, h) => ({ hour: h, pnl: 0 })),
        byWeekday: days.map((day) => ({ day, pnl: 0 })),
        byTag: [],
        scatter: [],
        heat: {
          avg: Array.from({ length: 7 }, () => Array(24).fill(0)),
          count: Array.from({ length: 7 }, () => Array(24).fill(0)),
          sum: Array.from({ length: 7 }, () => Array(24).fill(0)),
          min: 0,
          max: 0,
          bestCell: { d: 0, h: 0, v: 0 },
          days,
        },
        bestHour: { hour: 0, pnl: 0 },
        bestDay: { day: days[0], pnl: 0 },
        bestTag: null,
        corr: 0,
      };
    }

    const byHour = new Array(24)
      .fill(0)
      .map((_, h) => ({ hour: h, pnl: 0, trades: 0 }));
    const byWD = new Array(7).fill(0).map((_, d) => ({ d, pnl: 0 }));
    const byTagMap = new Map();
    const scatter = [];

    // Heatmap: sommes + comptes pour moyenne jour×heure
    const heatSum = Array.from({ length: 7 }, () => Array(24).fill(0));
    const heatCnt = Array.from({ length: 7 }, () => Array(24).fill(0));

    chartHistory.forEach((t) => {
      const pnl = toNumber(t.profit);
      const h = getHour(t.open_time);
      const wd = getWeekday(t.open_time);
      if (h != null) {
        byHour[h].pnl += pnl;
        byHour[h].trades += 1;
      }
      if (wd != null) byWD[wd].pnl += pnl;
      if (wd != null && h != null) {
        heatSum[wd][h] += pnl;
        heatCnt[wd][h] += 1;
      }
      const tags = notes[t.ticket]?.tags || [];
      tags.forEach((tag) => byTagMap.set(tag, (byTagMap.get(tag) || 0) + pnl));
      if (Number.isFinite(t.duration_ms)) {
        scatter.push({
          durMin: Math.max(0, +(t.duration_ms / 60000).toFixed(1)),
          profit: +pnl.toFixed(2),
          symbol: safeText(t.symbol),
          side: safeText(t.side || t.type),
          ticket: safeText(t.ticket),
        });
      }
    });

    const daysFR = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
    const daysEN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const days = i18n.language.startsWith("en") ? daysEN : daysFR;

    const byTag = Array.from(byTagMap, ([tag, pnl]) => ({
      tag,
      pnl: +pnl.toFixed(2),
    }))
      .sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl))
      .slice(0, 12);

    const byWeekday = byWD.map((x) => ({
      day: days[x.d],
      pnl: +x.pnl.toFixed(2),
    }));

    // Heatmap: moyennes + min/max + meilleure case
    const heatAvg = heatSum.map((row, i) =>
      row.map((sum, j) => (heatCnt[i][j] ? sum / heatCnt[i][j] : 0)),
    );
    let hmin = 0,
      hmax = 0,
      bestCell = { d: 0, h: 0, v: 0 };
    heatAvg.forEach((row, di) => {
      row.forEach((v, hj) => {
        if (v < hmin) hmin = v;
        if (v > hmax) hmax = v;
        if (Math.abs(v) > Math.abs(bestCell.v)) bestCell = { d: di, h: hj, v };
      });
    });

    // Le graphique des heures d'entrée affiche la moyenne par trade, pas le
    // cumul : une heure à 40 trades écrasait visuellement une heure à 2.
    byHour.forEach((row) => {
      row.avg = row.trades ? +(row.pnl / row.trades).toFixed(2) : 0;
    });

    // Best hour / day
    // Sur la moyenne, comme les barres. Le nombre de trades voyage avec pour
    // qu'une heure à un seul trade ne passe pas pour une heure de référence.
    const bestHour = byHour.reduce(
      (a, b) => (Math.abs(b.avg) > Math.abs(a.avg) ? b : a),
      { hour: 0, pnl: 0, avg: 0, trades: 0 },
    );
    const bestDay = byWeekday.reduce(
      (a, b) => (Math.abs(b.pnl) > Math.abs(a.pnl) ? b : a),
      { day: days[0], pnl: 0 },
    );
    const bestTag = byTag[0] || null;

    // Corrélation durée/profit (r de Pearson)
    let corr = 0;
    if (scatter.length > 1) {
      const n = scatter.length;
      const sx = scatter.reduce((s, p) => s + p.durMin, 0);
      const sy = scatter.reduce((s, p) => s + p.profit, 0);
      const sxx = scatter.reduce((s, p) => s + p.durMin * p.durMin, 0);
      const syy = scatter.reduce((s, p) => s + p.profit * p.profit, 0);
      const sxy = scatter.reduce((s, p) => s + p.durMin * p.profit, 0);
      const denom = Math.sqrt((n * sxx - sx * sx) * (n * syy - sy * sy));
      corr = denom ? (n * sxy - sx * sy) / denom : 0;
    }

    const renderedScatter = downsampleArray(scatter, MAX_SCATTER_POINTS);

    return {
      byHour,
      byWeekday,
      byTag,
      scatter: renderedScatter,
      heat: {
        avg: heatAvg,
        count: heatCnt,
        sum: heatSum,
        min: hmin,
        max: hmax,
        bestCell,
        days,
      },
      bestHour,
      bestDay,
      bestTag,
      corr: +corr.toFixed(2),
    };
  }, [chartsPanelOpen, chartHistory, notes, i18n.language]);

  const activeHeatCell = useMemo(() => {
    const heat = extraCharts?.heat || {};
    const fallback = heat?.bestCell || { d: 0, h: 0, v: 0 };
    const target = selectedHeatCell || fallback;
    const dayIndex = clamp(Number(target?.d ?? 0), 0, 6);
    const hourIndex = clamp(Number(target?.h ?? 0), 0, 23);
    const avg = Number(heat?.avg?.[dayIndex]?.[hourIndex] || 0);
    const trades = Number(heat?.count?.[dayIndex]?.[hourIndex] || 0);
    const total = Number(heat?.sum?.[dayIndex]?.[hourIndex] || 0);

    return {
      d: dayIndex,
      h: hourIndex,
      avg,
      trades,
      total,
      dayLabel: heat?.days?.[dayIndex] || "—",
      hourLabel: `${hourIndex}h`,
      isEmpty: !trades,
    };
  }, [extraCharts, selectedHeatCell]);

  /* ---- Période affichée dans les headers des charts ---- */
  const chartsRange = useMemo(() => {
    const times = (chartsPanelOpen ? deferredFilteredHistory : filteredHistory)
      .map((t) => tsToDate(t.close_time) || tsToDate(t.open_time))
      .filter(Boolean)
      .map((d) => d.getTime());
    if (!times.length) return { start: "", end: "" };
    const start = new Date(Math.min(...times));
    const end = new Date(Math.max(...times));
    return {
      start: fmtDate(start, i18n.language),
      end: fmtDate(end, i18n.language),
    };
  }, [chartsPanelOpen, deferredFilteredHistory, filteredHistory, i18n.language]);

  const proChartsData = useMemo(() => {
    if (!chartsPanelOpen) {
      return {
        monthly: [],
        pie: [],
        recentGrid: [],
        scoreData: [
          { metric: L("stats.radarWin"), value: 0 },
          { metric: L("stats.radarPF"), value: 0 },
          { metric: L("stats.radarExpect"), value: 0 },
          { metric: L("stats.radarRecovery"), value: 0 },
          { metric: L("stats.radarDD"), value: 0 },
          { metric: L("stats.radarStreak"), value: 0 },
        ],
        bestMonth: null,
        latestMonth: null,
      };
    }

    const closed = chartHistory.filter((t) => t.close_time != null && !isCashflowRecord(t));
    const monthlyMap = new Map();
    const dailyMap = new Map();

    closed.forEach((t) => {
      const d = tsToDate(t.close_time) || tsToDate(t.open_time);
      if (!d) return;
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const monthLabel = d.toLocaleDateString(localeOf(i18n.language), {
        month: "short",
        year: "numeric",
      });
      if (!monthlyMap.has(monthKey)) {
        monthlyMap.set(monthKey, {
          key: monthKey,
          label: monthLabel,
          net: 0,
          trades: 0,
          wins: 0,
          losses: 0,
          winSum: 0,
          lossSum: 0,
        });
      }
      const month = monthlyMap.get(monthKey);
      const profit = toNumber(t.profit);
      month.net += profit;
      month.trades += 1;
      if (profit > 0) {
        month.wins += 1;
        month.winSum += profit;
      } else if (profit < 0) {
        month.losses += 1;
        month.lossSum += Math.abs(profit);
      }

      // Cle en heure LOCALE (cf. toLocalISODate) : toISOString() renvoie la
      // date UTC, donc un trade pris apres 22h locale en UTC+2 basculait au
      // lendemain et le tracker le rangeait un jour trop loin.
      const dayKey = toLocalISODate(d);
      if (!dailyMap.has(dayKey)) {
        dailyMap.set(dayKey, {
          key: dayKey,
          pnl: 0,
          trades: 0,
          wins: 0,
          losses: 0,
          best: 0,
          worst: 0,
          date: new Date(dayKey + "T00:00:00"),
        });
      }
      const day = dailyMap.get(dayKey);
      day.pnl += profit;
      day.trades += 1;
      // Détail affiché quand on clique une case du trackeur : le seul P&L du
      // jour ne dit pas s'il vient d'un trade ou de quarante.
      if (profit > 0) day.wins += 1;
      else if (profit < 0) day.losses += 1;
      day.best = Math.max(day.best, profit);
      day.worst = Math.min(day.worst, profit);
    });

    const monthly = Array.from(monthlyMap.values())
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((item) => ({
        ...item,
        net: +item.net.toFixed(2),
        // Bilan brut du mois, séparé en deux barres : le net seul masquait
        // un mois à +10 000 obtenu avec 40 000 de gains et 30 000 de pertes.
        gains: +item.winSum.toFixed(2),
        losses: -Math.abs(+item.lossSum.toFixed(2)),
        avgWin: item.wins ? +(item.winSum / item.wins).toFixed(2) : 0,
        avgLoss: item.losses ? +(-item.lossSum / item.losses).toFixed(2) : 0,
        winRate: item.trades
          ? +((item.wins / item.trades) * 100).toFixed(1)
          : 0,
      }));

    const wins = closed.filter((t) => toNumber(t.profit) > 0).length;
    const losses = closed.filter((t) => toNumber(t.profit) < 0).length;
    const pie = [
      { name: "Gagnants", value: wins },
      { name: "Perdants", value: losses },
    ].filter((item) => item.value > 0);

    const recentDaily = [];
    const today = new Date();
    for (let i = 69; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setHours(0, 0, 0, 0);
      d.setDate(today.getDate() - i);
      // Meme cle locale que dailyMap : `d` est minuit LOCAL, dont
      // toISOString() renvoyait la veille sur tout fuseau positif. La case du
      // lundi lisait alors les trades du dimanche, et ceux du lundi
      // s'affichaient sur la case du mardi.
      const key = toLocalISODate(d);
      const day = dailyMap.get(key) || {
        pnl: 0,
        trades: 0,
        wins: 0,
        losses: 0,
        best: 0,
        worst: 0,
      };
      recentDaily.push({
        key,
        date: d,
        pnl: +toNumber(day.pnl).toFixed(2),
        trades: day.trades || 0,
        wins: day.wins || 0,
        losses: day.losses || 0,
        best: +toNumber(day.best).toFixed(2),
        worst: +toNumber(day.worst).toFixed(2),
        weekDay: (d.getDay() + 6) % 7,
      });
    }
    const recentGrid = [];
    for (let i = 0; i < recentDaily.length; i += 7) {
      recentGrid.push(recentDaily.slice(i, i + 7));
    }

    const ddAbs = Math.abs(metrics?.maxDDPct || 0);
    // `value` = score normalisé 0–100 qui dessine la surface ; `display` = la
    // statistique réelle, affichée sous le nom de l'axe. Un radar sans ses
    // chiffres oblige à deviner ce que vaut chaque branche.
    const scoreData = [
      {
        metric: L("stats.radarWin"),
        value: clamp(metrics?.winRate || 0, 0, 100),
        display: `${(metrics?.winRate || 0).toFixed(1)}%`,
      },
      {
        metric: L("stats.radarPF"),
        value: clamp((metrics?.profitFactor || 0) * 22, 0, 100),
        display: (metrics?.profitFactor || 0).toFixed(2),
      },
      {
        metric: L("stats.radarExpect"),
        value: clamp(50 + (metrics?.expectancy || 0) * 6, 0, 100),
        display: `${(metrics?.expectancy || 0) >= 0 ? "+" : ""}${(metrics?.expectancy || 0).toFixed(2)}`,
      },
      {
        metric: L("stats.radarRecovery"),
        value: clamp((metrics?.recoveryFactor || 0) * 25, 0, 100),
        display: (metrics?.recoveryFactor || 0).toFixed(2),
      },
      {
        metric: L("stats.radarDD"),
        value: clamp(100 - ddAbs * 5, 0, 100),
        display: `-${ddAbs.toFixed(1)}%`,
      },
      {
        metric: L("stats.radarStreak"),
        value: clamp(
          100 - Math.max(0, (metrics?.bestLoss || 0) - 1) * 12,
          0,
          100,
        ),
        display: `${metrics?.bestLoss || 0}L`,
      },
    ];

    const bestMonth = monthly.reduce(
      (best, item) => (item.net > (best?.net ?? -Infinity) ? item : best),
      null,
    );
    const latestMonth = monthly[monthly.length - 1] || null;

    return {
      monthly,
      pie,
      recentGrid,
      scoreData,
      bestMonth,
      latestMonth,
    };
  }, [chartsPanelOpen, chartHistory, metrics, i18n.language, L]);

  // Répartition gagnants / perdants : les parts se calculent sur les trades
  // décidés, les trades à profit nul n'appartenant à aucun des deux camps.
  const splitStats = useMemo(() => {
    const wins = Number(metrics?.wins || 0);
    const losses = Number(metrics?.losses || 0);
    const decided = wins + losses;
    return {
      wins,
      losses,
      total: Number(metrics?.total || decided),
      winPct: decided ? (wins / decided) * 100 : 0,
      lossPct: decided ? (losses / decided) * 100 : 0,
    };
  }, [metrics?.wins, metrics?.losses, metrics?.total]);

  // Moyenne des six scores normalisés du radar, affichée en pied de carte
  // maintenant que le badge porte le nombre de trades.
  const radarOverallScore = useMemo(() => {
    const rows = proChartsData.scoreData || [];
    if (!rows.length) return 0;
    return Math.round(
      rows.reduce((sum, item) => sum + (item.value || 0), 0) / rows.length,
    );
  }, [proChartsData.scoreData]);

  const histogramData = useMemo(() => {
    if (!chartsPanelOpen) return [];
    const arr = chartHistory.map((t) => toNumber(t.profit));
    if (!arr.length) return [];
    const min = Math.min(...arr);
    const max = Math.max(...arr);
    const bins = 14;
    const step = (max - min) / bins || 1;
    const bucket = new Array(bins).fill(0);
    arr.forEach((v) => {
      const idx = clamp(Math.floor((v - min) / step), 0, bins - 1);
      bucket[idx] += 1;
    });
    return bucket.map((count, i) => ({
      bin: `${(min + i * step).toFixed(0)}`,
      count,
      pnl: min + i * step,
    }));
  }, [chartsPanelOpen, chartHistory]);

  /* ---- Calendrier mensuel (états + helpers manquants) ---- */
  const today = new Date();

  // Mois (0–11) et année affichés dans le calendrier
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());

  // Données du calendrier : total P&L par jour du mois affiché
  const calData = useMemo(() => {
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const items = [];
    const dayMap = new Map();
    // Résumé du mois affiché, agrégé au passage : la bande sous la grille
    // remplaçait une simple légende de couleurs, qui n'apprenait rien.
    let trades = 0;
    let wins = 0;
    let losses = 0;
    let gains = 0;
    let lossesAmount = 0;

    filteredHistory.forEach((t) => {
      if (isCashflowRecord(t)) return;
      const d = tsToDate(t.close_time) || tsToDate(t.open_time);
      if (!d || d.getFullYear() !== calYear || d.getMonth() !== calMonth)
        return;
      const key = toLocalISODate(d);
      const bucket = dayMap.get(key) || { pnl: 0, trades: 0 };
      const profit = toNumber(t.profit);
      bucket.pnl += profit;
      bucket.trades += 1;
      dayMap.set(key, bucket);

      trades += 1;
      if (profit > 0) {
        wins += 1;
        gains += profit;
      } else if (profit < 0) {
        losses += 1;
        lossesAmount += profit;
      }
    });

    const tradedDays = dayMap.size;
    const net = gains + lossesAmount;
    // Les trades à profit nul ne comptent ni en gain ni en perte : les inclure
    // au dénominateur ferait chuter le taux sans qu'aucun trade soit perdant.
    const decided = wins + losses;
    const summary = {
      trades,
      tradedDays,
      gains: +gains.toFixed(2),
      losses: +lossesAmount.toFixed(2),
      net: +net.toFixed(2),
      winRate: decided ? (wins / decided) * 100 : 0,
      wins,
      lossCount: losses,
      avgPerTradedDay: tradedDays ? +(net / tradedDays).toFixed(2) : 0,
    };

    const intensityBase = Math.max(
      1,
      ...Array.from(dayMap.values()).map((item) => Math.abs(item.pnl || 0)),
    );

    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj = new Date(calYear, calMonth, d);
      const key = toLocalISODate(dateObj);
      const label = dateObj.toLocaleDateString(localeOf(i18n.language));
      const dayStats = dayMap.get(key) || { pnl: 0, trades: 0 };
      items.push({
        day: d,
        date: dateObj,
        iso: key,
        label,
        pnl: +toNumber(dayStats.pnl).toFixed(2),
        trades: dayStats.trades,
      });
    }
    return { items, intensityBase, summary };
  }, [calYear, calMonth, filteredHistory, i18n.language]);

  // Navigation mois précédent / suivant
  const goPrevMonth = useCallback(() => {
    setCalMonth((m) => {
      if (m === 0) {
        setCalYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const goNextMonth = useCallback(() => {
    setCalMonth((m) => {
      if (m === 11) {
        setCalYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const resetCalendarView = useCallback(() => {
    const now = new Date();
    setCalMonth(now.getMonth());
    setCalYear(now.getFullYear());
    setSelectedDayISO(null);
    setDateFrom("");
    setDateTo("");
  }, [setDateFrom, setDateTo]);

  /* ---------- Jour sélectionné dans le calendrier ---------- */
  // État dédié, volontairement séparé du filtre de dates du journal.
  // Auparavant le clic écrivait dateFrom/dateTo : le calendrier se filtrait
  // lui-même (tous les autres jours passaient à vide) et les KPI de l'en-tête
  // se réduisaient à la journée. Le clic ne fait plus que choisir le jour
  // détaillé dans le panneau de droite.
  const [selectedDayISO, setSelectedDayISO] = useState(null);
  const calendarRef = useRef(null);

  const clearSelectedDay = useCallback(() => {
    setSelectedDayISO(null);
  }, []);

  const selectDay = useCallback((iso) => {
    // Reclic sur le même jour = on déselectionne.
    setSelectedDayISO((current) => (current === iso ? null : iso));
  }, []);

  // Clic hors du calendrier (et de son panneau de détail) : retour à la normale.
  useEffect(() => {
    if (!selectedDayISO) return undefined;
    const onDown = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        clearSelectedDay();
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [selectedDayISO, clearSelectedDay]);

  // Changer de mois laisserait un détail affiché pour un jour hors de la grille.
  useEffect(() => {
    setSelectedDayISO(null);
  }, [calMonth, calYear]);

  const selectedDayStats = useMemo(() => {
    if (!selectedDayISO) return null;
    const rows = filteredHistory.filter((t) => {
      if (isCashflowRecord(t)) return false;
      const d = tsToDate(t.close_time) || tsToDate(t.open_time);
      return d ? toLocalISODate(d) === selectedDayISO : false;
    });
    const net = rows.reduce((sum, t) => sum + toNumber(t.profit), 0);
    const wins = rows.filter((t) => toNumber(t.profit) > 0).length;
    const losses = rows.filter((t) => toNumber(t.profit) < 0).length;
    const best = rows.reduce(
      (max, t) => Math.max(max, toNumber(t.profit)),
      Number.NEGATIVE_INFINITY,
    );
    const worst = rows.reduce(
      (min, t) => Math.min(min, toNumber(t.profit)),
      Number.POSITIVE_INFINITY,
    );
    return {
      rows,
      net,
      wins,
      losses,
      total: rows.length,
      winRate: rows.length ? (wins / rows.length) * 100 : 0,
      best: Number.isFinite(best) ? best : 0,
      worst: Number.isFinite(worst) ? worst : 0,
      label: new Date(`${selectedDayISO}T12:00:00`).toLocaleDateString(
        localeOf(i18n.language),
        { weekday: "long", day: "2-digit", month: "long", year: "numeric" },
      ),
    };
  }, [selectedDayISO, filteredHistory, i18n.language]);

  useEffect(() => {
    if (!openDropdown) return undefined;

    const handlePointerDown = (event) => {
      if (event.target.closest?.('[data-premium-select-root="true"]')) return;
      setOpenDropdown(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpenDropdown(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [openDropdown]);

  /* ===================== small UI atoms — FULL ANIM ===================== */

  /* Effet générique commun (fallback pour les boutons existants qui utilisent baseButton) */
  const pressFX =
    "transition-all duration-200 ease-out  active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40";

  /* Champs (inchangés mais avec focus animé) */
  const baseInput =
    "rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-[#141726] text-slate-900 dark:text-white px-2 py-2 outline-none transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-violet-500/40";
  /* Boutons “outline” existants continueront d’utiliser ça (avec pressFX) */
  const baseButton = `rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-white/10 ${pressFX}`;

  /* À ajouter aux boutons “pleins” (violet/rose/émeraude…) si tu gardes <button className="..."> */
  const solidBtnFX =
    "transition-all duration-200 ease-out  active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40";

  /* ---------- Framer Motion + Ripple pour “vraies” animations ---------- */

  /* Keyframes CSS pour l’onde (injecté une seule fois) */
  const RippleKeyframes = () => (
    <style>{`@keyframes ripple-anim{to{transform:scale(2.4);opacity:0}}`}</style>
  );

  /* Hook ripple: clique => onde circulaire */
  function useRipple() {
    const [items, setItems] = React.useState([]);
    const create = React.useCallback((e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const d = Math.max(rect.width, rect.height);
      const x = e.clientX - rect.left - d / 2;
      const y = e.clientY - rect.top - d / 2;
      const id = Math.random().toString(36).slice(2);
      setItems((s) => [
        ...s,
        <span
          key={id}
          className="absolute rounded-full opacity-30 bg-white pointer-events-none"
          style={{
            width: d,
            height: d,
            left: x,
            top: y,
            transform: "scale(0)",
            animation: "ripple-anim 700ms ease-out forwards",
          }}
        />,
      ]);
      /* Nettoyage automatique après le temps d’anim */
      setTimeout(() => setItems((s) => s.slice(1)), 700);
    }, []);
    return { ripples: items, create };
  }

  /* Variants utilitaires */
  const hoverLift = {
    y: -1,
    scale: 1.015,
    boxShadow: "0 8px 24px rgba(124,58,237,0.18)",
  };
  const tapPress = { scale: 0.97, y: 0 };

  /* Système de boutons animés (drop-in) */
  const btnBase =
    "inline-flex items-center gap-2 rounded-lg font-medium select-none relative overflow-hidden";
  const btnSizes = {
    xs: "text-[11px] px-2 py-1",
    sm: "text-xs px-2.5 py-1.5",
    md: "text-sm px-3 py-2",
    lg: "text-base px-4 py-2.5",
  };
  const btnVariants = {
    primary:
      "bg-violet-600 text-white hover:bg-violet-700 border border-violet-700/60",
    success:
      "bg-emerald-600 text-white hover:bg-emerald-700 border border-emerald-700/60",
    danger:
      "bg-rose-600 text-white hover:bg-rose-700 border border-rose-700/60",
    outline:
      "bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-white/10",
    ghost:
      "bg-transparent border border-transparent text-slate-700 dark:text-white/80 hover:bg-slate-50 dark:hover:bg-white/10",
  };

  /* Bouton animé universel */
  function MButton({
    variant = "outline",
    size = "md",
    ripple = true,
    className,
    children,
    onMouseDown,
    ...props
  }) {
    const { ripples, create } = useRipple();
    return (
      <motion.button
        {...props}
        onMouseDown={(e) => {
          onMouseDown?.(e);
          if (ripple) create(e);
        }}
        whileHover={hoverLift}
        whileTap={tapPress}
        transition={SPRING_FX}
        className={cn(btnBase, btnSizes[size], btnVariants[variant], className)}
      >
        {children}
        {/* calque ripple */}
        <span aria-hidden className="absolute inset-0">
          {ripples}
        </span>
      </motion.button>
    );
  }

  /* Raccourcis pratiques (mêmes API) */
  function BtnPrimary(props) {
    return <MButton variant="primary" {...props} />;
  }
  function BtnOutline(props) {
    return <MButton variant="outline" {...props} />;
  }
  function BtnGhost(props) {
    return <MButton variant="ghost" {...props} />;
  }

  function BtnIcon({ title, ariaLabel, className = "", children, ...props }) {
    return (
      <button
        type="button"
        title={title}
        aria-label={ariaLabel || title || "button"}
        className={cn(
          "inline-flex h-9 w-9 items-center justify-center rounded-xl border text-sm transition",
          "border-slate-200 bg-white/70 text-slate-700 hover:bg-white",
          "dark:border-white/10 dark:bg-white/10 dark:text-white/80 dark:hover:bg-white/15",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-950",
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  }

  // ✅ AJOUTE ces helpers (dans ton composant, avant le return)
  const toDate = (v) => {
    if (v == null) return null;
    if (typeof v === "number") return new Date(v < 1e12 ? v * 1000 : v);
    const d = new Date(v);
    return Number.isNaN(+d) ? null : d;
  };
  const fmtTS = (v) => {
    const d = toDate(v);
    return d ? d.toLocaleString() : "—";
  };

  const acct = status?.account ?? {};
  const accountLogin = String(
    acct.login ?? acct.account ?? acct.number ?? acct.id ?? "",
  );
  const brokerServer = String(
    acct.server ?? acct.trade_server ?? acct.broker_server ?? acct.broker ?? "",
  );
  const lastHeartbeat =
    status?.last_heartbeat ??
    status?.lastHeartbeat ??
    acct.last_heartbeat ??
    null;

  // si tu as déjà isConnected ailleurs, garde le tien.
  // sinon, dé-commente ça :
  // const isConnected = (() => {
  //   const d = toDate(lastHeartbeat);
  //   return !!d && Date.now() - d.getTime() < 15000;
  // })();

  /* ===================== Composants refactorisés ===================== */

  /* Cartes KPI (hover lift discret) */

  /* Bouton de tri -> animé + ripple */
  const SortBtn = ({ label, k }) => (
    <MButton
      size="xs"
      variant="ghost"
      title={label}
      onClick={() =>
        setSortBy((s) => ({
          key: k,
          dir: s.key === k && s.dir === "desc" ? "asc" : "desc",
        }))
      }
      className="px-2 py-1 text-slate-600 dark:text-white/70"
    >
      {label} <ArrowUpDown size={12} />
    </MButton>
  );

  function PremiumSelect({
    id,
    value,
    onChange,
    options = [],
    icon: Icon = null,
    className = "",
    triggerClassName = "",
    menuClassName = "",
    // dropUp : la liste s'ouvre vers le haut (utile en bas de page, sinon elle
    // sort du cadre et oblige à scroller).
    dropUp = false,
  }) {
    const isOpen = openDropdown === id;
    const selected =
      options.find((option) => String(option.value) === String(value)) ||
      options[0] ||
      null;

    return (
      <div
        data-premium-select-root="true"
        className={cn("relative min-w-0", className)}
        style={premiumTextStyle}
      >
        <motion.button
          type="button"
          whileTap={{ scale: 0.992 }}
          transition={SPRING_FX}
          onClick={() => setOpenDropdown((prev) => (prev === id ? null : id))}
          className={cn(
            "group flex w-full items-center gap-2 rounded-[22px] border px-3.5 py-3 text-left backdrop-blur-xl transition-all duration-200",
            "bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(246,247,252,0.92))] text-slate-900 ",
            "dark:bg-[linear-gradient(180deg,rgba(17,24,39,0.98),rgba(8,13,26,0.98))] dark:text-white dark:",
            isOpen
              ? "border-violet-300/90 dark:border-violet-400/55 ring-2 ring-violet-500/20 dark:ring-violet-400/20  dark:"
              : "border-slate-200/80 hover:border-violet-200/90 dark:border-slate-700/85 dark:hover:border-violet-400/32",
            triggerClassName,
          )}
        >
          {Icon ? (
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200/70 bg-white/88 text-slate-500  dark:border-slate-700/90 dark:bg-white/[0.05] dark:text-white/72">
              <Icon size={16} />
            </span>
          ) : null}

          <span className="min-w-0 flex-1 truncate text-[15px] font-semibold tracking-[-0.01em] text-slate-800 dark:text-white">
            {selected?.label || "—"}
          </span>

          <ChevronDown
            size={17}
            className={cn(
              "shrink-0 text-slate-400 transition-all duration-200 group-hover:text-violet-500 dark:text-white/60 dark:group-hover:text-violet-300",
              isOpen && "rotate-180 text-violet-500 dark:text-violet-300",
            )}
          />
        </motion.button>

        <AnimatePresence>
          {isOpen ? (
            <motion.div
              initial={{ opacity: 0, y: dropUp ? -10 : 10, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: dropUp ? -6 : 6, scale: 0.985 }}
              transition={SPRING_FX}
              className={cn(
                "absolute left-0 right-0 z-[140] overflow-hidden rounded-[24px] border backdrop-blur-2xl",
                dropUp ? "bottom-[calc(100%+0.6rem)]" : "top-[calc(100%+0.6rem)]",
                "border-slate-200/90 bg-[linear-gradient(180deg,rgba(255,255,255,0.985),rgba(245,247,252,0.965))] ",
                "dark:border-violet-500/16 dark:bg-[linear-gradient(180deg,rgba(7,11,22,0.995),rgba(2,6,18,0.99))] dark:",
                menuClassName,
              )}
              style={premiumTextStyle}
            >
              <div className="max-h-80 overflow-y-auto p-2.5 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-violet-500/80 [&::-webkit-scrollbar-track]:bg-transparent">
                {options.map((option) => {
                  const active = String(option.value) === String(value);
                  return (
                    <button
                      key={String(option.value)}
                      type="button"
                      onClick={() => {
                        onChange(option.value);
                        setOpenDropdown(null);
                      }}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-[18px] border px-3.5 py-3 text-left transition-all duration-150",
                        active
                          ? "border-violet-200/80 bg-[linear-gradient(135deg,rgba(139,92,246,0.14),rgba(99,102,241,0.1))] text-violet-800  dark:border-violet-400/35 dark:bg-[linear-gradient(135deg,rgba(139,92,246,0.26),rgba(79,70,229,0.18))] dark:text-white dark:"
                          : "border-transparent text-slate-700 hover:border-slate-200/90 hover:bg-slate-100/90 dark:text-slate-100/92 dark:hover:border-white/10 dark:hover:bg-white/[0.06]",
                      )}
                    >
                      <span
                        className={cn(
                          "h-3 w-3 shrink-0 rounded-full",
                          active
                            ? "bg-violet-500  dark:bg-violet-300 dark:"
                            : "bg-slate-300 dark:bg-white/18",
                        )}
                      />
                      <span className="min-w-0 flex-1 truncate text-[15px] font-medium tracking-[-0.01em]">
                        {option.label}
                      </span>
                      {active ? (
                        <Check
                          size={16}
                          className="shrink-0 text-violet-600 dark:text-violet-200"
                        />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }

  /* Carte d'historique (bouton “Note” animé) */
  function HistoryCard({ t: row }) {
    const n = notes[row.ticket] || {};
    const rating = Number(n.rating || 0);

    if (isCashflowRecord(row)) {
      const amount = cashflowAmountOf(row);
      const isDeposit = amount >= 0;
      return (
        <motion.div
          whileHover={{ y: -3 }}
          transition={SPRING_FX}
          className="rounded-[22px] border border-slate-200/80 bg-white/88 p-3.5 dark:border-white/10 dark:bg-[#0f1526]/86"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <span
                className={cn(
                  "inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]",
                  isDeposit
                    ? "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-400/20 dark:bg-sky-500/10 dark:text-sky-300"
                    : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-300",
                )}
              >
                {isDeposit ? L("journal.deposit") : L("journal.withdrawal")}
              </span>
              <div className="mt-2 text-xs text-slate-500 dark:text-white/55">
                {fmtDT(historyEventTime(row), i18n.language)}
              </div>
              {row.comment ? (
                <div className="mt-1 text-xs text-slate-600 dark:text-white/70">
                  {row.comment}
                </div>
              ) : null}
            </div>
            <div
              className={cn(
                "text-base font-semibold tabular-nums",
                isDeposit ? "text-sky-600 dark:text-sky-300" : "text-amber-600 dark:text-amber-300",
              )}
            >
              {amount > 0 ? "+" : ""}{amount.toFixed(2)}
            </div>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div
        whileHover={{ y: -4, boxShadow: "0 20px 42px rgba(15,18,32,0.14)" }}
        transition={SPRING_FX}
        className="rounded-[22px] border border-slate-200/80 dark:border-white/10 bg-white/88 dark:bg-[#0f1526]/86 p-3.5 space-y-2.5 "
      >
        <div className="flex justify-between text-sm">
          <div className="font-mono text-[11px]">{row.ticket}</div>
          <div
            className={cn(
              "font-semibold",
              toNumber(row.profit) >= 0 ? "text-emerald-600" : "text-rose-600",
            )}
          >
            {Number.isFinite(row.profit)
              ? row.profit.toFixed(2)
              : (row.profit ?? L("misc.none"))}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <div>
            <span className="text-slate-500">{L("misc.symbolShort")}</span>{" "}
            {row.symbol}
          </div>
          <div>
            <span
              className={cn(
                "inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]",
                row.side === "buy"
                  ? "border-emerald-200/80 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                  : "border-rose-200/80 bg-rose-50 text-rose-700 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-300",
              )}
            >
              {row.side?.toUpperCase?.() || row.side}
            </span>
          </div>
          <div>
            <span className="text-slate-500">{L("misc.volShort")}</span>{" "}
            {row.volume}
          </div>
          <div>
            <span className="text-slate-500">{L("misc.openShort")}</span>{" "}
            {fmtDT(row.open_time, i18n.language)}
          </div>
          <div>
            <span className="text-slate-500">{L("misc.priceShort")}</span>{" "}
            {row.open_price}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((st) => (
            <Star
              key={st}
              size={14}
              className={
                st <= rating
                  ? "fill-amber-400 text-amber-400"
                  : "text-slate-300"
              }
            />
          ))}
        </div>

        <div className="text-xs text-slate-600 dark:text-white/70 break-words line-clamp-4">
          {n.comment || row.comment || L("misc.none")}
        </div>

        {((n.tags || []).length > 0 || (n.attachments || []).length > 0) && (
          <div className="flex flex-wrap gap-1">
            {(n.tags || []).map((tag, i) => (
              <span
                key={i}
                className="px-2 py-0.5 rounded-full bg-violet-600/10 text-violet-700 dark:text-violet-300 text-[11px] border border-violet-200/40"
              >
                {tag}
              </span>
            ))}
            {(n.attachments || []).length > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-700 dark:text-sky-300 text-[11px] border border-sky-200/40">
                {(n.attachments || []).length} capture
                {(n.attachments || []).length > 1 ? "s" : ""}
              </span>
            )}
          </div>
        )}

        {(n.attachments || []).length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {(n.attachments || []).slice(0, 3).map((attachment) => {
              const previewSrc = getAttachmentPreviewSrc(attachment);
              if (!previewSrc) return null;
              return (
                <button
                  key={attachment.id || previewSrc}
                  type="button"
                  onClick={() => setNotePreview(attachment)}
                  className="group relative overflow-hidden rounded-xl border border-slate-200/70 dark:border-white/10 bg-slate-100 dark:bg-[#0d1427] aspect-[4/3]"
                  title={attachment.name || "Capture"}
                >
                  <img
                    src={previewSrc}
                    alt={attachment.name || "Capture"}
                    className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
                  />
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1 text-left text-[10px] text-white/90 truncate">
                    {attachment.name || "Capture"}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div>
          <BtnOutline size="xs" onClick={() => openNoteModal(row)}>
            <Pencil size={14} /> {L("journal.note")}
          </BtnOutline>
        </div>
      </motion.div>
    );
  }

  // Global CSS (ripple keyframes) is injected once via <RippleKeyframes /> below.

  // ---------------- Derived KPIs ----------------
  const balance = status?.account?.balance ?? status?.balance ?? null;
  const equity = status?.account?.equity ?? status?.equity ?? null;
  const margin =
    status?.account?.margin ??
    status?.margin ??
    status?.account?.margin_used ??
    status?.account?.marginUsed ??
    null;

  const positionsCount = Array.isArray(filteredOpenPositions)
    ? filteredOpenPositions.length
    : Number(
        status?.positions_count ??
          status?.positionsCount ??
          status?.positions?.length ??
          0,
      );

  const pendingCount = Array.isArray(filteredPendingOrders)
    ? filteredPendingOrders.length
    : Number(
        status?.pending_count ??
          status?.pendingCount ??
          status?.pending?.length ??
          0,
      );

  // La carte affiche le drawdown de la fenêtre récente, pas celui de toute
  // l'histoire du compte : sur un compte largement positif, montrer −50 %
  // hérité d'une chute encaissée quand le capital était quatre fois plus petit
  // décrit le passé, pas le risque courant.
  const maxDDAllTime = metrics?.maxDDPct ?? null;
  const maxDDRecentPct = metrics?.maxDDPctRecent ?? null;
  const recentDDTradeCount = Number(metrics?.recentDDTrades || 0);
  const recentDDWindowDays = Number(metrics?.recentDDDays || RECENT_DD_DAYS);
  // Sous 20 trades la fenêtre ne dit rien : on retombe sur l'historique.
  const maxDDFromAccount = metrics?.maxDDSource === "account";
  const maxDDUsesRecent =
    !maxDDFromAccount && recentDDTradeCount >= 20 && maxDDRecentPct != null;
  const maxDD = maxDDUsesRecent ? maxDDRecentPct : maxDDAllTime;

  const fmtMoney = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const fmtInt = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0";
    return String(Math.trunc(n));
  };

  const fmtPct1 = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return n.toFixed(1) + "%";
  };

  const fmtSigned = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return `${n > 0 ? "+" : ""}${n.toFixed(2)}`;
  };

  /* Sous-lignes des cartes : chaque KPI dit ce que le chiffre seul ne dit pas.
     Toujours une chaîne (« — » si la donnée manque) pour que les six cartes
     gardent la même hauteur. */
  const equityFloat =
    Number.isFinite(Number(equity)) && Number.isFinite(Number(balance))
      ? Number(equity) - Number(balance)
      : null;

  const equityFloatPct =
    equityFloat != null && Number(balance) > 0
      ? (equityFloat / Number(balance)) * 100
      : null;

  const marginUsagePct =
    Number.isFinite(Number(margin)) && Number(equity) > 0
      ? (Number(margin) / Number(equity)) * 100
      : null;

  const balanceHint = isConnected
    ? L("page.hintRealtime")
    : L("page.hintWaitingAgent");

  const equityHint =
    equityFloat == null
      ? "—"
      : `${L("page.hintFloat")} : ${fmtSigned(equityFloat)}${
          equityFloatPct == null ? "" : ` (${fmtSigned(equityFloatPct)}%)`
        }`;

  const marginHint =
    marginUsagePct == null
      ? "—"
      : `${L("page.hintUsage")} : ${marginUsagePct.toFixed(1)}%`;

  // La sous-ligne dit sur quelle période porte le chiffre, et rappelle le pire
  // creux historique quand il diffère : sans ça, deux chiffres différents
  // circulaient dans l'app sous le même nom de « Max DD ».
  // Résumé du périmètre actif, affiché dans la bande KPI.
  //
  // Ces cartes suivent les filtres, mais les contrôles vivent dans les onglets
  // Journal et Statistiques : depuis Calendrier ou Positions, les chiffres
  // pouvaient décrire un sous-ensemble sans que rien ne le signale. Un client a
  // buté exactement là-dessus.
  const activeScopeParts = useMemo(() => {
    const parts = [];
    if (symbolFilter !== "all") parts.push(symbolFilter);
    if (sideFilter !== "all")
      parts.push(sideFilter === "buy" ? "Buy" : "Sell");
    if (resultFilter !== "all") {
      parts.push(
        resultFilter === "win"
          ? L("charts.monthGains")
          : resultFilter === "loss"
            ? L("charts.monthLosses")
            : L("journal.resultBe"),
      );
    }
    const fmtScopeDate = (value) => {
      const d = value ? new Date(`${value}T12:00:00`) : null;
      return d && !Number.isNaN(d.getTime())
        ? d.toLocaleDateString(i18n.language, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : value;
    };
    if (dateFrom && dateTo)
      parts.push(`${fmtScopeDate(dateFrom)} → ${fmtScopeDate(dateTo)}`);
    else if (dateFrom)
      parts.push(`${L("statsFilters.from")} ${fmtScopeDate(dateFrom)}`);
    else if (dateTo)
      parts.push(`${L("statsFilters.to")} ${fmtScopeDate(dateTo)}`);
    if (search.trim()) parts.push(`« ${search.trim()} »`);
    return parts;
  }, [
    symbolFilter,
    sideFilter,
    resultFilter,
    dateFrom,
    dateTo,
    search,
    i18n.language,
    L,
  ]);

  const clearAllJournalFilters = useCallback(() => {
    setSymbolFilter("all");
    setSideFilter("all");
    setResultFilter("all");
    setDateFrom("");
    setDateTo("");
    setSearch("");
  }, []);

  // Le montant en devise avec le pourcentage : « −47 % » seul se confond avec
  // une performance, alors que c'est le pire creux depuis un sommet. Les deux
  // peuvent coexister — un compte à +350 % peut avoir encaissé −47 % en route.
  const maxDDAmount = maxDDUsesRecent
    ? metrics?.maxDDRecent
    : metrics?.maxDD;
  const maxDDHint = !maxDDUsesRecent
    ? `${maxDDFromAccount ? L("page.ddMt5Verified") : L("page.ddAllTime")}${
        Number.isFinite(Number(maxDDAmount))
          ? ` · ${fmtMoney(maxDDAmount)}`
          : ""
      }`
    : `${L("page.ddLastDays", { days: recentDDWindowDays })}${
        Number.isFinite(Number(maxDDAmount))
          ? ` · ${fmtMoney(maxDDAmount)}`
          : ""
      }${
        Math.abs((maxDDAllTime ?? 0) - (maxDDRecentPct ?? 0)) >= 3
          ? ` · ${L("page.ddAllTimeShort")} ${fmtPct1(maxDDAllTime)}`
          : ""
      }`;

  /* Cartes KPI : aplat teinté + barre d'accent, pas de carte blanche sur fond
     blanc. Libellés courts et tenant sur une ligne — « # En attente » passait
     sur deux lignes et décalait sa valeur par rapport aux cinq autres. */
  const summaryCards = [
    {
      label: L("page.kpiBalance"),
      info: L("page.infoBalance"),
      icon: Wallet,
      value: fmtMoney(balance),
      hint: balanceHint,
      cardTone:
        "border-emerald-200 bg-emerald-50/70 dark:border-emerald-400/20 dark:bg-emerald-500/[0.07]",
      barTone: "bg-emerald-500 dark:bg-emerald-400",
      iconWrap:
        "bg-emerald-500/12 text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-300",
      labelTone: "text-emerald-900/70 dark:text-emerald-100/70",
      valueTone: "text-emerald-700 dark:text-emerald-300",
    },
    {
      label: L("page.kpiEquity"),
      info: L("page.infoEquity"),
      icon: TrendingUp,
      value: fmtMoney(equity),
      hint: equityHint,
      cardTone:
        "border-violet-200 bg-violet-50/70 dark:border-violet-400/20 dark:bg-violet-500/[0.07]",
      barTone: "bg-violet-500 dark:bg-violet-400",
      iconWrap:
        "bg-violet-500/12 text-violet-600 dark:bg-violet-400/15 dark:text-violet-300",
      labelTone: "text-violet-900/70 dark:text-violet-100/70",
      valueTone: "text-violet-700 dark:text-violet-300",
    },
    {
      label: L("page.kpiMargin"),
      info: L("page.infoMargin"),
      icon: Layers,
      value: fmtMoney(margin),
      hint: marginHint,
      cardTone:
        "border-sky-200 bg-sky-50/70 dark:border-sky-400/20 dark:bg-sky-500/[0.07]",
      barTone: "bg-sky-500 dark:bg-sky-400",
      iconWrap: "bg-sky-500/12 text-sky-600 dark:bg-sky-400/15 dark:text-sky-300",
      labelTone: "text-sky-900/70 dark:text-sky-100/70",
      valueTone: "text-sky-700 dark:text-sky-300",
    },
    {
      label: L("page.kpiPositions"),
      info: L("page.infoPositions"),
      icon: ListChecks,
      value: fmtInt(positionsCount),
      hint: L("page.hintOpen"),
      cardTone:
        "border-amber-200 bg-amber-50/70 dark:border-amber-400/20 dark:bg-amber-500/[0.07]",
      barTone: "bg-amber-500 dark:bg-amber-400",
      iconWrap:
        "bg-amber-500/12 text-amber-600 dark:bg-amber-400/15 dark:text-amber-300",
      labelTone: "text-amber-900/70 dark:text-amber-100/70",
      valueTone: "text-amber-700 dark:text-amber-300",
    },
    {
      label: L("page.kpiPending"),
      info: L("page.infoPending"),
      icon: Clock,
      value: fmtInt(pendingCount),
      hint: L("page.hintPendingOrders"),
      cardTone:
        "border-fuchsia-200 bg-fuchsia-50/70 dark:border-fuchsia-400/20 dark:bg-fuchsia-500/[0.07]",
      barTone: "bg-fuchsia-500 dark:bg-fuchsia-400",
      iconWrap:
        "bg-fuchsia-500/12 text-fuchsia-600 dark:bg-fuchsia-400/15 dark:text-fuchsia-300",
      labelTone: "text-fuchsia-900/70 dark:text-fuchsia-100/70",
      valueTone: "text-fuchsia-700 dark:text-fuchsia-300",
    },
    {
      label: L("page.kpiMaxDD"),
      info: L("page.infoMaxDD"),
      icon: AlertTriangle,
      value: fmtPct1(maxDD),
      hint: maxDDHint,
      cardTone:
        "border-rose-200 bg-rose-50/70 dark:border-rose-400/20 dark:bg-rose-500/[0.07]",
      barTone: "bg-rose-500 dark:bg-rose-400",
      iconWrap:
        "bg-rose-500/12 text-rose-600 dark:bg-rose-400/15 dark:text-rose-300",
      labelTone: "text-rose-900/70 dark:text-rose-100/70",
      valueTone: "text-rose-700 dark:text-rose-300",
    },
  ];

  /* ========================= RENDER (glacé 2026) ========================= */
  return (
    <div className="relative min-h-[100dvh] w-full text-slate-900 dark:text-white">
      {/* Le fond « glass 2026 » (halos violet/fuchsia/émeraude) a été retiré :
          aucune autre page n'en a, le fond du shell suffit. */}

      <RippleKeyframes />

      {/* Toast flash / erreur — fixe, visible sur n'importe quel panneau
          (avant, il vivait dans l'en-tête « Aperçu » et restait invisible ailleurs). */}
      {(flash || err) && (
        <div className="fixed inset-x-0 top-[calc(3.5rem+env(safe-area-inset-top)+0.5rem)] z-[70] flex justify-center px-4 lg:top-4">
          <div
            className={cn(
              "pointer-events-auto max-w-md rounded-2xl border px-4 py-2.5 text-sm shadow-lg backdrop-blur-xl",
              err
                ? "border-rose-200/70 bg-rose-50/95 text-rose-700 dark:border-rose-400/30 dark:bg-rose-500/15 dark:text-rose-200"
                : "border-emerald-200/70 bg-emerald-50/95 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-200",
            )}
          >
            {err || flash}
          </div>
        </div>
      )}

      <div
        className="
        relative mx-auto w-full
        max-w-[1200px] xl:max-w-[1440px] 2xl:max-w-[1680px]
        px-3 sm:px-6 xl:px-10
        py-6
      "
      >
        {/* EN-TÊTE & TOKEN (« Aperçu ») — desktop : toujours visible ;
            mobile : uniquement sur l'onglet « Aperçu » (ne gêne plus les autres). */}
        {(isDesktop || activePanel === "overview") && (
        <div className="mb-5">
          {/* Carte à fond uni, comme les autres sections (plus de verre teinté). */}
          <div className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-white dark:border-white/10 dark:bg-white/[0.03]">
            <div className="relative">
              <div className="p-4 sm:p-6">
                {/* Header — desktop : titre à gauche, état du compte à droite
                    sur la même ligne, ce qui récupère la bande vide à droite de
                    la description et remonte les KPI d'une rangée entière.
                    Mobile : les chips repassent sous le titre. */}
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    {/* En-tête commun à toutes les pages (PageHeader). */}
                    <PageHeader
                      icon={BookOpenText}
                      title={L("page.title")}
                      subtitle={L("page.subtitle")}
                    />
                  </div>

                  <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs lg:justify-end">
                          <span
                            className={[
                              "inline-flex items-center gap-2 rounded-full border px-2.5 py-1",
                              isConnected
                                ? "border-emerald-200/70 bg-emerald-50/70 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-300"
                                : "border-amber-200/70 bg-amber-50/70 text-amber-800 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-200",
                            ].join(" ")}
                          >
                            {isConnected ? (
                              <CheckCircle2 size={14} />
                            ) : (
                              <AlertTriangle size={14} />
                            )}
                            {isConnected ? L("page.connected") : L("page.waiting")}
                          </span>

                          {/* Mobile: serveur */}
                          <span className="sm:hidden inline-flex w-full items-center gap-2 rounded-full border border-slate-200/70 dark:border-white/10 bg-white/70 dark:bg-white/5 px-2.5 py-1 text-slate-700 dark:text-white/70">
                            <Server size={14} className="opacity-80 shrink-0" />
                            <span className="truncate min-w-0">
                              {brokerServer ? brokerServer : `${L("page.server")}: —`}
                            </span>
                          </span>

                          {/* Mobile: compte */}
                          <span className="sm:hidden inline-flex w-full items-center gap-2 rounded-full border border-slate-200/70 dark:border-white/10 bg-white/70 dark:bg-white/5 px-2.5 py-1 text-slate-700 dark:text-white/70">
                            <Hash size={14} className="opacity-80 shrink-0" />
                            <span className="truncate min-w-0">
                              {accountLogin ? accountLogin : `${L("page.account")}: —`}
                            </span>
                          </span>

                          {/* Desktop: combiné */}
                          <span className="hidden sm:inline-flex items-center gap-2 rounded-full border border-slate-200/70 dark:border-white/10 bg-white/70 dark:bg-white/5 px-2.5 py-1 text-slate-700 dark:text-white/70">
                            <Server size={14} className="opacity-80" />
                            <span className="truncate max-w-[220px]">
                              {brokerServer || `${L("page.server")}: —`}
                            </span>
                            <span className="opacity-40">•</span>
                            <Hash size={14} className="opacity-80" />
                            <span className="truncate max-w-[160px]">
                              {accountLogin || `${L("page.account")}: —`}
                            </span>
                          </span>

                          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200/70 dark:border-white/10 bg-white/70 dark:bg-white/5 px-2.5 py-1 text-slate-600 dark:text-white/60">
                            <Globe size={14} className="opacity-80" />
                            {L("page.lastPing")}:{" "}
                            <span className="font-semibold text-slate-900 dark:text-white">
                              {fmtTS(lastHeartbeat)}
                            </span>
                          </span>

                          {/* Fuseau d'affichage des heures de trade.
                              Les horodatages viennent du pont MT5 et ne
                              coincident pas avec l'heure du terminal ; tant que
                              le pont n'expose pas le fuseau du courtier, le
                              trader cale lui-meme l'affichage — et voit lequel
                              s'applique. Un rechargement suit le changement :
                              les heures alimentent huit memos distincts, le
                              recalcul global est la seule garantie qu'aucune
                              vue ne reste sur l'ancien fuseau. */}
                          <label className="inline-flex items-center gap-2 rounded-full border border-slate-200/70 dark:border-white/10 bg-white/70 dark:bg-white/5 px-2.5 py-1 text-slate-600 dark:text-white/60">
                            <Clock size={14} className="opacity-80" />
                            <span className="hidden sm:inline">{L("charts.displayTz")}:</span>
                            <select
                              value={getDisplayOffsetMin() ?? "local"}
                              onChange={(e) => {
                                const v = e.target.value;
                                setDisplayOffsetMin(v === "local" ? null : Number(v));
                                window.location.reload();
                              }}
                              className="bg-transparent font-semibold text-slate-900 outline-none dark:text-white"
                            >
                              <option value="local">
                                {L("charts.displayTzLocal")} ({offsetLabel(browserOffsetMin())})
                              </option>
                              {OFFSET_CHOICES.map((m) => (
                                <option key={m} value={m}>
                                  {offsetLabel(m)}
                                </option>
                              ))}
                            </select>
                          </label>
                  </div>
                </div>

                {/* Périmètre actif. Visible depuis n'importe quel onglet, y
                    compris ceux qui ne portent aucun contrôle de filtre : sans
                    ça, les cartes ci-dessous décrivaient un sous-ensemble sans
                    l'annoncer. Cliquable pour tout remettre à zéro, puisque les
                    contrôles ne sont pas forcément à l'écran. */}
                {activeScopeParts.length ? (
                  <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-2xl border border-violet-200 bg-violet-50/70 px-4 py-2.5 text-[12.5px] text-violet-900 dark:border-violet-400/25 dark:bg-violet-500/10 dark:text-violet-100">
                    <Filter size={14} className="shrink-0 opacity-80" />
                    <span className="font-semibold uppercase tracking-[0.08em] text-[10px]">
                      {L("page.scopeActive")}
                    </span>
                    {activeScopeParts.map((part) => (
                      <span
                        key={part}
                        className="inline-flex items-center rounded-full border border-violet-300/60 bg-white/70 px-2 py-0.5 font-medium tabular-nums dark:border-violet-400/30 dark:bg-white/5"
                      >
                        {part}
                      </span>
                    ))}
                    <span className="opacity-80 tabular-nums">
                      {L("page.scopeCount", { count: fmtInt(metrics?.total) })}
                    </span>
                    <button
                      type="button"
                      onClick={clearAllJournalFilters}
                      className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-violet-300/70 px-2.5 py-1 font-medium transition-colors hover:bg-white/80 dark:border-violet-400/30 dark:hover:bg-white/10"
                    >
                      <RefreshCw size={12} />
                      {L("journal.reset")}
                    </button>
                  </div>
                ) : null}

                {/* Un token collé sur plusieurs terminaux fait remonter les
                    trades de tous les comptes, alors que les cartes ci-dessous
                    ne décrivent que le terminal connecté. On le dit au lieu de
                    laisser croire que tout vient du même compte. */}
                {multiAccountDetected ? (
                  <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12.5px] text-amber-900 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100">
                    <AlertTriangle size={15} className="shrink-0" />
                    <span className="font-semibold">
                      Ce token est utilisé sur {sourceOptions.length} comptes.
                    </span>
                    <span className="opacity-80">
                      Les chiffres ci-dessous ne concernent que le terminal
                      connecté ({buildSourceLabel(brokerServer, accountLogin)}).
                      Le journal est filtré sur ce compte — utilise le sélecteur
                      « Compte » pour voir les autres.
                    </span>
                  </div>
                ) : null}

                {/* KPIs */}
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-6 gap-3">
                  {summaryCards.map((it) => {
                    const Icon = it.icon;
                    return (
                      <div
                        key={it.label}
                        className={cn(
                          "relative overflow-hidden rounded-[20px] border pl-[18px] pr-4 py-3.5",
                          it.cardTone,
                        )}
                      >
                        {/* Barre d'accent pleine hauteur : identifie la carte
                            sans ombre ni dégradé. */}
                        <span
                          aria-hidden
                          className={cn(
                            "pointer-events-none absolute inset-y-0 left-0 w-[5px]",
                            it.barTone,
                          )}
                        />

                        {/* Icône alignée avec le titre, valeur pleine largeur
                            dessous : jamais indentée par l'icône. */}
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg",
                              it.iconWrap,
                            )}
                          >
                            <Icon size={15} />
                          </span>
                          <span
                            className={cn(
                              "truncate text-[11px] font-semibold uppercase tracking-[0.1em]",
                              it.labelTone,
                            )}
                          >
                            {it.label}
                          </span>
                          {/* Ces six cartes n'avaient aucune explication. Un
                              client a lu « MAX DD » comme la perte totale sur
                              le capital de départ, ce qui n'est pas ce que le
                              chiffre mesure. */}
                          {it.info ? (
                            <InfoHint
                              title={it.label}
                              text={it.info}
                              size={13}
                            />
                          ) : null}
                        </div>

                        <div
                          className={cn(
                            "mt-2 text-[26px] font-semibold leading-none tracking-tight tabular-nums",
                            it.valueTone,
                          )}
                        >
                          {it.value}
                        </div>

                        {/* Ton neutre volontaire : la sous-ligne informe, elle
                            ne doit pas concurrencer la valeur colorée. */}
                        <div
                          className="mt-1.5 truncate text-[11.5px] font-medium tabular-nums text-slate-500 dark:text-white/50"
                          title={it.hint}
                        >
                          {it.hint}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="h-px w-full bg-gradient-to-r from-violet-500/25 via-fuchsia-500/15 to-indigo-500/20" />
            </div>
          </div>
        </div>
        )}

        <div className="mb-5 sticky top-0 z-40 hidden py-2 lg:block">
          <div
            className={cn(
              "mx-auto w-full max-w-[920px] rounded-[28px] border p-2 backdrop-blur-2xl transition-colors duration-200",
              isDarkMode
                ? "border-white/10 bg-[linear-gradient(180deg,rgba(13,18,34,0.96),rgba(6,10,22,0.94))]  ring-1 ring-white/10"
                : "border-white/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(241,245,249,0.94))]  ring-1 ring-white/60",
            )}
          >
            <div className="relative grid grid-cols-5 gap-0 overflow-hidden rounded-[22px]">
              <motion.div
                aria-hidden="true"
                className={cn(
                  "absolute inset-y-0 w-1/5 rounded-[20px] ",
                  isDarkMode
                    ? "bg-[linear-gradient(135deg,#7c3aed,#8b5cf6,#a855f7)] ring-1 ring-violet-300/16"
                    : "bg-[linear-gradient(135deg,#7c3aed,#8b5cf6,#a855f7)] ring-1 ring-white/40",
                )}
                animate={{
                  x: `${(premiumCoachOpen || premiumCoachLoading
                    ? 4
                    : Math.max(0, ["trades", "calendar", "positions", "charts"].indexOf(activePanel))) * 100}%`,
                }}
                transition={{
                  type: "spring",
                  stiffness: 520,
                  damping: 38,
                  mass: 0.72,
                }}
                style={{ left: 0 }}
              />
              {[
                { key: "trades", label: L("page.tabJournal"), shortLabel: L("page.tabJournal"), kind: "panel" },
                { key: "calendar", label: L("page.tabCalendar"), shortLabel: L("page.tabCalendarShort"), kind: "panel" },
                { key: "positions", label: L("page.tabPositions"), shortLabel: L("page.tabPositions"), kind: "panel" },
                { key: "charts", label: L("page.tabStats"), shortLabel: L("page.tabStatsShort"), kind: "panel" },
                { key: "coach", label: L("page.tabCoach"), shortLabel: L("page.tabCoach"), kind: "modal" },
              ].map((tab) => {
                const isActive =
                  tab.key === "coach"
                    ? premiumCoachOpen
                    : activePanel === tab.key;
                const isCoach = tab.key === "coach";
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => {
                      if (isCoach) {
                        openPremiumCoachAudit();
                        return;
                      }
                      handlePanelSwitch(tab.key);
                    }}
                    disabled={isCoach && !canRunPremiumCoach}
                    title={
                      isCoach && !canRunPremiumCoach
                        ? L("page.noClosedTrade")
                        : undefined
                    }
                    className={cn(
                      "relative z-[1] rounded-[20px] px-2 py-3 text-[11px] font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40 sm:px-4 sm:py-3.5 sm:text-sm",
                      isCoach && !canRunPremiumCoach
                        ? isDarkMode
                          ? "cursor-not-allowed text-white/35"
                          : "cursor-not-allowed text-slate-400"
                        : isActive
                          ? "text-white"
                          : isDarkMode
                            ? "text-white/88 hover:text-white hover:bg-white/[0.05]"
                            : "text-slate-700 hover:text-slate-900 hover:bg-black/[0.04]",
                    )}
                    aria-pressed={isActive}
                  >
                    <span className="flex min-w-0 items-center justify-center gap-1.5 truncate sm:gap-2">
                      {isCoach && <Sigma size={14} className="shrink-0" />}
                      <span className="hidden truncate sm:inline">
                        {tab.label}
                        {tab.key === "charts" && isPanelPending ? "…" : ""}
                      </span>
                      <span className="truncate sm:hidden">
                        {tab.shortLabel}
                        {tab.key === "charts" && isPanelPending ? "…" : ""}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {activePanel === "charts" && (
          <>
            {/* Barre de filtres de l'onglet Statistiques.
                Elle pilote le MÊME état que les filtres du Journal
                (`symbolFilter`, `sideFilter`, `dateFrom`, `dateTo`) : tous les
                graphiques lisent déjà `filteredHistory`, la mécanique existait,
                il n'y avait simplement aucun contrôle accessible depuis cet
                onglet. Un second jeu de filtres aurait donné deux vérités
                différentes sur la même page. */}
            {/* `relative z-20` : `backdrop-blur` crée un contexte
                d'empilement, le z-index du menu déroulant reste enfermé
                dedans. Sans position ni z-index, la carte suivante (elle aussi
                floutée, donc elle aussi un contexte) est peinte par-dessus et
                tronque la liste au premier choix.
                Pas plus de 20 : la TopBar est collée en `z-30`, à égalité la
                barre de filtres la recouvrait au scroll sur mobile. */}
            <div className="relative z-20 mb-5 rounded-2xl border border-slate-200 bg-white/75 p-3 backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
              <div className="flex flex-col gap-3">
                {/* Chaque cellule est une colonne flex : les champs date
                    s'étirent sur la hauteur de la ligne, imposée par les
                    selects (icône `h-10` + `py-3`). Aucune hauteur en dur,
                    donc rien à resynchroniser si le select évolue. */}
                <div className="grid min-w-0 flex-1 grid-cols-1 items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="flex min-w-0 flex-col">
                    <label
                      htmlFor="stats-symbol-filter"
                      className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-white/45"
                    >
                      {L("statsFilters.market")}
                    </label>
                    <PremiumSelect
                      id="stats-symbol-filter"
                      icon={Filter}
                      value={symbolFilter}
                      onChange={setSymbolFilter}
                      options={allSymbols.map((s) => ({
                        value: s,
                        label: s === "all" ? L("journal.allSymbols") : s,
                      }))}
                    />
                  </div>

                  <div className="flex min-w-0 flex-col">
                    <label
                      htmlFor="stats-side-filter"
                      className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-white/45"
                    >
                      {L("statsFilters.side")}
                    </label>
                    <PremiumSelect
                      id="stats-side-filter"
                      icon={ArrowUpDown}
                      value={sideFilter}
                      onChange={setSideFilter}
                      options={[
                        { value: "all", label: L("journal.allSides") },
                        { value: "buy", label: "Buy" },
                        { value: "sell", label: "Sell" },
                      ]}
                    />
                  </div>

                  <div className="flex min-w-0 flex-col">
                    <label
                      htmlFor="stats-date-from"
                      className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-white/45"
                    >
                      {L("statsFilters.from")}
                    </label>
                    <input
                      id="stats-date-from"
                      type="date"
                      value={dateFrom}
                      // Une date de début postérieure à la fin ne renvoie
                      // jamais rien : on borne les deux champs l'un par l'autre.
                      max={dateTo || undefined}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className={`${statsDateInputClass} flex-1`}
                      style={premiumTextStyle}
                    />
                  </div>

                  <div className="flex min-w-0 flex-col">
                    <label
                      htmlFor="stats-date-to"
                      className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-white/45"
                    >
                      {L("statsFilters.to")}
                    </label>
                    <input
                      id="stats-date-to"
                      type="date"
                      value={dateTo}
                      min={dateFrom || undefined}
                      onChange={(e) => setDateTo(e.target.value)}
                      className={`${statsDateInputClass} flex-1`}
                      style={premiumTextStyle}
                    />
                  </div>
                </div>

              </div>

              {/* Raccourcis : saisir deux dates pour « les 30 derniers jours »
                  est pénible, et c'est la demande la plus courante.
                  Réinitialiser vit sur cette ligne : accolé aux champs, il
                  volait une colonne de la grille et cassait leur alignement. */}
              <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-slate-200 pt-3 dark:border-white/10">
                <span className="mr-1 text-[10px] uppercase tracking-[0.12em] text-slate-400 dark:text-white/35">
                  {L("statsFilters.quick")}
                </span>
                {STATS_QUICK_RANGES.map((range) => (
                  <button
                    key={range.days}
                    type="button"
                    onClick={() => applyStatsQuickRange(range.days)}
                    className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-violet-400 hover:text-violet-600 dark:border-white/10 dark:text-white/60 dark:hover:text-violet-300"
                  >
                    {i18n.language.startsWith("en")
                      ? range.labelEn
                      : range.labelFr}
                  </button>
                ))}

                <div className="ml-auto flex items-center gap-3">
                  {statsFiltersActive ? (
                    <span className="text-[11px] tabular-nums text-slate-500 dark:text-white/45">
                      {fmtInt(totalRows)} {L("charts.tradesCount").toLowerCase()}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setSymbolFilter("all");
                      setSideFilter("all");
                      setDateFrom("");
                      setDateTo("");
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-violet-400 hover:text-violet-600 dark:border-white/10 dark:text-white/60 dark:hover:text-violet-300"
                  >
                    <RefreshCw size={12} />
                    {L("journal.reset")}
                  </button>
                </div>
              </div>
            </div>

            {!chartsStage1Ready && (
              <ChartsPanelSkeleton
                openingLabel={L("page.statsOpening")}
                preparingLabel={L("page.statsPreparing")}
                isDarkMode={isDarkMode}
                pending={isPanelPending || chartsPanelOpen}
              />
            )}

            {/* Pas de trades : un seul état vide propre, pas ~12 cadres de
                graphes vides décalés à droite (axe Y réservé mais sans labels). */}
            {chartsStage1Ready && totalRows === 0 && (
              <div className="mb-6 flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white/70 px-6 py-16 text-center dark:border-white/10 dark:bg-white/5">
                <span className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-500/10 text-violet-500 dark:text-violet-300">
                  <LineIcon size={26} />
                </span>
                <div className="text-base font-semibold text-slate-900 dark:text-white">
                  Pas encore de statistiques
                </div>
                <p className="mt-1 max-w-sm text-sm text-slate-500 dark:text-white/60">
                  Les graphiques apparaîtront ici après tes premiers trades
                  clôturés sur la période sélectionnée.
                </p>
              </div>
            )}

            {chartsStage1Ready && totalRows > 0 && (
            <div className="mb-5 grid grid-cols-1 xl:grid-cols-[1.65fr,1fr] gap-4 items-stretch">
              <div className="grid grid-cols-1 gap-4">
                <PnLCurve
                  data={chartCurveData}
                  title={L("charts.curve")}
                  netValue={Number(metrics?.total)}
                  height={420}
                  color="#8b5cf6"
                  help={H("pnlCurve")}
                  rangeOptions={curveRangeOptions}
                  range={curveRange}
                  onRangeChange={setCurveRange}
                  containerClassName="bg-white/75 dark:bg-white/5 backdrop-blur-xl border-white/40 dark:border-white/10"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-3">
                  {[
                    {
                      label: "Profit factor",
                      info: L("infos.profitFactor"),
                      value: metrics?.profitFactor?.toFixed?.(2) ?? "—",
                      icon: Sigma,
                      iconWrap:
                        "bg-violet-500/12 text-violet-600 ring-1 ring-violet-500/20 dark:bg-violet-500/14 dark:text-violet-300 dark:ring-violet-400/20",
                      valueTone: "text-violet-700 dark:text-violet-300",
                    },
                    {
                      label: "Expectancy",
                      info: L("infos.expectancy"),
                      value: fmtSigned(metrics?.expectancy),
                      icon: TrendingUp,
                      iconWrap:
                        (metrics?.expectancy || 0) >= 0
                          ? "bg-sky-500/12 text-sky-600 ring-1 ring-sky-500/20 dark:bg-sky-500/14 dark:text-sky-300 dark:ring-sky-400/20"
                          : "bg-rose-500/12 text-rose-600 ring-1 ring-rose-500/20 dark:bg-rose-500/14 dark:text-rose-300 dark:ring-rose-400/20",
                      valueTone:
                        (metrics?.expectancy || 0) >= 0
                          ? "text-sky-700 dark:text-sky-300"
                          : "text-rose-700 dark:text-rose-300",
                    },
                    {
                      label: "Avg win",
                      info: L("infos.avgWin"),
                      value: fmtSigned(metrics?.avgWin),
                      icon: CheckCircle2,
                      iconWrap:
                        "bg-emerald-500/12 text-emerald-600 ring-1 ring-emerald-500/20 dark:bg-emerald-500/14 dark:text-emerald-300 dark:ring-emerald-400/20",
                      valueTone: "text-emerald-700 dark:text-emerald-300",
                    },
                    {
                      label: "Avg loss",
                      info: L("infos.avgLoss"),
                      value: fmtSigned(-Math.abs(metrics?.avgLoss || 0)),
                      icon: AlertTriangle,
                      iconWrap:
                        "bg-rose-500/12 text-rose-600 ring-1 ring-rose-500/20 dark:bg-rose-500/14 dark:text-rose-300 dark:ring-rose-400/20",
                      valueTone: "text-rose-700 dark:text-rose-300",
                    },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <motion.div
                        key={item.label}
                        whileHover={{
                          y: -2,
                          boxShadow: "0 18px 42px rgba(15,23,42,0.10)",
                        }}
                        transition={SPRING_FX}
                        className="group relative overflow-hidden rounded-[22px] border border-slate-200/85 dark:border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(245,247,252,0.92))] dark:bg-[linear-gradient(180deg,rgba(15,20,36,0.94),rgba(9,13,26,0.92))] backdrop-blur-xl px-3.5 py-3 "
                      >
                        <div className="pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-r from-white/40 via-violet-500/6 to-white/20 dark:from-white/[0.03] dark:via-violet-400/[0.08] dark:to-white/[0.02]" />
                        <div className="relative flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600 dark:text-white/65">
                              <span className="break-words">{item.label}</span>
                              {item.info ? (
                                <InfoHint
                                  title={item.label}
                                  text={item.info}
                                  size={13}
                                />
                              ) : null}
                            </div>
                            <div
                              className={cn(
                                "mt-3 text-[1.55rem] sm:text-[1.7rem] leading-none font-semibold tracking-[-0.03em]",
                                item.valueTone,
                              )}
                            >
                              {item.value}
                            </div>
                          </div>
                          <span
                            className={cn(
                              "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[18px] ",
                              item.iconWrap,
                            )}
                          >
                            <Icon size={16} />
                          </span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-3xl border border-white/40 dark:border-white/10 bg-white/75 dark:bg-white/5 backdrop-blur-xl p-4  flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">
                      {L("stats.performanceTitle")}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 rounded-full border border-violet-200/70 bg-violet-50/70 px-2.5 py-1 text-xs text-violet-700 dark:border-violet-400/25 dark:bg-violet-500/10 dark:text-violet-300">
                    {metrics?.net >= 0 ? "+" : ""}
                    {(metrics?.net || 0).toFixed(2)} {L("stats.netBadge")}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <StatTile
                    label={L("stats.total")}
                    value={metrics?.total}
                    info={L("infos.closed")}
                    icon={Activity}
                  />
                  <StatTile
                    label={L("stats.winrate")}
                    value={
                      Number.isFinite(+metrics?.winRate)
                        ? `${(+metrics.winRate).toFixed(1)}%`
                        : "—"
                    }
                    info={L("infos.winrate")}
                    icon={CheckCircle2}
                    iconTone="bg-emerald-500/12 text-emerald-600 dark:bg-emerald-500/14 dark:text-emerald-300"
                  />
                  <StatTile
                    label={L("stats.streakW")}
                    value={metrics?.bestWin}
                    info={L("infos.streakW")}
                    icon={TrendingUp}
                    iconTone="bg-emerald-500/12 text-emerald-600 dark:bg-emerald-500/14 dark:text-emerald-300"
                  />
                  <StatTile
                    label={L("stats.streakL")}
                    value={metrics?.bestLoss}
                    info={L("infos.streakL")}
                    icon={AlertTriangle}
                    iconTone="bg-rose-500/12 text-rose-600 dark:bg-rose-500/14 dark:text-rose-300"
                  />
                  <StatTile
                    label={L("stats.bestTrade")}
                    value={fmtSigned(metrics?.bestTrade)}
                    info={L("infos.bestTrade")}
                    icon={TrendingUp}
                    iconTone="bg-emerald-500/12 text-emerald-600 dark:bg-emerald-500/14 dark:text-emerald-300"
                    valueTone="text-emerald-600 dark:text-emerald-400"
                  />
                  <StatTile
                    label={L("stats.worstTrade")}
                    value={fmtSigned(metrics?.worstTrade)}
                    info={L("infos.worstTrade")}
                    icon={TrendingUp}
                    iconTone="bg-rose-500/12 text-rose-600 rotate-180 dark:bg-rose-500/14 dark:text-rose-300"
                    valueTone="text-rose-600 dark:text-rose-400"
                  />
                  <StatTile
                    label="Sharpe"
                    value={metrics?.sharpe?.toFixed?.(2) ?? "—"}
                    info={L("infos.sharpe")}
                    icon={Sigma}
                  />
                  <StatTile
                    label={L("stats.recovery")}
                    value={metrics?.recoveryFactor?.toFixed?.(2) ?? "—"}
                    info={L("infos.recovery")}
                    icon={Activity}
                    iconTone="bg-sky-500/12 text-sky-600 dark:bg-sky-500/14 dark:text-sky-300"
                  />
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3">
                  <div className="overflow-hidden rounded-[20px] border border-emerald-200/60 dark:border-emerald-400/20 bg-[linear-gradient(135deg,rgba(16,185,129,0.14),rgba(5,150,105,0.18))] dark:bg-[linear-gradient(135deg,rgba(16,185,129,0.14),rgba(6,78,59,0.32))] px-3 py-2.5 ">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300/85">
                          {L("stats.realizedGains")}
                        </div>
                      </div>
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-2xl border border-emerald-200/70 bg-white/60 text-emerald-700  dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-300">
                        <Wallet size={16} />
                      </span>
                    </div>
                    <div className="mt-2 flex items-end justify-between gap-3">
                      <div className="text-[1.65rem] font-semibold leading-none text-emerald-700 dark:text-emerald-300">
                        +{(metrics?.grossProfit || 0).toFixed(2)}
                      </div>
                      <div className="text-right text-[14px] font-semibold uppercase tracking-[0.12em] text-emerald-700/85 dark:text-emerald-200/80">
                        {gainsSharePct}% {L("stats.flow")}
                      </div>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-emerald-950/8 dark:bg-white/8">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-400"
                        style={{ width: `${Math.max(8, gainsSharePct)}%` }}
                      />
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-[20px] border border-rose-200/60 dark:border-rose-400/20 bg-[linear-gradient(135deg,rgba(244,63,94,0.14),rgba(190,24,93,0.16))] dark:bg-[linear-gradient(135deg,rgba(244,63,94,0.14),rgba(76,5,25,0.34))] px-3 py-2.5 ">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-rose-700 dark:text-rose-300/85">
                          {L("stats.realizedLosses")}
                        </div>
                      </div>
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-2xl border border-rose-200/70 bg-white/60 text-rose-700  dark:border-rose-400/20 dark:bg-rose-400/10 dark:text-rose-300">
                        <AlertTriangle size={16} />
                      </span>
                    </div>
                    <div className="mt-2 flex items-end justify-between gap-3">
                      <div className="text-[1.65rem] font-semibold leading-none text-rose-700 dark:text-rose-300">
                        -{(metrics?.grossLoss || 0).toFixed(2)}
                      </div>
                      <div className="text-right text-[14px] font-semibold uppercase tracking-[0.12em] text-rose-700/85 dark:text-rose-200/80">
                        {lossesSharePct}% {L("stats.flow")}
                      </div>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-rose-950/8 dark:bg-white/8">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-rose-400 via-rose-500 to-fuchsia-500"
                        style={{ width: `${Math.max(8, lossesSharePct)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {null}

            {chartsStage2Ready && totalRows > 0 ? (
              <>
                <div className="mb-6 grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <ChartCard
                    title={L("stats.scoreRadar")}
                    help={H("scoreRadar")}
                    startLabel={chartsRange.start}
                    endLabel={chartsRange.end}
                    badgeText={`${fmtInt(metrics?.total)} trades`}
                    badgeTrend="flat"
                    height={360}
                    className="bg-white/75 dark:bg-white/5 backdrop-blur-xl border-white/40 dark:border-white/10"
                  >
                    <div className="flex h-full flex-col">
                      {/* Carte pleine largeur : sans borne, l'hexagone
                          s'étirerait sur 1200 px pour rien. */}
                      <div className="mx-auto min-h-0 w-full max-w-[620px] flex-1">
                        <SafeResponsiveContainer width="100%" height="100%">
                          <RadarChart
                            data={proChartsData.scoreData}
                            outerRadius="62%"
                            margin={{ top: 8, right: 56, bottom: 8, left: 56 }}
                          >
                            <PolarGrid strokeOpacity={0.25} />
                            <PolarAngleAxis
                              dataKey="metric"
                              tick={(tickProps) => (
                                <RadarAxisTick
                                  {...tickProps}
                                  data={proChartsData.scoreData}
                                />
                              )}
                            />
                            <PolarRadiusAxis
                              domain={[0, 100]}
                              tick={false}
                              axisLine={false}
                            />
                            <Radar
                              dataKey="value"
                              stroke="#8b5cf6"
                              fill="#8b5cf6"
                              fillOpacity={0.28}
                              isAnimationActive={false}
                            />
                          </RadarChart>
                        </SafeResponsiveContainer>
                      </div>
                      {/* Le radar dessine des scores normalisés : sans cette
                          ligne, « 72 » sur un axe et « 1.28 » sur un autre
                          n'ont aucune échelle commune lisible. */}
                      <div className="flex items-center justify-between gap-3 px-4 pb-3 text-[10px] text-slate-500 dark:text-white/45">
                        <span>{L("charts.radarScale")}</span>
                        <span className="font-semibold text-slate-700 dark:text-white/70">
                          {L("charts.radarOverall")} {radarOverallScore}/100
                        </span>
                      </div>
                    </div>
                  </ChartCard>

                  <ChartCard
                    title={L("charts.tracker")}
                    help={H("tracker")}
                    startLabel={chartsRange.start}
                    endLabel={chartsRange.end}
                    badgeText={`${proChartsData.recentGrid.length} semaines`}
                    badgeTrend="flat"
                    height={330}
                    className="bg-white/75 dark:bg-white/5 backdrop-blur-xl border-white/40 dark:border-white/10"
                  >
                    {/* Tout en fractions, aucune dimension figée : les cases
                        occupaient 38 px de large dans un `inline-grid`, ce qui
                        laissait un vide à droite, et 26 px de haut, ce qui
                        laissait un vide en bas. */}
                    {(() => {
                      const weekCount = Math.max(
                        1,
                        proChartsData.recentGrid.length,
                      );
                      const weekColumns = `repeat(${weekCount}, minmax(0, 1fr))`;
                      const dayLabels = i18n.language.startsWith("en")
                        ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
                        : ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

                      return (
                        <div className="flex h-full flex-col px-4 pt-1 pb-3">
                          <div className="grid min-h-0 flex-1 grid-cols-[auto,minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto] gap-x-3">
                            {/* Colonne des jours */}
                            <div className="grid grid-rows-7 gap-1.5 pr-1 text-[11px] text-slate-500 dark:text-white/45">
                              {dayLabels.map((d) => (
                                <div key={d} className="flex items-center">
                                  {d}
                                </div>
                              ))}
                            </div>

                            {/* Grille des semaines */}
                            <div
                              className="grid min-w-0 gap-1.5"
                              style={{ gridTemplateColumns: weekColumns }}
                            >
                              {proChartsData.recentGrid.map(
                                (week, weekIndex) => (
                                  <div
                                    key={weekIndex}
                                    className="grid min-w-0 grid-rows-7 gap-1.5"
                                  >
                                    {Array.from({ length: 7 }, (_, rowIndex) => {
                                      const cell = week.find(
                                        (item) => item.weekDay === rowIndex,
                                      );
                                      const pnl = cell?.pnl ?? 0;
                                      const tone = calendarTone(
                                        pnl,
                                        cell?.trades || 0,
                                        Math.max(
                                          Math.abs(metrics?.bestTrade || 0),
                                          Math.abs(metrics?.worstTrade || 0),
                                          Math.abs(metrics?.avgWin || 0),
                                          Math.abs(metrics?.avgLoss || 0),
                                          1,
                                        ),
                                        isDarkMode,
                                      );
                                      const isPicked =
                                        cell &&
                                        trackerPick?.day?.key === cell.key;
                                      return (
                                        <button
                                          key={rowIndex}
                                          type="button"
                                          disabled={!cell}
                                          onClick={(event) =>
                                            openTrackerDay(cell, event)
                                          }
                                          title={
                                            cell
                                              ? `${fmtDate(cell.date, i18n.language)} • ${pnl >= 0 ? "+" : ""}${pnl.toFixed(2)} • ${cell.trades} trade(s)`
                                              : "—"
                                          }
                                          style={{
                                            backgroundColor: tone.bg,
                                            borderColor: tone.border,
                                          }}
                                          className={cn(
                                            "h-full min-h-[18px] w-full rounded-xl border shadow-inner transition-shadow",
                                            tone.glow,
                                            cell
                                              ? "cursor-pointer"
                                              : "cursor-default",
                                            isPicked &&
                                              "ring-2 ring-violet-500/60",
                                          )}
                                        />
                                      );
                                    })}
                                  </div>
                                ),
                              )}
                            </div>

                            {/* Cellule vide : elle cale l'axe des semaines sur
                                la largeur de la colonne des jours. */}
                            <div />

                            <div
                              className="grid min-w-0 gap-1.5 pt-1.5"
                              style={{ gridTemplateColumns: weekColumns }}
                            >
                              {proChartsData.recentGrid.map((_, weekIndex) => (
                                <div
                                  key={`w${weekIndex}`}
                                  className="truncate text-center text-[10px] text-slate-400 dark:text-white/35"
                                >
                                  S{weekIndex + 1}
                                </div>
                              ))}
                            </div>
                          </div>

                          <p className="mt-2 shrink-0 text-[10px] text-slate-400 dark:text-white/35">
                            {L("charts.trackerPick")}
                          </p>
                        </div>
                      );
                    })()}
                  </ChartCard>
                </div>

                {/* Détail du jour du trackeur : bulle ancrée à la case
                    cliquée. En `fixed`, donc hors du flux et insensible à
                    l'`overflow-hidden` de la carte. */}
                {trackerPick?.pos && typeof document !== "undefined"
                  ? createPortal(
                  <div
                    ref={trackerPopoverRef}
                    role="dialog"
                    style={{
                      left: trackerPick.pos.left,
                      top: trackerPick.pos.top,
                      bottom: trackerPick.pos.bottom,
                      width: trackerPick.pos.width || TRACKER_POPOVER_WIDTH,
                      maxWidth: "calc(100vw - 24px)",
                      maxHeight: Math.max(140, trackerPick.pos.maxHeight || 320),
                      overflowY: "auto",
                    }}
                    className="fixed z-[9999] rounded-xl border border-slate-200 bg-white p-3 shadow-xl dark:border-white/15 dark:bg-[#11162a]"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-white/45">
                          {L("charts.trackerDay")}
                        </div>
                        <div className="mt-0.5 truncate text-[12px] font-semibold text-slate-900 dark:text-white">
                          {fmtDate(trackerPick.day.date, i18n.language)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setTrackerPick(null)}
                        className="rounded-lg p-1 text-slate-400 hover:text-slate-700 dark:hover:text-white"
                        aria-label="Fermer"
                      >
                        <X size={13} />
                      </button>
                    </div>

                    <div
                      className={cn(
                        "mt-2 text-xl font-semibold tabular-nums",
                        trackerPick.day.pnl >= 0
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-rose-600 dark:text-rose-400",
                      )}
                    >
                      {trackerPick.day.pnl >= 0 ? "+" : ""}
                      {fmtMoney(trackerPick.day.pnl)}
                    </div>

                    {trackerPick.day.trades ? (
                      <dl className="mt-2 space-y-1.5 text-[11px]">
                        <div className="flex items-center justify-between gap-2">
                          <dt className="text-slate-500 dark:text-white/45">
                            {L("calendarBox.trades")}
                          </dt>
                          <dd className="font-semibold tabular-nums text-slate-800 dark:text-white/80">
                            {trackerPick.day.trades}
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <dt className="text-slate-500 dark:text-white/45">
                            {L("charts.winners")} / {L("charts.losers")}
                          </dt>
                          <dd className="font-semibold tabular-nums text-slate-800 dark:text-white/80">
                            {trackerPick.day.wins}W / {trackerPick.day.losses}L
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <dt className="text-slate-500 dark:text-white/45">
                            {L("calendarBox.bestWorst")}
                          </dt>
                          <dd className="font-semibold tabular-nums">
                            <span className="text-emerald-600 dark:text-emerald-400">
                              +{fmtMoney(trackerPick.day.best)}
                            </span>
                            <span className="text-slate-400"> / </span>
                            <span className="text-rose-600 dark:text-rose-400">
                              {fmtMoney(trackerPick.day.worst)}
                            </span>
                          </dd>
                        </div>
                      </dl>
                    ) : (
                      <p className="mt-2 text-[11px] text-slate-500 dark:text-white/45">
                        {L("charts.trackerNoTrade")}
                      </p>
                    )}
                  </div>,
                      document.body,
                    )
                  : null}

                {chartsStage3Ready ? (
                  <>
                <div className="mb-6 grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <ChartCard
                    title={L("charts.avgWinLoss")}
                    help={H("avgWinLoss")}
                    startLabel={chartsRange.start}
                    endLabel={chartsRange.end}
                    badgeText={
                      proChartsData.latestMonth
                        ? `${proChartsData.latestMonth.label} • ${proChartsData.latestMonth.trades} trades`
                        : "—"
                    }
                    badgeTrend={
                      proChartsData.latestMonth &&
                      proChartsData.latestMonth.net > 0
                        ? "up"
                        : proChartsData.latestMonth &&
                            proChartsData.latestMonth.net < 0
                          ? "down"
                          : "flat"
                    }
                    height={300}
                    className="bg-white/75 dark:bg-white/5 backdrop-blur-xl border-white/40 dark:border-white/10"
                  >
                    <SafeResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={proChartsData.monthly}
                        margin={{ top: 16, right: 18, left: 0, bottom: 4 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          strokeOpacity={0.18}
                        />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis
                          yAxisId="money"
                          width={52}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis
                          yAxisId="rate"
                          orientation="right"
                          width={44}
                          domain={[0, 100]}
                          tickFormatter={(v) => `${v}%`}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip
                          {...CHART_TOOLTIP_PROPS}
                          formatter={(value, key) => [
                            key === "winRate"
                              ? `${Number(value).toFixed(1)}%`
                              : fmtSigned(value),
                            key === "avgWin"
                              ? L("charts.monthGains")
                              : key === "avgLoss"
                                ? L("charts.monthLosses")
                                : L("stats.winrate"),
                          ]}
                        />
                        {/* Ligne du zéro : sans elle, rien ne sépare
                            visuellement les gains moyens des pertes moyennes,
                            les barres semblent flotter. */}
                        <ReferenceLine
                          yAxisId="money"
                          y={0}
                          stroke="currentColor"
                          strokeOpacity={0.45}
                        />
                        {/* Mêmes verts et rouges que « Stat mensuelles » :
                            deux graphiques voisins qui parlent des mêmes
                            gains et pertes ne peuvent pas avoir deux palettes. */}
                        <Bar
                          yAxisId="money"
                          dataKey="avgWin"
                          fill="#10b981"
                          shape={<SignedRoundedBar />}
                          maxBarSize={22}
                          isAnimationActive={false}
                        />
                        <Bar
                          yAxisId="money"
                          dataKey="avgLoss"
                          fill="#ef4444"
                          shape={<SignedRoundedBar />}
                          maxBarSize={22}
                          isAnimationActive={false}
                        />
                        <Line
                          yAxisId="rate"
                          type="monotone"
                          dataKey="winRate"
                          stroke="#8b5cf6"
                          strokeWidth={3}
                          dot={{ r: 3 }}
                          isAnimationActive={false}
                        />
                      </ComposedChart>
                    </SafeResponsiveContainer>
                  </ChartCard>

                  <ChartCard
                    title={L("charts.winLossSplit")}
                    help={H("winLossSplit")}
                    startLabel={chartsRange.start}
                    endLabel={chartsRange.end}
                    badgeText={`${fmtInt(splitStats.total)} trades • WR ${splitStats.winPct.toFixed(1)}%`}
                    badgeTrend={
                      metrics?.wins > metrics?.losses
                        ? "up"
                        : metrics?.wins < metrics?.losses
                          ? "down"
                          : "flat"
                    }
                    height={300}
                    className="bg-white/75 dark:bg-white/5 backdrop-blur-xl border-white/40 dark:border-white/10"
                  >
                    {/* Le donut seul ne donnait qu'un pourcentage. Les deux
                        colonnes portent la part et le nombre de chaque camp. */}
                    <div className="grid h-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-3 pb-3">
                      <div className="min-w-0 text-center">
                        <div className="text-xl font-semibold tabular-nums text-rose-600 dark:text-rose-400">
                          {splitStats.lossPct.toFixed(1)}%
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-white/45">
                          {L("charts.losers")}
                        </div>
                        <div className="mt-0.5 text-[11px] font-semibold tabular-nums text-rose-600/80 dark:text-rose-400/80">
                          {fmtInt(splitStats.losses)} trades
                        </div>
                      </div>

                      <div className="relative h-full w-[190px] sm:w-[220px]">
                        <SafeResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={proChartsData.pie}
                              dataKey="value"
                              nameKey="name"
                              innerRadius="62%"
                              outerRadius="92%"
                              paddingAngle={3}
                              isAnimationActive={false}
                            >
                              {proChartsData.pie.map((entry, index) => (
                                <Cell
                                  key={entry.name}
                                  fill={index === 0 ? "#10b981" : "#ef4444"}
                                />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </SafeResponsiveContainer>
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 dark:text-white/45">
                              Win rate
                            </div>
                            <div className="mt-0.5 text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">
                              {splitStats.winPct.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="min-w-0 text-center">
                        <div className="text-xl font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                          {splitStats.winPct.toFixed(1)}%
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-white/45">
                          {L("charts.winners")}
                        </div>
                        <div className="mt-0.5 text-[11px] font-semibold tabular-nums text-emerald-600/80 dark:text-emerald-400/80">
                          {fmtInt(splitStats.wins)} trades
                        </div>
                      </div>
                    </div>
                  </ChartCard>

                  <ChartCard
                    title={L("charts.monthlyStats")}
                    help={H("monthlyStats")}
                    startLabel={chartsRange.start}
                    endLabel={chartsRange.end}
                    badgeText={
                      proChartsData.bestMonth
                        ? `${proChartsData.bestMonth.label} • ${proChartsData.bestMonth.net >= 0 ? "+" : ""}${proChartsData.bestMonth.net.toFixed(2)}`
                        : "—"
                    }
                    badgeTrend={
                      proChartsData.bestMonth && proChartsData.bestMonth.net > 0
                        ? "up"
                        : proChartsData.bestMonth &&
                            proChartsData.bestMonth.net < 0
                          ? "down"
                          : "flat"
                    }
                    height={280}
                    className="bg-white/75 dark:bg-white/5 backdrop-blur-xl border-white/40 dark:border-white/10"
                  >
                    <SafeResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={proChartsData.monthly}
                        margin={{ top: 16, right: 16, left: 0, bottom: 8 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          strokeOpacity={0.18}
                        />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis
                          yAxisId="left"
                          width={52}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          width={42}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip
                          {...CHART_TOOLTIP_PROPS}
                          formatter={(value, key) => [
                            fmtSigned(value),
                            key === "gains"
                              ? L("charts.monthGains")
                              : key === "losses"
                                ? L("charts.monthLosses")
                                : key === "net"
                                  ? L("charts.netLabel")
                                  : L("calendarBox.trades"),
                          ]}
                        />
                        <ReferenceLine
                          yAxisId="left"
                          y={0}
                          stroke="currentColor"
                          strokeOpacity={0.25}
                        />
                        {/* Deux barres : total des gains au-dessus de zéro,
                            total des pertes en dessous. */}
                        <Bar
                          yAxisId="left"
                          dataKey="gains"
                          fill="#10b981"
                          shape={<SignedRoundedBar />}
                          maxBarSize={22}
                          isAnimationActive={false}
                        >
                          <LabelList
                            dataKey="gains"
                            position="top"
                            className="fill-emerald-600 dark:fill-emerald-400"
                            fontSize={10}
                            formatter={(v) => fmtCompactMoney(v)}
                          />
                        </Bar>
                        <Bar
                          yAxisId="left"
                          dataKey="losses"
                          fill="#ef4444"
                          shape={<SignedRoundedBar />}
                          maxBarSize={22}
                          isAnimationActive={false}
                        >
                          <LabelList
                            dataKey="losses"
                            position="bottom"
                            className="fill-rose-600 dark:fill-rose-400"
                            fontSize={10}
                            formatter={(v) => fmtCompactMoney(v)}
                          />
                        </Bar>
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="trades"
                          stroke="#06b6d4"
                          strokeWidth={3}
                          dot={{ r: 3 }}
                          isAnimationActive={false}
                        />
                      </ComposedChart>
                    </SafeResponsiveContainer>
                  </ChartCard>

                  <ChartCard
                    title={L("charts.pnlDist")}
                    help={H("resultDistribution")}
                    startLabel={chartsRange.start}
                    endLabel={chartsRange.end}
                    badgeText={`${metrics.total || 0} trades`}
                    badgeTrend={
                      metrics.net > 0 ? "up" : metrics.net < 0 ? "down" : "flat"
                    }
                    height={280}
                    className="bg-white/75 dark:bg-white/5 backdrop-blur-xl border-white/40 dark:border-white/10"
                  >
                    <SafeResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={histogramData}
                        margin={{ top: 22, right: 12, left: 0, bottom: 4 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          strokeOpacity={0.18}
                        />
                        <XAxis dataKey="bin" tick={{ fontSize: 11 }} />
                        <YAxis width={44} tick={{ fontSize: 11 }} />
                        <Tooltip
                          {...CHART_TOOLTIP_PROPS}
                          formatter={(value) => [
                            `${value} trades`,
                            L("charts.tradesCount"),
                          ]}
                        />
                        <Bar
                          dataKey="count"
                          radius={[8, 8, 0, 0]}
                          isAnimationActive={false}
                        >
                          {histogramData.map((d, i) => (
                            <Cell
                              key={i}
                              fill={d.pnl >= 0 ? "#10b981" : "#ef4444"}
                            />
                          ))}
                          {/* Le nombre de trades est la seule information de
                              cette barre : la cacher derrière un survol
                              obligeait à pointer chaque colonne. */}
                          <LabelList
                            dataKey="count"
                            position="top"
                            fontSize={10}
                            className="fill-slate-600 dark:fill-white/70"
                            formatter={(v) => (v ? v : "")}
                          />
                        </Bar>
                      </BarChart>
                    </SafeResponsiveContainer>
                  </ChartCard>
                  <ChartCard
                    title={L("charts.heatmap")}
                    help={H("pnlDayHour")}
                    startLabel={chartsRange.start}
                    endLabel={chartsRange.end}
                    // Tout le détail de la case dans le bandeau du haut : le
                    // pop-up posé sur la grille reste petit et se lit mal.
                    badgeText={
                      activeHeatCell
                        ? activeHeatCell.isEmpty
                          ? `${activeHeatCell.dayLabel} ${activeHeatCell.hourLabel} • ${L("charts.trackerNoTrade")}`
                          : `${activeHeatCell.dayLabel} ${activeHeatCell.hourLabel} • ${L("charts.avgShort")} ${fmtSigned(activeHeatCell.avg)} • ${activeHeatCell.trades} trades • ${L("charts.netLabel")} ${fmtSigned(activeHeatCell.total)}`
                        : ""
                    }
                    badgeTrend={
                      activeHeatCell && activeHeatCell.avg > 0
                        ? "up"
                        : activeHeatCell && activeHeatCell.avg < 0
                          ? "down"
                          : "flat"
                    }
                    height={340}
                    className="bg-white/80 dark:bg-white/[0.07] backdrop-blur-xl border-white/50 dark:border-white/10"
                  >
                    <div className="h-full px-4 pb-3 pt-3">
                      <div className="grid grid-cols-[auto_repeat(24,minmax(12px,1fr))] gap-1.5 text-[10px] sm:text-[11px]">
                        {extraCharts.heat.avg.map((row, i) => (
                          <React.Fragment key={i}>
                            <div className="flex items-center justify-end pr-2 text-right font-medium text-slate-600 dark:text-white/60">
                              {extraCharts.heat.days[i]}
                            </div>
                            {row.map((v, j) => {
                              const isSelected =
                                selectedHeatCell?.d === i &&
                                selectedHeatCell?.h === j;
                              const tradeCount =
                                extraCharts.heat.count?.[i]?.[j] || 0;
                              const showBelow = i <= 1;
                              /* La bulle fait 150px de large pour une case de
                                 quelques pixels : centrée, elle dépasse à
                                 gauche sur les premières heures et à droite sur
                                 les dernières. On l'accroche au bord côté
                                 intérieur plutôt que de la centrer. */
                              const anchorX =
                                j <= 2 ? "start" : j >= row.length - 3 ? "end" : "center";
                              return (
                                <div
                                  key={j}
                                  className="relative flex items-center justify-center"
                                >
                                  <button
                                    type="button"
                                    title={`${extraCharts.heat.days[i]} ${j}h • moyenne ${v.toFixed(2)} • ${tradeCount} trade${tradeCount > 1 ? "s" : ""}`}
                                    onClick={() =>
                                      setSelectedHeatCell((prev) =>
                                        prev && prev.d === i && prev.h === j
                                          ? null
                                          : { d: i, h: j },
                                      )
                                    }
                                    className={cn(
                                      "h-7 w-full rounded-lg border transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/45",
                                      isSelected
                                        ? "scale-[1.04] border-slate-900/20 ring-2 ring-violet-500/35 dark:border-white/20"
                                        : "border-white/60 dark:border-white/5 hover:scale-[1.03]  dark:",
                                    )}
                                    style={{
                                      backgroundColor: heatColor(
                                        v,
                                        extraCharts.heat.min,
                                        extraCharts.heat.max,
                                        tradeCount > 0,
                                      ),
                                      opacity: tradeCount ? 1 : 0.35,
                                    }}
                                  />

                                  {isSelected ? (
                                    <div
                                      className={cn(
                                        // Fond opaque, pas de translucide : la
                                        // case colorée dessous rendait le texte
                                        // illisible (et `bg-white/96` n'existe
                                        // pas dans l'échelle Tailwind, la classe
                                        // était purement et simplement ignorée).
                                        "absolute z-20 min-w-[150px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-left shadow-xl dark:border-white/15 dark:bg-[#0d1527]",
                                        anchorX === "start"
                                          ? "left-0"
                                          : anchorX === "end"
                                            ? "right-0"
                                            : "left-1/2 -translate-x-1/2",
                                        showBelow
                                          ? "top-[calc(100%+8px)]"
                                          : "bottom-[calc(100%+8px)]",
                                      )}
                                    >
                                      <div className="text-[11px] font-semibold text-slate-900 dark:text-white">
                                        {extraCharts.heat.days[i]} • {j}h
                                      </div>
                                      <div
                                        className={cn(
                                          "mt-1 text-[11px] font-medium",
                                          v > 0
                                            ? "text-emerald-600 dark:text-emerald-300"
                                            : v < 0
                                              ? "text-rose-600 dark:text-rose-300"
                                              : "text-slate-600 dark:text-white/70",
                                        )}
                                      >
                                        Moyenne: {v.toFixed(2)}
                                      </div>
                                      <div className="mt-0.5 text-[10px] text-slate-500 dark:text-white/55">
                                        {tradeCount} trade
                                        {tradeCount > 1 ? "s" : ""} • Total{" "}
                                        {(
                                          extraCharts.heat.sum?.[i]?.[j] || 0
                                        ).toFixed(2)}
                                      </div>
                                      <div
                                        className={cn(
                                          "absolute left-1/2 h-2.5 w-2.5 -translate-x-1/2 rotate-45 border-l border-t border-slate-200 bg-white dark:border-white/15 dark:bg-[#0d1527]",
                                          showBelow
                                            ? "-top-[6px]"
                                            : "-bottom-[6px] rotate-[225deg]",
                                        )}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </div>

                      <div className="mt-2 grid grid-cols-[auto_repeat(24,minmax(12px,1fr))] gap-1.5 text-[10px] sm:text-[11px] text-slate-500 dark:text-white/50">
                        <div className="pr-2 text-right font-medium">Heure</div>
                        {Array.from({ length: 24 }, (_, h) => (
                          <div key={h} className="text-center">
                            {h}
                          </div>
                        ))}
                      </div>

                      <div className="mt-2 text-xs text-slate-500 dark:text-white/55">
                        {L("charts.heatLegend")}
                      </div>
                    </div>
                  </ChartCard>

                  <ChartCard
                    title={L("charts.durVsProfit")}
                    help={H("durationVsPnl")}
                    startLabel={chartsRange.start}
                    endLabel={chartsRange.end}
                    badgeText={`r = ${extraCharts.corr.toFixed(2)}`}
                    badgeTrend={
                      extraCharts.corr > 0
                        ? "up"
                        : extraCharts.corr < 0
                          ? "down"
                          : "flat"
                    }
                    height={340}
                    className="bg-white/80 dark:bg-white/[0.07] backdrop-blur-xl border-white/50 dark:border-white/10"
                  >
                    <div className="h-full px-2 pb-2 pt-1 sm:px-3">
                      <SafeResponsiveContainer width="100%" height="100%">
                        <ScatterChart
                          margin={{ top: 18, right: 18, bottom: 30, left: 6 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 4"
                            strokeOpacity={0.14}
                          />
                          <ReferenceLine
                            y={0}
                            stroke="currentColor"
                            strokeOpacity={0.18}
                            strokeDasharray="4 4"
                          />
                          <XAxis
                            type="number"
                            dataKey="durMin"
                            name={L("page.duration")}
                            domain={[0, "auto"]}
                            allowDecimals={false}
                            minTickGap={18}
                            tickMargin={10}
                            tick={{
                              fontSize: 11,
                              fill: "currentColor",
                              opacity: 0.75,
                            }}
                            tickFormatter={formatDurationTick}
                          >
                            <Label
                              value={L("charts.axisDuration")}
                              position="insideBottom"
                              offset={-14}
                              fill="currentColor"
                              fontSize={11}
                              opacity={0.7}
                            />
                          </XAxis>
                          <YAxis
                            type="number"
                            dataKey="profit"
                            name="Profit"
                            width={74}
                            tickMargin={8}
                            tick={{
                              fontSize: 11,
                              fill: "currentColor",
                              opacity: 0.75,
                            }}
                            tickFormatter={formatCompactAxisNumber}
                          >
                            <Label
                              value={L("charts.axisPerf")}
                              angle={-90}
                              position="insideLeft"
                              offset={14}
                              style={{ textAnchor: "middle" }}
                              fill="currentColor"
                              fontSize={11}
                              opacity={0.7}
                            />
                          </YAxis>
                          <Tooltip
                            cursor={{
                              strokeDasharray: "3 3",
                              strokeOpacity: 0.22,
                            }}
                            content={
                              <DurationPerformanceTooltip
                                isDarkMode={isDarkMode}
                              />
                            }
                            // Le tooltip reste dans la zone du graphique :
                            // Recharts le bascule automatiquement de l'autre
                            // côté du point quand on atteint un bord.
                            allowEscapeViewBox={{ x: false, y: false }}
                            offset={12}
                            isAnimationActive={false}
                            wrapperStyle={{
                              zIndex: 70,
                              outline: "none",
                              pointerEvents: "none",
                              maxWidth: "calc(100% - 8px)",
                            }}
                          />
                          <Scatter
                            data={extraCharts.scatter}
                            fill="#8b5cf6"
                            fillOpacity={0.78}
                            isAnimationActive={false}
                          />
                        </ScatterChart>
                      </SafeResponsiveContainer>
                    </div>
                  </ChartCard>

                  <ChartCard
                    title={L("charts.hourPnl")}
                    help={H("entryHour")}
                    startLabel={chartsRange.start}
                    endLabel={chartsRange.end}
                    badgeText={
                      extraCharts.bestHour
                        ? `${extraCharts.bestHour.hour}h • ${L("charts.avgShort")} ${fmtSigned(extraCharts.bestHour.avg)} • ${extraCharts.bestHour.trades || 0} trades`
                        : ""
                    }
                    badgeTrend={
                      extraCharts.bestHour && extraCharts.bestHour.avg > 0
                        ? "up"
                        : extraCharts.bestHour && extraCharts.bestHour.avg < 0
                          ? "down"
                          : "flat"
                    }
                    height={290}
                    className="bg-white/80 dark:bg-white/[0.07] backdrop-blur-xl border-white/50 dark:border-white/10"
                  >
                    <SafeResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={extraCharts.byHour}
                        margin={{ top: 14, right: 12, bottom: 26, left: 8 }}
                        barCategoryGap="18%"
                      >
                        <CartesianGrid
                          strokeDasharray="3 4"
                          strokeOpacity={0.14}
                        />
                        <ReferenceLine
                          y={0}
                          stroke="currentColor"
                          strokeOpacity={0.18}
                        />
                        <XAxis
                          dataKey="hour"
                          tick={{ fontSize: 11 }}
                          tickMargin={8}
                          interval={0}
                        >
                          {/* Les heures viennent de `open_time` lue dans le
                              fuseau du navigateur : on le nomme, sinon un
                              trader sur un autre fuseau lit de travers. */}
                          <Label
                            value={`${L("charts.axisEntryHour")} (${localTimeZoneLabel})`}
                            position="insideBottom"
                            offset={-8}
                            fill="currentColor"
                            fontSize={11}
                            opacity={0.7}
                          />
                        </XAxis>
                        <YAxis
                          width={72}
                          tick={{ fontSize: 11 }}
                          tickFormatter={formatCompactAxisNumber}
                        >
                          {/* `offset` : sans lui, `insideLeft` colle le
                              libellé sur l'origine de la boîte d'axe, donc au
                              ras du bord de la carte. */}
                          <Label
                            value={L("charts.axisAvgPnl")}
                            angle={-90}
                            position="insideLeft"
                            offset={14}
                            style={{ textAnchor: "middle" }}
                            fill="currentColor"
                            fontSize={11}
                            opacity={0.7}
                          />
                        </YAxis>
                        <Tooltip
                          {...CHART_TOOLTIP_PROPS}
                          labelFormatter={(h) => `${h}h`}
                          formatter={(value, key, entry) => {
                            const row = entry?.payload || {};
                            return [
                              `${fmtSigned(value)}  •  ${row.trades || 0} trades  •  ${L("charts.cumShort")} ${fmtSigned(row.pnl)}`,
                              L("charts.axisAvgPnl"),
                            ];
                          }}
                        />
                        <Bar
                          dataKey="avg"
                          shape={<SignedRoundedBar />}
                          maxBarSize={22}
                          isAnimationActive={false}
                        >
                          {extraCharts.byHour.map((d, i) => (
                            <Cell
                              key={i}
                              fill={d.avg >= 0 ? "#10b981" : "#ef4444"}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </SafeResponsiveContainer>
                  </ChartCard>

                  <ChartCard
                    title={L("charts.dayPnl")}
                    help={H("weekdayPerf")}
                    startLabel={chartsRange.start}
                    endLabel={chartsRange.end}
                    badgeText={
                      extraCharts.bestDay
                        ? `${extraCharts.bestDay.day} • ${extraCharts.bestDay.pnl.toFixed(2)}`
                        : ""
                    }
                    badgeTrend={
                      extraCharts.bestDay && extraCharts.bestDay.pnl > 0
                        ? "up"
                        : extraCharts.bestDay && extraCharts.bestDay.pnl < 0
                          ? "down"
                          : "flat"
                    }
                    height={250}
                    className="bg-white/80 dark:bg-white/[0.07] backdrop-blur-xl border-white/50 dark:border-white/10"
                  >
                    <SafeResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={extraCharts.byWeekday}
                        layout="vertical"
                        margin={{ top: 12, right: 12, bottom: 8, left: 0 }}
                        barCategoryGap="22%"
                      >
                        <CartesianGrid
                          strokeDasharray="3 4"
                          strokeOpacity={0.14}
                        />
                        <ReferenceLine
                          x={0}
                          stroke="currentColor"
                          strokeOpacity={0.18}
                        />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11 }}
                          tickFormatter={formatCompactAxisNumber}
                        />
                        <YAxis
                          type="category"
                          dataKey="day"
                          width={44}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip
                          formatter={(v) => [Number(v).toFixed(2), "P&L"]}
                        />
                        <Bar
                          dataKey="pnl"
                          radius={[0, 8, 8, 0]}
                          maxBarSize={24}
                          isAnimationActive={false}
                        >
                          {extraCharts.byWeekday.map((d, i) => (
                            <Cell
                              key={i}
                              fill={d.pnl >= 0 ? "#10b981" : "#ef4444"}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </SafeResponsiveContainer>
                  </ChartCard>

                  <ChartCard
                    title={L("charts.tagPnl")}
                    help={H("tagPerf")}
                    startLabel={chartsRange.start}
                    endLabel={chartsRange.end}
                    badgeText={
                      extraCharts.bestTag
                        ? `${extraCharts.bestTag.tag} • ${extraCharts.bestTag.pnl.toFixed(2)}`
                        : extraCharts.byTag.length
                          ? ""
                          : "—"
                    }
                    badgeTrend={
                      extraCharts.bestTag && extraCharts.bestTag.pnl > 0
                        ? "up"
                        : extraCharts.bestTag && extraCharts.bestTag.pnl < 0
                          ? "down"
                          : "flat"
                    }
                    height={250}
                    className="bg-white/80 dark:bg-white/[0.07] backdrop-blur-xl border-white/50 dark:border-white/10"
                  >
                    <SafeResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={extraCharts.byTag}
                        layout="vertical"
                        margin={{ top: 12, right: 12, bottom: 8, left: 0 }}
                        barCategoryGap="24%"
                      >
                        <CartesianGrid
                          strokeDasharray="3 4"
                          strokeOpacity={0.14}
                        />
                        <ReferenceLine
                          x={0}
                          stroke="currentColor"
                          strokeOpacity={0.18}
                        />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11 }}
                          tickFormatter={formatCompactAxisNumber}
                        />
                        <YAxis
                          type="category"
                          dataKey="tag"
                          width={60}
                          tick={{ fontSize: 11 }}
                          tickFormatter={(value) => truncateAxisLabel(value, 9)}
                        />
                        <Tooltip
                          formatter={(v) => [Number(v).toFixed(2), "P&L"]}
                          labelFormatter={(label) => `Tag: ${label}`}
                        />
                        <Bar
                          dataKey="pnl"
                          radius={[0, 8, 8, 0]}
                          maxBarSize={24}
                          isAnimationActive={false}
                        >
                          {extraCharts.byTag.map((d, i) => (
                            <Cell
                              key={i}
                              fill={d.pnl >= 0 ? "#10b981" : "#ef4444"}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </SafeResponsiveContainer>
                  </ChartCard>
                </div>
                  </>
                ) : (
                  <ChartsPanelSkeleton
                    isDarkMode={isDarkMode}
                    pending={true}
                    openingLabel={L("page.statsOpening")}
                    preparingLabel={L("page.statsPreparing")}
                  />
                )}
              </>
            ) : chartsStage1Ready && advancedChartsRequested ? (
              <ChartsPanelSkeleton
                openingLabel={L("page.statsOpening")}
                preparingLabel={L("page.statsPreparing")}
                isDarkMode={isDarkMode}
                pending={true}
              />
            ) : null}
          </>
        )}

      <AnimatePresence>
        {activePanel === "charts" && chartsLoadingModalOpen && (
          <motion.div
            className={cn(
              "fixed inset-0 z-[9998] isolate flex items-end justify-center px-3 py-3 backdrop-blur-md sm:items-center sm:px-4 sm:py-5",
              isDarkMode ? "bg-slate-950/82" : "bg-slate-900/38",
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={cn(
                "relative w-full max-w-[440px] overflow-hidden rounded-[28px] border px-5 py-5 text-center  backdrop-blur-xl sm:px-6 sm:py-6",
                isDarkMode
                  ? "border-white/12 bg-[linear-gradient(180deg,rgba(7,11,22,0.98),rgba(15,23,42,0.97))] text-white ring-1 ring-white/10"
                  : "border-white/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.98))] text-slate-950 ring-1 ring-slate-200/70",
              )}
              initial={{ y: 12, scale: 0.985, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 8, scale: 0.985, opacity: 0 }}
              transition={SPRING_FX}
              style={premiumTextStyle}
            >
              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div
                  className={cn(
                    "absolute left-1/2 top-[-78px] h-44 w-44 -translate-x-1/2 rounded-full blur-3xl",
                    isDarkMode ? "bg-violet-500/24" : "bg-violet-300/35",
                  )}
                />
                <div
                  className={cn(
                    "absolute -right-10 bottom-0 h-28 w-28 rounded-full blur-3xl",
                    isDarkMode ? "bg-sky-500/12" : "bg-sky-300/18",
                  )}
                />
              </div>

              <div className="relative mx-auto flex h-14 w-14 items-center justify-center rounded-[18px] border border-violet-400/25 bg-violet-500/12 ">
                <motion.span
                  className="absolute h-14 w-14 rounded-[18px] border-2 border-violet-500/25 border-t-violet-500"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 0.95, repeat: Infinity, ease: "linear" }}
                />
                <LineIcon className="relative h-6 w-6 text-violet-500 dark:text-violet-300" />
              </div>

              <div className="relative mt-4">
                <div
                  className={cn(
                    "inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]",
                    isDarkMode
                      ? "border-violet-400/20 bg-violet-500/10 text-violet-200"
                      : "border-violet-200 bg-violet-50 text-violet-700",
                  )}
                >
                  FullMetrix · Statistiques avancées
                </div>
              </div>

              <h3
                className={cn(
                  "relative mt-3 text-[1.08rem] font-semibold tracking-[-0.025em] sm:text-[1.18rem]",
                  isDarkMode ? "text-white" : "text-slate-900",
                )}
              >
                Chargement des statistiques
              </h3>

              <p
                className={cn(
                  "relative mx-auto mt-2 max-w-[32ch] text-[13px] leading-5 sm:text-[13.5px]",
                  isDarkMode ? "text-slate-300" : "text-slate-600",
                )}
              >
                Préparation des données, calcul des indicateurs et affichage progressif des graphiques avancés.
              </p>

              <div
                className={cn(
                  "relative mt-5 overflow-hidden rounded-full p-[3px]",
                  isDarkMode ? "bg-white/10" : "bg-slate-200/80",
                )}
              >
                <div
                  className={cn(
                    "absolute inset-0 rounded-full",
                    isDarkMode ? "bg-white/5" : "bg-white/65",
                  )}
                />
                <div className="relative h-[10px] overflow-hidden rounded-full bg-transparent">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-500 to-sky-500 "
                    initial={{ x: "-100%" }}
                    animate={{ x: "100%" }}
                    transition={{ duration: 1.05, repeat: Infinity, ease: "easeInOut" }}
                    style={{ width: "56%" }}
                  />
                </div>
              </div>

              <div
                className={cn(
                  "relative mt-3 text-[11.5px] font-medium leading-5",
                  isDarkMode ? "text-slate-400" : "text-slate-500",
                )}
              >
                Les graphiques s’afficheront automatiquement dès que le chargement sera prêt.
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

        {activePanel === "calendar" && (
          <>
            {/* CALENDRIER (+ détail du jour sélectionné à droite) */}

            <div
              ref={calendarRef}
              className="mb-6 grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]"
            >
            <div className="rounded-3xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold inline-flex items-center gap-2">
                  <Calendar size={16} />{" "}
                  {t("calendarBox.title", {
                    monthYear: new Date(calYear, calMonth).toLocaleString(
                      localeOf(i18n.language),
                      {
                        month: "long",
                        year: "numeric",
                      },
                    ),
                  })}
                </div>
                <div className="inline-flex items-center gap-2">
                  {dateFrom && dateTo && (
                    <span className="hidden sm:inline-flex items-center rounded-full border border-violet-200/60 bg-violet-50/70 px-2.5 py-1 text-[11px] font-medium text-violet-700 dark:border-violet-400/20 dark:bg-violet-500/10 dark:text-violet-300">
                      Filtre date actif
                    </span>
                  )}
                  <BtnOutline
                    onClick={goPrevMonth}
                    size="sm"
                    className="rounded-xl px-3 py-2"
                  >
                    ‹
                  </BtnOutline>
                  <BtnOutline
                    onClick={goNextMonth}
                    size="sm"
                    className="rounded-xl px-3 py-2"
                  >
                    ›
                  </BtnOutline>
                  <BtnOutline
                    onClick={resetCalendarView}
                    size="sm"
                    className="rounded-xl px-3 py-2 gap-1.5"
                  >
                    <RefreshCw size={14} />
                    <span className="hidden sm:inline">
                      {L("journal.reset")}
                    </span>
                  </BtnOutline>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 sm:gap-2 text-[10px] sm:text-xs">
                {(i18n.language.startsWith("en")
                  ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
                  : ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"]
                ).map((d, i) => (
                  <div key={i} className="min-w-0 text-center opacity-60">
                    {d}
                  </div>
                ))}

                {(() => {
                  const firstDayRaw = new Date(calYear, calMonth, 1).getDay(); // 0 Sun … 6 Sat
                  const firstDay = ((firstDayRaw || 7) + 6) % 7; // Monday=0
                  const blanks = Array.from({ length: firstDay }, (_, i) => (
                    <div key={`b${i}`} />
                  ));
                  const cells = calData.items.map((it, i) => {
                    const tone = calendarTone(
                      it.pnl,
                      it.trades,
                      calData.intensityBase,
                      isDarkMode,
                    );
                    const isFiltered = selectedDayISO === it.iso;
                    return (
                      <button
                        key={i}
                        onClick={() => selectDay(it.iso)}
                        className={cn(
                          "flex min-w-0 flex-col items-center justify-center rounded-xl border p-1 text-center transition-all duration-200 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40 aspect-square sm:aspect-auto sm:min-h-[92px] sm:items-start sm:justify-start sm:rounded-[20px] sm:p-2.5 sm:text-left",
                          isFiltered ? "ring-2 ring-violet-500/35" : "",
                        )}
                        style={{
                          backgroundColor: tone.bg,
                          borderColor: tone.border,
                        }}
                        title={`${it.label} • ${it.pnl >= 0 ? "+" : ""}${it.pnl.toFixed(2)} • ${it.trades} trade(s)`}
                      >
                        <div
                          className={cn(
                            "text-xs font-medium sm:text-[11px] sm:font-normal",
                            tone.text,
                          )}
                        >
                          {it.day}
                        </div>
                        {it.trades ? (
                          <div
                            className={cn(
                              "mt-0.5 text-[11px] font-semibold leading-tight break-words sm:mt-1 sm:text-sm",
                              tone.value,
                            )}
                          >
                            {`${it.pnl >= 0 ? "+" : ""}${it.pnl.toFixed(2)}`}
                          </div>
                        ) : null}
                      </button>
                    );
                  });
                  return [...blanks, ...cells];
                })()}
              </div>

              {/* Résumé du mois affiché. Remplace l'ancienne légende de
                  couleurs, qui ne disait rien que la grille ne montrait déjà. */}
              <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-slate-200 pt-3 sm:grid-cols-3 lg:grid-cols-7 dark:border-white/10">
                {[
                  {
                    key: "trades",
                    icon: <ListChecks size={13} />,
                    label: L("calendarBox.trades"),
                    value: fmtInt(calData.summary.trades),
                    tone: "text-slate-900 dark:text-white",
                  },
                  {
                    key: "tradedDays",
                    icon: <Calendar size={13} />,
                    label: L("calendarBox.tradedDays"),
                    value: fmtInt(calData.summary.tradedDays),
                    tone: "text-slate-900 dark:text-white",
                  },
                  {
                    key: "gains",
                    icon: <TrendingUp size={13} />,
                    label: L("calendarBox.gains"),
                    value: `+${fmtMoney(calData.summary.gains)}`,
                    tone: "text-emerald-600 dark:text-emerald-400",
                  },
                  {
                    key: "losses",
                    icon: <TrendingUp size={13} className="rotate-180" />,
                    label: L("calendarBox.losses"),
                    value: fmtMoney(calData.summary.losses),
                    tone: "text-rose-600 dark:text-rose-400",
                  },
                  {
                    key: "net",
                    icon: <Sigma size={13} />,
                    label: L("calendarBox.net"),
                    value: `${calData.summary.net >= 0 ? "+" : ""}${fmtMoney(calData.summary.net)}`,
                    tone:
                      calData.summary.net >= 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-rose-600 dark:text-rose-400",
                  },
                  {
                    key: "winRate",
                    icon: <Activity size={13} />,
                    label: L("calendarBox.monthWinRate"),
                    value: `${calData.summary.winRate.toFixed(1)}%`,
                    tone: "text-slate-900 dark:text-white",
                    hint: `${calData.summary.wins}W / ${calData.summary.lossCount}L`,
                  },
                  {
                    key: "avgPerTradedDay",
                    icon: <Wallet size={13} />,
                    label: L("calendarBox.avgPerTradedDay"),
                    full: L("calendarBox.avgPerTradedDayFull"),
                    value: `${calData.summary.avgPerTradedDay >= 0 ? "+" : ""}${fmtMoney(calData.summary.avgPerTradedDay)}`,
                    tone:
                      calData.summary.avgPerTradedDay >= 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-rose-600 dark:text-rose-400",
                  },
                ].map((cell) => (
                  <div
                    key={cell.key}
                    className="min-w-0"
                    title={cell.full || cell.label}
                  >
                    <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.04em] text-slate-500 dark:text-white/45">
                      <span className="shrink-0">{cell.icon}</span>
                      <span className="truncate">{cell.label}</span>
                    </div>
                    <div
                      className={cn(
                        "mt-1 text-sm font-semibold tabular-nums",
                        cell.tone,
                      )}
                    >
                      {cell.value}
                    </div>
                    {cell.hint ? (
                      <div className="text-[10px] tabular-nums text-slate-400 dark:text-white/35">
                        {cell.hint}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            {/* Détail du jour cliqué. Clic ailleurs = retour à la normale. */}
            <div className="rounded-3xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
              {selectedDayStats ? (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-white/45">
                        {L("calendarBox.dayDetail")}
                      </div>
                      <div className="mt-1 text-[15px] font-semibold capitalize text-slate-900 dark:text-white">
                        {selectedDayStats.label}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearSelectedDay}
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-100 dark:hover:bg-white/10"
                      title={L("journal.reset")}
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="rounded-2xl border border-slate-200 px-3 py-2 dark:border-white/10">
                      <div className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-white/45">
                        {L("calendarBox.net")}
                      </div>
                      <div
                        className={cn(
                          "mt-0.5 text-[17px] font-extrabold",
                          selectedDayStats.net >= 0
                            ? "text-emerald-600 dark:text-emerald-300"
                            : "text-rose-600 dark:text-rose-300",
                        )}
                      >
                        {`${selectedDayStats.net >= 0 ? "+" : ""}${selectedDayStats.net.toFixed(2)}`}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 px-3 py-2 dark:border-white/10">
                      <div className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-white/45">
                        {L("calendarBox.trades")}
                      </div>
                      <div className="mt-0.5 text-[17px] font-extrabold text-slate-900 dark:text-white">
                        {selectedDayStats.total}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 px-3 py-2 dark:border-white/10">
                      <div className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-white/45">
                        {L("calendarBox.winRate")}
                      </div>
                      <div className="mt-0.5 text-[17px] font-extrabold text-slate-900 dark:text-white">
                        {selectedDayStats.winRate.toFixed(0)}%
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-white/45">
                        {selectedDayStats.wins}W / {selectedDayStats.losses}L
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 px-3 py-2 dark:border-white/10">
                      <div className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-white/45">
                        {L("calendarBox.bestWorst")}
                      </div>
                      <div className="mt-0.5 text-[13px] font-bold text-emerald-600 dark:text-emerald-300">
                        {`+${selectedDayStats.best.toFixed(2)}`}
                      </div>
                      <div className="text-[13px] font-bold text-rose-600 dark:text-rose-300">
                        {selectedDayStats.worst.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 max-h-[320px] space-y-2 overflow-y-auto pr-1">
                    {selectedDayStats.rows.map((trade, index) => {
                      const profit = toNumber(trade.profit);
                      const time = tsToDate(trade.close_time ?? trade.open_time);
                      return (
                        <div
                          key={String(trade.ticket || index)}
                          className="flex items-center justify-between gap-2 rounded-2xl border border-slate-200 px-3 py-2 dark:border-white/10"
                        >
                          <div className="min-w-0">
                            <div className="truncate text-[13px] font-semibold text-slate-900 dark:text-white">
                              {trade.symbol || "—"}
                            </div>
                            <div className="text-[11px] text-slate-500 dark:text-white/45">
                              {(trade.side || "").toUpperCase()}
                              {time
                                ? ` · ${time.toLocaleTimeString(localeOf(i18n.language), {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}`
                                : ""}
                            </div>
                          </div>
                          <div
                            className={cn(
                              "shrink-0 text-[13px] font-bold",
                              profit >= 0
                                ? "text-emerald-600 dark:text-emerald-300"
                                : "text-rose-600 dark:text-rose-300",
                            )}
                          >
                            {`${profit >= 0 ? "+" : ""}${profit.toFixed(2)}`}
                          </div>
                        </div>
                      );
                    })}

                    {selectedDayStats.rows.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-200 px-3 py-6 text-center text-[12px] text-slate-500 dark:border-white/10 dark:text-white/45">
                        {L("journal.none")}
                      </div>
                    ) : null}
                  </div>
                </>
              ) : (
                <div className="flex min-h-[180px] flex-col items-center justify-center gap-2 text-center">
                  <Calendar size={22} className="text-slate-300 dark:text-white/25" />
                  <p className="text-[12.5px] text-slate-500 dark:text-white/45">
                    {L("calendarBox.pickDay")}
                  </p>
                </div>
              )}
            </div>
            </div>
          </>
        )}
        {activePanel === "positions" && (
          <>

            {/* POSITIONS OUVERTES */}
            <section className="mb-6">
              <SectionTitle
                icon={LineIcon}
                title={L("positions.openSection")}
                accent="emerald"
                right={
                  // Le journal ne fait que lire : la gestion des positions se
                  // fait dans Exécution.
                  <button
                    type="button"
                    onClick={() => navigate("/execution")}
                    className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-[13px] font-semibold text-white transition hover:bg-violet-700"
                  >
                    <SendHorizontal size={15} />
                    {L("positions.manage")}
                  </button>
                }
              />

              {/* mobile */}
              <div className="md:hidden space-y-2">
                {filteredOpenPositions.length ? (
                  filteredOpenPositions.map((t, i) => {
                    const nt = normalizeOpenTrade(t);
                    return (
                      <div
                        key={String(nt.ticket) || i}
                        className="rounded-[24px] border border-emerald-100/80 dark:border-emerald-400/12 bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(244,248,247,0.9))] dark:bg-[linear-gradient(180deg,rgba(12,19,31,0.96),rgba(9,14,24,0.94))] backdrop-blur-xl p-4  dark:"
                        style={premiumTextStyle}
                      >
                        <div className="flex justify-between text-sm">
                          <div className="font-mono text-[11px]">
                            {nt.ticket}
                          </div>
                          <div
                            className={cn(
                              "font-semibold",
                              toNumber(nt.profit) >= 0
                                ? "text-emerald-600"
                                : "text-rose-600",
                            )}
                          >
                            {Number.isFinite(nt.profit)
                              ? nt.profit.toFixed(2)
                              : (nt.profit ?? L("misc.none"))}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-1">
                          <div>{nt.symbol}</div>
                          <div
                            className={
                              nt.side === "buy"
                                ? "text-emerald-600"
                                : "text-rose-600"
                            }
                          >
                            {nt.side?.toUpperCase?.() || nt.side}
                          </div>
                          <div className="text-slate-600 dark:text-white/70">
                            {L("misc.volShort")} {nt.volume}
                          </div>
                          <div className="text-slate-600 dark:text-white/70">
                            {L("misc.openShort")}{" "}
                            {fmtDT(nt.open_time, i18n.language)}
                          </div>
                          <div className="text-slate-600 dark:text-white/70">
                            {L("misc.priceShort")} {nt.open_price}
                          </div>
                          <div className="font-semibold text-slate-800 dark:text-white/85">
                            {L("positions.currentPrice")}: {nt.current_price || "—"}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div
                    className="rounded-[24px] border border-emerald-100/80 dark:border-emerald-400/12 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(246,250,249,0.92))] dark:bg-[linear-gradient(180deg,rgba(10,18,30,0.98),rgba(7,13,24,0.96))] backdrop-blur-xl p-5 text-center text-slate-500 dark:text-slate-300  dark:"
                    style={premiumTextStyle}
                  >
                    {L("positions.none")}
                  </div>
                )}
              </div>

              {/* desktop */}
              <div
                className="hidden md:block overflow-x-auto rounded-[30px] border border-emerald-100/80 dark:border-emerald-400/12 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(244,248,247,0.92))] dark:bg-[linear-gradient(180deg,rgba(9,15,27,0.98),rgba(5,10,20,0.96))] backdrop-blur-xl  dark:"
                style={premiumTextStyle}
              >
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-[linear-gradient(180deg,rgba(255,255,255,0.92),rgba(246,249,248,0.9))] dark:bg-[linear-gradient(180deg,rgba(15,22,36,0.95),rgba(11,16,28,0.92))] backdrop-blur">
                    <tr className="[&>th]:px-4 [&>th]:py-3 text-left text-[12px] uppercase tracking-[0.12em] text-slate-600 dark:text-slate-300/88">
                      <th>{L("positions.ticket")}</th>
                      <th>{L("positions.symbol")}</th>
                      <th>{L("positions.side")}</th>
                      <th>{L("positions.vol")}</th>
                      <th>{L("positions.open")}</th>
                      <th>{L("positions.openPrice")}</th>
                      <th>{L("positions.currentPrice")}</th>
                      <th>{L("positions.sl")}</th>
                      <th>{L("positions.tp")}</th>
                      <th className="whitespace-nowrap">
                        <SortBtn label={L("positions.pnl")} k="pnl" />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="[&>tr:nth-child(odd)]:bg-white/58 [&>tr:nth-child(even)]:bg-white/32 dark:[&>tr:nth-child(odd)]:bg-white/[0.02] dark:[&>tr:nth-child(even)]:bg-white/[0.04]">
                    {filteredOpenPositions.length ? (
                      filteredOpenPositions.map((t, i) => {
                        const nt = normalizeOpenTrade(t);
                        return (
                          <tr
                            key={String(nt.ticket) || i}
                            className="[&>td]:px-4 [&>td]:py-3 border-t border-emerald-100/70 dark:border-white/[0.05] hover:bg-emerald-500/[0.06] dark:hover:bg-emerald-400/[0.05] transition-colors"
                          >
                            <td className="text-xs" style={premiumMonoStyle}>
                              {nt.ticket}
                            </td>
                            <td>{nt.symbol}</td>
                            <td
                              className={
                                nt.side === "buy"
                                  ? "text-emerald-600"
                                  : "text-rose-500"
                              }
                            >
                              {nt.side?.toUpperCase?.() || nt.side}
                            </td>
                            <td>{nt.volume}</td>
                            <td>{fmtDT(nt.open_time, i18n.language)}</td>
                            <td>{nt.open_price}</td>
                            <td className="font-semibold tabular-nums text-slate-900 dark:text-white">
                              {nt.current_price || L("misc.none")}
                            </td>
                            <td>{nt.sl ?? L("misc.none")}</td>
                            <td>{nt.tp ?? L("misc.none")}</td>
                            <td
                              className={
                                toNumber(nt.profit) >= 0
                                  ? "text-emerald-600"
                                  : "text-rose-500"
                              }
                            >
                              {Number.isFinite(nt.profit)
                                ? nt.profit.toFixed(2)
                                : (nt.profit ?? L("misc.none"))}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-3 py-6 text-center text-slate-500 dark:text-white/60"
                        >
                          <span
                            className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-50/80 px-4 py-2 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-200"
                            style={premiumTextStyle}
                          >
                            <CheckCircle2 size={14} strokeWidth={2.1} />{" "}
                            {L("positions.none")}
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* ORDRES EN ATTENTE */}
            <section className="mb-6">
              <SectionTitle
                icon={Clock}
                title={L("pendings.title")}
                accent="violet"
                right={
                  <span className="text-xs inline-flex items-center gap-1 rounded-full border border-white/40 dark:border-white/10 px-3 py-1 bg-white/70 dark:bg-white/5 backdrop-blur">
                    <Clock size={12} />{" "}
                    {t("pendings.count", { n: filteredPendingOrders.length })}
                  </span>
                }
              />

              {/* mobile */}
              <div className="md:hidden space-y-2">
                {filteredPendingOrders.length ? (
                  filteredPendingOrders.map((t, i) => {
                    const nt = normalizeOpenTrade(t);
                    return (
                      <div
                        key={String(nt.ticket) || i}
                        className="rounded-[24px] border border-violet-100/80 dark:border-violet-400/12 bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(247,244,252,0.9))] dark:bg-[linear-gradient(180deg,rgba(12,18,34,0.96),rgba(8,12,24,0.94))] backdrop-blur-xl p-4  dark:"
                        style={premiumTextStyle}
                      >
                        <div className="flex justify-between text-sm">
                          <div className="font-mono text-[11px]">
                            {nt.ticket}
                          </div>
                          <div className="text-slate-600 dark:text-white/70 text-xs">
                            {(nt.order_type || "PENDING").toUpperCase()}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-1">
                          <div>{nt.symbol}</div>
                          <div className="text-slate-600 dark:text-white/70">
                            {L("misc.volShort")} {nt.volume}
                          </div>
                          <div className="text-slate-600 dark:text-white/70">
                            {L("pendings.price")}{" "}
                            {nt.requested_price || nt.open_price}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div
                    className="rounded-[24px] border border-violet-100/80 dark:border-violet-400/12 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(249,246,253,0.92))] dark:bg-[linear-gradient(180deg,rgba(11,17,32,0.98),rgba(6,10,21,0.96))] backdrop-blur-xl p-5 text-center text-slate-500 dark:text-slate-300  dark:"
                    style={premiumTextStyle}
                  >
                    <span
                      className="inline-flex items-center gap-2 rounded-full border border-violet-200/70 bg-violet-50/80 px-4 py-2 text-violet-700 dark:border-violet-400/20 dark:bg-violet-500/10 dark:text-violet-200"
                      style={premiumTextStyle}
                    >
                      <ListChecks size={14} strokeWidth={2.1} />{" "}
                      {L("pendings.none")}
                    </span>
                  </div>
                )}
              </div>

              {/* desktop */}
              <div
                className="hidden md:block overflow-x-auto rounded-[30px] border border-violet-100/80 dark:border-violet-400/12 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(247,244,252,0.92))] dark:bg-[linear-gradient(180deg,rgba(8,13,27,0.98),rgba(4,8,18,0.96))] backdrop-blur-xl  dark:"
                style={premiumTextStyle}
              >
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-[linear-gradient(180deg,rgba(255,255,255,0.92),rgba(249,246,253,0.9))] dark:bg-[linear-gradient(180deg,rgba(15,21,35,0.95),rgba(10,15,26,0.92))] backdrop-blur">
                    <tr className="[&>th]:px-4 [&>th]:py-3 text-left text-[12px] uppercase tracking-[0.12em] text-slate-600 dark:text-slate-300/88">
                      <th>{L("positions.ticket")}</th>
                      <th>{L("pendings.type")}</th>
                      <th>{L("positions.symbol")}</th>
                      <th>{L("positions.vol")}</th>
                      <th>{L("pendings.price")}</th>
                      <th>{L("positions.sl")}</th>
                      <th>{L("positions.tp")}</th>
                    </tr>
                  </thead>
                  <tbody className="[&>tr:nth-child(odd)]:bg-white/58 [&>tr:nth-child(even)]:bg-white/32 dark:[&>tr:nth-child(odd)]:bg-white/[0.02] dark:[&>tr:nth-child(even)]:bg-white/[0.04]">
                    {filteredPendingOrders.length ? (
                      filteredPendingOrders.map((t, i) => {
                        const nt = normalizeOpenTrade(t);
                        return (
                          <tr
                            key={String(nt.ticket) || i}
                            className="[&>td]:px-4 [&>td]:py-3 border-t border-violet-100/70 dark:border-white/[0.05] hover:bg-violet-500/[0.06] dark:hover:bg-violet-400/[0.05] transition-colors"
                          >
                            <td className="text-xs" style={premiumMonoStyle}>
                              {nt.ticket}
                            </td>
                            <td className="uppercase">
                              {nt.order_type || "PENDING"}
                            </td>
                            <td>{nt.symbol}</td>
                            <td>{nt.volume}</td>
                            <td>{nt.requested_price || nt.open_price}</td>
                            <td>{nt.sl ?? L("misc.none")}</td>
                            <td>{nt.tp ?? L("misc.none")}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-3 py-6 text-center text-slate-500 dark:text-white/60"
                        >
                          <span
                            className="inline-flex items-center gap-2 rounded-full border border-violet-200/70 bg-violet-50/80 px-4 py-2 text-violet-700 dark:border-violet-400/20 dark:bg-violet-500/10 dark:text-violet-200"
                            style={premiumTextStyle}
                          >
                            <ListChecks size={14} strokeWidth={2.1} />{" "}
                            {L("pendings.none")}
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
        {activePanel === "trades" && (
          <>

            {/* JOURNAL */}
            <section className="mb-6">
              <div className="mb-2">
                <SectionTitle
                  icon={LineIcon}
                  title={L("journal.title")}
                  accent="rose"
                  right={
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Depuis quand le journal couvre l'activité du compte. */}
                      {journalStartDate ? (
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm font-medium text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-white/70">
                          {L("journal.since").replace(
                            "{date}",
                            journalStartDate.toLocaleDateString(
                              i18n.language?.startsWith("en") ? "en-GB" : "fr-FR",
                              { day: "2-digit", month: "long", year: "numeric" },
                            ),
                          )}
                        </span>
                      ) : null}

                      <span
                        className={cn(
                          "text-sm font-medium rounded-full border px-3 py-1 bg-white/70 dark:bg-white/5 backdrop-blur",
                          metrics.net >= 0
                            ? "border-emerald-200/60 dark:border-emerald-400/30 text-emerald-600 dark:text-emerald-300"
                            : "border-rose-200/60 dark:border-rose-400/30 text-rose-600 dark:text-rose-300",
                        )}
                      >
                        {L("journal.pnlFiltered")} :{" "}
                        {Number(metrics.net || 0).toFixed(2)}
                      </span>

                      {Number(metrics.depositsTotal || 0) > 0 ? (
                        <span className="rounded-full border border-sky-200/70 bg-sky-50/80 px-3 py-1 text-sm font-medium text-sky-700 dark:border-sky-400/20 dark:bg-sky-500/10 dark:text-sky-300">
                          {L("journal.deposit")} : +{Number(metrics.depositsTotal || 0).toFixed(2)}
                        </span>
                      ) : null}

                      {Number(metrics.withdrawalsTotal || 0) > 0 ? (
                        <span className="rounded-full border border-amber-200/70 bg-amber-50/80 px-3 py-1 text-sm font-medium text-amber-700 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-300">
                          {L("journal.withdrawal")} : -{Number(metrics.withdrawalsTotal || 0).toFixed(2)}
                        </span>
                      ) : null}

                      <div className="flex items-center gap-2">
                        {exporting ? (
                          <span
                            className="inline-flex items-center gap-2 rounded-full border border-violet-200/80 bg-violet-50/90 px-3 py-2 text-[11px] font-semibold text-violet-700 dark:border-violet-400/20 dark:bg-violet-500/12 dark:text-violet-200"
                            style={premiumTextStyle}
                          >
                            <RefreshCw size={13} className="animate-spin" />
                            {L("journal.exporting")}
                          </span>
                        ) : null}
                        <BtnOutline
                          onClick={exportCSV}
                          disabled={exporting}
                          className="rounded-2xl px-3 py-2 text-xs sm:text-sm"
                          style={premiumTextStyle}
                        >
                          {exporting ? (
                            <RefreshCw size={16} className="animate-spin" />
                          ) : (
                            <Download size={16} />
                          )}{" "}
                          {exporting
                            ? L("journal.exporting")
                            : L("journal.csv")}
                        </BtnOutline>
                      </div>
                    </div>
                  }
                />

                {/* FILTRES */}
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)_minmax(0,0.85fr)_auto]">
                  <div
                    className="flex items-center gap-2 px-3.5 py-3 rounded-[22px] border border-slate-200/80 dark:border-slate-700/85 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(246,247,252,0.92))] dark:bg-[linear-gradient(180deg,rgba(17,24,39,0.98),rgba(8,13,26,0.98))] backdrop-blur min-w-0 sm:col-span-2 xl:col-span-1  dark:"
                    style={premiumTextStyle}
                  >
                    <Search size={14} className="opacity-70 shrink-0" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={L("journal.searchPh")}
                      className="bg-transparent outline-none text-sm text-slate-800 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 w-full min-w-0"
                    />
                  </div>

                  <div
                    className={cn(
                      "grid grid-cols-1 gap-2 min-w-0",
                      multiAccountDetected ? "sm:grid-cols-4" : "sm:grid-cols-3",
                    )}
                  >
                    {/* Compte : n'apparaît que si le token remonte plusieurs
                        terminaux — sinon la ligne serait un filtre inutile. */}
                    {multiAccountDetected ? (
                      <PremiumSelect
                        id="journal-source-filter"
                        icon={Server}
                        value={selectedSourceKeys[0] || "all"}
                        onChange={(v) =>
                          setSelectedSourceKeys(v === "all" ? [] : [v])
                        }
                        options={[
                          { value: "all", label: L("journal.allAccounts") },
                          ...sourceOptions.map((item) => ({
                            value: item.key,
                            label: item.label,
                          })),
                        ]}
                      />
                    ) : null}
                    <PremiumSelect
                      id="journal-symbol-filter"
                      icon={Filter}
                      value={symbolFilter}
                      onChange={setSymbolFilter}
                      options={allSymbols.map((s) => ({
                        value: s,
                        label: s === "all" ? L("journal.allSymbols") : s,
                      }))}
                    />
                    <PremiumSelect
                      id="journal-side-filter"
                      icon={ArrowUpDown}
                      value={sideFilter}
                      onChange={setSideFilter}
                      options={[
                        { value: "all", label: L("journal.allSides") },
                        { value: "buy", label: "Buy" },
                        { value: "sell", label: "Sell" },
                      ]}
                    />
                    {/* Résultat : ne porte que sur les trades clôturés. */}
                    <PremiumSelect
                      id="journal-result-filter"
                      icon={TrendingUp}
                      value={resultFilter}
                      onChange={setResultFilter}
                      options={[
                        { value: "all", label: L("journal.allResults") },
                        // Mêmes libellés que la puce « périmètre actif », qui
                        // lit déjà ces deux clés : deux textes différents pour
                        // le même filtre se lisaient comme deux filtres.
                        { value: "win", label: L("charts.monthGains") },
                        { value: "loss", label: L("charts.monthLosses") },
                        { value: "be", label: L("journal.resultBe") },
                      ]}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2 min-w-0">
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className={`${baseInput} rounded-[22px] bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(246,247,252,0.92))] dark:bg-[linear-gradient(180deg,rgba(17,24,39,0.98),rgba(8,13,26,0.98))] backdrop-blur w-full min-w-0  dark:`}
                      style={premiumTextStyle}
                    />
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className={`${baseInput} rounded-[22px] bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(246,247,252,0.92))] dark:bg-[linear-gradient(180deg,rgba(17,24,39,0.98),rgba(8,13,26,0.98))] backdrop-blur w-full min-w-0  dark:`}
                      style={premiumTextStyle}
                    />
                  </div>

                  <BtnOutline
                    onClick={() => {
                      setSymbolFilter("all");
                      setSideFilter("all");
                      setResultFilter("all");
                      setDateFrom("");
                      setDateTo("");
                      setSearch("");
                      setSelectedServers([]);
                      setSelectedAccounts([]);
                      setSelectedSourceKeys([]);
                    }}
                    className="rounded-[22px] px-4 py-3 text-xs sm:text-sm w-full xl:w-auto justify-center"
                    style={premiumTextStyle}
                  >
                    {L("journal.reset")}
                  </BtnOutline>
                </div>
              </div>

              {/* mobile: cards */}
              <div className="lg:hidden space-y-2">
                {pageData.length ? (
                  mobileHistoryCards
                ) : (
                  <div className="rounded-2xl border border-white/40 dark:border-white/10 bg-white/70 dark:bg-white/5 backdrop-blur-xl p-4 text-center text-slate-500 dark:text-white/60">
                    {L("journal.none")}
                  </div>
                )}
              </div>

              {/* desktop: table */}
              <div className="hidden lg:block overflow-x-auto rounded-[28px] border border-slate-200/80 dark:border-slate-700/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.92),rgba(244,247,251,0.84))] dark:bg-[linear-gradient(180deg,rgba(9,13,24,0.98),rgba(4,8,18,0.94))] backdrop-blur-xl  dark: p-3">
                <table className="min-w-full border-collapse text-sm">
                  <thead className="sticky top-0 z-10 backdrop-blur">
                    <tr className="[&>th]:border-b [&>th]:border-slate-200 [&>th]:px-3 [&>th]:pb-2.5 [&>th]:pt-1 text-left text-[11px] uppercase tracking-[0.14em] text-slate-600 dark:[&>th]:border-white/10 dark:text-white/50">
                      <th>{L("positions.ticket")}</th>
                      <th>{L("positions.symbol")}</th>
                      <th>{L("positions.side")}</th>
                      <th>
                        <SortBtn label={L("journal.vol")} k="volume" />
                      </th>
                      <th>{L("journal.open")}</th>
                      <th>{L("journal.openP")}</th>
                      <th>{L("journal.close")}</th>
                      <th>{L("journal.closeP")}</th>
                      <th>
                        <SortBtn label={L("journal.profit")} k="pnl" />
                      </th>
                      <th>{L("journal.tags")}</th>
                      <th>{L("journal.note")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.length ? (
                      desktopHistoryRows
                    ) : (
                      <tr>
                        <td
                          colSpan={12}
                          className="rounded-2xl border border-white/45 px-3 py-6 text-center text-slate-500 dark:border-white/10 dark:text-white/60"
                        >
                          {L("journal.none")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION — plage lisible à gauche, taille de page et
                  numéros à droite (avant : « Page 1 / 4 — 35 rows » et deux
                  flèches, sans accès direct à une page). */}
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-[22px] border border-slate-200/80 bg-white/70 px-4 py-3 dark:border-slate-700/85 dark:bg-white/[0.03]">
                <div
                  className="text-xs text-slate-600 dark:text-white/60"
                  style={premiumTextStyle}
                >
                  {L("page.showingRange", {
                    from: rangeFrom,
                    to: rangeTo,
                    total: totalRows,
                  })}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600 dark:text-white/60">
                      {L("page.rowsPerPage")}
                    </span>
                    <PremiumSelect
                      id="journal-per-page"
                      value={String(perPage)}
                      onChange={(v) => setPerPage(Number(v))}
                      options={[10, 25, 50, 100].map((n) => ({
                        value: String(n),
                        label: String(n),
                      }))}
                      triggerClassName="min-w-[86px]"
                      menuClassName="left-auto right-0 w-[110px]"
                      dropUp
                    />
                  </div>

                  <div className="flex items-center gap-1">
                    <PageBtn
                      onClick={() => setPage(1)}
                      disabled={page <= 1}
                      title={L("page.firstPage")}
                    >
                      <ChevronsLeft size={15} />
                    </PageBtn>
                    <PageBtn
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      title={L("page.prevPage")}
                    >
                      <ChevronLeft size={15} />
                    </PageBtn>

                    {pageNumbers.map((p, idx) =>
                      p === "…" ? (
                        <span
                          key={`gap-${idx}`}
                          className="px-1.5 text-xs text-slate-400 dark:text-white/35"
                        >
                          …
                        </span>
                      ) : (
                        <PageBtn
                          key={p}
                          onClick={() => setPage(p)}
                          active={p === page}
                          title={`Page ${p}`}
                        >
                          {p}
                        </PageBtn>
                      ),
                    )}

                    <PageBtn
                      onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                      disabled={page >= pageCount}
                      title={L("page.nextPage")}
                    >
                      <ChevronRight size={15} />
                    </PageBtn>
                    <PageBtn
                      onClick={() => setPage(pageCount)}
                      disabled={page >= pageCount}
                      title={L("page.lastPage")}
                    >
                      <ChevronsRight size={15} />
                    </PageBtn>
                  </div>
                </div>
              </div>
            </section>

          </>
        )}
      </div>

      {/* PREMIUM COACH MODAL */}
      <AnimatePresence>
        {(premiumCoachOpen || premiumCoachLoading) && (
          <motion.div
            className={cn(
              // Mobile : aucune marge, la modale occupe l'écran entier.
              // Desktop : marges réduites pour laisser la carte s'étendre.
              "fixed inset-0 z-[9999] isolate flex items-stretch justify-center backdrop-blur-md sm:items-center sm:px-4 sm:py-4",
              isDarkMode ? "bg-slate-950/78" : "bg-slate-900/34",
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePremiumCoachAudit}
            // Pas de `paddingLeft` compensant la sidebar : le voile couvre
            // déjà tout l'écran, donc décaler la modale de la largeur de la
            // sidebar la faisait juste pencher à droite du centre.
          >
            {premiumCoachLoading ? (
              <motion.div
                className={cn(
                  // `self-center` : le conteneur est en `items-stretch` pour
                  // le plein écran mobile de l'audit, ce petit carton ne doit
                  // pas s'étirer avec.
                  "relative m-auto w-full max-w-[410px] self-center overflow-hidden rounded-[26px] border p-4 sm:p-5 text-center backdrop-blur-2xl",
                  isDarkMode
                    ? "border-white/12 bg-slate-950/92 text-white ring-1 ring-white/10"
                    : "border-slate-200 bg-white/96 text-slate-950 ring-1 ring-white/80",
                )}
                initial={{ y: 14, scale: 0.98, opacity: 0 }}
                animate={{ y: 0, scale: 1, opacity: 1 }}
                exit={{ y: 10, scale: 0.98, opacity: 0 }}
                transition={SPRING_FX}
                onClick={(e) => e.stopPropagation()}
                style={premiumTextStyle}
              >
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div
                    className={cn(
                      "absolute left-1/2 top-[-90px] h-52 w-52 -translate-x-1/2 rounded-full blur-3xl",
                      isDarkMode ? "bg-violet-500/24" : "bg-violet-300/35",
                    )}
                  />
                </div>
                <div className="relative mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-violet-400/25 bg-violet-500/10">
                  <motion.span
                    className="absolute h-12 w-12 rounded-2xl border-2 border-violet-500/25 border-t-violet-500"
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 0.9,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                  <img
                    src="/favicon.ico"
                    alt="Full Metrix"
                    className="relative h-7 w-7 rounded-lg object-contain drop-"
                    draggable={false}
                  />
                </div>
                <h3 className="relative mt-3.5 text-[15px] font-semibold tracking-[-0.02em]">
                  {L("coachUi.loadingTitle")}
                </h3>
                <p
                  className={cn(
                    "relative mt-1.5 text-[13px] leading-5",
                    isDarkMode ? "text-slate-300" : "text-slate-600",
                  )}
                >
                  {L("coachUi.loadingBody")}
                </p>
                <div
                  className={cn(
                    "relative mt-3.5 h-2 overflow-hidden rounded-full",
                    isDarkMode ? "bg-white/10" : "bg-slate-100",
                  )}
                >
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-violet-600 via-sky-500 to-emerald-500"
                    initial={{ x: "-100%" }}
                    animate={{ x: "100%" }}
                    transition={{
                      duration: 0.95,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    style={{ width: "55%" }}
                  />
                </div>
              </motion.div>
            ) : (
              <motion.section
                className={cn(
                  // Mobile : plein écran, bords carrés, hauteur en `dvh` pour
                  // tenir compte de la barre d'URL qui se rétracte au scroll.
                  // Desktop : nettement plus large et plus haut qu'avant,
                  // l'audit tient sur deux colonnes denses.
                  "relative flex h-[100dvh] w-full flex-col overflow-hidden border sm:h-auto sm:max-h-[94dvh] sm:max-w-[1400px] sm:rounded-[28px]",
                  isDarkMode
                    ? "border-white/12 bg-[linear-gradient(180deg,#070b16_0%,#0f172a_48%,#07111f_100%)] text-white ring-1 ring-white/10"
                    : "border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_52%,#eef2ff_100%)] text-slate-950 ring-1 ring-white/80",
                )}
                initial={{ y: 18, scale: 0.985, opacity: 0 }}
                animate={{ y: 0, scale: 1, opacity: 1 }}
                exit={{ y: 10, scale: 0.985, opacity: 0 }}
                transition={SPRING_FX}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={L("coachUi.ariaPanel")}
                style={premiumTextStyle}
              >
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div
                    className={cn(
                      "absolute -top-36 left-1/2 h-72 w-[680px] -translate-x-1/2 rounded-full blur-3xl",
                      isDarkMode ? "bg-violet-500/18" : "bg-violet-300/30",
                    )}
                  />
                  <div
                    className={cn(
                      "absolute -right-28 top-20 h-72 w-72 rounded-full blur-3xl",
                      isDarkMode ? "bg-sky-500/12" : "bg-sky-300/22",
                    )}
                  />
                  <div
                    className={cn(
                      "absolute -left-24 bottom-0 h-72 w-72 rounded-full blur-3xl",
                      isDarkMode ? "bg-emerald-400/10" : "bg-emerald-300/20",
                    )}
                  />
                </div>

                <header
                  className={cn(
                    "relative shrink-0 overflow-hidden border-b px-4 py-3.5 backdrop-blur-xl sm:px-5 sm:py-4",
                    isDarkMode
                      ? "border-white/10 bg-[linear-gradient(180deg,rgba(2,6,23,0.76),rgba(15,23,42,0.58))]"
                      : "border-slate-200/90 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.95))]",
                  )}
                >
                  <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div className={cn(
                      "absolute -top-16 left-10 h-36 w-36 rounded-full blur-3xl",
                      isDarkMode ? "bg-violet-500/12" : "bg-violet-200/45",
                    )} />
                    <div className={cn(
                      "absolute -right-10 top-3 h-28 w-28 rounded-full blur-3xl",
                      isDarkMode ? "bg-sky-500/12" : "bg-sky-200/45",
                    )} />
                  </div>
                  <div className="relative flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={cn(
                            "inline-flex items-center gap-2 rounded-full border px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-[0.09em] ",
                            isDarkMode
                              ? "border-sky-300/24 bg-sky-400/10 text-sky-100"
                              : "border-sky-200 bg-sky-50 text-sky-800",
                          )}
                        >
                          <LineIcon size={13} /> {L("coachUi.brandBadge")}
                        </span>
                        <span
                          className={cn(
                            "inline-flex items-center gap-2 rounded-full px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-[0.09em] ",
                            coachBadgeClasses(
                              premiumCoach.permissionStatus?.tone,
                              isDarkMode,
                            ),
                          )}
                        >
                          {premiumCoach.permissionStatus?.label}
                        </span>
                      </div>
                      <h2
                        className={cn(
                          "mt-2.5 text-[1.05rem] font-semibold leading-[1.2] tracking-[-0.018em] sm:text-[1.28rem] lg:text-[1.5rem]",
                          isDarkMode ? "text-white" : "text-slate-900",
                        )}
                      >
                        {L("coachUi.doneTitle")}
                      </h2>
                      <p
                        className={cn(
                          "mt-1.5 max-w-4xl text-[13px] font-medium leading-5 sm:leading-6 sm:text-[14px]",
                          isDarkMode ? "text-slate-200/90" : "text-slate-600",
                        )}
                      >
                        {premiumCoach.headline}
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      <div
                        className={cn(
                          "hidden items-center gap-3 rounded-[20px] border px-3 py-2 sm:flex",
                          isDarkMode
                            ? "border-white/12 bg-white/[0.07]"
                            : "border-slate-200 bg-slate-50",
                        )}
                      >
                        <div
                          className="relative flex h-12 w-12 items-center justify-center rounded-2xl"
                          style={{
                            background: `conic-gradient(from 180deg, #7c3aed ${clamp(premiumCoach.score, 0, 100)}%, rgba(148,163,184,.22) 0)`,
                          }}
                        >
                          <div
                            className={cn(
                              "absolute inset-[4px] rounded-[16px]",
                              isDarkMode ? "bg-slate-950" : "bg-white",
                            )}
                          />
                          <div
                            className={cn(
                              "relative text-base font-semibold tracking-[-0.02em]",
                              coachBadgeClasses(premiumCoach.aiTone || "neutral", isDarkMode),
                            )}
                          >
                            {premiumCoach.score}
                          </div>
                        </div>
                        <div className="text-left">
                          <div
                            className={cn(
                              "text-[8px] font-semibold uppercase tracking-[0.11em]",
                              isDarkMode ? "text-slate-300" : "text-slate-500",
                            )}
                          >
                            Score
                          </div>
                          <div
                            className={cn(
                              "text-[13px] font-medium",
                              isDarkMode ? "text-white" : "text-slate-900",
                            )}
                          >
                            {premiumCoach.grade}
                          </div>
                          <div
                            className={cn(
                              "text-[10px] font-medium",
                              isDarkMode ? "text-slate-400" : "text-slate-500",
                            )}
                          >
                            {L("coachUi.riskInline", { level: premiumCoach.riskLevel })}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={closePremiumCoachAudit}
                        className={cn(
                          "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40",
                          isDarkMode
                            ? "border-white/12 bg-white/[0.08] text-slate-100 hover:bg-white/[0.16]"
                            : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-900",
                        )}
                        aria-label={L("coachUi.close")}
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                </header>

                <main className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 sm:px-4 sm:py-3.5">
                  <section className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {[
                      [
                        L("coachUi.statScore"),
                        `${premiumCoach.score}/100`,
                        premiumCoach.score >= 70
                          ? "emerald"
                          : premiumCoach.score >= 45
                            ? "amber"
                            : "rose",
                      ],
                      [L("coachUi.statProfile"), premiumCoach.grade, "sky"],
                      [
                        L("coachUi.statRisk"),
                        premiumCoach.riskLevel,
                        premiumCoach.riskKey === "critical"
                          ? "rose"
                          : premiumCoach.riskKey === "high"
                            ? "amber"
                            : "emerald",
                      ],
                      [L("coachUi.statConfidence"), premiumCoach.confidence, "neutral"],
                    ].map(([label, value, tone]) => (
                      <div
                        key={label}
                        className={cn(
                          "rounded-[20px] border p-3.5 ",
                          coachToneClasses(tone, isDarkMode),
                        )}
                      >
                        <CoachLabelPill tone={tone} isDarkMode={isDarkMode}>
                          {label}
                        </CoachLabelPill>
                        <div
                          className={cn(
                            "mt-2 truncate text-[0.98rem] font-semibold tracking-[-0.01em] sm:text-[1.05rem]",
                            isDarkMode ? "text-white" : "text-slate-900",
                          )}
                        >
                          {value}
                        </div>
                      </div>
                    ))}
                  </section>

                  <section
                    className={cn(
                      "relative mt-3 overflow-hidden rounded-[28px] border p-4  sm:p-5",
                      isDarkMode
                        ? "border-violet-300/18 bg-[linear-gradient(135deg,rgba(18,24,45,0.96),rgba(76,29,149,0.28),rgba(8,13,25,0.96))]"
                        : "border-violet-200 bg-[linear-gradient(135deg,rgba(255,255,255,0.98),rgba(245,243,255,0.95),rgba(239,246,255,0.92))]",
                    )}
                  >
                    <div className="pointer-events-none absolute inset-0 overflow-hidden">
                      <div
                        className={cn(
                          "absolute -left-24 -top-24 h-56 w-56 rounded-full blur-3xl",
                          isDarkMode ? "bg-violet-500/20" : "bg-violet-300/35",
                        )}
                      />
                      <div
                        className={cn(
                          "absolute -right-20 bottom-[-90px] h-60 w-60 rounded-full blur-3xl",
                          isDarkMode ? "bg-sky-500/14" : "bg-sky-300/28",
                        )}
                      />
                    </div>

                    <div className="relative grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
                      <div className="min-w-0">
                        <div className="flex items-start gap-3">
                          <div
                            className={cn(
                              "grid h-10 w-10 shrink-0 place-items-center rounded-[16px] border ",
                              isDarkMode
                                ? "border-violet-300/24 bg-violet-400/14 text-violet-50"
                                : "border-violet-200 bg-violet-100 text-violet-700",
                            )}
                          >
                            <LineIcon size={18} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div
                              className={cn(
                                "inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ",
                                coachBadgeClasses("sky", isDarkMode),
                              )}
                            >
                              {L("coachUi.analysisBadge")}
                            </div>
                            <h3
                              className={cn(
                                "mt-2.5 max-w-4xl text-[1.08rem] font-semibold leading-[1.22] tracking-[-0.018em] sm:text-[1.36rem] lg:text-[1.54rem]",
                                isDarkMode ? "text-white" : "text-slate-900",
                              )}
                            >
                              {premiumCoach.aiVoiceTitle || premiumCoach.headline}
                            </h3>
                            <p
                              className={cn(
                                "mt-3 max-w-3xl text-[14px] font-medium leading-6 sm:text-[15px] sm:leading-7",
                                isDarkMode ? "text-slate-200" : "text-slate-600",
                              )}
                            >
                              {renderAuditRichText(
                                premiumCoach.aiVoiceIntro || premiumCoach.headline,
                                isDarkMode,
                                { tone: premiumCoach.aiTone || "neutral" },
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 space-y-2.5">
                          {(premiumCoach.aiConversation || [])
                            .slice(0, 4)
                            .map((line, index) => (
                              <div
                                key={index}
                                className={cn(
                                  "relative overflow-hidden rounded-[20px] border px-3.5 py-3 text-[13.5px] font-medium leading-6  sm:px-4",
                                  isDarkMode
                                    ? "border-white/10 bg-[linear-gradient(180deg,rgba(2,6,23,0.34),rgba(15,23,42,0.22))] text-slate-100"
                                    : "border-white/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(248,250,252,0.92))] text-slate-600",
                                )}
                              >
                                <span
                                  className={cn(
                                    "mr-2 inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ",
                                    coachBadgeClasses(index === 0 ? premiumCoach.aiTone : index === 1 ? "emerald" : "rose", isDarkMode),
                                  )}
                                >
                                  {index === 0
                                    ? L("coachUi.tagDiagnostic")
                                    : index === 1
                                      ? L("coachUi.tagStrength")
                                      : L("coachUi.tagAlert")}
                                </span>
                                {renderAuditRichText(line, isDarkMode, {
                                  tone: index === 0 ? premiumCoach.aiTone : index === 1 ? "emerald" : "rose",
                                  contextTone: index === 1 ? "emerald" : index === 2 ? "rose" : premiumCoach.aiTone,
                                })}
                              </div>
                            ))}
                        </div>
                      </div>

                      <div className="grid gap-2.5">
                        {(premiumCoach.aiFocusCards || []).map((card) => (
                          <article
                            key={card.label}
                            className={cn(
                              "relative overflow-hidden rounded-[22px] border p-3.5 ",
                              coachToneClasses(card.tone, isDarkMode),
                            )}
                          >
                            <div
                              className={cn(
                                "absolute inset-x-0 top-0 h-1.5",
                                card.tone === "emerald"
                                  ? "bg-gradient-to-r from-emerald-400/58 via-teal-400/48 to-emerald-300/40"
                                  : card.tone === "rose"
                                    ? "bg-gradient-to-r from-rose-400/58 via-orange-400/48 to-rose-300/40"
                                    : card.tone === "amber"
                                      ? "bg-gradient-to-r from-amber-400/58 via-orange-400/48 to-yellow-300/40"
                                      : "bg-gradient-to-r from-violet-400/58 via-fuchsia-400/48 to-sky-300/40",
                              )}
                            />
                            <div
                              className={cn(
                                "inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ",
                                coachBadgeClasses(card.tone, isDarkMode),
                              )}
                            >
                              {card.label}
                            </div>
                            <div
                              className={cn(
                                "mt-2.5 text-[0.95rem] font-semibold leading-6 tracking-[-0.01em] sm:text-base",
                                isDarkMode ? "text-white" : "text-slate-900",
                              )}
                            >
                              {card.title}
                            </div>
                            <p
                              className={cn(
                                "mt-1.5 text-[13px] font-medium leading-5",
                                isDarkMode ? "text-slate-100" : "text-slate-600",
                              )}
                            >
                              {renderAuditRichText(card.body, isDarkMode, {
                                tone: card.tone || "neutral",
                                contextTone: card.tone || "neutral",
                              })}
                            </p>
                          </article>
                        ))}
                      </div>
                    </div>
                  </section>

                  <section className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-[1.18fr,0.82fr]">
                    <div
                      className={cn(
                        "relative overflow-hidden rounded-[26px] border p-4 ",
                        isDarkMode
                          ? "border-violet-300/18 bg-[linear-gradient(135deg,rgba(17,24,39,0.58),rgba(76,29,149,0.18),rgba(8,13,25,0.70))]"
                          : "border-violet-200/90 bg-[linear-gradient(135deg,rgba(255,255,255,0.98),rgba(245,243,255,0.92),rgba(240,249,255,0.92))]",
                      )}
                    >
                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)]">
                        <div>
                          <div
                            className={cn(
                              "inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ",
                              coachBadgeClasses(premiumCoach.aiTone || "neutral", isDarkMode),
                            )}
                          >
                            Priorité IA · à corriger maintenant
                          </div>
                          <div
                            className={cn(
                              "mt-2.5 max-w-3xl text-[1.05rem] font-semibold leading-7 tracking-[-0.02em] sm:text-[1.35rem]",
                              isDarkMode ? "text-white" : "text-slate-900",
                            )}
                          >
                            {premiumCoach.priorityFocus}
                          </div>
                          <p
                            className={cn(
                              "mt-2 max-w-2xl text-[13px] font-medium leading-6 sm:text-sm",
                              isDarkMode ? "text-slate-200" : "text-slate-600",
                            )}
                          >
                            Profil d’exécution : {premiumCoach.traderProfile}
                          </p>
                          <div
                            className={cn(
                              "mt-3 rounded-[20px] border px-3.5 py-3 text-xs font-semibold leading-5 sm:text-sm",
                              isDarkMode
                                ? "border-violet-300/18 bg-slate-950/24 text-violet-50"
                                : "border-violet-200 bg-white/72 text-violet-900",
                            )}
                          >
                            <span className="mr-2 font-semibold uppercase tracking-[0.14em]">Avant ton prochain trade :</span>
                            {premiumCoach.aiNextTradeScript}
                          </div>
                        </div>
                        <div
                          className={cn(
                            "relative overflow-hidden rounded-[24px] border p-4  sm:p-5",
                            coachToneClasses(
                              premiumCoach.permissionStatus?.tone,
                              isDarkMode,
                            ),
                          )}
                        >
                          <div
                            className={cn(
                              "pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full blur-2xl",
                              premiumCoach.permissionStatus?.tone === "rose"
                                ? "bg-rose-300/28 dark:bg-rose-400/16"
                                : premiumCoach.permissionStatus?.tone === "amber"
                                  ? "bg-amber-300/30 dark:bg-amber-400/16"
                                  : "bg-emerald-300/28 dark:bg-emerald-400/16",
                            )}
                          />

                          <div className="relative flex flex-col gap-3">
                            <div className="flex items-start justify-between gap-3">
                              <span
                                className={cn(
                                  "inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-[18px] border  ring-1 ring-white/40",
                                  premiumCoach.permissionStatus?.tone === "rose"
                                    ? isDarkMode
                                      ? "border-rose-300/20 bg-rose-400/14 text-rose-100"
                                      : "border-rose-200 bg-rose-50 text-rose-700"
                                    : premiumCoach.permissionStatus?.tone === "amber"
                                      ? isDarkMode
                                        ? "border-amber-300/20 bg-amber-400/14 text-amber-100"
                                        : "border-amber-200 bg-amber-50 text-amber-700"
                                      : isDarkMode
                                        ? "border-emerald-300/20 bg-emerald-400/14 text-emerald-100"
                                        : "border-emerald-200 bg-emerald-50 text-emerald-700",
                                )}
                              >
                                {premiumCoach.permissionStatus?.tone === "emerald" ? (
                                  <CheckCircle2 size={23} strokeWidth={2.4} />
                                ) : (
                                  <AlertTriangle size={23} strokeWidth={2.4} />
                                )}
                              </span>

                              <span
                                className={cn(
                                  "inline-flex max-w-[10.5rem] shrink-0 items-center justify-center rounded-full border px-3 py-1.5 text-center text-[10px] font-semibold uppercase leading-4 tracking-[0.14em] ",
                                  coachBadgeClasses(
                                    premiumCoach.permissionStatus?.tone,
                                    isDarkMode,
                                  ),
                                )}
                              >
                                {premiumCoach.permissionStatus?.label}
                              </span>
                            </div>

                            <div className="min-w-0">
                              <div
                                className={cn(
                                  "mb-1.5 text-[10px] font-semibold uppercase leading-4 tracking-[0.12em]",
                                  isDarkMode ? "text-white/60" : "text-slate-500",
                                )}
                              >
                                Cadre de trading
                              </div>
                              <h3
                                className={cn(
                                  "m-0 text-[1.04rem] font-semibold leading-[1.18] tracking-[-0.015em] sm:text-[1.12rem] lg:text-[1.04rem] xl:text-[1.12rem]",
                                  isDarkMode ? "text-white" : "text-slate-900",
                                )}
                              >
                                {premiumCoach.permissionStatus?.title}
                              </h3>
                            </div>
                          </div>

                          <p
                            className={cn(
                              "relative mt-3 text-[13px] font-medium leading-6",
                              isDarkMode ? "text-slate-100" : "text-slate-600",
                            )}
                          >
                            {renderAuditRichText(
                              premiumCoach.permissionStatus?.reason,
                              isDarkMode,
                              { tone: premiumCoach.permissionStatus?.tone || "neutral" },
                            )}
                          </p>

                          <div className="relative mt-3 grid gap-2">
                            {(premiumCoach.permissionStatus?.conditions || [])
                              .slice(0, 4)
                              .map((item, index) => (
                                <div
                                  key={index}
                                  className={cn(
                                    "flex gap-2.5 rounded-2xl border px-3 py-2 text-[11px] font-semibold leading-4",
                                    isDarkMode
                                      ? "border-white/10 bg-slate-950/20 text-slate-100"
                                      : "border-white/80 bg-white/68 text-slate-700 ",
                                  )}
                                >
                                  <Check
                                    size={14}
                                    className={cn(
                                      "mt-0.5 shrink-0",
                                      premiumCoach.permissionStatus?.tone === "rose"
                                        ? "text-rose-600 dark:text-rose-200"
                                        : premiumCoach.permissionStatus?.tone === "amber"
                                          ? "text-amber-600 dark:text-amber-200"
                                          : "text-emerald-600 dark:text-emerald-200",
                                    )}
                                  />
                                  <span>{item}</span>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {(premiumCoach.traderHealth || []).map((item) => (
                        <div
                          key={item.label}
                          className={cn(
                            "relative overflow-hidden rounded-[22px] border p-3.5 min-h-[128px] ",
                            coachToneClasses(item.tone, isDarkMode),
                          )}
                        >
                          <div
                            className={cn(
                              "absolute inset-x-0 top-0 h-1.5",
                              item.tone === "emerald"
                                ? "bg-gradient-to-r from-emerald-400/62 via-teal-400/52 to-emerald-300/42"
                                : item.tone === "rose"
                                  ? "bg-gradient-to-r from-rose-400/62 via-orange-400/52 to-rose-300/42"
                                  : item.tone === "amber"
                                    ? "bg-gradient-to-r from-amber-400/62 via-yellow-400/52 to-orange-300/42"
                                    : "bg-gradient-to-r from-sky-400/62 via-cyan-400/52 to-blue-300/42",
                            )}
                          />
                          <div
                            className={cn(
                              "inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ",
                              coachBadgeClasses(item.tone, isDarkMode),
                            )}
                          >
                            {item.label}
                          </div>
                          <div
                            className={cn(
                              "mt-3 text-[1.02rem] font-semibold leading-none tracking-[-0.015em]",
                              isDarkMode ? "text-white" : "text-slate-900",
                            )}
                          >
                            {item.value}
                          </div>
                          <p
                            className={cn(
                              "mt-2 text-[12.5px] font-medium leading-5",
                              isDarkMode ? "text-slate-100" : "text-slate-600",
                            )}
                          >
                            {renderAuditRichText(item.detail, isDarkMode, { tone: item.tone || "neutral" })}
                          </p>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-4">
                    {(premiumCoach.evidenceStack || []).map((item) => (
                      <div
                        key={item.label}
                        className={cn(
                          "rounded-[22px] border p-3.5 ",
                          coachToneClasses(item.tone, isDarkMode),
                        )}
                      >
                        <CoachLabelPill tone={item.tone} isDarkMode={isDarkMode}>
                          {item.label}
                        </CoachLabelPill>
                        <div
                          className={cn(
                            "mt-1.5 text-[0.95rem] font-medium tracking-[-0.015em]",
                            isDarkMode ? "text-white" : "text-slate-900",
                          )}
                        >
                          {item.value}
                        </div>
                        <p
                          className={cn(
                            "mt-1.5 text-[11.5px] font-medium leading-5",
                            isDarkMode ? "text-slate-200" : "text-slate-600",
                          )}
                        >
                          {renderAuditRichText(item.detail, isDarkMode, { tone: item.tone || "neutral" })}
                        </p>
                      </div>
                    ))}
                  </section>

                  <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-[0.86fr,1.14fr]">
                    <section
                      className={cn(
                        "rounded-[24px] border p-3.5 ",
                        isDarkMode
                          ? "border-white/10 bg-white/[0.065]"
                          : "border-slate-200 bg-white",
                      )}
                    >
                      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <div
                            className={cn(
                              "flex items-center gap-2 text-sm font-semibold tracking-[-0.02em]",
                              isDarkMode ? "text-white" : "text-slate-900",
                            )}
                          >
                            <span
                              className={cn(
                                "inline-flex h-8 w-8 items-center justify-center rounded-2xl border",
                                isDarkMode
                                  ? "border-violet-300/20 bg-violet-400/12 text-violet-100"
                                  : "border-violet-200 bg-violet-50 text-violet-700",
                              )}
                            >
                              <Sigma size={16} />
                            </span>
                            Score par catégorie
                          </div>
                          <p
                            className={cn(
                              "mt-1 text-[11.5px] font-medium leading-5",
                              isDarkMode ? "text-slate-300" : "text-slate-600",
                            )}
                          >
                            Calculé avec tes trades clôturés, ton P&amp;L, ton drawdown,
                            ta fréquence et tes notes de journal.
                          </p>
                        </div>
                        <span
                          className={cn(
                            "w-fit shrink-0 rounded-full px-3 py-1.5 text-[11px] font-semibold",
                            coachBadgeClasses(
                              premiumCoach.score >= 70
                                ? "emerald"
                                : premiumCoach.score >= 45
                                  ? "amber"
                                  : "rose",
                              isDarkMode,
                            ),
                          )}
                        >
                          Score global {premiumCoach.score}/100
                        </span>
                      </div>
                      <div className="grid grid-cols-1 gap-2.5">
                        {(premiumCoach.scoreBreakdown || []).map((item) => (
                          <article
                            key={item.label}
                            className={cn(
                              "rounded-[20px] border p-3 ",
                              coachToneClasses(item.tone, isDarkMode),
                            )}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <CoachLabelPill tone={item.tone} isDarkMode={isDarkMode}>
                                  {item.label}
                                </CoachLabelPill>
                                <p
                                  className={cn(
                                    "mt-2 text-[12px] font-medium leading-5",
                                    isDarkMode ? "text-slate-100" : "text-slate-600",
                                  )}
                                >
                                  {renderAuditRichText(item.detail, isDarkMode, { tone: item.tone || "neutral" })}
                                </p>
                              </div>
                              <div
                                className={cn(
                                  "shrink-0 text-right text-[1.18rem] font-semibold leading-none tracking-[-0.012em]",
                                  item.tone === "emerald"
                                    ? "text-emerald-700 dark:text-emerald-100"
                                    : item.tone === "sky"
                                      ? "text-sky-700 dark:text-sky-100"
                                      : item.tone === "amber"
                                        ? "text-amber-700 dark:text-amber-100"
                                        : "text-rose-700 dark:text-rose-100",
                                )}
                              >
                                {item.value}
                                <span className="ml-0.5 text-[11px] font-semibold opacity-70">
                                  /100
                                </span>
                              </div>
                            </div>
                            <div
                              className={cn(
                                "mt-3 h-2.5 overflow-hidden rounded-full",
                                isDarkMode ? "bg-white/12" : "bg-white/80",
                              )}
                            >
                              <div
                                className={cn(
                                  "h-full rounded-full bg-gradient-to-r",
                                  item.tone === "emerald"
                                    ? "from-emerald-500 via-teal-400 to-emerald-300"
                                    : item.tone === "sky"
                                      ? "from-sky-500 via-cyan-400 to-blue-400"
                                      : item.tone === "amber"
                                        ? "from-amber-500 via-orange-400 to-yellow-300"
                                        : "from-rose-500 via-red-400 to-orange-300",
                                )}
                                style={{ width: `${clamp(item.value, 0, 100)}%` }}
                              />
                            </div>
                            <p
                              className={cn(
                                "mt-2 text-[11.5px] font-medium leading-5",
                                isDarkMode ? "text-slate-200" : "text-slate-600",
                              )}
                            >
                              {item.description}
                            </p>
                          </article>
                        ))}
                      </div>
                    </section>

                    <section
                      className={cn(
                        "rounded-[24px] border p-3.5 ",
                        isDarkMode
                          ? "border-white/10 bg-white/[0.065]"
                          : "border-slate-200 bg-white",
                      )}
                    >
                      <div
                        className={cn(
                          "mb-3 flex items-center gap-2 text-sm font-medium",
                          isDarkMode ? "text-white" : "text-slate-900",
                        )}
                      >
                        <ListChecks size={16} /> Ce que je te recommande maintenant
                      </div>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {(premiumCoach.actions || [])
                          .slice(0, 6)
                          .map((action, index) => (
                            <div
                              key={index}
                              className={cn(
                                "flex gap-2 rounded-2xl border p-2.5 text-xs font-medium leading-5",
                                isDarkMode
                                  ? "border-white/10 bg-slate-950/22 text-slate-100"
                                  : "border-slate-200 bg-slate-50 text-slate-600",
                              )}
                            >
                              <span
                                className={cn(
                                  "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-medium",
                                  isDarkMode
                                    ? "bg-violet-400/20 text-violet-100"
                                    : "bg-violet-100 text-violet-700",
                                )}
                              >
                                {index + 1}
                              </span>
                              <span>{action}</span>
                            </div>
                          ))}
                      </div>
                    </section>
                  </div>

                  <section className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
                    {(premiumCoach.executionPhases || []).map((phase) => (
                      <div
                        key={phase.title}
                        className={cn(
                          "rounded-[22px] border p-3.5",
                          coachToneClasses(phase.tone, isDarkMode),
                        )}
                      >
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <Layers size={16} /> {phase.title}
                        </div>
                        <div className="mt-2 space-y-1.5">
                          {(phase.items || []).map((item, index) => (
                            <div
                              key={index}
                              className={cn(
                                "rounded-2xl border px-3 py-2 text-[11px] font-medium leading-4",
                                isDarkMode
                                  ? "border-white/10 bg-slate-950/22 text-slate-100"
                                  : "border-white/70 bg-white/75 text-slate-600",
                              )}
                            >
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </section>

                  <section
                    className={cn(
                      "mt-3 rounded-[26px] border p-3.5 ",
                      isDarkMode
                        ? "border-white/10 bg-white/[0.055]"
                        : "border-slate-200 bg-white/88",
                    )}
                  >
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div
                          className={cn(
                            "flex items-center gap-2 text-sm font-medium",
                            isDarkMode ? "text-white" : "text-slate-900",
                          )}
                        >
                          <FileText size={16} /> Je t’explique les chiffres importants
                        </div>
                        <p
                          className={cn(
                            "mt-1 text-xs font-medium leading-5",
                            isDarkMode ? "text-slate-300" : "text-slate-600",
                          )}
                        >
                          Je transforme chaque chiffre en lecture trader : ce que ça veut dire, ce que je détecte et ce que tu dois appliquer.
                        </p>
                      </div>
                      <span
                        className={cn(
                          "rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]",
                          isDarkMode
                            ? "bg-white/10 text-slate-100"
                            : "bg-slate-100 text-slate-600",
                        )}
                      >
                        {(premiumCoach.metricExplanations || []).length}{" "}
                        statistiques expliquées
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {(premiumCoach.metricExplanations || []).map((card) => (
                        <article
                          key={card.label}
                          className={cn(
                            "rounded-[22px] border p-3.5 ",
                            coachToneClasses(card.tone, isDarkMode),
                          )}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <CoachLabelPill tone={card.tone} isDarkMode={isDarkMode}>
                                {card.label}
                              </CoachLabelPill>
                              <div
                                className={cn(
                                  "mt-1 truncate text-lg font-medium tracking-[-0.02em]",
                                  isDarkMode ? "text-white" : "text-slate-900",
                                )}
                              >
                                {card.value}
                              </div>
                            </div>
                            <span
                              className={cn(
                                "rounded-full px-2 py-1 text-[10px] font-semibold",
                                coachBadgeClasses(card.tone, isDarkMode),
                              )}
                            >
                              {card.tone === "emerald"
                                ? "OK"
                                : card.tone === "rose"
                                  ? "Risque"
                                  : L("page.upNext")}
                            </span>
                          </div>
                          <div className="mt-3 space-y-2">
                            {[
                              ["Signification", card.meaning],
                              ["Lecture", card.reading],
                              ["Action", card.action],
                            ].map(([label, text]) => (
                              <div
                                key={label}
                                className={cn(
                                  "rounded-2xl border px-3 py-2",
                                  isDarkMode
                                    ? "border-white/10 bg-slate-950/22"
                                    : "border-white/75 bg-white/72",
                                )}
                              >
                                <div
                                  className={cn(
                                    "text-[9px] font-semibold uppercase tracking-[0.14em]",
                                    isDarkMode
                                      ? "text-slate-400"
                                      : "text-slate-500",
                                  )}
                                >
                                  {label}
                                </div>
                                <div
                                  className={cn(
                                    "mt-0.5 text-[11px] font-medium leading-4",
                                    isDarkMode
                                      ? "text-slate-100"
                                      : "text-slate-600",
                                  )}
                                >
                                  {text}
                                </div>
                              </div>
                            ))}
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>

                  <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                    <section
                      className={cn(
                        "rounded-[24px] border p-3.5",
                        isDarkMode
                          ? "border-emerald-300/18 bg-emerald-400/[0.055]"
                          : "border-emerald-200 bg-emerald-50/80",
                      )}
                    >
                      <div
                        className={cn(
                          "mb-2 flex items-center gap-2 text-sm font-medium",
                          isDarkMode ? "text-emerald-100" : "text-emerald-700",
                        )}
                      >
                        <CheckCircle2 size={16} /> Ce que tu dois continuer
                      </div>
                      <div className="space-y-1.5">
                        {(premiumCoach.strengths || [])
                          .slice(0, 5)
                          .map((item, index) => (
                            <div
                              key={index}
                              className={cn(
                                "rounded-2xl border px-3 py-2 text-xs font-medium leading-5",
                                isDarkMode
                                  ? "border-white/10 bg-slate-950/22 text-slate-100"
                                  : "border-emerald-200 bg-white/80 text-slate-600",
                              )}
                            >
                              {item}
                            </div>
                          ))}
                      </div>
                    </section>

                    <section
                      className={cn(
                        "rounded-[24px] border p-3.5",
                        isDarkMode
                          ? "border-rose-300/18 bg-rose-400/[0.055]"
                          : "border-rose-200 bg-rose-50/80",
                      )}
                    >
                      <div
                        className={cn(
                          "mb-2 flex items-center gap-2 text-sm font-medium",
                          isDarkMode ? "text-rose-100" : "text-rose-700",
                        )}
                      >
                        <AlertTriangle size={16} /> Ce qui te coûte de
                        l’argent
                      </div>
                      <div className="space-y-1.5">
                        {(premiumCoach.problems || [])
                          .slice(0, 5)
                          .map((problem, index) => (
                            <div
                              key={`${problem.title}-${index}`}
                              className={cn(
                                "rounded-2xl border px-3 py-2",
                                coachToneClasses(problem.tone, isDarkMode),
                              )}
                            >
                              <div
                                className={cn(
                                  "text-xs font-medium",
                                  isDarkMode ? "text-white" : "text-slate-900",
                                )}
                              >
                                {problem.title}
                              </div>
                              <div
                                className={cn(
                                  "mt-0.5 text-[11px] font-medium leading-4",
                                  isDarkMode
                                    ? "text-slate-100"
                                    : "text-slate-600",
                                )}
                              >
                                {problem.detail}
                              </div>
                            </div>
                          ))}
                      </div>
                    </section>
                  </div>

                  {(premiumCoach.contextMatrix || []).length > 0 && (
                    <section
                      className={cn(
                        "mt-3 rounded-[24px] border p-3.5",
                        isDarkMode
                          ? "border-white/10 bg-white/[0.055]"
                          : "border-slate-200 bg-white/88",
                      )}
                    >
                      <div
                        className={cn(
                          "mb-3 flex items-center gap-2 text-sm font-medium",
                          isDarkMode ? "text-white" : "text-slate-900",
                        )}
                      >
                        <TrendingUp size={16} /> Où tu es fort / où tu dois éviter de trader
                      </div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {(premiumCoach.contextMatrix || []).map((item) => (
                          <div
                            key={`${item.label}-${item.value}`}
                            className={cn(
                              "rounded-[20px] border p-3.5",
                              coachToneClasses(item.tone, isDarkMode),
                            )}
                          >
                            <CoachLabelPill tone={item.tone} isDarkMode={isDarkMode}>
                              {item.label}
                            </CoachLabelPill>
                            <div
                              className={cn(
                                "mt-1 text-sm font-medium",
                                isDarkMode ? "text-white" : "text-slate-900",
                              )}
                            >
                              {item.value}
                            </div>
                            <div
                              className={cn(
                                "mt-1 text-[11px] font-medium leading-4",
                                isDarkMode
                                  ? "text-slate-100"
                                  : "text-slate-600",
                              )}
                            >
                              {renderAuditRichText(item.detail, isDarkMode, { tone: item.tone || "neutral" })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  <section className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
                    {[
                      {
                        title: L("page.protectionRules"),
                        icon: Clock,
                        items: premiumCoach.hardRules || [],
                      },
                      {
                        title: L("page.entryChecklist"),
                        icon: Check,
                        items: premiumCoach.nextTradeChecklist || [],
                      },
                      {
                        title: L("page.nextTradeRules"),
                        icon: ListChecks,
                        items: premiumCoach.permissionStatus?.conditions || [],
                      },
                    ].map(({ title, icon: Icon, items }) => (
                      <section
                        key={title}
                        className={cn(
                          "rounded-[22px] border p-3 ",
                          isDarkMode
                            ? "border-white/10 bg-white/[0.06]"
                            : "border-slate-200 bg-white",
                        )}
                      >
                        <div
                          className={cn(
                            "flex items-center gap-2 text-xs font-medium",
                            isDarkMode ? "text-white" : "text-slate-900",
                          )}
                        >
                          <Icon size={15} /> {title}
                        </div>
                        <div className="mt-2 space-y-1.5">
                          {(items || []).slice(0, 5).map((item, index) => (
                            <div
                              key={index}
                              className={cn(
                                "rounded-2xl border px-2.5 py-2 text-[11px] font-medium leading-4",
                                isDarkMode
                                  ? "border-white/10 bg-slate-950/22 text-slate-100"
                                  : "border-slate-200 bg-slate-50 text-slate-600",
                              )}
                            >
                              {item}
                            </div>
                          ))}
                        </div>
                      </section>
                    ))}
                  </section>

                  <section
                    className={cn(
                      "mt-3 rounded-[26px] border p-3.5",
                      isDarkMode
                        ? "border-white/10 bg-white/[0.055]"
                        : "border-slate-200 bg-white/90",
                    )}
                  >
                    <div
                      className={cn(
                        "mb-3 flex items-center gap-2 text-sm font-medium",
                        isDarkMode ? "text-white" : "text-slate-900",
                      )}
                    >
                      <Calendar size={16} /> Plan IA sur 7 jours
                    </div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
                      {(premiumCoach.sevenDayPlan || []).map((item) => (
                        <div
                          key={item.day}
                          className={cn(
                            "rounded-[20px] border p-3",
                            isDarkMode
                              ? "border-white/10 bg-slate-950/22"
                              : "border-slate-200 bg-slate-50",
                          )}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span
                              className={cn(
                                "rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.13em]",
                                isDarkMode
                                  ? "bg-violet-400/18 text-violet-100"
                                  : "bg-violet-100 text-violet-700",
                              )}
                            >
                              {item.day}
                            </span>
                            <span
                              className={cn(
                                "text-[10px] font-medium",
                                isDarkMode
                                  ? "text-slate-400"
                                  : "text-slate-500",
                              )}
                            >
                              {item.title}
                            </span>
                          </div>
                          <div
                            className={cn(
                              "mt-2 text-xs font-medium leading-5",
                              isDarkMode ? "text-slate-100" : "text-slate-600",
                            )}
                          >
                            {item.task}
                          </div>
                          <div
                            className={cn(
                              "mt-2 rounded-2xl border px-2.5 py-2 text-[11px] font-medium leading-4",
                              isDarkMode
                                ? "border-white/10 bg-white/[0.04] text-slate-300"
                                : "border-slate-200 bg-white text-slate-600",
                            )}
                          >
                            Mesure : {item.measure}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
                    {[
                      [
                        "Net",
                        `${premiumCoach.net >= 0 ? "+" : ""}${formatPremiumAmount(premiumCoach.net)}`,
                        "net",
                      ],
                      [L("charts.tradesCount"), premiumCoach.total, "trades"],
                      [
                        L("stats.winrate"),
                        `${premiumCoach.winRate}%`,
                        "win rate",
                      ],
                      [
                        "PF",
                        premiumCoach.profitFactor?.toFixed?.(2) ||
                          premiumCoach.profitFactor,
                        "pf",
                      ],
                      [
                        "Expectancy",
                        `${premiumCoach.expectancy >= 0 ? "+" : ""}${formatPremiumAmount(premiumCoach.expectancy)}`,
                        "expectancy",
                      ],
                      [
                        "DD max",
                        `${Math.abs(premiumCoach.maxDDPct || 0).toFixed(2)}%`,
                        "dd max",
                      ],
                      [L("page.noted"), `${premiumCoach.noteRate}%`, "notés"],
                      [
                        L("page.peakPerDay"),
                        premiumCoach.maxDailyTrades,
                        "pic/jour",
                      ],
                      // La clé de ton est explicite : la déduire du libellé
                      // affiché cassait la couleur dès qu'il était traduit.
                    ].map(([label, value, toneKey]) => {
                      const tone = compactAuditMetricTone(toneKey, value, premiumCoach);
                      return (
                        <div
                          key={label}
                          className={cn(
                            "rounded-[18px] border p-3.5 ",
                            coachToneClasses(tone, isDarkMode),
                          )}
                        >
                          <CoachLabelPill tone={tone} isDarkMode={isDarkMode}>
                            {label}
                          </CoachLabelPill>
                          <div
                            className={cn(
                              "mt-1.5 truncate text-sm font-semibold",
                              tone === "emerald"
                                ? "text-emerald-700 dark:text-emerald-100"
                                : tone === "rose"
                                  ? "text-rose-700 dark:text-rose-100"
                                  : tone === "amber"
                                    ? "text-amber-700 dark:text-amber-100"
                                    : isDarkMode
                                      ? "text-white"
                                      : "text-slate-900",
                            )}
                          >
                            {value}
                          </div>
                        </div>
                      );
                    })}
                  </section>
                </main>
              </motion.section>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {noteOpen && (
          <motion.div
            className="fixed inset-y-0 right-0 left-0 z-[100] flex items-end justify-center p-2 transition-[left] duration-150 ease-out sm:items-center sm:p-4 lg:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ left: `${modalSidebarLeft}px` }}
          >
            {/* Fond non cliquable : une note à moitié rédigée se perdait sur un
                clic hors du panneau. On ferme par la croix, Annuler ou Échap. */}
            <motion.div
              className="absolute inset-0 bg-slate-950/58"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <motion.section
              className={cn(
                "relative w-full max-w-[1120px] h-[min(100dvh-0.75rem,980px)] sm:h-[min(100dvh-2rem,940px)] rounded-[26px] sm:rounded-[30px] border  overflow-hidden flex flex-col will-change-transform",
                noteModalSurfaceClass,
              )}
              initial={{ y: 10, scale: 0.995, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 8, scale: 0.995, opacity: 0 }}
              transition={{ duration: 0.09, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <header
                className={cn(
                  "shrink-0 border-b px-4 py-3.5 sm:px-5",
                  noteModalHeaderClass,
                )}
              >
                <div className="flex items-start gap-3 justify-between">
                  <div className="min-w-0">
                    <div className="inline-flex items-center gap-2 rounded-full border border-violet-200/60 dark:border-violet-400/20 bg-violet-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-violet-700 dark:text-violet-300">
                      {L("note.title")}
                    </div>
                    <h3 className="mt-3 text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-white truncate">
                      #{noteFor?.ticket ?? "—"} · {noteFor?.symbol ?? "—"}
                    </h3>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                      {noteFor?.side ? (
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2.5 py-1 border",
                            noteFor.side === "buy"
                              ? "border-emerald-200/70 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 dark:border-emerald-400/20"
                              : "border-rose-200/70 bg-rose-500/10 text-rose-700 dark:text-rose-300 dark:border-rose-400/20",
                          )}
                        >
                          {String(noteFor.side).toUpperCase()}
                        </span>
                      ) : null}
                      {Number.isFinite(noteFor?.profit) ? (
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2.5 py-1 border",
                            noteFor.profit >= 0
                              ? "border-emerald-200/70 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 dark:border-emerald-400/20"
                              : "border-rose-200/70 bg-rose-500/10 text-rose-700 dark:text-rose-300 dark:border-rose-400/20",
                          )}
                        >
                          P/L {fmtMoney(noteFor.profit)}
                        </span>
                      ) : null}
                      {noteFor?.volume ? (
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2.5 py-1 border",
                            noteModalSoftChipClass,
                          )}
                        >
                          Vol {noteFor.volume}
                        </span>
                      ) : null}
                      {noteFor?.open_time || noteFor?.openTime ? (
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2.5 py-1 border",
                            noteModalSoftChipClass,
                          )}
                        >
                          Ouvert{" "}
                          {fmtDT(
                            noteFor?.open_time || noteFor?.openTime,
                            i18n.language,
                          )}
                        </span>
                      ) : null}
                      {noteFor?.close_time || noteFor?.closeTime ? (
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2.5 py-1 border",
                            noteModalSoftChipClass,
                          )}
                        >
                          Fermé{" "}
                          {fmtDT(
                            noteFor?.close_time || noteFor?.closeTime,
                            i18n.language,
                          )}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <BtnIcon
                    ariaLabel={L("note.cancel", "Fermer")}
                    onClick={closeNoteModal}
                    className="rounded-2xl shrink-0"
                  >
                    <X size={18} />
                  </BtnIcon>
                </div>
              </header>

              {noteContentReady ? (
                <div className="flex-1 overflow-hidden bg-slate-50/75 dark:bg-[#08111f]">
                  <div className="grid h-full grid-cols-1 xl:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]">
                  <aside
                    className={cn(
                      "order-1 xl:order-none border-b xl:border-b-0 xl:border-r overflow-y-auto px-4 py-4 sm:px-5",
                      noteModalDark ? "border-white/10" : "border-slate-200/70",
                    )}
                  >
                    <div className="space-y-4">
                      <section
                        className={cn(
                          "rounded-[26px] border p-4 ",
                          noteModalPanelClass,
                        )}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <label className="text-xs font-semibold tracking-wide uppercase text-slate-600 dark:text-white/55">
                            Captures d’écran
                          </label>
                          <span className="text-[11px] text-slate-500 dark:text-white/45">
                            {noteAttachmentCount}
                            /6
                          </span>
                        </div>

                        <input
                          ref={noteFileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            addNoteImages(e.target.files);
                            e.target.value = "";
                          }}
                        />

                        <button
                          type="button"
                          onClick={() => noteFileInputRef.current?.click()}
                          onDragEnter={(e) => {
                            e.preventDefault();
                            setNoteDropActive(true);
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setNoteDropActive(true);
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault();
                            setNoteDropActive(false);
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            setNoteDropActive(false);
                            addNoteImages(e.dataTransfer?.files || []);
                          }}
                          className={cn(
                            "mt-3 w-full rounded-[24px] border border-dashed px-4 py-6 text-left transition-all duration-200 cursor-pointer",
                            noteModalDropZoneClass,
                            noteDropActive
                              ? "border-violet-400/70 ring-2 ring-violet-500/20 "
                              : "border-slate-200/80 dark:border-white/10 hover:border-violet-300/70 dark:hover:border-violet-400/30",
                          )}
                        >
                          <div className="flex flex-col items-center justify-center text-center gap-3 sm:min-h-[180px]">
                            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-500/12 text-violet-600 dark:text-violet-300 shadow-inner">
                              <ImagePlus size={24} />
                            </span>
                            <div>
                              <div className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white">
                                Ajoute une capture à ta note
                              </div>
                              <div className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-white/55">
                                Clique, glisse-dépose ou colle une image depuis
                                le presse-papiers
                              </div>
                            </div>
                          </div>
                        </button>

                        {Array.isArray(deferredNoteAttachments) &&
                        deferredNoteAttachments.length > 0 ? (
                          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3">
                            {deferredNoteAttachments.map((attachment) => (
                              <article
                                key={attachment.id}
                                className={cn(
                                  "group relative overflow-hidden rounded-[22px] border  cursor-zoom-in",
                                  noteModalPanelClass,
                                )}
                                onClick={() => setNotePreview(attachment)}
                              >
                                <img
                                  src={getAttachmentPreviewSrc(attachment)}
                                  alt={attachment.name || "capture"}
                                  className="h-28 sm:h-32 w-full object-cover"
                                  loading="lazy"
                                />
                                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-slate-950/75 via-slate-950/15 to-transparent px-3 py-2 text-[11px] text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                  <span className="truncate pr-2">
                                    Visualiser
                                  </span>
                                  <Eye size={13} />
                                </div>
                                <div className="p-2.5">
                                  <div className="truncate text-[11px] font-medium text-slate-700 dark:text-white/70">
                                    {attachment.name}
                                  </div>
                                  <div className="mt-1 text-[10px] text-slate-500 dark:text-white/40">
                                    {attachment.width}×{attachment.height}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeNoteAttachment(attachment.id);
                                  }}
                                  className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-xl border border-white/50 dark:border-white/10 bg-slate-950/85 text-white transition hover:bg-rose-600"
                                  title="Supprimer"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </article>
                            ))}
                          </div>
                        ) : (
                          <div
                            className={cn(
                              "mt-4 rounded-[22px] border px-4 py-4 text-center text-xs",
                              noteModalEmptyClass,
                            )}
                          >
                            Aucune capture ajoutée pour le moment.
                          </div>
                        )}
                      </section>

                      <section
                        className={cn(
                          "rounded-[26px] border p-4 ",
                          noteModalPanelClass,
                        )}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <label className="text-xs font-semibold tracking-wide uppercase text-slate-600 dark:text-white/55">
                            {L("note.rating")}
                          </label>
                          <div className="flex items-center gap-2">
                            <span
                              className={cn(
                                "inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold",
                                noteModalSoftChipClass,
                              )}
                            >
                              {noteDraft.rating || 0}/5
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setNoteDraft((s) => ({ ...s, rating: 0 }))
                              }
                              className="inline-flex h-9 items-center justify-center rounded-xl border border-slate-200/70 bg-white/70 px-3 text-xs font-medium text-slate-600 transition hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-white/65 dark:hover:bg-white/10"
                            >
                              Réinitialiser
                            </button>
                          </div>
                        </div>
                        <div className="mt-4 grid grid-cols-5 gap-2 sm:gap-3">
                          {[1, 2, 3, 4, 5].map((k) => {
                            const active = (noteDraft.rating || 0) >= k;
                            return (
                              <button
                                key={k}
                                type="button"
                                onClick={() =>
                                  setNoteDraft((s) => ({ ...s, rating: k }))
                                }
                                className={cn(
                                  "group flex h-14 flex-col items-center justify-center rounded-[18px] border transition-all duration-150",
                                  active
                                    ? "border-amber-400/70 bg-amber-400/16 text-amber-600  dark:text-amber-300"
                                    : "border-slate-200/70 dark:border-white/10 bg-white/70 dark:bg-white/5 text-slate-500 dark:text-white/60  hover:bg-white/95 dark:hover:bg-white/10",
                                )}
                                title={`${k}/5`}
                              >
                                <Star
                                  size={18}
                                  className={cn(
                                    "transition-transform duration-150",
                                    active
                                      ? "fill-current scale-105"
                                      : "group-hover:scale-105",
                                  )}
                                />
                                <span className="mt-1 text-[11px] font-semibold">
                                  {k}/5
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </section>
                    </div>
                  </aside>

                  <main className="order-2 overflow-y-auto px-4 py-4 sm:px-5">
                    <div className="space-y-4">
                      <section
                        className={cn(
                          "rounded-[26px] border p-4 ",
                          noteModalPanelClass,
                        )}
                      >
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                          <div className="lg:col-span-2">
                            <label className="text-xs font-semibold tracking-wide uppercase text-slate-600 dark:text-white/55">
                              {L("note.setup")}
                            </label>
                            <input
                              value={noteDraft.setup ?? ""}
                              onChange={(e) =>
                                setNoteDraft((s) => ({
                                  ...s,
                                  setup: e.target.value,
                                }))
                              }
                              placeholder={L(
                                "note.setupPh",
                                L("page.setupPlaceholder"),
                              )}
                              className={cn(
                                baseInput,
                                "mt-2 w-full rounded-2xl min-h-[48px]",
                                noteModalInputClass,
                              )}
                            />
                          </div>

                          <div className="lg:col-span-2">
                            <label className="text-xs font-semibold tracking-wide uppercase text-slate-600 dark:text-white/55">
                              {L("note.tags")}
                            </label>
                            <input
                              value={noteDraft.tags ?? ""}
                              onChange={(e) =>
                                setNoteDraft((s) => ({
                                  ...s,
                                  tags: e.target.value,
                                }))
                              }
                              placeholder={L(
                                "note.tagsPh",
                                "Ex: breakout, london, news, scalp",
                              )}
                              className={cn(
                                baseInput,
                                "mt-2 w-full rounded-2xl min-h-[48px]",
                                noteModalInputClass,
                              )}
                            />
                            <div className="mt-1 text-[11px] text-slate-500 dark:text-white/50">
                              {L(
                                "note.tagsHelp",
                                L("page.tagsHint"),
                              )}
                            </div>
                            <div className="mt-3 space-y-3">
                              <div>
                                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-white/45">
                                  Tags rapides
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {allNoteTagPresets.map((tag) => {
                                    const active = noteTagsArray.some(
                                      (item) =>
                                        item.toLowerCase() ===
                                        tag.toLowerCase(),
                                    );
                                    const isCustom = customTagPresets.some(
                                      (item) =>
                                        item.toLowerCase() ===
                                        tag.toLowerCase(),
                                    );
                                    return (
                                      <span
                                        key={tag}
                                        className={cn(
                                          "inline-flex items-center rounded-full border transition-all duration-150 overflow-hidden",
                                          active
                                            ? "border-violet-400/65 bg-violet-500/14 text-violet-700  dark:text-violet-300"
                                            : "border-slate-200/70 bg-white/70 text-slate-600 hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10",
                                        )}
                                      >
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setNoteDraft((s) => ({
                                              ...s,
                                              tags: toggleTagString(
                                                s.tags,
                                                tag,
                                              ),
                                            }))
                                          }
                                          className="px-3 py-1.5 text-xs font-medium"
                                        >
                                          {tag}
                                        </button>
                                        {isCustom ? (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              removeCustomTagPreset(tag)
                                            }
                                            className="border-l border-black/5 px-2 py-1.5 text-slate-500 transition hover:bg-rose-500/12 hover:text-rose-600 dark:border-white/10 dark:text-white/50 dark:hover:text-rose-300"
                                            title={L("page.deletePreset")}
                                          >
                                            <X size={12} />
                                          </button>
                                        ) : null}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>

                              <div className="rounded-[22px] border border-slate-200/80 bg-white/70 p-3  dark:border-white/10 dark:bg-white/[0.04]">
                                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-white/45">
                                  Créer un tag prédéfini
                                </div>
                                <div className="flex flex-col gap-2 sm:flex-row">
                                  <input
                                    value={newTagPreset}
                                    onChange={(e) =>
                                      setNewTagPreset(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        addCustomTagPreset();
                                      }
                                    }}
                                    placeholder="Ex: reversal, NY open, macro"
                                    className={cn(
                                      baseInput,
                                      "min-h-[46px] flex-1 rounded-2xl",
                                      noteModalInputClass,
                                    )}
                                  />
                                  <BtnOutline
                                    type="button"
                                    onClick={addCustomTagPreset}
                                    className="rounded-2xl px-4 py-2 justify-center"
                                  >
                                    Ajouter
                                  </BtnOutline>
                                </div>
                                <div className="mt-2 text-[11px] text-slate-500 dark:text-white/45">
                                  Les tags créés ici restent disponibles pour
                                  tes prochaines notes.
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="lg:col-span-2">
                            <label className="text-xs font-semibold tracking-wide uppercase text-slate-600 dark:text-white/55">
                              {L("note.comment")}
                            </label>
                            <textarea
                              value={noteDraft.comment ?? ""}
                              onChange={(e) =>
                                setNoteDraft((s) => ({
                                  ...s,
                                  comment: e.target.value,
                                }))
                              }
                              placeholder={L(
                                "note.commentPh",
                                L("page.commentPlaceholder"),
                              )}
                              rows={10}
                              className={cn(
                                baseInput,
                                "mt-2 w-full rounded-2xl resize-y min-h-[220px] sm:min-h-[280px]",
                                noteModalInputClass,
                              )}
                            />
                          </div>
                        </div>
                      </section>
                    </div>
                  </main>
                </div>
                </div>
              ) : (
                <div className="flex flex-1 items-center justify-center bg-slate-50/75 px-4 py-8 text-center dark:bg-[#08111f]">
                  <div className="w-full max-w-sm rounded-[24px] border border-slate-200 bg-white p-5  dark:border-white/10 dark:bg-[#101827]">
                    <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-500/10 text-violet-600 dark:text-violet-300">
                      <Pencil size={18} />
                    </div>
                    <div className="mt-3 text-sm font-semibold text-slate-900 dark:text-white">
                      Ouverture de la note
                    </div>
                    <div className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                      Préparation rapide du formulaire et des captures.
                    </div>
                  </div>
                </div>
              )}

              <AnimatePresence>
                {notePreview && (
                  <motion.div
                    className="absolute inset-0 z-[5] flex items-center justify-center bg-slate-950/88 p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setNotePreview(null)}
                  >
                    <motion.div
                      className="relative max-h-full w-full max-w-4xl overflow-hidden rounded-[28px] border border-white/10 bg-[#0b1222] "
                      initial={{ scale: 0.96, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.98, opacity: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 260,
                        damping: 26,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-slate-950/35 px-4 py-3 text-white/80">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">
                            {notePreview.name || "Capture"}
                          </div>
                          <div className="text-xs text-white/45">
                            {notePreview.width}×{notePreview.height}
                          </div>
                        </div>
                        <BtnIcon
                          ariaLabel={L("page.closePreview")}
                          onClick={() => setNotePreview(null)}
                          className="rounded-2xl border-white/10 bg-white/10 text-white hover:bg-white/15"
                        >
                          <X size={18} />
                        </BtnIcon>
                      </div>
                      <div className="flex max-h-[75dvh] items-center justify-center bg-[#060b16] p-3 sm:p-4">
                        <img
                          src={getAttachmentPreviewSrc(notePreview)}
                          alt={notePreview.name || "capture"}
                          className="max-h-[70dvh] w-auto max-w-full rounded-2xl object-contain"
                        />
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              <footer
                className={cn(
                  "shrink-0 border-t px-4 py-3.5 sm:px-5",
                  noteModalHeaderClass,
                )}
              >
                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div
                    className={cn(
                      "text-xs",
                      noteModalDark ? "text-slate-400" : "text-slate-500",
                    )}
                  >
                    Les notes, tags et captures restent liées à ce trade pour
                    tes futures analyses.
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <BtnOutline
                      onClick={closeNoteModal}
                      className="rounded-2xl px-4 py-2 w-full sm:w-auto justify-center"
                    >
                      {L("note.cancel")}
                    </BtnOutline>
                    <BtnPrimary
                      onClick={saveNote}
                      className="rounded-2xl px-4 py-2 w-full sm:w-auto justify-center"
                      disabled={!noteFor?.ticket}
                    >
                      <Save size={16} /> {L("note.save")}
                    </BtnPrimary>
                  </div>
                </div>
              </footer>
            </motion.section>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
